"use strict";

import Bitstring from "/app/deps/hologram/assets/js/bitstring.mjs";
import ERTS from "/app/deps/hologram/assets/js/erts.mjs";
import Hologram from "/app/deps/hologram/assets/js/hologram.mjs";
import HologramBoxedError from "/app/deps/hologram/assets/js/errors/boxed_error.mjs";
import HologramInterpreterError from "/app/deps/hologram/assets/js/errors/interpreter_error.mjs";
import Interpreter from "/app/deps/hologram/assets/js/interpreter.mjs";
import MemoryStorage from "/app/deps/hologram/assets/js/memory_storage.mjs";
import PerformanceTimer from "/app/deps/hologram/assets/js/performance_timer.mjs";
import Type from "/app/deps/hologram/assets/js/type.mjs";
import Utils from "/app/deps/hologram/assets/js/utils.mjs";

const startTime = PerformanceTimer.start();

Interpreter.defineNotImplementedErlangFunction(
	"application",
	"get_application",
	1,
);

Interpreter.defineNotImplementedErlangFunction("application", "get_key", 2);

Interpreter.defineErlangFunction(
	"binary",
	"_aho_corasick_search",
	3,
	(subject, rootNode, startIndex) => {
		Bitstring.maybeSetBytesFromText(subject);

		let candidateNode = rootNode;
		let bestMatch = null;

		for (let index = startIndex; index < subject.bytes.length; index++) {
			const byte = subject.bytes[index];

			// Follow failure links until we find a matching transition
			while (candidateNode !== null && !candidateNode.children.has(byte)) {
				candidateNode = candidateNode.failure;
			}

			// Transition to next state or reset to root
			candidateNode = candidateNode
				? candidateNode.children.get(byte) || rootNode
				: rootNode;

			// Check if current state has any pattern matches
			if (candidateNode.output.length > 0) {
				// Check all matches at this position and keep the longest one
				for (const matchedPattern of candidateNode.output) {
					const matchedPatternLength = matchedPattern.length;
					const matchIndex = index - matchedPatternLength + 1;

					// Update best match if this is first match or a longer match at same/earlier position
					if (
						bestMatch === null ||
						matchIndex < bestMatch.index ||
						(matchIndex === bestMatch.index &&
							matchedPatternLength > bestMatch.length)
					) {
						bestMatch = { index: matchIndex, length: matchedPatternLength };
					}
				}
			}

			// Check if we should return the current best match
			// Return when we have a match AND we're past the match end AND we're not matching any pattern
			// (candidateNode == rootNode means we're not in the middle of matching)
			if (
				bestMatch !== null &&
				index >= bestMatch.index + bestMatch.length &&
				candidateNode === rootNode
			) {
				return bestMatch;
			}
		}

		return bestMatch;
	},
);

Interpreter.defineErlangFunction(
	"binary",
	"_boyer_moore_search",
	4,
	(subject, patternBytes, badShift, startIndex) => {
		Bitstring.maybeSetBytesFromText(subject);

		const patternLength = patternBytes.length;
		const patternMaxIndex = patternLength - 1;
		const searchLimit = subject.bytes.length - patternLength;

		for (let index = startIndex; index <= searchLimit; index++) {
			// Compare pattern from right to left
			let patternIndex = patternMaxIndex;

			while (
				patternIndex >= 0 &&
				patternBytes[patternIndex] === subject.bytes[index + patternIndex]
			) {
				// Full match found
				if (patternIndex === 0) {
					return { index, length: patternLength };
				}

				patternIndex--;
			}

			// No match - use bad character shift to skip ahead
			const currentByte = subject.bytes[index + patternMaxIndex];
			const shiftValue = badShift[currentByte];
			const shift = shiftValue >= 0 ? shiftValue : patternLength;

			if (shift > 0) {
				index += shift - 1; // -1 because loop will increment
			}
		}

		return null;
	},
);

Interpreter.defineErlangFunction(
	"binary",
	"_parse_search_opts",
	2,
	(opts, argPosition) => {
		const raiseInvalidOptions = () => {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(argPosition, "invalid options"),
			);
		};

		if (!Type.isList(opts) || Type.isImproperList(opts)) {
			raiseInvalidOptions();
		}

		let global = false;
		let trim = false;
		let trimAll = false;
		let scopeStart = 0;
		let scopeLength = null; // null means "until end"

		opts.data.forEach((option) => {
			if (Type.isAtom(option)) {
				if (option.value === "global") {
					global = true;
					return;
				}

				if (option.value === "trim") {
					trim = true;
					return;
				}

				if (option.value === "trim_all") {
					trimAll = true;
					return;
				}

				raiseInvalidOptions();
			}

			const isScopeTuple =
				Type.isTuple(option) &&
				option.data.length === 2 &&
				Type.isAtom(option.data[0]) &&
				option.data[0].value === "scope";

			if (!isScopeTuple) {
				raiseInvalidOptions();
			}

			const scopeData = option.data[1];

			const isValidScope =
				Type.isTuple(scopeData) &&
				scopeData.data.length === 2 &&
				Type.isInteger(scopeData.data[0]) &&
				Type.isInteger(scopeData.data[1]);

			if (!isValidScope) {
				raiseInvalidOptions();
			}

			const startValue = scopeData.data[0].value;
			const lengthValue = scopeData.data[1].value;

			if (startValue < 0n) {
				raiseInvalidOptions();
			}

			scopeStart = Number(startValue);
			scopeLength = Number(lengthValue);
		});

		return { global, trim, trimAll, scopeStart, scopeLength };
	},
);

Interpreter.defineErlangFunction("binary", "compile_pattern", 1, (pattern) => {
	const raiseInvalidPattern = () => {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not a valid pattern"),
		);
	};

	const compileBoyerMoorePattern = (singlePattern) => {
		Bitstring.maybeSetBytesFromText(singlePattern);

		if (singlePattern.bytes.length == 0) {
			raiseInvalidPattern();
		}

		const badShift = {};
		const length = singlePattern.bytes.length - 1;
		const patternBytes = singlePattern.bytes;

		// Seed the badShift object with an initial value of -1 for each byte
		for (let i = 0; i < 256; i++) {
			badShift[i] = -1;
		}

		// Overwrite with the actual value for each byte in the pattern
		singlePattern.bytes.forEach((byte, index) => {
			badShift[byte] = length - index;
		});

		const ref = Erlang["make_ref/0"]();
		const compiledPatternData = { type: "bm", badShift, patternBytes };
		ERTS.binaryPatternRegistry.put(ref, compiledPatternData);

		return Type.tuple([Type.atom("bm"), ref]);
	};

	const compileAhoCorasickPattern = (patterns) => {
		const rootNode = {
			children: new Map(),
			output: [],
			failure: null,
		};

		// Build tries for each pattern
		patterns.data.forEach((p) => {
			Bitstring.maybeSetBytesFromText(p);

			if (p.bytes.length === 0) {
				raiseInvalidPattern();
			}

			let node = rootNode;

			p.bytes.forEach((byte) => {
				if (!node.children.has(byte)) {
					node.children.set(byte, {
						children: new Map(),
						output: [],
						failure: null,
					});
				}

				node = node.children.get(byte);
			});

			node.output.push(p.bytes);
		});

		// Build failure links (where to fall back when a match fails)
		const queue = [];

		for (const [_byte, childNode] of rootNode.children) {
			childNode.failure = rootNode;
			queue.push(childNode);
		}

		while (queue.length > 0) {
			const node = queue.shift();

			for (const [byte, childNode] of node.children) {
				queue.push(childNode);

				let failureNode = node.failure;

				while (failureNode !== null && !failureNode.children.has(byte)) {
					failureNode = failureNode.failure;
				}

				childNode.failure =
					failureNode === null ? rootNode : failureNode.children.get(byte);

				childNode.output = childNode.output.concat(childNode.failure.output);
			}
		}

		const ref = Erlang["make_ref/0"]();
		const compiledPatternData = { type: "ac", rootNode };
		ERTS.binaryPatternRegistry.put(ref, compiledPatternData);

		return Type.tuple([Type.atom("ac"), ref]);
	};

	if (Type.isBinary(pattern)) {
		return compileBoyerMoorePattern(pattern);
	} else if (
		Type.isList(pattern) &&
		pattern.data.length > 0 &&
		pattern.data.every((i) => Type.isBinary(i))
	) {
		return pattern.data.length == 1
			? compileBoyerMoorePattern(pattern.data[0])
			: compileAhoCorasickPattern(pattern);
	}

	raiseInvalidPattern();
});

Interpreter.defineErlangFunction(
	"binary",
	"match",
	3,
	(subject, pattern, options) => {
		if (!Type.isBinary(subject)) {
			const msg = Type.isBitstring(subject)
				? "is a bitstring (expected a binary)"
				: "not a binary";

			Interpreter.raiseArgumentError(Interpreter.buildArgumentErrorMsg(1, msg));
		}

		// Ensure subject bytes are available after validation
		Bitstring.maybeSetBytesFromText(subject);

		// Parse options and reject unsupported flags for match/3
		const { global, trim, trimAll, scopeStart, scopeLength } = Erlang_Binary[
			"_parse_search_opts/2"
		](options, 3);

		// match/3 only supports :scope; reject :global, :trim, and :trim_all
		// Validate scope start is within subject bounds
		if (global || trim || trimAll || scopeStart > subject.bytes.length) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(3, "invalid options"),
			);
		}

		// Validate that if scopeLength is specified, scopeStart + scopeLength >= 0
		if (scopeLength !== null && scopeStart + scopeLength < 0) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(3, "invalid options"),
			);
		}

		const effectiveLength =
			scopeLength === null ? subject.bytes.length - scopeStart : scopeLength;

		// Validate scope doesn't extend beyond subject
		if (scopeStart + effectiveLength > subject.bytes.length) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(3, "invalid options"),
			);
		}

		const scopeEnd = scopeStart + effectiveLength;

		// For negative scopeLength, ensure slice bounds are in correct order
		const actualStart = Math.min(scopeStart, scopeEnd);
		const actualEnd = Math.max(scopeStart, scopeEnd);

		// Validate pattern before checking scope length - pattern errors take priority
		const isCompiledPattern = Type.isCompiledPattern(pattern);

		let compiledPattern;

		try {
			compiledPattern = isCompiledPattern
				? pattern
				: Erlang_Binary["compile_pattern/1"](pattern);
		} catch (error) {
			// Re-raise pattern compilation errors with correct argument position
			if (error.struct) {
				Interpreter.raiseArgumentError(
					Interpreter.buildArgumentErrorMsg(2, "not a valid pattern"),
				);
			}

			throw error;
		}

		const patternType = compiledPattern.data[0].value;
		const patternRef = compiledPattern.data[1];
		const compiledData = ERTS.binaryPatternRegistry.get(patternRef);

		if (!compiledData || compiledData.type !== patternType) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(2, "not a valid pattern"),
			);
		}

		// After pattern validation passes, check if search range is available
		if (scopeLength === 0) {
			return Type.atom("nomatch");
		}

		const scopedBytes = subject.bytes.slice(actualStart, actualEnd);
		const scopedSubject = Bitstring.fromBytes(scopedBytes);

		// Find first pattern match based on algorithm type
		let match = null;

		if (patternType === "bm") {
			const patternBytes = compiledData.patternBytes;

			if (!patternBytes) {
				Interpreter.raiseArgumentError(
					Interpreter.buildArgumentErrorMsg(2, "not a valid pattern"),
				);
			}

			match = Erlang_Binary["_boyer_moore_search/4"](
				scopedSubject,
				patternBytes,
				compiledData.badShift,
				0,
			);
		} else {
			match = Erlang_Binary["_aho_corasick_search/3"](
				scopedSubject,
				compiledData.rootNode,
				0,
			);
		}

		if (match === null) {
			return Type.atom("nomatch");
		}

		// Convert match position from scoped to absolute
		const absolutePos = actualStart + match.index;

		return Type.tuple([Type.integer(absolutePos), Type.integer(match.length)]);
	},
);

Interpreter.defineErlangFunction("binary", "split", 2, (subject, pattern) => {
	return Erlang_Binary["split/3"](subject, pattern, Type.list());
});

Interpreter.defineErlangFunction(
	"binary",
	"split",
	3,
	(subject, pattern, options) => {
		// Helper: Apply trimming options to the split parts
		const applyTrim = (parts) => {
			if (parts.length === 0) {
				return parts;
			}

			if (trimAll) {
				return parts.filter((part) => !Bitstring.isEmpty(part));
			}

			if (!trim) {
				return parts;
			}

			let end = parts.length;
			while (end > 0 && Bitstring.isEmpty(parts[end - 1])) {
				end--;
			}

			return parts.slice(0, end);
		};

		// Helper: Convert byte slice to bitstring (text-based if valid UTF-8)
		const bytesToBitstring = (bytes) => {
			try {
				const text = ERTS.utf8Decoder.decode(bytes);
				return Bitstring.fromText(text);
			} catch {
				return Bitstring.fromBytes(bytes);
			}
		};

		// Validate subject is a binary
		if (!Type.isBinary(subject)) {
			const msg = Type.isBitstring(subject)
				? "is a bitstring (expected a binary)"
				: "not a binary";

			Interpreter.raiseArgumentError(Interpreter.buildArgumentErrorMsg(1, msg));
		}

		// Ensure subject bytes are available after validation
		Bitstring.maybeSetBytesFromText(subject);

		const { global, trim, trimAll, scopeStart, scopeLength } = Erlang_Binary[
			"_parse_search_opts/2"
		](options, 3);

		// Validate scope start is within subject bounds
		if (scopeStart > subject.bytes.length) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(3, "invalid options"),
			);
		}

		// Validate that if scopeLength is specified, scopeStart + scopeLength >= 0
		if (scopeLength !== null && scopeStart + scopeLength < 0) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(3, "invalid options"),
			);
		}

		// Validate pattern before compiling (to raise with correct arg position)
		const raiseInvalidPattern = () => {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(2, "not a valid pattern"),
			);
		};

		const isCompiledPattern = Type.isCompiledPattern(pattern);

		if (!isCompiledPattern) {
			const isValidBinary = Type.isBinary(pattern);

			const isValidList =
				Type.isList(pattern) &&
				pattern.data.length > 0 &&
				pattern.data.every((p) => Type.isBinary(p));

			// Check if pattern is valid before compiling
			if (!isValidBinary && !isValidList) {
				raiseInvalidPattern();
			}

			// Check for empty patterns
			if (isValidBinary) {
				Bitstring.maybeSetBytesFromText(pattern);

				if (pattern.bytes.length === 0) {
					raiseInvalidPattern();
				}
			} else if (isValidList) {
				for (const p of pattern.data) {
					Bitstring.maybeSetBytesFromText(p);

					if (p.bytes.length === 0) {
						raiseInvalidPattern();
					}
				}
			}
		}

		// Pre-compile pattern once before loop to avoid recompilation on each match
		const compiledPattern = isCompiledPattern
			? pattern
			: Erlang_Binary["compile_pattern/1"](pattern);

		const effectiveLength =
			scopeLength === null ? subject.bytes.length - scopeStart : scopeLength;

		// Validate scope doesn't extend beyond subject
		if (scopeStart + effectiveLength > subject.bytes.length) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(3, "invalid options"),
			);
		}

		const scopeEnd = scopeStart + effectiveLength;

		// For negative scopeLength, ensure slice bounds are in correct order
		const actualStart = Math.min(scopeStart, scopeEnd);
		const actualEnd = Math.max(scopeStart, scopeEnd);

		// No search range available - return unsplit subject
		if (actualStart >= subject.bytes.length) {
			const parts = [bytesToBitstring(subject.bytes)];
			return Type.list(applyTrim(parts));
		}

		// Main split logic using match/3

		const results = [];
		let cursor = 0; // position in full subject where next segment starts
		let searchPos = actualStart; // current search position
		let foundMatch = true; // track if loop exited naturally vs via break

		while (searchPos < actualEnd) {
			const remainingLength = actualEnd - searchPos;

			const matchOptions = Type.list([
				Type.tuple([
					Type.atom("scope"),
					Type.tuple([Type.integer(searchPos), Type.integer(remainingLength)]),
				]),
			]);

			// Use match/3 to find next occurrence with pre-compiled pattern
			const matchResult = Erlang_Binary["match/3"](
				subject,
				compiledPattern,
				matchOptions,
			);

			// No more matches found
			if (Type.isAtom(matchResult) && matchResult.value === "nomatch") {
				const remaining =
					cursor < subject.bytes.length
						? bytesToBitstring(subject.bytes.slice(cursor))
						: Bitstring.fromText("");

				results.push(remaining);
				foundMatch = false;

				break;
			}

			// Extract match position and length
			const matchPos = matchResult.data[0].value;
			const matchLen = matchResult.data[1].value;
			const matchStart = Number(matchPos);
			const matchLength = Number(matchLen);

			// Add part before match (if any)
			if (matchStart > cursor) {
				const beforeMatch = bytesToBitstring(
					subject.bytes.slice(cursor, matchStart),
				);

				results.push(beforeMatch);
			} else if (matchStart === cursor) {
				// Empty part before match
				results.push(Bitstring.fromText(""));
			}

			// Update cursor to position after match
			cursor = matchStart + matchLength;
			searchPos = cursor;

			if (!global) {
				// For non-global split, append remaining and stop
				const remaining =
					cursor < subject.bytes.length
						? bytesToBitstring(subject.bytes.slice(cursor))
						: Bitstring.fromText("");

				results.push(remaining);
				foundMatch = false;

				break;
			}
		}

		// Collect any remaining bytes after the last match (for global splits that exited naturally)
		if (global && foundMatch && cursor < subject.bytes.length) {
			const remaining = bytesToBitstring(subject.bytes.slice(cursor));
			results.push(remaining);
		}

		// Add trailing empty when global split ends at subject boundary
		if (global && cursor === subject.bytes.length && results.length > 0) {
			results.push(Bitstring.fromText(""));
		}

		if (results.length === 0) {
			results.push(bytesToBitstring(subject.bytes));
		}

		const trimmedResults = applyTrim(results);

		return Type.list(trimmedResults);
	},
);

Interpreter.defineErlangFunction("code", "ensure_loaded", 1, function (module) {
	if (!Type.isAtom(module)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(
				":code.ensure_loaded/1",
				arguments,
			),
		);
	}

	return typeof Interpreter.moduleProxy(module) === "undefined"
		? Type.tuple([Type.atom("error"), Type.atom("nofile")])
		: Type.tuple([Type.atom("module"), module]);
});

Interpreter.defineNotImplementedErlangFunction("crypto", "hash_final", 1);

Interpreter.defineNotImplementedErlangFunction("crypto", "hash_init", 1);

Interpreter.defineNotImplementedErlangFunction("crypto", "hash_update", 2);

Interpreter.defineNotImplementedErlangFunction("elixir_config", "get", 2);

Interpreter.defineNotImplementedErlangFunction(
	"elixir_config",
	"identifier_tokenizer",
	0,
);

Interpreter.defineNotImplementedErlangFunction(
	"elixir_module",
	"compiler_modules",
	0,
);

Interpreter.defineErlangFunction("erlang", "*", 2, (left, right) => {
	if (!Type.isNumber(left) || !Type.isNumber(right)) {
		const blame = `${Interpreter.inspect(left)} * ${Interpreter.inspect(right)}`;
		Interpreter.raiseArithmeticError(blame);
	}

	const [type, leftValue, rightValue] = Type.maybeNormalizeNumberTerms(
		left,
		right,
	);

	const result = leftValue.value * rightValue.value;

	return type === "float" ? Type.float(result) : Type.integer(result);
});

Interpreter.defineErlangFunction("erlang", "+", 2, (left, right) => {
	if (!Type.isNumber(left) || !Type.isNumber(right)) {
		const blame = `${Interpreter.inspect(left)} + ${Interpreter.inspect(right)}`;
		Interpreter.raiseArithmeticError(blame);
	}

	const [type, leftValue, rightValue] = Type.maybeNormalizeNumberTerms(
		left,
		right,
	);

	const result = leftValue.value + rightValue.value;

	return type === "float" ? Type.float(result) : Type.integer(result);
});

Interpreter.defineErlangFunction("erlang", "++", 2, (left, right) => {
	if (!Type.isProperList(left)) {
		Interpreter.raiseArgumentError("argument error");
	}

	const data = left.data.concat(Type.isList(right) ? right.data : [right]);

	return Type.isProperList(right) ? Type.list(data) : Type.improperList(data);
});

Interpreter.defineErlangFunction("erlang", "-", 1, (number) => {
	if (!Type.isNumber(number)) {
		const blame = `-(${Interpreter.inspect(number)})`;
		Interpreter.raiseArithmeticError(blame);
	}

	return number.value == 0 ? number : Type[number.type](-number.value);
});

Interpreter.defineErlangFunction("erlang", "-", 2, (left, right) => {
	if (!Type.isNumber(left) || !Type.isNumber(right)) {
		const blame = `${Interpreter.inspect(left)} - ${Interpreter.inspect(right)}`;
		Interpreter.raiseArithmeticError(blame);
	}

	const [type, leftValue, rightValue] = Type.maybeNormalizeNumberTerms(
		left,
		right,
	);

	const result = leftValue.value - rightValue.value;

	return type === "float" ? Type.float(result) : Type.integer(result);
});

Interpreter.defineErlangFunction("erlang", "/", 2, (left, right) => {
	if (!Type.isNumber(left) || !Type.isNumber(right) || right.value == 0) {
		const blame = `${Interpreter.inspect(left)} / ${Interpreter.inspect(right)}`;
		Interpreter.raiseArithmeticError(blame);
	}

	return Type.float(Number(left.value) / Number(right.value));
});

Interpreter.defineErlangFunction("erlang", "/=", 2, (left, right) => {
	return Type.boolean(!Interpreter.isEqual(left, right));
});

Interpreter.defineErlangFunction("erlang", "<", 2, (left, right) => {
	return Type.boolean(Interpreter.compareTerms(left, right) === -1);
});

Interpreter.defineErlangFunction("erlang", "=/=", 2, (left, right) => {
	return Type.boolean(!Interpreter.isStrictlyEqual(left, right));
});

Interpreter.defineErlangFunction("erlang", "=:=", 2, (left, right) => {
	return Type.boolean(Interpreter.isStrictlyEqual(left, right));
});

Interpreter.defineErlangFunction("erlang", "=<", 2, (left, right) => {
	Interpreter.assertStructuralComparisonSupportedType(left);
	Interpreter.assertStructuralComparisonSupportedType(right);

	const result =
		Type.isTrue(Erlang["==/2"](left, right)) ||
		Type.isTrue(Erlang["</2"](left, right));

	return Type.boolean(result);
});

Interpreter.defineErlangFunction("erlang", "==", 2, (left, right) => {
	return Type.boolean(Interpreter.isEqual(left, right));
});

Interpreter.defineErlangFunction("erlang", ">", 2, (left, right) => {
	return Type.boolean(Interpreter.compareTerms(left, right) === 1);
});

Interpreter.defineErlangFunction("erlang", ">=", 2, (left, right) => {
	Interpreter.assertStructuralComparisonSupportedType(left);
	Interpreter.assertStructuralComparisonSupportedType(right);

	const result =
		Type.isTrue(Erlang["==/2"](left, right)) ||
		Type.isTrue(Erlang[">/2"](left, right));

	return Type.boolean(result);
});

Interpreter.defineErlangFunction(
	"erlang",
	"_validate_time_unit",
	2,
	(unit, argumentIndex) => {
		const validAtomUnits = [
			"nanosecond",
			"nano_seconds",
			"microsecond",
			"micro_seconds",
			"millisecond",
			"milli_seconds",
			"second",
			"seconds",
			"native",
			"perf_counter",
		];

		if (
			!(Type.isAtom(unit) && validAtomUnits.includes(unit.value)) &&
			!(Type.isInteger(unit) && unit.value > 0n)
		) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(argumentIndex, "invalid time unit"),
			);
		}
	},
);

Interpreter.defineErlangFunction("erlang", "abs", 1, (number) => {
	if (Type.isFloat(number)) {
		return Type.float(Math.abs(number.value));
	} else if (Type.isInteger(number)) {
		const value = number.value;
		return Type.integer(value < 0n ? -value : value);
	}

	Interpreter.raiseArgumentError(
		Interpreter.buildArgumentErrorMsg(1, "not a number"),
	);
});

Interpreter.defineErlangFunction(
	"erlang",
	"andalso",
	2,
	(leftFun, rightFun, context) => {
		const left = leftFun(context);

		if (!Type.isBoolean(left)) {
			Interpreter.raiseArgumentError(
				`argument error: ${Interpreter.inspect(left)}`,
			);
		}

		return Type.isTrue(left) ? rightFun(context) : left;
	},
);

Interpreter.defineErlangFunction("erlang", "apply", 3, (module, fun, args) => {
	if (!Type.isAtom(module)) {
		Interpreter.raiseArgumentError(
			`you attempted to apply a function named ${Interpreter.inspect(fun)} on ${Interpreter.inspect(module)}. If you are using Kernel.apply/3, make sure the module is an atom. If you are using the dot syntax, such as module.function(), make sure the left-hand side of the dot is an atom representing a module`,
		);
	}

	if (!Type.isAtom(fun)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(2, "not an atom"),
		);
	}

	if (!Type.isList(args)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(3, "not a list"),
		);
	}

	if (!Type.isProperList(args)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(3, "not a proper list"),
		);
	}

	const context = Interpreter.buildContext({ module: Type.nil() });

	return Interpreter.callNamedFunction(module, fun, args, context);
});

Interpreter.defineErlangFunction("erlang", "atom_to_binary", 1, (atom) => {
	return Erlang["atom_to_binary/2"](atom, Type.atom("utf8"));
});

Interpreter.defineErlangFunction(
	"erlang",
	"atom_to_binary",
	2,
	(atom, encoding) => {
		if (!Type.isAtom(atom)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(1, "not an atom"),
			);
		}

		// TODO: implement encoding argument validation

		// TODO: implement other encodings for encoding param
		if (!Interpreter.isStrictlyEqual(encoding, Type.atom("utf8"))) {
			throw new HologramInterpreterError(
				"encodings other than utf8 are not yet implemented in Hologram",
			);
		}

		return Type.bitstring(atom.value);
	},
);

Interpreter.defineErlangFunction("erlang", "atom_to_list", 1, (atom) => {
	if (!Type.isAtom(atom)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not an atom"),
		);
	}

	return Bitstring.toCodepoints(Type.bitstring(atom.value));
});

Interpreter.defineErlangFunction(
	"erlang",
	"binary_part",
	3,
	(subject, start, length) => {
		if (!Type.isBinary(subject)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(1, "not a binary"),
			);
		}

		if (!Type.isInteger(start)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(2, "not an integer"),
			);
		}

		if (!Type.isInteger(length)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(3, "not an integer"),
			);
		}

		const totalBytes = Bitstring.calculateBitCount(subject) / 8;

		if (start.value < 0n || start.value > totalBytes) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(2, "out of range"),
			);
		}

		const isReverse = length.value < 0n;

		const outOfRangeForward =
			!isReverse && start.value + length.value > totalBytes;

		const outOfRangeReverse = isReverse && start.value + length.value < 0n;

		if (outOfRangeForward || outOfRangeReverse) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(3, "out of range"),
			);
		}

		const actualStart = isReverse ? start.value + length.value : start.value;
		const actualLength = isReverse ? -length.value : length.value;

		return Bitstring.takeChunk(
			subject,
			Number(actualStart) * 8,
			Number(actualLength) * 8,
		);
	},
);

Interpreter.defineErlangFunction("erlang", "binary_to_atom", 1, (binary) => {
	return Erlang["binary_to_atom/2"](binary, Type.atom("utf8"));
});

Interpreter.defineErlangFunction(
	"erlang",
	"binary_to_atom",
	2,
	(binary, encoding) => {
		if (!Type.isBinary(binary)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(1, "not a binary"),
			);
		}

		// TODO: implement encoding argument validation

		// TODO: implement other encodings for encoding param
		if (!Interpreter.isStrictlyEqual(encoding, Type.atom("utf8"))) {
			throw new HologramInterpreterError(
				"encodings other than utf8 are not yet implemented in Hologram",
			);
		}

		return Type.atom(Bitstring.toText(binary));
	},
);

Interpreter.defineErlangFunction("erlang", "byte_size", 1, (bitstring) => {
	if (!Type.isBitstring(bitstring)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not a bitstring"),
		);
	}

	Bitstring.maybeSetBytesFromText(bitstring);

	return Type.integer(bitstring.bytes.length);
});

Interpreter.defineNotImplementedErlangFunction("erlang", "cancel_timer", 2);

Interpreter.defineErlangFunction(
	"erlang",
	"convert_time_unit",
	3,
	(time, fromUnit, toUnit) => {
		// :native and :perf_counter are technically platform-dependent in Erlang/OTP,
		// but in practice they're nanoseconds on all major platforms (Linux, macOS, Windows).
		// We standardize on nanoseconds to match typical Erlang behavior while keeping
		// JS behavior predictable.
		const NATIVE_TIME_UNIT = 1_000_000_000n;
		const PERF_COUNTER_TIME_UNIT = 1_000_000_000n;

		const resolveTimeUnit = (unit) => {
			switch (unit.value) {
				case "nanosecond":
				case "nano_seconds":
					return 1_000_000_000n;

				case "microsecond":
				case "micro_seconds":
					return 1_000_000n;

				case "millisecond":
				case "milli_seconds":
					return 1_000n;

				case "second":
				case "seconds":
					return 1n;

				case "native":
					return NATIVE_TIME_UNIT;

				case "perf_counter":
					return PERF_COUNTER_TIME_UNIT;

				// integer
				default:
					return unit.value;
			}
		};

		if (!Type.isInteger(time)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(1, "not an integer"),
			);
		}

		Erlang["_validate_time_unit/2"](fromUnit, 2);
		Erlang["_validate_time_unit/2"](toUnit, 3);

		const fromUnitValue = resolveTimeUnit(fromUnit);
		const toUnitValue = resolveTimeUnit(toUnit);
		const numerator = toUnitValue * time.value;

		const adjustedNumerator =
			time.value < 0n ? numerator - (fromUnitValue - 1n) : numerator;

		const result = adjustedNumerator / fromUnitValue;

		return Type.integer(result);
	},
);

Interpreter.defineNotImplementedErlangFunction("erlang", "demonitor", 2);

Interpreter.defineErlangFunction("erlang", "div", 2, (integer1, integer2) => {
	if (!Type.isInteger(integer1) || !Type.isInteger(integer2)) {
		const arg1 = Interpreter.inspect(integer1);
		const arg2 = Interpreter.inspect(integer2);

		Interpreter.raiseArgumentError(
			`bad argument in arithmetic expression: div(${arg1}, ${arg2})`,
		);
	}

	if (integer2.value === 0n) {
		const arg1 = Interpreter.inspect(integer1);
		const arg2 = Interpreter.inspect(integer2);

		Interpreter.raiseArithmeticError(`div(${arg1}, ${arg2})`);
	}

	// TODO: support integers outside Number range
	return Type.integer(
		Math.trunc(Number(integer1.value) / Number(integer2.value)),
	);
});

Interpreter.defineErlangFunction("erlang", "element", 2, (index, tuple) => {
	if (!Type.isInteger(index)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not an integer"),
		);
	}

	if (!Type.isTuple(tuple)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(2, "not a tuple"),
		);
	}

	if (index.value > tuple.data.length || index.value < 1) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "out of range"),
		);
	}

	return tuple.data[Number(index.value) - 1];
});

Interpreter.defineErlangFunction("erlang", "error", 1, (reason) => {
	Erlang["error/2"](reason, Type.atom("none"));
});

Interpreter.defineErlangFunction("erlang", "error", 2, (reason, _args) => {
	throw new HologramBoxedError(reason);
});

Interpreter.defineNotImplementedErlangFunction("erlang", "error", 3);

Interpreter.defineNotImplementedErlangFunction("erlang", "exit", 1);

Interpreter.defineErlangFunction(
	"erlang",
	"float_to_binary",
	2,
	(float, opts) => {
		if (!Type.isFloat(float)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(1, "not a float"),
			);
		}

		if (!Type.isList(opts)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(2, "not a list"),
			);
		}

		if (!Type.isProperList(opts)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(2, "not a proper list"),
			);
		}

		const SHORT_EXPONENTIAL_THRESHOLD = 9_007_199_254_740_992.0; // 2^53 - Erlang always uses exponential notation at this boundary
		const JS_PRECISION_LIMIT = 100; // Max precision for toFixed() and toExponential()
		const ERLANG_BUFFER_LIMIT = 256;
		const ERLANG_DEFAULT_SCIENTIFIC = 20;
		const FIXED_PRECISION_FOR_NEGATIVE = 6;

		let decimals = null;
		let scientific = ERLANG_DEFAULT_SCIENTIFIC;
		let isCompact = false;
		let isShort = false;
		let lastOpt = null;

		// Parse options
		for (const opt of opts.data) {
			if (Interpreter.isStrictlyEqual(opt, Type.atom("short"))) {
				isShort = true;
				lastOpt = "short";
				continue;
			}

			if (Interpreter.isStrictlyEqual(opt, Type.atom("compact"))) {
				isCompact = true;
				continue;
			}

			if (!Type.isTuple(opt) || opt.data.length !== 2) {
				Interpreter.raiseArgumentError(
					Interpreter.buildArgumentErrorMsg(2, "invalid option in list"),
				);
			}

			const [key, value] = opt.data;

			if (
				Interpreter.isStrictlyEqual(key, Type.atom("decimals")) &&
				Type.isInteger(value) &&
				value.value >= 0n &&
				value.value <= 253n
			) {
				decimals = Number(value.value);
				lastOpt = "decimals";
			} else if (
				Interpreter.isStrictlyEqual(key, Type.atom("scientific")) &&
				Type.isInteger(value) &&
				value.value <= 249n
			) {
				scientific = Number(value.value);
				lastOpt = "scientific";
			} else {
				Interpreter.raiseArgumentError(
					Interpreter.buildArgumentErrorMsg(2, "invalid option in list"),
				);
			}
		}

		// Only keep the last formatting option, reset others to defaults
		if (lastOpt === "short") {
			decimals = null;
			scientific = ERLANG_DEFAULT_SCIENTIFIC;
		} else if (lastOpt === "decimals") {
			isShort = false;
			scientific = ERLANG_DEFAULT_SCIENTIFIC;
		} else if (lastOpt === "scientific") {
			isShort = false;
			decimals = null;
		}

		// Check if we have negative zero (JavaScript preserves signed zero)
		const isNegativeZero = Object.is(float.value, -0);

		let result;

		if (isShort) {
			const absVal = Math.abs(float.value);

			if (absVal >= SHORT_EXPONENTIAL_THRESHOLD) {
				// For values >= 2^53, always use exponential notation per Erlang spec
				result = float.value.toExponential();
			} else {
				// For values < 2^53, compare character counts of decimal vs exponential
				// and choose the shorter representation (decimal wins ties per Erlang spec)

				let decimalResult = float.value.toString();

				// Ensure decimal point exists for proper float format
				if (decimalResult === "0") {
					decimalResult = "0.0";
				} else if (
					absVal >= 1 &&
					!decimalResult.includes(".") &&
					!decimalResult.includes("e")
				) {
					decimalResult += ".0";
				}

				let expResult = float.value.toExponential();

				// Ensure mantissa has at least one decimal digit (e.g., "9e-4" → "9.0e-4")
				if (!expResult.includes(".")) {
					expResult = expResult.replace(/e/, ".0e");
				}

				// Choose the representation with fewer characters (decimal wins ties)
				result =
					expResult.length < decimalResult.length ? expResult : decimalResult;
			}

			// Format exponent: remove + sign (e.g., e+15 → e15), keep - sign as-is
			if (result.includes("e")) {
				result = result.replace(/e\+/, "e");
			}
		} else if (decimals !== null) {
			// JavaScript's toFixed() has a limit of 100, but Erlang allows up to 253.
			// For values > 100, we use toFixed(100) and manually pad with zeros.
			if (decimals > JS_PRECISION_LIMIT) {
				result = float.value.toFixed(JS_PRECISION_LIMIT);
				const additionalZeros = decimals - JS_PRECISION_LIMIT;
				result = result + "0".repeat(additionalZeros);
			} else {
				result = float.value.toFixed(decimals);
			}

			if (isCompact && decimals > 0) {
				result = result.replace(/0+$/, "").replace(/\.$/, ".0");
			}
		} else {
			// For negative scientific, Erlang uses fixed precision of 6 decimal places
			const precision =
				scientific < 0 ? FIXED_PRECISION_FOR_NEGATIVE : scientific;

			// JavaScript's toExponential() has a limit of 100, but Erlang allows up to 249.
			// For values > 100, we use toExponential(100) and manually pad with zeros.
			if (precision > JS_PRECISION_LIMIT) {
				result = float.value.toExponential(JS_PRECISION_LIMIT);
				const [mantissa, exponent] = result.split("e");
				const currentDigits = mantissa.split(".")[1].length;
				const additionalZeros = precision - currentDigits;
				result = mantissa + "0".repeat(additionalZeros) + "e" + exponent;
			} else {
				result = float.value.toExponential(precision);
			}

			// Erlang format uses zero-padded exponents (e.g., e+00, e-04)
			result = result.replace(/e([+-])(\d)$/, "e$10$2");
		}

		// Preserve negative zero sign if needed
		// JavaScript's toString/toFixed/toExponential lose the sign of -0, so we restore it
		if (isNegativeZero && !result.startsWith("-")) {
			result = "-" + result;
		}

		// Erlang enforces a 256-byte buffer limit for the result
		if (result.length >= ERLANG_BUFFER_LIMIT) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(2, "invalid option in list"),
			);
		}

		return Type.bitstring(result);
	},
);

Interpreter.defineNotImplementedErlangFunction("erlang", "garbage_collect", 1);

Interpreter.defineNotImplementedErlangFunction("erlang", "get", 1);

Interpreter.defineErlangFunction("erlang", "hd", 1, (list) => {
	if (!Type.isList(list) || list.data.length === 0) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not a nonempty list"),
		);
	}

	return list.data[0];
});

Interpreter.defineErlangFunction(
	"erlang",
	"integer_to_binary",
	1,
	(integer) => {
		return Erlang["integer_to_binary/2"](integer, Type.integer(10));
	},
);

Interpreter.defineErlangFunction(
	"erlang",
	"integer_to_binary",
	2,
	(integer, base) => {
		if (!Type.isInteger(integer)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(1, "not an integer"),
			);
		}

		if (!Type.isInteger(base) || base.value < 2 || base.value > 36) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(
					2,
					"not an integer in the range 2 through 36",
				),
			);
		}

		const str = integer.value.toString(Number(base.value)).toUpperCase();

		return Type.bitstring(str);
	},
);

Interpreter.defineErlangFunction("erlang", "integer_to_list", 1, (integer) => {
	return Erlang["integer_to_list/2"](integer, Type.integer(10));
});

Interpreter.defineErlangFunction(
	"erlang",
	"integer_to_list",
	2,
	(integer, base) => {
		if (!Type.isInteger(integer)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(1, "not an integer"),
			);
		}

		if (!Type.isInteger(base) || base.value < 2n || base.value > 36n) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(
					2,
					"not an integer in the range 2 through 36",
				),
			);
		}

		const text = integer.value.toString(Number(base.value)).toUpperCase();

		return Bitstring.toCodepoints(Type.bitstring(text));
	},
);

Interpreter.defineErlangFunction(
	"erlang",
	"iolist_to_binary",
	1,
	(ioListOrBinary) => {
		if (Type.isBinary(ioListOrBinary)) {
			return ioListOrBinary;
		}

		if (!Type.isList(ioListOrBinary)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(1, "not an iodata term"),
			);
		}

		// :erlang.list_to_binary/1 raises ArgumentError "not an iolist term" on invalid input.
		// Remap to "not an iodata term" to match OTP's iolist_to_binary/1 behavior.
		try {
			return Erlang["list_to_binary/1"](ioListOrBinary);
		} catch {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(1, "not an iodata term"),
			);
		}
	},
);

Interpreter.defineErlangFunction("erlang", "is_atom", 1, (term) => {
	return Type.boolean(Type.isAtom(term));
});

Interpreter.defineErlangFunction("erlang", "is_binary", 1, (term) => {
	return Type.boolean(Type.isBinary(term));
});

Interpreter.defineErlangFunction("erlang", "is_bitstring", 1, (term) => {
	return Type.boolean(Type.isBitstring(term));
});

Interpreter.defineErlangFunction("erlang", "is_boolean", 1, (term) => {
	return Type.boolean(Type.isBoolean(term));
});

Interpreter.defineErlangFunction("erlang", "is_float", 1, (term) => {
	return Type.boolean(Type.isFloat(term));
});

Interpreter.defineErlangFunction("erlang", "is_function", 1, (term) => {
	return Type.boolean(Type.isAnonymousFunction(term));
});

Interpreter.defineErlangFunction("erlang", "is_function", 2, (term, arity) => {
	return Type.boolean(
		Type.isAnonymousFunction(term) && term.arity === Number(arity.value),
	);
});

Interpreter.defineErlangFunction("erlang", "is_integer", 1, (term) => {
	return Type.boolean(Type.isInteger(term));
});

Interpreter.defineErlangFunction("erlang", "is_list", 1, (term) => {
	return Type.boolean(Type.isList(term));
});

Interpreter.defineErlangFunction("erlang", "is_map", 1, (term) => {
	return Type.boolean(Type.isMap(term));
});

Interpreter.defineErlangFunction("erlang", "is_map_key", 2, (key, map) => {
	return Erlang_Maps["is_key/2"](key, map);
});

Interpreter.defineErlangFunction("erlang", "is_pid", 1, (term) => {
	return Type.boolean(Type.isPid(term));
});

Interpreter.defineErlangFunction("erlang", "is_port", 1, (term) => {
	return Type.boolean(Type.isPort(term));
});

Interpreter.defineErlangFunction("erlang", "is_reference", 1, (term) => {
	return Type.boolean(Type.isReference(term));
});

Interpreter.defineErlangFunction("erlang", "is_tuple", 1, (term) => {
	return Type.boolean(Type.isTuple(term));
});

Interpreter.defineErlangFunction("erlang", "length", 1, (list) => {
	if (!Type.isList(list)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not a list"),
		);
	}

	return Type.integer(list.data.length);
});

Interpreter.defineErlangFunction("erlang", "list_to_binary", 1, (ioList) => {
	if (!Type.isList(ioList)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not an iolist term"),
		);
	}

	const chunks = [];

	const collect = (list) => {
		const data = list.data;
		const len = data.length;
		const elemCount = list.isProper ? len : len - 1;

		for (let i = 0; i < elemCount; i++) {
			const item = data[i];

			if (Type.isInteger(item)) {
				if (item.value < 0n || item.value > 255n) {
					Interpreter.raiseArgumentError(
						Interpreter.buildArgumentErrorMsg(1, "not an iolist term"),
					);
				}

				const segment = Type.bitstringSegment(item, {
					type: "integer",
					size: Type.integer(8),
					unit: 1n,
					endianness: "big",
				});

				chunks.push(Bitstring.fromSegmentWithIntegerValue(segment));
			} else if (Type.isBinary(item)) {
				chunks.push(item);
			} else if (Type.isList(item)) {
				collect(item);
			} else {
				Interpreter.raiseArgumentError(
					Interpreter.buildArgumentErrorMsg(1, "not an iolist term"),
				);
			}
		}

		if (!list.isProper) {
			const tail = data[len - 1];

			if (Type.isBinary(tail)) {
				chunks.push(tail);
			} else if (Type.isList(tail)) {
				collect(tail);
			} else {
				Interpreter.raiseArgumentError(
					Interpreter.buildArgumentErrorMsg(1, "not an iolist term"),
				);
			}
		}
	};

	collect(ioList);

	return Bitstring.concat(chunks);
});

Interpreter.defineErlangFunction("erlang", "make_ref", 0, () => {
	return ERTS.uniqueReference();
});

Interpreter.defineErlangFunction("erlang", "map_size", 1, (map) => {
	if (!Type.isMap(map)) {
		Interpreter.raiseBadMapError(map);
	}

	return Type.integer(Object.keys(map.data).length);
});

Interpreter.defineErlangFunction("erlang", "max", 2, (term1, term2) => {
	switch (Interpreter.compareTerms(term1, term2)) {
		case -1:
			return term2;

		case 0:
			return term1;

		case 1:
			return term1;
	}
});

Interpreter.defineErlangFunction("erlang", "min", 2, (term1, term2) => {
	switch (Interpreter.compareTerms(term1, term2)) {
		case -1:
			return term1;

		case 0:
			return term1;

		case 1:
			return term2;
	}
});

Interpreter.defineNotImplementedErlangFunction("erlang", "monitor", 2);

Interpreter.defineErlangFunction("erlang", "monotonic_time", 0, () => {
	// performance.now() returns milliseconds with sub-ms precision.
	// We convert to nanoseconds (multiply by 1_000_000).
	//
	// MAX_SAFE_INTEGER == 9_007_199_254_740_991
	// MAX_SAFE_INTEGER / 1_000_000 ≈ 9_007_199_254 ms ≈ 104 days.
	// Beyond that, ms * 1_000_000 exceeds MAX_SAFE_INTEGER and loses precision.
	//
	// Fast path: direct multiplication when safely within bounds.
	// Safe path: split whole/fractional parts to avoid large float multiplication.
	const ms = performance.now();

	if (ms < 9_007_199_254) {
		return Type.integer(BigInt(Math.round(ms * 1_000_000)));
	}

	const msWhole = Math.trunc(ms);

	return Type.integer(
		BigInt(msWhole) * 1_000_000n +
			BigInt(Math.round((ms - msWhole) * 1_000_000)),
	);
});

Interpreter.defineErlangFunction("erlang", "node", 0, () => {
	return Type.atom(ERTS.nodeTable.CLIENT_NODE);
});

Interpreter.defineErlangFunction("erlang", "not", 1, (term) => {
	if (!Type.isBoolean(term)) {
		Interpreter.raiseArgumentError("argument error");
	}

	return Type.boolean(term.value == "true" ? false : true);
});

Interpreter.defineErlangFunction(
	"erlang",
	"orelse",
	2,
	(leftFun, rightFun, context) => {
		const left = leftFun(context);

		if (!Type.isBoolean(left)) {
			Interpreter.raiseArgumentError(
				`argument error: ${Interpreter.inspect(left)}`,
			);
		}

		return Type.isTrue(left) ? left : rightFun(context);
	},
);

Interpreter.defineNotImplementedErlangFunction("erlang", "process_info", 2);

Interpreter.defineNotImplementedErlangFunction("erlang", "raise", 3);

Interpreter.defineErlangFunction("erlang", "rem", 2, (integer1, integer2) => {
	if (
		!Type.isInteger(integer1) ||
		!Type.isInteger(integer2) ||
		integer2.value === 0n
	) {
		const arg1 = Interpreter.inspect(integer1);
		const arg2 = Interpreter.inspect(integer2);

		Interpreter.raiseArithmeticError(`rem(${arg1}, ${arg2})`);
	}

	// JavaScript's % operator on BigInt has the same sign behavior as Erlang's rem
	// The result has the same sign as the dividend (integer1)
	return Type.integer(integer1.value % integer2.value);
});

Interpreter.defineNotImplementedErlangFunction("erlang", "self", 0);

Interpreter.defineNotImplementedErlangFunction("erlang", "send", 2);

Interpreter.defineErlangFunction("erlang", "tl", 1, (list) => {
	if (!Type.isList(list) || list.data.length === 0) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not a nonempty list"),
		);
	}

	const length = list.data.length;

	if (length === 1) {
		return Type.list();
	}

	const isProper = Type.isProperList(list);

	if (length === 2 && !isProper) {
		return list.data[1];
	}

	const data = list.data.slice(1);

	return isProper ? Type.list(data) : Type.improperList(data);
});

Interpreter.defineErlangFunction("erlang", "tuple_size", 1, (tuple) => {
	if (!Type.isTuple(tuple)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not a tuple"),
		);
	}

	return Type.integer(tuple.data.length);
});

Interpreter.defineNotImplementedErlangFunction("erpc", "call", 4);

Interpreter.defineNotImplementedErlangFunction("ets", "give_away", 3);

Interpreter.defineNotImplementedErlangFunction("ets", "lookup", 2);

Interpreter.defineNotImplementedErlangFunction("ets", "update_element", 3);

Interpreter.defineNotImplementedErlangFunction("file", "close", 1);

Interpreter.defineNotImplementedErlangFunction("file", "open", 2);

Interpreter.defineNotImplementedErlangFunction("file", "position", 2);

Interpreter.defineNotImplementedErlangFunction("file", "read", 2);

Interpreter.defineNotImplementedErlangFunction("file", "read_line", 1);

Interpreter.defineNotImplementedErlangFunction("gen", "call", 4);

Interpreter.defineNotImplementedErlangFunction(
	"gen_event",
	"delete_handler",
	3,
);

Interpreter.defineNotImplementedErlangFunction("io", "get_chars", 3);

Interpreter.defineNotImplementedErlangFunction("io", "get_line", 2);

Interpreter.defineNotImplementedErlangFunction("io", "put_chars", 2);

Interpreter.defineNotImplementedErlangFunction("io_lib_format", "fwrite_g", 1);

Interpreter.defineErlangFunction("lists", "duplicate", 2, (n, elem) => {
	if (!Type.isInteger(n) || n.value < 0n) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":lists.duplicate/2", [n, elem]),
		);
	}

	const count = Number(n.value);
	const result = new Array(count);

	for (let i = 0; i < count; i++) {
		result[i] = elem;
	}

	return Type.list(result);
});

Interpreter.defineErlangFunction("lists", "filter", 2, function (fun, list) {
	if (!Type.isAnonymousFunction(fun) || fun.arity !== 1) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":lists.filter/2", arguments),
		);
	}

	if (!Type.isList(list)) {
		Interpreter.raiseErlangError(
			Interpreter.buildErlangErrorMsg(
				`{:bad_generator, ${Interpreter.inspect(list)}}`,
			),
		);
	}

	if (!Type.isProperList(list)) {
		Interpreter.raiseErlangError(
			Interpreter.buildErlangErrorMsg(
				`{:bad_generator, ${Interpreter.inspect(list.data.at(-1))}}`,
			),
		);
	}

	return Type.list(
		list.data.filter((elem) => {
			const result = Interpreter.callAnonymousFunction(fun, [elem]);

			if (!Type.isBoolean(result)) {
				Interpreter.raiseErlangError(
					Interpreter.buildErlangErrorMsg(
						`{:bad_filter, ${Interpreter.inspect(result)}}`,
					),
				);
			}

			return Type.isTrue(result);
		}),
	);
});

Interpreter.defineErlangFunction("lists", "flatten", 1, (list) => {
	if (!Type.isList(list)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":lists.flatten/1", [list]),
		);
	}

	if (!Type.isProperList(list)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":lists.flatten/1", [list]),
		);
	}

	const data = list.data.reduce((acc, elem) => {
		if (Type.isList(elem)) {
			elem = Erlang_Lists["flatten/1"](elem);
			return acc.concat(elem.data);
		} else {
			return acc.concat(elem);
		}
	}, []);

	return Type.list(data);
});

Interpreter.defineErlangFunction(
	"lists",
	"foldl",
	3,
	function (fun, initialAcc, list) {
		if (!Type.isAnonymousFunction(fun) || fun.arity !== 2) {
			Interpreter.raiseFunctionClauseError(
				Interpreter.buildFunctionClauseErrorMsg(":lists.foldl/3", arguments),
			);
		}

		if (!Type.isList(list)) {
			Interpreter.raiseCaseClauseError(list);
		}

		if (!Type.isProperList(list)) {
			// Client-side error message is intentionally simplified.
			Interpreter.raiseFunctionClauseError(
				Interpreter.buildFunctionClauseErrorMsg(":lists.foldl_1/3"),
			);
		}

		return list.data.reduce(
			(acc, elem) => Interpreter.callAnonymousFunction(fun, [elem, acc]),
			initialAcc,
		);
	},
);

Interpreter.defineErlangFunction(
	"lists",
	"keyfind",
	3,
	(value, index, tuples) => {
		if (!Type.isInteger(index)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(2, "not an integer"),
			);
		}

		if (index.value < 1) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(2, "out of range"),
			);
		}

		if (!Type.isList(tuples)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(3, "not a list"),
			);
		}

		if (!Type.isProperList(tuples)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(3, "not a proper list"),
			);
		}

		for (const tuple of tuples.data) {
			if (Type.isTuple(tuple)) {
				if (
					tuple.data.length >= index.value &&
					Interpreter.isEqual(tuple.data[Number(index.value) - 1], value)
				) {
					return tuple;
				}
			}
		}

		return Type.boolean(false);
	},
);

Interpreter.defineErlangFunction(
	"lists",
	"keymember",
	3,
	(value, index, tuples) => {
		return Type.boolean(
			Type.isTuple(Erlang_Lists["keyfind/3"](value, index, tuples)),
		);
	},
);

Interpreter.defineErlangFunction("lists", "map", 2, function (fun, list) {
	if (!Type.isAnonymousFunction(fun) || fun.arity !== 1) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":lists.map/2", arguments),
		);
	}

	if (!Type.isList(list)) {
		Interpreter.raiseCaseClauseError(list);
	}

	if (!Type.isProperList(list)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":lists.map_1/2"),
		);
	}

	return Type.list(
		list.data.map((elem) => Interpreter.callAnonymousFunction(fun, [elem])),
	);
});

Interpreter.defineErlangFunction(
	"lists",
	"mapfoldl",
	3,
	function (fun, initialAcc, list) {
		if (!Type.isAnonymousFunction(fun) || fun.arity !== 2) {
			Interpreter.raiseFunctionClauseError(
				Interpreter.buildFunctionClauseErrorMsg(":lists.mapfoldl/3", arguments),
			);
		}

		if (!Type.isList(list)) {
			Interpreter.raiseFunctionClauseError(
				Interpreter.buildFunctionClauseErrorMsg(
					":lists.mapfoldl_1/3",
					arguments,
				),
			);
		}

		const isProperList = Type.isProperList(list);

		const elementsCount = isProperList
			? list.data.length
			: Math.max(list.data.length - 1, 0);

		let acc = initialAcc;
		const mappedElements = [];

		for (let i = 0; i < elementsCount; ++i) {
			const result = Interpreter.callAnonymousFunction(fun, [
				list.data[i],
				acc,
			]);

			if (!Type.isTuple(result) || result.data.length !== 2) {
				Interpreter.raiseMatchError(Interpreter.buildMatchErrorMsg(result));
			}

			mappedElements.push(result.data[0]);
			acc = result.data[1];
		}

		if (!isProperList) {
			const improperTail = list.data.at(-1);

			Interpreter.raiseFunctionClauseError(
				Interpreter.buildFunctionClauseErrorMsg(":lists.mapfoldl_1/3", [
					fun,
					acc,
					improperTail,
				]),
			);
		}

		return Type.tuple([Type.list(mappedElements), acc]);
	},
);

Interpreter.defineErlangFunction("lists", "member", 2, (elem, list) => {
	if (!Type.isList(list)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(2, "not a list"),
		);
	}

	const isProperList = Type.isProperList(list);

	for (let i = 0; i < list.data.length; ++i) {
		if (Interpreter.isStrictlyEqual(list.data[i], elem)) {
			if (i < list.data.length - 1 || isProperList) {
				return Type.boolean(true);
			} else {
				Interpreter.raiseArgumentError(
					Interpreter.buildArgumentErrorMsg(2, "not a proper list"),
				);
			}
		}
	}

	if (!isProperList) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(2, "not a proper list"),
		);
	}

	return Type.boolean(false);
});

Interpreter.defineErlangFunction("lists", "reverse", 1, (list) => {
	if (!Type.isList(list)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":lists.reverse/1", [list]),
		);
	}

	if (!Type.isProperList(list)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not a list"),
		);
	}

	return Type.list(list.data.toReversed());
});

Interpreter.defineErlangFunction("lists", "reverse", 2, (list, tail) => {
	if (!Type.isList(list)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not a list"),
		);
	}

	if (!Type.isProperList(list)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not a proper list"),
		);
	}

	if (list.data.length === 0 && !Type.isList(tail)) {
		return tail;
	}

	const data = list.data
		.toReversed()
		.concat(Type.isList(tail) ? tail.data : [tail]);

	return Type.isProperList(tail) ? Type.list(data) : Type.improperList(data);
});

Interpreter.defineErlangFunction(
	"lists",
	"seq",
	3,
	(fromTerm, toTerm, incrTerm) => {
		if (!Type.isInteger(fromTerm)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(1, "not an integer"),
			);
		}

		if (!Type.isInteger(toTerm)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(2, "not an integer"),
			);
		}

		if (!Type.isInteger(incrTerm)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(3, "not an integer"),
			);
		}

		const from = fromTerm.value;
		const to = toTerm.value;
		const incr = incrTerm.value;

		// Special case: seq(same, same, 0) when is_integer(same) -> [same]
		if (from === to && incr === 0n) {
			return Type.list([Type.integer(from)]);
		}

		// Erlang guard conditions:
		// (incr > 0 andalso from - incr =< to) orelse (incr < 0 andalso from - incr >= to)
		// Negating this (to find error cases):
		// incr > 0 andalso from - incr > to  (i.e., to < from - incr when incr > 0)
		// incr < 0 andalso from - incr < to  (i.e., to > from - incr when incr < 0)
		// incr === 0 (special case already handled above when from === to)

		if (incr > 0n && to < from - incr) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(3, "not a negative increment"),
			);
		}

		if ((incr < 0n && to > from - incr) || incr === 0n) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(3, "not a positive increment"),
			);
		}

		const result = [];

		if (incr > 0n) {
			for (let i = from; i <= to; i += incr) {
				result.push(Type.integer(i));
			}
		} else {
			for (let i = from; i >= to; i += incr) {
				result.push(Type.integer(i));
			}
		}

		return Type.list(result);
	},
);

Interpreter.defineErlangFunction("lists", "sort", 1, (list) => {
	if (!Type.isList(list)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":lists.sort/1", [list]),
		);
	}

	if (!Type.isProperList(list)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":lists.split_1/5"),
		);
	}

	return Type.list(list.data.slice().sort(Interpreter.compareTerms));
});

Interpreter.defineNotImplementedErlangFunction("logger", "macro_log", 4);

Interpreter.defineNotImplementedErlangFunction("logger_config", "allow", 2);

Interpreter.defineErlangFunction("maps", "fold", 3, (fun, initialAcc, map) => {
	if (!Type.isAnonymousFunction(fun) || fun.arity !== 3) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(
				1,
				"not a fun that takes three arguments",
			),
		);
	}

	if (!Type.isMap(map)) {
		Interpreter.raiseBadMapError(map);
	}

	return Object.values(map.data).reduce(
		(acc, [key, value]) =>
			Interpreter.callAnonymousFunction(fun, [key, value, acc]),
		initialAcc,
	);
});

Interpreter.defineErlangFunction("maps", "from_keys", 2, (keys, value) => {
	if (!Type.isList(keys)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not a list"),
		);
	}

	if (!Type.isProperList(keys)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not a proper list"),
		);
	}

	return Type.map(keys.data.map((key) => [key, value]));
});

Interpreter.defineErlangFunction("maps", "from_list", 1, (list) => {
	if (!Type.isList(list)) {
		Interpreter.raiseArgumentError(
			Interpreter.buildArgumentErrorMsg(1, "not a list"),
		);
	}

	return Type.map(list.data.map((tuple) => tuple.data));
});

Interpreter.defineErlangFunction("maps", "get", 2, (key, map) => {
	const value = Erlang_Maps["get/3"](key, map, null);

	if (value !== null) {
		return value;
	}

	Interpreter.raiseKeyError(Interpreter.buildKeyErrorMsg(key, map));
});

Interpreter.defineErlangFunction("maps", "get", 3, (key, map, defaultValue) => {
	if (!Type.isMap(map)) {
		Interpreter.raiseBadMapError(map);
	}

	const encodedKey = Type.encodeMapKey(key);

	if (map.data[encodedKey]) {
		return map.data[encodedKey][1];
	}

	return defaultValue;
});

Interpreter.defineErlangFunction("maps", "is_key", 2, (key, map) => {
	if (!Type.isMap(map)) {
		Interpreter.raiseBadMapError(map);
	}

	return Type.boolean(Type.encodeMapKey(key) in map.data);
});

Interpreter.defineErlangFunction("maps", "keys", 1, (map) => {
	if (!Type.isMap(map)) {
		Interpreter.raiseBadMapError(map);
	}

	return Type.list(Object.values(map.data).map(([key, _value]) => key));
});

Interpreter.defineErlangFunction("maps", "merge", 2, (map1, map2) => {
	if (!Type.isMap(map1)) {
		Interpreter.raiseBadMapError(map1);
	}

	if (!Type.isMap(map2)) {
		Interpreter.raiseBadMapError(map2);
	}

	return { type: "map", data: { ...map1.data, ...map2.data } };
});

Interpreter.defineErlangFunction("maps", "put", 3, (key, value, map) => {
	if (!Type.isMap(map)) {
		Interpreter.raiseBadMapError(map);
	}

	const newMap = Type.cloneMap(map);
	newMap.data[Type.encodeMapKey(key)] = [key, value];

	return newMap;
});

Interpreter.defineErlangFunction("maps", "to_list", 1, (mapOrIterator) => {
	if (!Type.isMap(mapOrIterator)) {
		Interpreter.raiseBadMapError(mapOrIterator);
	}

	return Type.list(
		Object.values(mapOrIterator.data).map((item) => Type.tuple(item)),
	);
});

Interpreter.defineErlangFunction("maps", "update", 3, (key, value, map) => {
	if (!Type.isMap(map)) {
		Interpreter.raiseBadMapError(map);
	}

	if (Type.isFalse(Erlang_Maps["is_key/2"](key, map))) {
		Interpreter.raiseKeyError(Interpreter.buildKeyErrorMsg(key, map));
	}

	return Erlang_Maps["put/3"](key, value, map);
});

Interpreter.defineErlangFunction("sets", "_validate_opts", 1, (opts) => {
	if (!Type.isList(opts)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":proplists.get_value/3", [
				Type.atom("version"),
				opts,
				Type.integer(1),
			]),
		);
	}

	if (Type.isImproperList(opts)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":proplists.get_value/3"),
		);
	}

	const versionOptTuple = Erlang_Lists["keyfind/3"](
		Type.atom("version"),
		Type.integer(1),
		opts,
	);

	if (Type.isFalse(versionOptTuple)) {
		throw new HologramInterpreterError(
			"Hologram requires to specify :sets version explicitely",
		);
	}

	const version = versionOptTuple.data[1];

	if (Type.isInteger(version)) {
		if (version.value === 2n) return;

		if (version.value === 1n) {
			throw new HologramInterpreterError(
				"Hologram doesn't support :sets version 1",
			);
		}
	}

	Interpreter.raiseCaseClauseError(version);
});

Interpreter.defineErlangFunction("sets", "add_element", 2, (element, set) => {
	if (!Type.isMap(set)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":sets.add_element/2", [
				element,
				set,
			]),
		);
	}

	return Erlang_Maps["put/3"](element, Type.list(), set);
});

Interpreter.defineErlangFunction("sets", "from_list", 2, (list, opts) => {
	Erlang_Sets["_validate_opts/1"](opts);
	return Erlang_Maps["from_keys/2"](list, Type.list());
});

Interpreter.defineErlangFunction("sets", "is_element", 2, (element, set) => {
	if (!Type.isMap(set)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":sets.is_element/2", [
				element,
				set,
			]),
		);
	}

	return Erlang_Maps["is_key/2"](element, set);
});

Interpreter.defineErlangFunction("sets", "size", 1, (set) => {
	if (!Type.isMap(set)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":sets.size/1", [set]),
		);
	}

	return Erlang["map_size/1"](set);
});

Interpreter.defineErlangFunction("sets", "to_list", 1, (set) => {
	if (!Type.isMap(set)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":sets.to_list/1", [set]),
		);
	}

	return Erlang_Maps["keys/1"](set);
});

Interpreter.defineErlangFunction("sets", "union", 2, (set1, set2) => {
	if (!Type.isMap(set1) || !Type.isMap(set2)) {
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":sets.size/1", [
				!Type.isMap(set1) ? set1 : set2,
			]),
		);
	}

	const encodedKeys1 = new Set(Object.keys(set1.data));
	const encodedKeys2 = new Set(Object.keys(set2.data));
	const unionedKeys = encodedKeys1.union(encodedKeys2);

	const data = {};
	for (const encodedKey of unionedKeys) {
		data[encodedKey] =
			encodedKey in set1.data ? set1.data[encodedKey] : set2.data[encodedKey];
	}

	return { type: "map", data };
});

Interpreter.defineNotImplementedErlangFunction("telemetry", "execute", 3);

Interpreter.defineErlangFunction(
	"unicode",
	"characters_to_binary",
	1,
	(input) => {
		const encoding = Type.atom("utf8");
		return Erlang_Unicode["characters_to_binary/3"](input, encoding, encoding);
	},
);

Interpreter.defineErlangFunction(
	"unicode",
	"characters_to_binary",
	3,
	(input, inputEncoding, outputEncoding) => {
		// TODO: implement inputEncoding and outputEncoding arguments validation

		// TODO: implement other encodings for inputEncoding param
		if (!Interpreter.isStrictlyEqual(inputEncoding, Type.atom("utf8"))) {
			throw new HologramInterpreterError(
				"encodings other than utf8 are not yet implemented in Hologram",
			);
		}

		// TODO: implement other encodings for outputEncoding param
		if (!Interpreter.isStrictlyEqual(outputEncoding, Type.atom("utf8"))) {
			throw new HologramInterpreterError(
				"encodings other than utf8 are not yet implemented in Hologram",
			);
		}

		if (Type.isBinary(input)) {
			return input;
		}

		if (!Type.isList(input)) {
			Interpreter.raiseArgumentError(
				Interpreter.buildArgumentErrorMsg(
					1,
					"not valid character data (an iodata term)",
				),
			);
		}

		const flatInput = Erlang_Lists["flatten/1"](input);
		const chunks = [];

		for (let i = 0; i < flatInput.data.length; ++i) {
			const elem = flatInput.data[i];

			if (Type.isBinary(elem)) {
				chunks.push(elem);
			} else if (Type.isInteger(elem)) {
				if (Bitstring.validateCodePoint(elem.value)) {
					const segment = Type.bitstringSegment(elem, { type: "utf8" });
					chunks.push(Bitstring.fromSegments([segment]));
				} else {
					const remainingElems = flatInput.data.slice(i);

					return Type.tuple([
						Type.atom("error"),
						Bitstring.concat(chunks),
						Type.list(remainingElems),
					]);
				}
			} else {
				Interpreter.raiseArgumentError(
					Interpreter.buildArgumentErrorMsg(
						1,
						"not valid character data (an iodata term)",
					),
				);
			}
		}

		return Bitstring.concat(chunks);
	},
);

Interpreter.defineErlangFunction(
	"unicode_util",
	"_cpl",
	2,
	(list, restList) => {
		const isCP = Erlang_Unicode_Util["_is_cp/1"];

		// [C] when is_integer(C)
		if (isCP(list)) {
			return Type.improperList([
				list,
				Erlang_Unicode_Util["_cpl_1_cont/1"](restList),
			]);
		}

		if (Type.isList(list) && list.data.length > 0) {
			// Inline optimized index-based logic to avoid O(n²) slicing behavior
			const result = [];
			let idx = 0;

			// Fast path: collect consecutive codepoints iteratively
			while (idx < list.data.length && isCP(list.data[idx])) {
				result.push(list.data[idx]);
				idx++;
			}

			// If we collected all remaining elements as codepoints
			if (idx >= list.data.length) {
				if (result.length === 0) {
					// When we've exhausted the nested list, check if restList starts with a binary
					if (restList.data.length > 0 && Type.isBinary(restList.data[0])) {
						return restList;
					}

					return Erlang_Unicode_Util["cp/1"](restList);
				}

				// Merge collected codepoints with restList
				if (restList.data.length > 0 && Type.isBinary(restList.data[0])) {
					if (restList.data.length === 1) {
						return Type.improperList([...result, restList.data[0]]);
					}

					return Type.list([...result, ...restList.data]);
				}

				const restListResult = Erlang_Unicode_Util["cp/1"](restList);

				if (Type.isList(restListResult) && restListResult.isProper) {
					return Type.list([...result, ...restListResult.data]);
				}

				return Type.improperList([...result, restListResult]);
			}

			// If we collected some codepoints but hit a non-codepoint element
			const firstElement = list.data[idx];

			if (idx === list.data.length - 1) {
				const contResult = Erlang_Unicode_Util["_cpl_cont/2"](
					firstElement,
					restList,
				);

				if (result.length === 0) {
					return contResult;
				}

				if (Type.isList(contResult) && contResult.isProper) {
					return Type.list([...result, ...contResult.data]);
				}

				return Type.improperList([...result, contResult]);
			}

			const newRestList =
				restList.data.length === 0
					? Type.list([Type.list(list.data.slice(idx + 1))])
					: Type.list([Type.list(list.data.slice(idx + 1)), ...restList.data]);

			const contResult = Erlang_Unicode_Util["_cpl_cont/2"](
				firstElement,
				newRestList,
			);

			if (result.length === 0) {
				return contResult;
			}

			if (Type.isList(contResult) && contResult.isProper) {
				return Type.list([...result, ...contResult.data]);
			}

			return Type.improperList([...result, contResult]);
		}

		// []
		if (Type.isList(list) && list.data.length === 0) {
			// When we've exhausted the nested list, check if there's more to process
			// If restList starts with a binary, don't extract from it - just return restList
			// Otherwise, continue processing (e.g., for nested empty lists)
			if (restList.data.length > 0 && Type.isBinary(restList.data[0])) {
				// If next element in restList is binary, return it as-is (don't extract from it)
				return restList;
			}

			// Continue processing restList for non-binary elements or nested structures
			return Erlang_Unicode_Util["cp/1"](restList);
		}

		// Binary handling: <<C/utf8, T/binary>>
		if (Type.isBinary(list)) {
			const text = Bitstring.toText(list);

			// Invalid UTF-8
			if (text === false) {
				const errorPayload = Erlang_Unicode_Util["_merge_lcr/2"](
					list,
					restList,
				);
				return Type.tuple([Type.atom("error"), errorPayload]);
			}

			if (text.length === 0) {
				return Erlang_Unicode_Util["cp/1"](restList);
			}

			const codepoint = text.codePointAt(0);
			const charLength = codepoint > 0xffff ? 2 : 1;
			const restText = text.slice(charLength);
			const restBinary = Bitstring.fromText(restText);

			// If restList is improper with single element (the tail), create improper result
			if (!restList.isProper && restList.data.length === 1) {
				return Type.improperList([
					Type.integer(codepoint),
					restBinary,
					restList.data[0],
				]);
			}

			return Type.list([Type.integer(codepoint), restBinary, ...restList.data]);
		}

		// Non-byte-aligned bitstring
		if (Type.isBitstring(list)) {
			Interpreter.raiseFunctionClauseError(
				Interpreter.buildFunctionClauseErrorMsg(":unicode_util.cpl/2", [
					list,
					restList,
				]),
			);
		}

		// Should not reach here
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":unicode_util.cpl/2", [
				list,
				restList,
			]),
		);
	},
);

Interpreter.defineErlangFunction(
	"unicode_util",
	"_cpl_1_cont",
	1,
	(restList) => {
		const isCP = Erlang_Unicode_Util["_is_cp/1"];

		if (!Type.isList(restList)) {
			return restList;
		}

		if (restList.data.length === 0) {
			return restList;
		}

		const firstElement = restList.data[0];
		const tail = Type.list(restList.data.slice(1));

		// [C|T] when is_integer(C)
		if (isCP(firstElement)) {
			return Type.improperList([
				firstElement,
				Erlang_Unicode_Util["_cpl_1_cont2/1"](tail),
			]);
		}

		// [L|T]
		return Erlang_Unicode_Util["_cpl_cont/2"](firstElement, tail);
	},
);

Interpreter.defineErlangFunction(
	"unicode_util",
	"_cpl_1_cont2",
	1,
	(restList) => {
		const isCP = Erlang_Unicode_Util["_is_cp/1"];

		if (!Type.isList(restList)) {
			return restList;
		}

		if (restList.data.length === 0) {
			return restList;
		}

		const firstElement = restList.data[0];
		const tail = Type.list(restList.data.slice(1));

		// [C|T] when is_integer(C)
		if (isCP(firstElement)) {
			return Type.improperList([
				firstElement,
				Erlang_Unicode_Util["_cpl_1_cont3/1"](tail),
			]);
		}

		// [L]
		if (restList.data.length === 1) {
			return Erlang_Unicode_Util["_cpl_1_cont2/1"](firstElement);
		}

		// [L|T]
		return Erlang_Unicode_Util["_cpl_cont2/2"](firstElement, tail);
	},
);

Interpreter.defineErlangFunction(
	"unicode_util",
	"_cpl_1_cont3",
	1,
	(restList) => {
		const isCP = Erlang_Unicode_Util["_is_cp/1"];

		if (!Type.isList(restList)) {
			return restList;
		}

		if (restList.data.length === 0) {
			return restList;
		}

		const firstElement = restList.data[0];

		// [C|_]=T when is_integer(C)
		if (isCP(firstElement)) {
			return restList;
		}

		// [L]
		if (restList.data.length === 1) {
			return Erlang_Unicode_Util["_cpl_1_cont3/1"](firstElement);
		}

		// [L|T]
		const tail = Type.list(restList.data.slice(1));

		return Erlang_Unicode_Util["_cpl_cont3/2"](firstElement, tail);
	},
);

Interpreter.defineErlangFunction(
	"unicode_util",
	"_cpl_cont",
	2,
	(list, restList) => {
		if (Type.isList(list) && list.data.length > 0) {
			// Inline optimized index-based logic to avoid O(n²) slicing behavior
			const isCP = Erlang_Unicode_Util["_is_cp/1"];
			const result = [];
			let idx = 0;

			// Fast path: collect consecutive codepoints iteratively
			while (idx < list.data.length && isCP(list.data[idx])) {
				result.push(list.data[idx]);
				idx++;
			}

			// If we collected all remaining elements as codepoints
			if (idx >= list.data.length) {
				if (result.length === 0) {
					if (restList.data.length > 0 && Type.isBinary(restList.data[0])) {
						return restList;
					}

					return Erlang_Unicode_Util["cp/1"](restList);
				}

				if (restList.data.length > 0 && Type.isBinary(restList.data[0])) {
					if (restList.data.length === 1) {
						return Type.improperList([...result, restList.data[0]]);
					}

					return Type.list([...result, ...restList.data]);
				}

				const restListResult = Erlang_Unicode_Util["cp/1"](restList);

				if (Type.isList(restListResult) && restListResult.isProper) {
					return Type.list([...result, ...restListResult.data]);
				}

				return Type.improperList([...result, restListResult]);
			}

			const firstElement = list.data[idx];

			if (idx === list.data.length - 1) {
				const cplResult = Erlang_Unicode_Util["_cpl/2"](firstElement, restList);

				if (result.length === 0) {
					return cplResult;
				}

				if (Type.isList(cplResult) && cplResult.isProper) {
					return Type.list([...result, ...cplResult.data]);
				}

				return Type.improperList([...result, cplResult]);
			}

			const newRestList =
				restList.data.length === 0
					? Type.list([Type.list(list.data.slice(idx + 1))])
					: Type.list([Type.list(list.data.slice(idx + 1)), ...restList.data]);

			const cplResult = Erlang_Unicode_Util["_cpl/2"](
				firstElement,
				newRestList,
			);

			if (result.length === 0) {
				return cplResult;
			}

			if (Type.isList(cplResult) && cplResult.isProper) {
				return Type.list([...result, ...cplResult.data]);
			}

			return Type.improperList([...result, cplResult]);
		}

		// []
		if (Type.isList(list) && list.data.length === 0) {
			return Erlang_Unicode_Util["cp/1"](restList);
		}

		// Binary handling: <<C/utf8, T/binary>>
		if (Type.isBinary(list)) {
			const text = Bitstring.toText(list);

			// Invalid UTF-8: return error tuple
			if (text === false) {
				const errorPayload = Erlang_Unicode_Util["_merge_lcr/2"](
					list,
					restList,
				);
				return Type.tuple([Type.atom("error"), errorPayload]);
			}

			if (text.length === 0) {
				return Erlang_Unicode_Util["cp/1"](restList);
			}

			// Calculate character length in UTF-16 code units (surrogate pairs are 2 units)
			const codepoint = text.codePointAt(0);
			const charLength = codepoint > 0xffff ? 2 : 1;

			const restText = text.slice(charLength);
			const restBinary = Bitstring.fromText(restText);

			// If restList is improper (has a non-list tail), propagate improper structure
			if (!restList.isProper && restList.data.length === 1) {
				// Append extracted codepoint and remaining binary, then the original tail
				return Type.improperList([
					Type.integer(codepoint),
					restBinary,
					restList.data[0],
				]);
			}

			// Proper list: prepend extracted codepoint and remaining binary to restList elements
			return Type.list([Type.integer(codepoint), restBinary, ...restList.data]);
		}

		// Non-byte-aligned bitstring
		if (Type.isBitstring(list)) {
			Interpreter.raiseFunctionClauseError(
				Interpreter.buildFunctionClauseErrorMsg(":unicode_util.cpl_cont/2", [
					list,
					restList,
				]),
			);
		}

		// Should not reach here
		Interpreter.raiseFunctionClauseError(
			Interpreter.buildFunctionClauseErrorMsg(":unicode_util.cpl_cont/2", [
				list,
				restList,
			]),
		);
	},
);

Interpreter.defineErlangFunction(
	"unicode_util",
	"_cpl_cont2",
	2,
	(list, restList) => {
		if (Type.isList(list) && list.data.length > 0) {
			// Inline optimized index-based logic to avoid O(n²) slicing behavior
			const isCP = Erlang_Unicode_Util["_is_cp/1"];
			const result = [];
			let idx = 0;

			// Fast path: collect consecutive codepoints iteratively
			while (idx < list.data.length && isCP(list.data[idx])) {
				result.push(list.data[idx]);
				idx++;
			}

			if (idx >= list.data.length) {
				if (result.length === 0) {
					return restList;
				}

				// For improper list continuation, chain codepoints
				let current = restList;
				for (let i = result.length - 1; i >= 0; i--) {
					current = Type.improperList([result[i], current]);
				}

				return current;
			}

			const firstElement = list.data[idx];

			if (idx === list.data.length - 1) {
				const cont2Result = Erlang_Unicode_Util["_cpl_1_cont2/1"](
					Type.list([firstElement, ...restList.data]),
				);

				if (result.length === 0) {
					return cont2Result;
				}

				let current = cont2Result;

				for (let i = result.length - 1; i >= 0; i--) {
					current = Type.improperList([result[i], current]);
				}

				return current;
			}

			const newRestList =
				restList.data.length === 0
					? Type.list([Type.list(list.data.slice(idx + 1))])
					: Type.list([Type.list(list.data.slice(idx + 1)), ...restList.data]);

			const cont2Result = Erlang_Unicode_Util["_cpl_cont2/2"](
				firstElement,
				newRestList,
			);

			if (result.length === 0) {
				return cont2Result;
			}

			let current = cont2Result;

			for (let i = result.length - 1; i >= 0; i--) {
				current = Type.improperList([result[i], current]);
			}

			return current;
		}

		return restList;
	},
);

Interpreter.defineErlangFunction(
	"unicode_util",
	"_cpl_cont3",
	2,
	(list, restList) => {
		if (Type.isList(list) && list.data.length > 0) {
			// Inline optimized index-based logic to avoid O(n²) slicing behavior
			const isCP = Erlang_Unicode_Util["_is_cp/1"];
			const result = [];
			let idx = 0;

			// Fast path: collect consecutive codepoints iteratively
			while (idx < list.data.length && isCP(list.data[idx])) {
				result.push(list.data[idx]);
				idx++;
			}

			if (idx >= list.data.length) {
				if (result.length === 0) {
					return restList;
				}

				// For improper list continuation, chain codepoints
				let current = restList;
				for (let i = result.length - 1; i >= 0; i--) {
					current = Type.improperList([result[i], current]);
				}

				return current;
			}

			const firstElement = list.data[idx];

			if (idx === list.data.length - 1) {
				const cont3Result = Erlang_Unicode_Util["_cpl_1_cont3/1"](
					Type.list([firstElement, ...restList.data]),
				);

				if (result.length === 0) {
					return cont3Result;
				}

				let current = cont3Result;

				for (let i = result.length - 1; i >= 0; i--) {
					current = Type.improperList([result[i], current]);
				}

				return current;
			}

			const newRestList =
				restList.data.length === 0
					? Type.list([Type.list(list.data.slice(idx + 1))])
					: Type.list([Type.list(list.data.slice(idx + 1)), ...restList.data]);

			const cont3Result = Erlang_Unicode_Util["_cpl_cont3/2"](
				firstElement,
				newRestList,
			);

			if (result.length === 0) {
				return cont3Result;
			}

			let current = cont3Result;

			for (let i = result.length - 1; i >= 0; i--) {
				current = Type.improperList([result[i], current]);
			}

			return current;
		}

		return restList;
	},
);

Interpreter.defineErlangFunction("unicode_util", "_is_cp", 1, (value) => {
	if (!Type.isInteger(value)) {
		return false;
	}

	const codepoint = Number(value.value);

	return codepoint >= 0 && codepoint <= 0x10ffff;
});

Interpreter.defineErlangFunction(
	"unicode_util",
	"_merge_lcr",
	2,
	(binary, restList) => {
		// Combine an invalid binary with a rest list for error reporting
		// If rest list is empty or not a list, return just the binary
		if (!Type.isList(restList) || restList.data.length === 0) {
			return binary;
		}

		// Prepend binary to rest list elements
		return Type.list([binary, ...restList.data]);
	},
);

Interpreter.defineErlangFunction("unicode_util", "cp", 1, (string) => {
	const isCP = Erlang_Unicode_Util["_is_cp/1"];

	// [C|_] when is_integer(C)
	if (Type.isList(string) && string.data.length > 0 && isCP(string.data[0])) {
		return string;
	}

	// [List]
	if (Type.isList(string) && string.data.length === 1) {
		return Erlang_Unicode_Util["cp/1"](string.data[0]);
	}

	// [List|R] - proper list with multiple elements
	if (Type.isList(string) && string.isProper && string.data.length > 1) {
		const restList = Type.list(string.data.slice(1));
		return Erlang_Unicode_Util["_cpl/2"](string.data[0], restList);
	}

	// [List|Tail] - improper list (tail is not a list)
	if (Type.isList(string) && !string.isProper && string.data.length === 2) {
		const head = string.data[0];
		const tail = string.data[1];

		// If head is empty list, continue with tail
		if (Type.isList(head) && head.data.length === 0) {
			return Erlang_Unicode_Util["cp/1"](tail);
		}

		// If head is empty binary, continue with tail
		if (Type.isBinary(head) && Bitstring.isEmpty(head)) {
			return Erlang_Unicode_Util["cp/1"](tail);
		}

		// If tail is empty list, treat as proper list [head]
		if (Type.isList(tail) && tail.data.length === 0) {
			return Erlang_Unicode_Util["cp/1"](head);
		}

		// For other cases, process head element and combine result with tail
		// Case: head is a binary - extract first codepoint from it
		if (Type.isBinary(head)) {
			const text = Bitstring.toText(head);

			if (text === false) {
				return Type.tuple([Type.atom("error"), head]);
			}

			if (text.length === 0) {
				return Erlang_Unicode_Util["cp/1"](tail);
			}

			const codepoint = text.codePointAt(0);
			const charLength = codepoint > 0xffff ? 2 : 1;
			const restText = text.slice(charLength);
			const restBinary = Bitstring.fromText(restText);

			// Create improper list: [codepoint | [restBinary | tail]]
			return Type.improperList([Type.integer(codepoint), restBinary, tail]);
		}

		// Case: head is a list - recursively process it, then append tail
		if (Type.isList(head)) {
			const headResult = Erlang_Unicode_Util["cp/1"](head);

			// If head processing yielded empty list, continue with tail
			if (Type.isList(headResult) && headResult.data.length === 0) {
				return Erlang_Unicode_Util["cp/1"](tail);
			}

			// If head processing yielded a list (proper or improper), append tail
			if (Type.isList(headResult)) {
				return Type.improperList([...headResult.data, tail]);
			}
		}
	}

	// []
	if (Type.isList(string) && string.data.length === 0) {
		return Type.list();
	}

	// <<C/utf8, R/binary>>
	if (Type.isBinary(string)) {
		const text = Bitstring.toText(string);

		// Invalid UTF-8
		if (text === false) {
			return Type.tuple([Type.atom("error"), string]);
		}

		if (text.length === 0) {
			return Type.list();
		}

		// Calculate character length in UTF-16 code units (surrogate pairs are 2 units)
		const codepoint = text.codePointAt(0);
		const charLength = codepoint > 0xffff ? 2 : 1;

		const restText = text.slice(charLength);
		const restBinary = Bitstring.fromText(restText);

		// Return improper list: [codepoint | restBinary] - codepoint followed by remaining binary
		return Type.improperList([Type.integer(codepoint), restBinary]);
	}

	// <<>>
	if (Type.isBitstring(string) && Bitstring.isEmpty(string)) {
		return Type.list();
	}

	// All other cases: invalid input type (including non-byte-aligned bitstrings)
	Interpreter.raiseFunctionClauseError(
		Interpreter.buildFunctionClauseErrorMsg(":unicode_util.cp/1", [string]),
	);
});

Interpreter.defineErlangFunction("unicode_util", "gc", 1, (arg) => {
	const segmenter = ERTS.graphemeSegmenter;

	const firstSegment = (text) => {
		const iterator = segmenter.segment(text)[Symbol.iterator]();
		const { done, value } = iterator.next();

		return done ? "" : value.segment;
	};

	const buildCluster = (codepoints) => {
		if (codepoints.length === 1) return Type.integer(codepoints[0]);

		return Type.list(codepoints.map((cp) => Type.integer(cp)));
	};

	const prependCodepointToRest = (codepoint, rest) => {
		if (Type.isBitstring(rest)) {
			const restText = Bitstring.toText(rest);

			// Handle invalid UTF-8: when restText is false, treat as boundary
			if (restText === false) {
				// Return a list [codepointBinary, invalidBinary] to preserve the boundary
				const codepointBinary = Type.bitstring(String.fromCodePoint(codepoint));

				return Type.list([codepointBinary, rest]);
			}
			return Type.bitstring(String.fromCodePoint(codepoint) + restText);
		}

		if (!Type.isList(rest)) return rest;

		const data = [Type.integer(codepoint), ...rest.data];

		return Type.isImproperList(rest)
			? Type.improperList(data)
			: Type.list(data);
	};

	const extractHead = (input) => {
		const cpResult = Erlang_Unicode_Util["cp/1"](input);

		if (Type.isTuple(cpResult)) return { error: cpResult };

		if (cpResult.data.length === 0) return { empty: true };

		const headCodepoint = Number(cpResult.data[0].value);

		const tail = Type.isImproperList(cpResult)
			? cpResult.data.length === 2
				? cpResult.data[1]
				: Type.improperList(cpResult.data.slice(1))
			: Type.list(cpResult.data.slice(1));

		return { codepoint: headCodepoint, tail };
	};

	if (Type.isBinary(arg)) {
		const text = Bitstring.toText(arg);

		if (text === false) return Type.tuple([Type.atom("error"), arg]);
		if (text.length === 0) return Type.list();

		const segment = firstSegment(text);

		const clusterCodepoints = Array.from(segment).map((char) =>
			char.codePointAt(0),
		);

		const cluster = buildCluster(clusterCodepoints);
		const restText = text.slice(segment.length);

		return Type.improperList([cluster, Type.bitstring(restText)]);
	}

	if (Type.isList(arg)) {
		if (arg.data.length === 0) return Type.list();

		// Recursively collect codepoints until we can determine the grapheme boundary.
		// We may need to look ahead one codepoint to know if the current cluster is complete
		// (e.g., to check if the next character is a combining mark).
		const collectCluster = (remaining, collectedText, collectedCodepoints) => {
			const { codepoint, tail, error, empty } = extractHead(remaining);

			if (error) return error;

			if (empty) {
				if (collectedCodepoints.length > 0) {
					const cluster = buildCluster(collectedCodepoints);
					return Type.list([cluster]);
				}

				return Type.list();
			}

			const newCollectedText = collectedText + String.fromCodePoint(codepoint);

			const newCollectedCodepoints = [...collectedCodepoints, codepoint];
			const segment = firstSegment(newCollectedText);
			const segmentLength = Array.from(segment).length;
			const consumedLength = newCollectedCodepoints.length;

			// Check if we've reached a definitive boundary
			const tailIsEmptyList = Type.isList(tail) && tail.data.length === 0;

			const tailBinaryTextForEmptyCheck = Type.isBinary(tail)
				? Bitstring.toText(tail)
				: null;

			const tailIsEmptyBinary =
				Type.isBinary(tail) &&
				(tailBinaryTextForEmptyCheck === null ||
					tailBinaryTextForEmptyCheck === false ||
					tailBinaryTextForEmptyCheck.length === 0);

			const tailFirstBinaryText =
				Type.isList(tail) && tail.data.length > 0 && Type.isBinary(tail.data[0])
					? Bitstring.toText(tail.data[0])
					: null;

			const tailStartsWithEmptyBinary =
				tailFirstBinaryText !== null &&
				(tailFirstBinaryText === false || tailFirstBinaryText.length === 0);

			// When we've consumed exactly one grapheme and the tail starts with a binary,
			// check if that binary starts with a combining character. If it starts with a
			// non-combining character (base character), we've found a boundary.
			const shouldCheckCombining =
				segmentLength === consumedLength &&
				Type.isList(tail) &&
				tail.data.length > 0 &&
				Type.isBinary(tail.data[0]);

			const tailBinaryTextRaw = shouldCheckCombining
				? Bitstring.toText(tail.data[0])
				: null;

			const tailBinaryText =
				tailBinaryTextRaw === false ? null : tailBinaryTextRaw;

			const hasValidBinaryText =
				typeof tailBinaryText === "string" && tailBinaryText.length > 0;

			// Test if the next character combines with a base character.
			// If "a" + nextChar segments as just "a", then nextChar doesn't combine.
			const nextCharForCombiningCheck = hasValidBinaryText
				? Array.from(tailBinaryText)[0]
				: null;

			const tailStartsWithNonCombiningChar =
				nextCharForCombiningCheck !== null &&
				firstSegment("a" + nextCharForCombiningCheck) === "a";

			const boundaryKnown =
				segmentLength < consumedLength ||
				tailIsEmptyList ||
				tailIsEmptyBinary ||
				tailStartsWithEmptyBinary ||
				tailStartsWithNonCombiningChar;

			// Keep collecting codepoints if boundary not yet determined
			if (!boundaryKnown)
				return collectCluster(tail, newCollectedText, newCollectedCodepoints);

			// We've found the boundary. Check if we overshot (consumed too many codepoints).

			const clusterLength = segmentLength;
			const extraCount = consumedLength - clusterLength;

			const adjustedTail =
				extraCount > 0
					? prependCodepointToRest(
							newCollectedCodepoints[newCollectedCodepoints.length - 1],
							tail,
						)
					: tail;

			const clusterCodepoints = newCollectedCodepoints.slice(0, clusterLength);

			const cluster = buildCluster(clusterCodepoints);

			if (Type.isBitstring(adjustedTail))
				return Type.improperList([cluster, adjustedTail]);

			if (Type.isList(adjustedTail)) {
				const tailData = adjustedTail.data;

				const singleInvalidBinary =
					tailData.length === 1 &&
					Type.isBinary(tailData[0]) &&
					Bitstring.toText(tailData[0]) === false;

				if (singleInvalidBinary)
					return Type.improperList([cluster, tailData[0]]);

				// Preserve improper list structure if adjustedTail is improper
				if (Type.isImproperList(adjustedTail)) {
					return Type.improperList([cluster, ...tailData]);
				}

				return Type.list([cluster, ...tailData]);
			}

			return Type.list([cluster]);
		};

		return collectCluster(arg, "", []);
	}

	return Erlang_Unicode_Util["cp/1"](arg);
});

Interpreter.defineElixirFunction("Access", "get", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("x0_0"),
			Type.variablePattern("x1_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Access["get/3"](
				context.vars.x0_0,
				context.vars.x1_1,
				Type.atom("nil"),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Access", "get", 3, "public", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("container_1"),
				Type.map([[Type.atom("__struct__"), Type.variablePattern("module_0")]]),
				context,
			),
			Type.variablePattern("key_2"),
			Type.variablePattern("default_3"),
		],
		guards: [],
		body: (context) => {
			globalThis.Hologram.return = Interpreter.try(
				(context) => {
					return Interpreter.callNamedFunction(
						context.vars.module_0,
						Type.atom("fetch"),
						Type.list([context.vars.container_1, context.vars.key_2]),
						context,
					);
				},
				[
					{
						variable: Type.variablePattern("exception_4"),
						modules: [Type.atom("Elixir.UndefinedFunctionError")],
						body: (context) => {
							Interpreter.matchOperator(
								Interpreter.case(
									Type.matchPlaceholder(),
									[
										{
											match: Type.consPattern(
												Type.tuple([
													context.vars.module_0,
													Type.atom("fetch"),
													Type.list([
														context.vars.container_1,
														context.vars.key_2,
													]),
													Type.matchPlaceholder(),
												]),
												Type.matchPlaceholder(),
											),
											guards: [],
											body: (context) => {
												Interpreter.matchOperator(
													Type.bitstring([
														Type.bitstringSegment(
															Elixir_Kernel["inspect/1"](context.vars.module_0),
															{ type: "binary" },
														),
														Type.bitstringSegment(
															Type.string(
																' does not implement the Access behaviour\n\nYou can use the "struct.field" syntax to access struct fields. You can also use Access.key!/1 to access struct fields dynamically inside get_in/put_in/update_in',
															),
															{ type: "binary" },
														),
													]),
													Type.variablePattern("reason_5"),
													context,
												);
												Interpreter.updateVarsToMatchedValues(context);
												return Elixir_Map["merge/2"](
													context.vars.exception_4,
													Type.map([
														[Type.atom("reason"), context.vars.reason_5],
													]),
												);
											},
										},
										{
											match: Type.matchPlaceholder(),
											guards: [],
											body: (context) => {
												return context.vars.exception_4;
											},
										},
									],
									context,
								),
								Type.variablePattern("exception_6"),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							return Erlang["raise/3"](
								Type.atom("error"),
								Elixir_Kernel_Utils["raise/1"](context.vars.exception_6),
								Type.matchPlaceholder(),
							);
						},
					},
				],
				[],
				[],
				null,
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
	{
		params: (context) => [
			Type.variablePattern("map_0"),
			Type.variablePattern("key_1"),
			Type.variablePattern("default_2"),
		],
		guards: [(context) => Erlang["is_map/1"](context.vars.map_0)],
		body: (context) => {
			return Interpreter.case(
				context.vars.map_0,
				[
					{
						match: Type.map([
							[context.vars.key_1, Type.variablePattern("value_3")],
						]),
						guards: [],
						body: (context) => {
							return context.vars.value_3;
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return context.vars.default_2;
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("list_0"),
			Type.variablePattern("key_1"),
			Type.variablePattern("default_2"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_list/1"](context.vars.list_0),
					(context) => Erlang["is_atom/1"](context.vars.key_1),
					context,
				),
		],
		body: (context) => {
			return Interpreter.case(
				Erlang_Lists["keyfind/3"](
					context.vars.key_1,
					Type.integer(1n),
					context.vars.list_0,
				),
				[
					{
						match: Type.tuple([
							Type.matchPlaceholder(),
							Type.variablePattern("value_3"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.value_3;
						},
					},
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return context.vars.default_2;
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("list_0"),
			Type.variablePattern("key_1"),
			Type.matchPlaceholder(),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_list/1"](context.vars.list_0),
					(context) => Erlang["is_integer/1"](context.vars.key_1),
					context,
				),
		],
		body: (context) => {
			return Erlang["error/1"](
				Elixir_ArgumentError["exception/1"](
					Type.bitstring([
						Type.bitstringSegment(
							Type.string(
								"the Access module does not support accessing lists by index, got: ",
							),
							{ type: "binary" },
						),
						Type.bitstringSegment(
							Elixir_Kernel["inspect/1"](context.vars.key_1),
							{ type: "binary" },
						),
						Type.bitstringSegment(
							Type.string(
								"\n\nAccessing a list by index is typically discouraged in Elixir, instead we prefer to use the Enum module to manipulate lists as a whole. If you really must access a list element by index, you can use Enum.at/2 or the functions in the List module",
							),
							{ type: "binary" },
						),
					]),
				),
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("list_0"),
			Type.variablePattern("key_1"),
			Type.matchPlaceholder(),
		],
		guards: [(context) => Erlang["is_list/1"](context.vars.list_0)],
		body: (context) => {
			return Erlang["error/1"](
				Elixir_ArgumentError["exception/1"](
					Type.bitstring([
						Type.bitstringSegment(
							Type.string(
								"the Access module supports only keyword lists (with atom keys), got: ",
							),
							{ type: "binary" },
						),
						Type.bitstringSegment(
							Elixir_Kernel["inspect/1"](context.vars.key_1),
							{ type: "binary" },
						),
						Type.bitstringSegment(
							Type.string(
								"\n\nIf you want to search lists of tuples, use List.keyfind/3",
							),
							{ type: "binary" },
						),
					]),
				),
			);
		},
	},
	{
		params: (context) => [
			Type.atom("nil"),
			Type.matchPlaceholder(),
			Type.variablePattern("default_1"),
		],
		guards: [],
		body: (context) => {
			return context.vars.default_1;
		},
	},
]);

Interpreter.defineElixirFunction("ArgumentError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.ArgumentError")],
				[Type.atom("message"), Type.bitstring("argument error")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("ArgumentError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.ArgumentError")],
					[Type.atom("message"), Type.bitstring("argument error")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("ArgumentError", "exception", 1, "public", [
	{
		params: (context) => [Type.variablePattern("msg_0")],
		guards: [(context) => Erlang["is_binary/1"](context.vars.msg_0)],
		body: (context) => {
			return Elixir_ArgumentError["exception/1"](
				Type.list([Type.tuple([Type.atom("message"), context.vars.msg_0])]),
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("args_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.args_0)],
		body: (context) => {
			return Elixir_Kernel["struct!/2"](
				Type.atom("Elixir.ArgumentError"),
				context.vars.args_0,
			);
		},
	},
]);

Interpreter.defineElixirFunction("ArithmeticError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.ArithmeticError")],
				[
					Type.atom("message"),
					Type.bitstring("bad argument in arithmetic expression"),
				],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("ArithmeticError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.ArithmeticError")],
					[
						Type.atom("message"),
						Type.bitstring("bad argument in arithmetic expression"),
					],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("BadArityError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.BadArityError")],
				[Type.atom("args"), Type.atom("nil")],
				[Type.atom("function"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("BadArityError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.BadArityError")],
					[Type.atom("args"), Type.atom("nil")],
					[Type.atom("function"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("BadBooleanError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.BadBooleanError")],
				[Type.atom("operator"), Type.atom("nil")],
				[Type.atom("term"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("BadBooleanError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.BadBooleanError")],
					[Type.atom("operator"), Type.atom("nil")],
					[Type.atom("term"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"BadFunctionError",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.BadFunctionError")],
					[Type.atom("term"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"BadFunctionError",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[Type.atom("__exception__"), Type.atom("true")],
						[Type.atom("__struct__"), Type.atom("Elixir.BadFunctionError")],
						[Type.atom("term"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("BadMapError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.BadMapError")],
				[Type.atom("term"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("BadMapError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.BadMapError")],
					[Type.atom("term"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("BadStructError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.BadStructError")],
				[Type.atom("struct"), Type.atom("nil")],
				[Type.atom("term"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("BadStructError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.BadStructError")],
					[Type.atom("struct"), Type.atom("nil")],
					[Type.atom("term"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"days_before_month",
	1,
	"private",
	[
		{
			params: (context) => [Type.integer(1n)],
			guards: [],
			body: (context) => {
				return Type.integer(0n);
			},
		},
		{
			params: (context) => [Type.integer(2n)],
			guards: [],
			body: (context) => {
				return Type.integer(31n);
			},
		},
		{
			params: (context) => [Type.integer(3n)],
			guards: [],
			body: (context) => {
				return Type.integer(59n);
			},
		},
		{
			params: (context) => [Type.integer(4n)],
			guards: [],
			body: (context) => {
				return Type.integer(90n);
			},
		},
		{
			params: (context) => [Type.integer(5n)],
			guards: [],
			body: (context) => {
				return Type.integer(120n);
			},
		},
		{
			params: (context) => [Type.integer(6n)],
			guards: [],
			body: (context) => {
				return Type.integer(151n);
			},
		},
		{
			params: (context) => [Type.integer(7n)],
			guards: [],
			body: (context) => {
				return Type.integer(181n);
			},
		},
		{
			params: (context) => [Type.integer(8n)],
			guards: [],
			body: (context) => {
				return Type.integer(212n);
			},
		},
		{
			params: (context) => [Type.integer(9n)],
			guards: [],
			body: (context) => {
				return Type.integer(243n);
			},
		},
		{
			params: (context) => [Type.integer(10n)],
			guards: [],
			body: (context) => {
				return Type.integer(273n);
			},
		},
		{
			params: (context) => [Type.integer(11n)],
			guards: [],
			body: (context) => {
				return Type.integer(304n);
			},
		},
		{
			params: (context) => [Type.integer(12n)],
			guards: [],
			body: (context) => {
				return Type.integer(334n);
			},
		},
	],
);

Interpreter.defineElixirFunction("Calendar.ISO", "days_in_month", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("year_0"),
			Type.variablePattern("month_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_integer/1"](context.vars.year_0),
					(context) =>
						Erlang["andalso/2"](
							(context) => Erlang["is_integer/1"](context.vars.month_1),
							(context) =>
								Erlang["andalso/2"](
									(context) =>
										Erlang[">=/2"](context.vars.month_1, Type.integer(1n)),
									(context) =>
										Erlang["=</2"](context.vars.month_1, Type.integer(12n)),
									context,
								),
							context,
						),
					context,
				),
		],
		body: (context) => {
			return Elixir_Calendar_ISO["days_in_month_guarded/2"](
				context.vars.year_0,
				context.vars.month_1,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"datetime_to_string",
	11,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("x0_0"),
				Type.variablePattern("x1_1"),
				Type.variablePattern("x2_2"),
				Type.variablePattern("x3_3"),
				Type.variablePattern("x4_4"),
				Type.variablePattern("x5_5"),
				Type.variablePattern("x6_6"),
				Type.variablePattern("x7_7"),
				Type.variablePattern("x8_8"),
				Type.variablePattern("x9_9"),
				Type.variablePattern("x10_10"),
			],
			guards: [],
			body: (context) => {
				return Elixir_Calendar_ISO["datetime_to_string/12"](
					context.vars.x0_0,
					context.vars.x1_1,
					context.vars.x2_2,
					context.vars.x3_3,
					context.vars.x4_4,
					context.vars.x5_5,
					context.vars.x6_6,
					context.vars.x7_7,
					context.vars.x8_8,
					context.vars.x9_9,
					context.vars.x10_10,
					Type.atom("extended"),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"date_to_string",
	3,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("x0_0"),
				Type.variablePattern("x1_1"),
				Type.variablePattern("x2_2"),
			],
			guards: [],
			body: (context) => {
				return Elixir_Calendar_ISO["date_to_string/4"](
					context.vars.x0_0,
					context.vars.x1_1,
					context.vars.x2_2,
					Type.atom("extended"),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"zone_to_iodata",
	4,
	"private",
	[
		{
			params: (context) => [
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
				Type.bitstring("Etc/UTC"),
			],
			guards: [],
			body: (context) => {
				return Type.list([]);
			},
		},
		{
			params: (context) => [
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
				Type.variablePattern("abbr_0"),
				Type.variablePattern("zone_1"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.consOperator(
					Type.integer(32n),
					Interpreter.consOperator(
						context.vars.abbr_0,
						Interpreter.consOperator(Type.integer(32n), context.vars.zone_1),
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"naive_datetime_to_string",
	7,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("x0_0"),
				Type.variablePattern("x1_1"),
				Type.variablePattern("x2_2"),
				Type.variablePattern("x3_3"),
				Type.variablePattern("x4_4"),
				Type.variablePattern("x5_5"),
				Type.variablePattern("x6_6"),
			],
			guards: [],
			body: (context) => {
				return Elixir_Calendar_ISO["naive_datetime_to_string/8"](
					context.vars.x0_0,
					context.vars.x1_1,
					context.vars.x2_2,
					context.vars.x3_3,
					context.vars.x4_4,
					context.vars.x5_5,
					context.vars.x6_6,
					Type.atom("extended"),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"time_to_iodata_guarded",
	5,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("hour_0"),
				Type.variablePattern("minute_1"),
				Type.variablePattern("second_2"),
				Type.tuple([Type.matchPlaceholder(), Type.integer(0n)]),
				Type.variablePattern("format_3"),
			],
			guards: [],
			body: (context) => {
				return Elixir_Calendar_ISO["time_to_iodata_format/4"](
					context.vars.hour_0,
					context.vars.minute_1,
					context.vars.second_2,
					context.vars.format_3,
				);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("hour_0"),
				Type.variablePattern("minute_1"),
				Type.variablePattern("second_2"),
				Type.tuple([
					Type.variablePattern("microsecond_3"),
					Type.variablePattern("precision_4"),
				]),
				Type.variablePattern("format_5"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.consOperator(
					Elixir_Calendar_ISO["time_to_iodata_format/4"](
						context.vars.hour_0,
						context.vars.minute_1,
						context.vars.second_2,
						context.vars.format_5,
					),
					Interpreter.consOperator(
						Type.integer(46n),
						Elixir_Calendar_ISO["microseconds_to_iodata/2"](
							context.vars.microsecond_3,
							context.vars.precision_4,
						),
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"date_to_iodata",
	4,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("year_0"),
				Type.variablePattern("month_1"),
				Type.variablePattern("day_2"),
				Type.variablePattern("format_3"),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) =>
							Erlang["andalso/2"](
								(context) =>
									Erlang["andalso/2"](
										(context) => Erlang["is_integer/1"](context.vars.year_0),
										(context) => Erlang["is_integer/1"](context.vars.month_1),
										context,
									),
								(context) => Erlang["is_integer/1"](context.vars.day_2),
								context,
							),
						(context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["=:=/2"](context.vars.format_3, Type.atom("basic")),
								(context) =>
									Erlang["=:=/2"](context.vars.format_3, Type.atom("extended")),
								context,
							),
						context,
					),
			],
			body: (context) => {
				return Elixir_Calendar_ISO["date_to_iodata_guarded/4"](
					context.vars.year_0,
					context.vars.month_1,
					context.vars.day_2,
					context.vars.format_3,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Calendar.ISO", "zero_pad", 2, "private", [
	{
		params: (context) => [
			Type.variablePattern("val_0"),
			Type.variablePattern("count_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang[">=/2"](context.vars.val_0, Type.integer(0n)),
					(context) => Erlang["=</2"](context.vars.count_1, Type.integer(6n)),
					context,
				),
		],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["integer_to_binary/1"](context.vars.val_0),
				Type.variablePattern("num_2"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				Erlang["max/2"](
					Erlang["-/2"](
						context.vars.count_1,
						Erlang["byte_size/1"](context.vars.num_2),
					),
					Type.integer(0n),
				),
				[
					{
						match: Type.integer(0n),
						guards: [],
						body: (context) => {
							return context.vars.num_2;
						},
					},
					{
						match: Type.integer(1n),
						guards: [],
						body: (context) => {
							return Interpreter.consOperator(
								Type.bitstring("0"),
								context.vars.num_2,
							);
						},
					},
					{
						match: Type.integer(2n),
						guards: [],
						body: (context) => {
							return Interpreter.consOperator(
								Type.bitstring("00"),
								context.vars.num_2,
							);
						},
					},
					{
						match: Type.integer(3n),
						guards: [],
						body: (context) => {
							return Interpreter.consOperator(
								Type.bitstring("000"),
								context.vars.num_2,
							);
						},
					},
					{
						match: Type.integer(4n),
						guards: [],
						body: (context) => {
							return Interpreter.consOperator(
								Type.bitstring("0000"),
								context.vars.num_2,
							);
						},
					},
					{
						match: Type.integer(5n),
						guards: [],
						body: (context) => {
							return Interpreter.consOperator(
								Type.bitstring("00000"),
								context.vars.num_2,
							);
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("val_0"),
			Type.variablePattern("count_1"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.consOperator(
				Type.integer(45n),
				Elixir_Calendar_ISO["zero_pad/2"](
					Erlang["-/1"](context.vars.val_0),
					context.vars.count_1,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"naive_datetime_to_iodata",
	8,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("year_0"),
				Type.variablePattern("month_1"),
				Type.variablePattern("day_2"),
				Type.variablePattern("hour_3"),
				Type.variablePattern("minute_4"),
				Type.variablePattern("second_5"),
				Type.variablePattern("microsecond_6"),
				Type.variablePattern("format_7"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.consOperator(
					Elixir_Calendar_ISO["date_to_iodata/4"](
						context.vars.year_0,
						context.vars.month_1,
						context.vars.day_2,
						context.vars.format_7,
					),
					Interpreter.consOperator(
						Type.integer(32n),
						Elixir_Calendar_ISO["time_to_iodata/5"](
							context.vars.hour_3,
							context.vars.minute_4,
							context.vars.second_5,
							context.vars.microsecond_6,
							context.vars.format_7,
						),
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"datetime_to_iodata",
	12,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("year_0"),
				Type.variablePattern("month_1"),
				Type.variablePattern("day_2"),
				Type.variablePattern("hour_3"),
				Type.variablePattern("minute_4"),
				Type.variablePattern("second_5"),
				Type.variablePattern("microsecond_6"),
				Type.variablePattern("time_zone_7"),
				Type.variablePattern("zone_abbr_8"),
				Type.variablePattern("utc_offset_9"),
				Type.variablePattern("std_offset_10"),
				Type.variablePattern("format_11"),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) =>
							Erlang["andalso/2"](
								(context) =>
									Erlang["andalso/2"](
										(context) =>
											Erlang["is_binary/1"](context.vars.time_zone_7),
										(context) =>
											Erlang["is_binary/1"](context.vars.zone_abbr_8),
										context,
									),
								(context) => Erlang["is_integer/1"](context.vars.utc_offset_9),
								context,
							),
						(context) => Erlang["is_integer/1"](context.vars.std_offset_10),
						context,
					),
			],
			body: (context) => {
				return Type.list([
					Elixir_Calendar_ISO["date_to_iodata/4"](
						context.vars.year_0,
						context.vars.month_1,
						context.vars.day_2,
						context.vars.format_11,
					),
					Type.integer(32n),
					Elixir_Calendar_ISO["time_to_iodata/5"](
						context.vars.hour_3,
						context.vars.minute_4,
						context.vars.second_5,
						context.vars.microsecond_6,
						context.vars.format_11,
					),
					Elixir_Calendar_ISO["offset_to_iodata/4"](
						context.vars.utc_offset_9,
						context.vars.std_offset_10,
						context.vars.time_zone_7,
						context.vars.format_11,
					),
					Elixir_Calendar_ISO["zone_to_iodata/4"](
						context.vars.utc_offset_9,
						context.vars.std_offset_10,
						context.vars.zone_abbr_8,
						context.vars.time_zone_7,
					),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"date_from_iso_days",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("days_0")],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Elixir_Calendar_ISO["days_to_year/1"](context.vars.days_0),
					Type.tuple([
						Type.variablePattern("year_1"),
						Type.variablePattern("day_of_year_2"),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Interpreter.case(
						Elixir_Calendar_ISO["leap_year?/1"](context.vars.year_1),
						[
							{
								match: Type.variablePattern("x_3"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_3, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_3, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return Type.integer(0n);
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Type.integer(1n);
								},
							},
						],
						context,
					),
					Type.variablePattern("extra_day_4"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Elixir_Calendar_ISO["year_day_to_year_date/2"](
						context.vars.extra_day_4,
						context.vars.day_of_year_2,
					),
					Type.tuple([
						Type.variablePattern("month_5"),
						Type.variablePattern("day_in_month_6"),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Type.tuple([
					context.vars.year_1,
					context.vars.month_5,
					Erlang["+/2"](context.vars.day_in_month_6, Type.integer(1n)),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"microseconds_to_iodata",
	2,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder(), Type.integer(0n)],
			guards: [],
			body: (context) => {
				return Type.list([]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("microsecond_0"),
				Type.integer(6n),
			],
			guards: [],
			body: (context) => {
				return Elixir_Calendar_ISO["zero_pad/2"](
					context.vars.microsecond_0,
					Type.integer(6n),
				);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("microsecond_0"),
				Type.variablePattern("precision_1"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Erlang["div/2"](
						context.vars.microsecond_0,
						Elixir_Calendar_ISO["scale_factor/1"](context.vars.precision_1),
					),
					Type.variablePattern("num_2"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Calendar_ISO["zero_pad/2"](
					context.vars.num_2,
					context.vars.precision_1,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"days_in_previous_years",
	1,
	"private",
	[
		{
			params: (context) => [Type.integer(0n)],
			guards: [],
			body: (context) => {
				return Type.integer(0n);
			},
		},
		{
			params: (context) => [Type.variablePattern("year_0")],
			guards: [
				(context) => Erlang[">/2"](context.vars.year_0, Type.integer(0n)),
			],
			body: (context) => {
				Interpreter.matchOperator(
					Erlang["-/2"](context.vars.year_0, Type.integer(1n)),
					Type.variablePattern("previous_year_1"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Erlang["+/2"](
					Erlang["+/2"](
						Erlang["+/2"](
							Erlang["-/2"](
								Erlang["div/2"](context.vars.previous_year_1, Type.integer(4n)),
								Erlang["div/2"](
									context.vars.previous_year_1,
									Type.integer(100n),
								),
							),
							Erlang["div/2"](context.vars.previous_year_1, Type.integer(400n)),
						),
						Erlang["*/2"](context.vars.previous_year_1, Type.integer(365n)),
					),
					Type.integer(366n),
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("year_0")],
			guards: [
				(context) => Erlang["</2"](context.vars.year_0, Type.integer(0n)),
			],
			body: (context) => {
				Interpreter.matchOperator(
					Erlang["-/2"](context.vars.year_0, Type.integer(1n)),
					Type.variablePattern("previous_year_1"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Erlang["+/2"](
					Erlang["+/2"](
						Erlang["-/2"](
							Erlang["+/2"](
								Erlang["-/2"](
									Erlang["div/2"](context.vars.year_0, Type.integer(4n)),
									Erlang["div/2"](context.vars.year_0, Type.integer(100n)),
								),
								Erlang["div/2"](context.vars.year_0, Type.integer(400n)),
							),
							Type.integer(1n),
						),
						Erlang["*/2"](context.vars.previous_year_1, Type.integer(365n)),
					),
					Type.integer(366n),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Calendar.ISO", "leap_year?", 1, "public", [
	{
		params: (context) => [Type.variablePattern("year_0")],
		guards: [(context) => Erlang["is_integer/1"](context.vars.year_0)],
		body: (context) => {
			return Interpreter.case(
				Erlang["=:=/2"](
					Erlang["rem/2"](context.vars.year_0, Type.integer(4n)),
					Type.integer(0n),
				),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Type.atom("false");
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Interpreter.case(
								Erlang["=/=/2"](
									Erlang["rem/2"](context.vars.year_0, Type.integer(100n)),
									Type.integer(0n),
								),
								[
									{
										match: Type.atom("false"),
										guards: [],
										body: (context) => {
											return Erlang["=:=/2"](
												Erlang["rem/2"](
													context.vars.year_0,
													Type.integer(400n),
												),
												Type.integer(0n),
											);
										},
									},
									{
										match: Type.atom("true"),
										guards: [],
										body: (context) => {
											return Type.atom("true");
										},
									},
								],
								context,
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"format_offset",
	4,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("total_0"),
				Type.variablePattern("hour_1"),
				Type.variablePattern("minute_2"),
				Type.atom("extended"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.consOperator(
					Elixir_Calendar_ISO["sign/1"](context.vars.total_0),
					Interpreter.consOperator(
						Elixir_Calendar_ISO["zero_pad/2"](
							context.vars.hour_1,
							Type.integer(2n),
						),
						Interpreter.consOperator(
							Type.integer(58n),
							Elixir_Calendar_ISO["zero_pad/2"](
								context.vars.minute_2,
								Type.integer(2n),
							),
						),
					),
				);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("total_0"),
				Type.variablePattern("hour_1"),
				Type.variablePattern("minute_2"),
				Type.atom("basic"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.consOperator(
					Elixir_Calendar_ISO["sign/1"](context.vars.total_0),
					Interpreter.consOperator(
						Elixir_Calendar_ISO["zero_pad/2"](
							context.vars.hour_1,
							Type.integer(2n),
						),
						Elixir_Calendar_ISO["zero_pad/2"](
							context.vars.minute_2,
							Type.integer(2n),
						),
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"naive_datetime_to_string",
	8,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("year_0"),
				Type.variablePattern("month_1"),
				Type.variablePattern("day_2"),
				Type.variablePattern("hour_3"),
				Type.variablePattern("minute_4"),
				Type.variablePattern("second_5"),
				Type.variablePattern("microsecond_6"),
				Type.variablePattern("format_7"),
			],
			guards: [],
			body: (context) => {
				return Erlang["iolist_to_binary/1"](
					Elixir_Calendar_ISO["naive_datetime_to_iodata/8"](
						context.vars.year_0,
						context.vars.month_1,
						context.vars.day_2,
						context.vars.hour_3,
						context.vars.minute_4,
						context.vars.second_5,
						context.vars.microsecond_6,
						context.vars.format_7,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"date_to_string",
	4,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("year_0"),
				Type.variablePattern("month_1"),
				Type.variablePattern("day_2"),
				Type.variablePattern("format_3"),
			],
			guards: [],
			body: (context) => {
				return Erlang["iolist_to_binary/1"](
					Elixir_Calendar_ISO["date_to_iodata/4"](
						context.vars.year_0,
						context.vars.month_1,
						context.vars.day_2,
						context.vars.format_3,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Calendar.ISO", "scale_factor", 1, "private", [
	{
		params: (context) => [Type.integer(1n)],
		guards: [],
		body: (context) => {
			return Type.integer(100000n);
		},
	},
	{
		params: (context) => [Type.integer(2n)],
		guards: [],
		body: (context) => {
			return Type.integer(10000n);
		},
	},
	{
		params: (context) => [Type.integer(3n)],
		guards: [],
		body: (context) => {
			return Type.integer(1000n);
		},
	},
	{
		params: (context) => [Type.integer(4n)],
		guards: [],
		body: (context) => {
			return Type.integer(100n);
		},
	},
	{
		params: (context) => [Type.integer(5n)],
		guards: [],
		body: (context) => {
			return Type.integer(10n);
		},
	},
	{
		params: (context) => [Type.integer(6n)],
		guards: [],
		body: (context) => {
			return Type.integer(1n);
		},
	},
]);

Interpreter.defineElixirFunction("Calendar.ISO", "days_to_year", 3, "private", [
	{
		params: (context) => [
			Type.variablePattern("year_0"),
			Type.variablePattern("days1_1"),
			Type.variablePattern("days2_2"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["</2"](context.vars.year_0, Type.integer(0n)),
					(context) =>
						Erlang[">=/2"](context.vars.days1_1, context.vars.days2_2),
					context,
				),
		],
		body: (context) => {
			return Elixir_Calendar_ISO["days_to_year/3"](
				Erlang["+/2"](context.vars.year_0, Type.integer(1n)),
				context.vars.days1_1,
				Elixir_Calendar_ISO["days_to_end_of_epoch/1"](
					Erlang["+/2"](context.vars.year_0, Type.integer(1n)),
				),
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("year_0"),
			Type.variablePattern("days1_1"),
			Type.variablePattern("days2_2"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang[">=/2"](context.vars.year_0, Type.integer(0n)),
					(context) =>
						Erlang["</2"](context.vars.days1_1, context.vars.days2_2),
					context,
				),
		],
		body: (context) => {
			return Elixir_Calendar_ISO["days_to_year/3"](
				Erlang["-/2"](context.vars.year_0, Type.integer(1n)),
				context.vars.days1_1,
				Elixir_Calendar_ISO["days_in_previous_years/1"](
					Erlang["-/2"](context.vars.year_0, Type.integer(1n)),
				),
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("year_0"),
			Type.matchPlaceholder(),
			Type.variablePattern("days2_2"),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([context.vars.year_0, context.vars.days2_2]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"datetime_to_string",
	12,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("year_0"),
				Type.variablePattern("month_1"),
				Type.variablePattern("day_2"),
				Type.variablePattern("hour_3"),
				Type.variablePattern("minute_4"),
				Type.variablePattern("second_5"),
				Type.variablePattern("microsecond_6"),
				Type.variablePattern("time_zone_7"),
				Type.variablePattern("zone_abbr_8"),
				Type.variablePattern("utc_offset_9"),
				Type.variablePattern("std_offset_10"),
				Type.variablePattern("format_11"),
			],
			guards: [],
			body: (context) => {
				return Erlang["iolist_to_binary/1"](
					Elixir_Calendar_ISO["datetime_to_iodata/12"](
						context.vars.year_0,
						context.vars.month_1,
						context.vars.day_2,
						context.vars.hour_3,
						context.vars.minute_4,
						context.vars.second_5,
						context.vars.microsecond_6,
						context.vars.time_zone_7,
						context.vars.zone_abbr_8,
						context.vars.utc_offset_9,
						context.vars.std_offset_10,
						context.vars.format_11,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"days_to_end_of_epoch",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("year_0")],
			guards: [
				(context) => Erlang["</2"](context.vars.year_0, Type.integer(0n)),
			],
			body: (context) => {
				Interpreter.matchOperator(
					Erlang["+/2"](context.vars.year_0, Type.integer(1n)),
					Type.variablePattern("previous_year_1"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Erlang["+/2"](
					Erlang["+/2"](
						Erlang["-/2"](
							Erlang["div/2"](context.vars.previous_year_1, Type.integer(4n)),
							Erlang["div/2"](context.vars.previous_year_1, Type.integer(100n)),
						),
						Erlang["div/2"](context.vars.previous_year_1, Type.integer(400n)),
					),
					Erlang["*/2"](context.vars.previous_year_1, Type.integer(365n)),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Calendar.ISO", "days_to_year", 1, "private", [
	{
		params: (context) => [Type.variablePattern("days_0")],
		guards: [(context) => Erlang["</2"](context.vars.days_0, Type.integer(0n))],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["-/2"](
					Erlang["-/1"](
						Erlang["div/2"](
							Erlang["-/1"](context.vars.days_0),
							Type.integer(365n),
						),
					),
					Type.integer(1n),
				),
				Type.variablePattern("year_estimate_1"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_Calendar_ISO["days_to_year/3"](
					context.vars.year_estimate_1,
					context.vars.days_0,
					Elixir_Calendar_ISO["days_to_end_of_epoch/1"](
						context.vars.year_estimate_1,
					),
				),
				Type.tuple([
					Type.variablePattern("year_2"),
					Type.variablePattern("days_before_year_3"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Interpreter.case(
					Elixir_Calendar_ISO["leap_year?/1"](context.vars.year_2),
					[
						{
							match: Type.variablePattern("x_4"),
							guards: [
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](context.vars.x_4, Type.atom("false")),
										(context) =>
											Erlang["=:=/2"](context.vars.x_4, Type.atom("nil")),
										context,
									),
							],
							body: (context) => {
								return Type.integer(0n);
							},
						},
						{
							match: Type.matchPlaceholder(),
							guards: [],
							body: (context) => {
								return Type.integer(1n);
							},
						},
					],
					context,
				),
				Type.variablePattern("leap_year_pad_5"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([
				context.vars.year_2,
				Erlang["-/2"](
					Erlang["+/2"](
						Erlang["+/2"](context.vars.leap_year_pad_5, Type.integer(365n)),
						context.vars.days_0,
					),
					context.vars.days_before_year_3,
				),
			]);
		},
	},
	{
		params: (context) => [Type.variablePattern("days_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["div/2"](context.vars.days_0, Type.integer(365n)),
				Type.variablePattern("year_estimate_1"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_Calendar_ISO["days_to_year/3"](
					context.vars.year_estimate_1,
					context.vars.days_0,
					Elixir_Calendar_ISO["days_in_previous_years/1"](
						context.vars.year_estimate_1,
					),
				),
				Type.tuple([
					Type.variablePattern("year_2"),
					Type.variablePattern("days_before_year_3"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([
				context.vars.year_2,
				Erlang["-/2"](context.vars.days_0, context.vars.days_before_year_3),
			]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"ensure_day_in_month!",
	3,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("year_0"),
				Type.variablePattern("month_1"),
				Type.variablePattern("day_2"),
			],
			guards: [(context) => Erlang["is_integer/1"](context.vars.day_2)],
			body: (context) => {
				return Interpreter.case(
					Interpreter.case(
						Erlang["</2"](context.vars.day_2, Type.integer(1n)),
						[
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return Erlang[">/2"](
										context.vars.day_2,
										Elixir_Calendar_ISO["days_in_month/2"](
											context.vars.year_0,
											context.vars.month_1,
										),
									);
								},
							},
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Type.atom("true");
								},
							},
						],
						context,
					),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Erlang["error/1"](
									Elixir_ArgumentError["exception/1"](
										Type.bitstring([
											Type.bitstringSegment(Type.string("invalid date: "), {
												type: "binary",
											}),
											Type.bitstringSegment(
												Elixir_String_Chars["to_string/1"](
													Elixir_Calendar_ISO["date_to_string/3"](
														context.vars.year_0,
														context.vars.month_1,
														context.vars.day_2,
													),
												),
												{ type: "binary" },
											),
										]),
									),
								);
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"time_to_iodata",
	5,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("hour_0"),
				Type.variablePattern("minute_1"),
				Type.variablePattern("second_2"),
				Interpreter.matchOperator(
					Type.variablePattern("microsecond_5"),
					Type.tuple([
						Type.variablePattern("ms_value_3"),
						Type.variablePattern("ms_precision_4"),
					]),
					context,
				),
				Type.variablePattern("format_6"),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) =>
							Erlang["andalso/2"](
								(context) =>
									Erlang["andalso/2"](
										(context) =>
											Erlang["andalso/2"](
												(context) =>
													Erlang["andalso/2"](
														(context) =>
															Erlang["is_integer/1"](context.vars.hour_0),
														(context) =>
															Erlang["andalso/2"](
																(context) =>
																	Erlang[">=/2"](
																		context.vars.hour_0,
																		Type.integer(0n),
																	),
																(context) =>
																	Erlang["=</2"](
																		context.vars.hour_0,
																		Type.integer(23n),
																	),
																context,
															),
														context,
													),
												(context) =>
													Erlang["andalso/2"](
														(context) =>
															Erlang["is_integer/1"](context.vars.minute_1),
														(context) =>
															Erlang["andalso/2"](
																(context) =>
																	Erlang[">=/2"](
																		context.vars.minute_1,
																		Type.integer(0n),
																	),
																(context) =>
																	Erlang["=</2"](
																		context.vars.minute_1,
																		Type.integer(59n),
																	),
																context,
															),
														context,
													),
												context,
											),
										(context) =>
											Erlang["andalso/2"](
												(context) =>
													Erlang["is_integer/1"](context.vars.second_2),
												(context) =>
													Erlang["andalso/2"](
														(context) =>
															Erlang[">=/2"](
																context.vars.second_2,
																Type.integer(0n),
															),
														(context) =>
															Erlang["=</2"](
																context.vars.second_2,
																Type.integer(59n),
															),
														context,
													),
												context,
											),
										context,
									),
								(context) =>
									Erlang["andalso/2"](
										(context) =>
											Erlang["andalso/2"](
												(context) =>
													Erlang["is_integer/1"](context.vars.ms_value_3),
												(context) =>
													Erlang["andalso/2"](
														(context) =>
															Erlang[">=/2"](
																context.vars.ms_value_3,
																Type.integer(0n),
															),
														(context) =>
															Erlang["=</2"](
																context.vars.ms_value_3,
																Type.integer(999999n),
															),
														context,
													),
												context,
											),
										(context) =>
											Erlang["andalso/2"](
												(context) =>
													Erlang["is_integer/1"](context.vars.ms_precision_4),
												(context) =>
													Erlang["andalso/2"](
														(context) =>
															Erlang[">=/2"](
																context.vars.ms_precision_4,
																Type.integer(0n),
															),
														(context) =>
															Erlang["=</2"](
																context.vars.ms_precision_4,
																Type.integer(6n),
															),
														context,
													),
												context,
											),
										context,
									),
								context,
							),
						(context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["=:=/2"](context.vars.format_6, Type.atom("basic")),
								(context) =>
									Erlang["=:=/2"](context.vars.format_6, Type.atom("extended")),
								context,
							),
						context,
					),
			],
			body: (context) => {
				return Elixir_Calendar_ISO["time_to_iodata_guarded/5"](
					context.vars.hour_0,
					context.vars.minute_1,
					context.vars.second_2,
					context.vars.microsecond_5,
					context.vars.format_6,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"date_to_iso_days",
	3,
	"public",
	[
		{
			params: (context) => [
				Type.integer(0n),
				Type.integer(1n),
				Type.integer(1n),
			],
			guards: [],
			body: (context) => {
				return Type.integer(0n);
			},
		},
		{
			params: (context) => [
				Type.integer(1970n),
				Type.integer(1n),
				Type.integer(1n),
			],
			guards: [],
			body: (context) => {
				return Type.integer(719528n);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("year_0"),
				Type.variablePattern("month_1"),
				Type.variablePattern("day_2"),
			],
			guards: [],
			body: (context) => {
				Elixir_Calendar_ISO["ensure_day_in_month!/3"](
					context.vars.year_0,
					context.vars.month_1,
					context.vars.day_2,
				);
				return Erlang["-/2"](
					Erlang["+/2"](
						Erlang["+/2"](
							Erlang["+/2"](
								Elixir_Calendar_ISO["days_in_previous_years/1"](
									context.vars.year_0,
								),
								Elixir_Calendar_ISO["days_before_month/1"](
									context.vars.month_1,
								),
							),
							Elixir_Calendar_ISO["leap_day_offset/2"](
								context.vars.year_0,
								context.vars.month_1,
							),
						),
						context.vars.day_2,
					),
					Type.integer(1n),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"time_to_string",
	5,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("hour_0"),
				Type.variablePattern("minute_1"),
				Type.variablePattern("second_2"),
				Type.variablePattern("microsecond_3"),
				Type.variablePattern("format_4"),
			],
			guards: [],
			body: (context) => {
				return Erlang["iolist_to_binary/1"](
					Elixir_Calendar_ISO["time_to_iodata/5"](
						context.vars.hour_0,
						context.vars.minute_1,
						context.vars.second_2,
						context.vars.microsecond_3,
						context.vars.format_4,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"days_in_month_guarded",
	2,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("year_0"), Type.integer(2n)],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Elixir_Calendar_ISO["leap_year?/1"](context.vars.year_0),
					[
						{
							match: Type.variablePattern("x_1"),
							guards: [
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](context.vars.x_1, Type.atom("false")),
										(context) =>
											Erlang["=:=/2"](context.vars.x_1, Type.atom("nil")),
										context,
									),
							],
							body: (context) => {
								return Type.integer(28n);
							},
						},
						{
							match: Type.matchPlaceholder(),
							guards: [],
							body: (context) => {
								return Type.integer(29n);
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [
				Type.matchPlaceholder(),
				Type.variablePattern("month_0"),
			],
			guards: [
				(context) =>
					Erlang["orelse/2"](
						(context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](context.vars.month_0, Type.integer(4n)),
										(context) =>
											Erlang["=:=/2"](context.vars.month_0, Type.integer(6n)),
										context,
									),
								(context) =>
									Erlang["=:=/2"](context.vars.month_0, Type.integer(9n)),
								context,
							),
						(context) =>
							Erlang["=:=/2"](context.vars.month_0, Type.integer(11n)),
						context,
					),
			],
			body: (context) => {
				return Type.integer(30n);
			},
		},
		{
			params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.integer(31n);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"offset_to_iodata",
	4,
	"public",
	[
		{
			params: (context) => [
				Type.integer(0n),
				Type.integer(0n),
				Type.bitstring("Etc/UTC"),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				return Type.integer(90n);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("utc_0"),
				Type.variablePattern("std_1"),
				Type.matchPlaceholder(),
				Type.variablePattern("format_3"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Erlang["+/2"](context.vars.utc_0, context.vars.std_1),
					Type.variablePattern("total_4"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Erlang["abs/1"](context.vars.total_4),
					Type.variablePattern("second_5"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Erlang["div/2"](
						Erlang["rem/2"](context.vars.second_5, Type.integer(3600n)),
						Type.integer(60n),
					),
					Type.variablePattern("minute_6"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Erlang["div/2"](context.vars.second_5, Type.integer(3600n)),
					Type.variablePattern("hour_7"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Calendar_ISO["format_offset/4"](
					context.vars.total_4,
					context.vars.hour_7,
					context.vars.minute_6,
					context.vars.format_3,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"leap_day_offset",
	2,
	"private",
	[
		{
			params: (context) => [
				Type.matchPlaceholder(),
				Type.variablePattern("month_1"),
			],
			guards: [
				(context) => Erlang["</2"](context.vars.month_1, Type.integer(3n)),
			],
			body: (context) => {
				return Type.integer(0n);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("year_0"),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Elixir_Calendar_ISO["leap_year?/1"](context.vars.year_0),
					[
						{
							match: Type.variablePattern("x_2"),
							guards: [
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](context.vars.x_2, Type.atom("false")),
										(context) =>
											Erlang["=:=/2"](context.vars.x_2, Type.atom("nil")),
										context,
									),
							],
							body: (context) => {
								return Type.integer(0n);
							},
						},
						{
							match: Type.matchPlaceholder(),
							guards: [],
							body: (context) => {
								return Type.integer(1n);
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"year_day_to_year_date",
	2,
	"private",
	[
		{
			params: (context) => [
				Type.matchPlaceholder(),
				Type.variablePattern("day_of_year_1"),
			],
			guards: [
				(context) =>
					Erlang["</2"](context.vars.day_of_year_1, Type.integer(31n)),
			],
			body: (context) => {
				return Type.tuple([Type.integer(1n), context.vars.day_of_year_1]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("extra_day_0"),
				Type.variablePattern("day_of_year_1"),
			],
			guards: [
				(context) =>
					Erlang["</2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(59n), context.vars.extra_day_0),
					),
			],
			body: (context) => {
				return Type.tuple([
					Type.integer(2n),
					Erlang["-/2"](context.vars.day_of_year_1, Type.integer(31n)),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("extra_day_0"),
				Type.variablePattern("day_of_year_1"),
			],
			guards: [
				(context) =>
					Erlang["</2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(90n), context.vars.extra_day_0),
					),
			],
			body: (context) => {
				return Type.tuple([
					Type.integer(3n),
					Erlang["-/2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(59n), context.vars.extra_day_0),
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("extra_day_0"),
				Type.variablePattern("day_of_year_1"),
			],
			guards: [
				(context) =>
					Erlang["</2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(120n), context.vars.extra_day_0),
					),
			],
			body: (context) => {
				return Type.tuple([
					Type.integer(4n),
					Erlang["-/2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(90n), context.vars.extra_day_0),
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("extra_day_0"),
				Type.variablePattern("day_of_year_1"),
			],
			guards: [
				(context) =>
					Erlang["</2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(151n), context.vars.extra_day_0),
					),
			],
			body: (context) => {
				return Type.tuple([
					Type.integer(5n),
					Erlang["-/2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(120n), context.vars.extra_day_0),
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("extra_day_0"),
				Type.variablePattern("day_of_year_1"),
			],
			guards: [
				(context) =>
					Erlang["</2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(181n), context.vars.extra_day_0),
					),
			],
			body: (context) => {
				return Type.tuple([
					Type.integer(6n),
					Erlang["-/2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(151n), context.vars.extra_day_0),
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("extra_day_0"),
				Type.variablePattern("day_of_year_1"),
			],
			guards: [
				(context) =>
					Erlang["</2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(212n), context.vars.extra_day_0),
					),
			],
			body: (context) => {
				return Type.tuple([
					Type.integer(7n),
					Erlang["-/2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(181n), context.vars.extra_day_0),
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("extra_day_0"),
				Type.variablePattern("day_of_year_1"),
			],
			guards: [
				(context) =>
					Erlang["</2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(243n), context.vars.extra_day_0),
					),
			],
			body: (context) => {
				return Type.tuple([
					Type.integer(8n),
					Erlang["-/2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(212n), context.vars.extra_day_0),
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("extra_day_0"),
				Type.variablePattern("day_of_year_1"),
			],
			guards: [
				(context) =>
					Erlang["</2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(273n), context.vars.extra_day_0),
					),
			],
			body: (context) => {
				return Type.tuple([
					Type.integer(9n),
					Erlang["-/2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(243n), context.vars.extra_day_0),
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("extra_day_0"),
				Type.variablePattern("day_of_year_1"),
			],
			guards: [
				(context) =>
					Erlang["</2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(304n), context.vars.extra_day_0),
					),
			],
			body: (context) => {
				return Type.tuple([
					Type.integer(10n),
					Erlang["-/2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(273n), context.vars.extra_day_0),
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("extra_day_0"),
				Type.variablePattern("day_of_year_1"),
			],
			guards: [
				(context) =>
					Erlang["</2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(334n), context.vars.extra_day_0),
					),
			],
			body: (context) => {
				return Type.tuple([
					Type.integer(11n),
					Erlang["-/2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(304n), context.vars.extra_day_0),
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("extra_day_0"),
				Type.variablePattern("day_of_year_1"),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.integer(12n),
					Erlang["-/2"](
						context.vars.day_of_year_1,
						Erlang["+/2"](Type.integer(334n), context.vars.extra_day_0),
					),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"time_to_string",
	4,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("x0_0"),
				Type.variablePattern("x1_1"),
				Type.variablePattern("x2_2"),
				Type.variablePattern("x3_3"),
			],
			guards: [],
			body: (context) => {
				return Elixir_Calendar_ISO["time_to_string/5"](
					context.vars.x0_0,
					context.vars.x1_1,
					context.vars.x2_2,
					context.vars.x3_3,
					Type.atom("extended"),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Calendar.ISO", "sign", 1, "private", [
	{
		params: (context) => [Type.variablePattern("total_0")],
		guards: [
			(context) => Erlang["</2"](context.vars.total_0, Type.integer(0n)),
		],
		body: (context) => {
			return Type.integer(45n);
		},
	},
	{
		params: (context) => [Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.integer(43n);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"time_to_iodata_format",
	4,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("hour_0"),
				Type.variablePattern("minute_1"),
				Type.variablePattern("second_2"),
				Type.atom("extended"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.consOperator(
					Elixir_Calendar_ISO["zero_pad/2"](
						context.vars.hour_0,
						Type.integer(2n),
					),
					Interpreter.consOperator(
						Type.integer(58n),
						Interpreter.consOperator(
							Elixir_Calendar_ISO["zero_pad/2"](
								context.vars.minute_1,
								Type.integer(2n),
							),
							Interpreter.consOperator(
								Type.integer(58n),
								Elixir_Calendar_ISO["zero_pad/2"](
									context.vars.second_2,
									Type.integer(2n),
								),
							),
						),
					),
				);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("hour_0"),
				Type.variablePattern("minute_1"),
				Type.variablePattern("second_2"),
				Type.atom("basic"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.consOperator(
					Elixir_Calendar_ISO["zero_pad/2"](
						context.vars.hour_0,
						Type.integer(2n),
					),
					Interpreter.consOperator(
						Elixir_Calendar_ISO["zero_pad/2"](
							context.vars.minute_1,
							Type.integer(2n),
						),
						Elixir_Calendar_ISO["zero_pad/2"](
							context.vars.second_2,
							Type.integer(2n),
						),
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Calendar.ISO",
	"date_to_iodata_guarded",
	4,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("year_0"),
				Type.variablePattern("month_1"),
				Type.variablePattern("day_2"),
				Type.atom("extended"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.consOperator(
					Elixir_Calendar_ISO["zero_pad/2"](
						context.vars.year_0,
						Type.integer(4n),
					),
					Interpreter.consOperator(
						Type.integer(45n),
						Interpreter.consOperator(
							Elixir_Calendar_ISO["zero_pad/2"](
								context.vars.month_1,
								Type.integer(2n),
							),
							Interpreter.consOperator(
								Type.integer(45n),
								Elixir_Calendar_ISO["zero_pad/2"](
									context.vars.day_2,
									Type.integer(2n),
								),
							),
						),
					),
				);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("year_0"),
				Type.variablePattern("month_1"),
				Type.variablePattern("day_2"),
				Type.atom("basic"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.consOperator(
					Elixir_Calendar_ISO["zero_pad/2"](
						context.vars.year_0,
						Type.integer(4n),
					),
					Interpreter.consOperator(
						Elixir_Calendar_ISO["zero_pad/2"](
							context.vars.month_1,
							Type.integer(2n),
						),
						Elixir_Calendar_ISO["zero_pad/2"](
							context.vars.day_2,
							Type.integer(2n),
						),
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("CaseClauseError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.CaseClauseError")],
				[Type.atom("term"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("CaseClauseError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.CaseClauseError")],
					[Type.atom("term"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Code",
	"can_await_module_compilation?",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Erlang["==/2"](
					Erlang["process_info/2"](
						Erlang["self/0"](),
						Type.atom("error_handler"),
					),
					Type.tuple([
						Type.atom("error_handler"),
						Type.atom("Elixir.Kernel.ErrorHandler"),
					]),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Code", "ensure_compiled", 1, "public", [
	{
		params: (context) => [Type.variablePattern("module_0")],
		guards: [(context) => Erlang["is_atom/1"](context.vars.module_0)],
		body: (context) => {
			return Elixir_Code["ensure_compiled/2"](
				context.vars.module_0,
				Type.atom("soft"),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Code", "ensure_compiled", 2, "private", [
	{
		params: (context) => [
			Type.variablePattern("module_0"),
			Type.variablePattern("mode_1"),
		],
		guards: [],
		body: (context) => {
			globalThis.Hologram.return = Interpreter.case(
				Erlang_Code["ensure_loaded/1"](context.vars.module_0),
				[
					{
						match: Interpreter.matchOperator(
							Type.variablePattern("error_2"),
							Type.tuple([Type.atom("error"), Type.atom("nofile")]),
							context,
						),
						guards: [],
						body: (context) => {
							return Interpreter.case(
								Elixir_Code["can_await_module_compilation?/0"](),
								[
									{
										match: Type.variablePattern("x_3"),
										guards: [
											(context) =>
												Erlang["orelse/2"](
													(context) =>
														Erlang["=:=/2"](
															context.vars.x_3,
															Type.atom("false"),
														),
													(context) =>
														Erlang["=:=/2"](context.vars.x_3, Type.atom("nil")),
													context,
												),
										],
										body: (context) => {
											return context.vars.error_2;
										},
									},
									{
										match: Type.matchPlaceholder(),
										guards: [],
										body: (context) => {
											return Interpreter.case(
												Elixir_Kernel_ErrorHandler["ensure_compiled/4"](
													context.vars.module_0,
													Type.atom("module"),
													context.vars.mode_1,
													Type.atom("nil"),
												),
												[
													{
														match: Type.atom("found"),
														guards: [],
														body: (context) => {
															return Type.tuple([
																Type.atom("module"),
																context.vars.module_0,
															]);
														},
													},
													{
														match: Type.atom("deadlock"),
														guards: [],
														body: (context) => {
															return Type.tuple([
																Type.atom("error"),
																Type.atom("unavailable"),
															]);
														},
													},
													{
														match: Type.atom("not_found"),
														guards: [],
														body: (context) => {
															return Type.tuple([
																Type.atom("error"),
																Type.atom("nofile"),
															]);
														},
													},
												],
												context,
											);
										},
									},
								],
								context,
							);
						},
					},
					{
						match: Type.variablePattern("other_4"),
						guards: [],
						body: (context) => {
							return context.vars.other_4;
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("Code.Identifier", "binary_op", 1, "public", [
	{
		params: (context) => [Type.variablePattern("op_0")],
		guards: [],
		body: (context) => {
			return Interpreter.cond(
				[
					{
						condition: (context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["=:=/2"](context.vars.op_0, Type.atom("<-")),
								(context) =>
									Erlang["=:=/2"](context.vars.op_0, Type.atom("\\\\")),
								context,
							),
						body: (context) => {
							return Type.tuple([Type.atom("left"), Type.integer(40n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["=:=/2"](context.vars.op_0, Type.atom("when")),
						body: (context) => {
							return Type.tuple([Type.atom("right"), Type.integer(50n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["=:=/2"](context.vars.op_0, Type.atom("::")),
						body: (context) => {
							return Type.tuple([Type.atom("right"), Type.integer(60n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["=:=/2"](context.vars.op_0, Type.atom("|")),
						body: (context) => {
							return Type.tuple([Type.atom("right"), Type.integer(70n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["=:=/2"](context.vars.op_0, Type.atom("=")),
						body: (context) => {
							return Type.tuple([Type.atom("right"), Type.integer(100n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](context.vars.op_0, Type.atom("||")),
										(context) =>
											Erlang["=:=/2"](context.vars.op_0, Type.atom("|||")),
										context,
									),
								(context) =>
									Erlang["=:=/2"](context.vars.op_0, Type.atom("or")),
								context,
							),
						body: (context) => {
							return Type.tuple([Type.atom("left"), Type.integer(120n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](context.vars.op_0, Type.atom("&&")),
										(context) =>
											Erlang["=:=/2"](context.vars.op_0, Type.atom("&&&")),
										context,
									),
								(context) =>
									Erlang["=:=/2"](context.vars.op_0, Type.atom("and")),
								context,
							),
						body: (context) => {
							return Type.tuple([Type.atom("left"), Type.integer(130n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["orelse/2"](
												(context) =>
													Erlang["orelse/2"](
														(context) =>
															Erlang["=:=/2"](
																context.vars.op_0,
																Type.atom("=="),
															),
														(context) =>
															Erlang["=:=/2"](
																context.vars.op_0,
																Type.atom("!="),
															),
														context,
													),
												(context) =>
													Erlang["=:=/2"](context.vars.op_0, Type.atom("=~")),
												context,
											),
										(context) =>
											Erlang["=:=/2"](context.vars.op_0, Type.atom("===")),
										context,
									),
								(context) =>
									Erlang["=:=/2"](context.vars.op_0, Type.atom("!==")),
								context,
							),
						body: (context) => {
							return Type.tuple([Type.atom("left"), Type.integer(140n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["orelse/2"](
												(context) =>
													Erlang["=:=/2"](context.vars.op_0, Type.atom("<")),
												(context) =>
													Erlang["=:=/2"](context.vars.op_0, Type.atom("<=")),
												context,
											),
										(context) =>
											Erlang["=:=/2"](context.vars.op_0, Type.atom(">=")),
										context,
									),
								(context) => Erlang["=:=/2"](context.vars.op_0, Type.atom(">")),
								context,
							),
						body: (context) => {
							return Type.tuple([Type.atom("left"), Type.integer(150n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["orelse/2"](
												(context) =>
													Erlang["orelse/2"](
														(context) =>
															Erlang["orelse/2"](
																(context) =>
																	Erlang["orelse/2"](
																		(context) =>
																			Erlang["orelse/2"](
																				(context) =>
																					Erlang["orelse/2"](
																						(context) =>
																							Erlang["=:=/2"](
																								context.vars.op_0,
																								Type.atom("|>"),
																							),
																						(context) =>
																							Erlang["=:=/2"](
																								context.vars.op_0,
																								Type.atom("<<<"),
																							),
																						context,
																					),
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.op_0,
																						Type.atom(">>>"),
																					),
																				context,
																			),
																		(context) =>
																			Erlang["=:=/2"](
																				context.vars.op_0,
																				Type.atom("<~"),
																			),
																		context,
																	),
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.op_0,
																		Type.atom("~>"),
																	),
																context,
															),
														(context) =>
															Erlang["=:=/2"](
																context.vars.op_0,
																Type.atom("<<~"),
															),
														context,
													),
												(context) =>
													Erlang["=:=/2"](context.vars.op_0, Type.atom("~>>")),
												context,
											),
										(context) =>
											Erlang["=:=/2"](context.vars.op_0, Type.atom("<~>")),
										context,
									),
								(context) =>
									Erlang["=:=/2"](context.vars.op_0, Type.atom("<|>")),
								context,
							),
						body: (context) => {
							return Type.tuple([Type.atom("left"), Type.integer(160n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["=:=/2"](context.vars.op_0, Type.atom("in")),
						body: (context) => {
							return Type.tuple([Type.atom("left"), Type.integer(170n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["=:=/2"](context.vars.op_0, Type.atom("^^^")),
						body: (context) => {
							return Type.tuple([Type.atom("left"), Type.integer(180n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["orelse/2"](
												(context) =>
													Erlang["orelse/2"](
														(context) =>
															Erlang["orelse/2"](
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.op_0,
																		Type.atom("++"),
																	),
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.op_0,
																		Type.atom("--"),
																	),
																context,
															),
														(context) =>
															Erlang["=:=/2"](
																context.vars.op_0,
																Type.atom(".."),
															),
														context,
													),
												(context) =>
													Erlang["=:=/2"](context.vars.op_0, Type.atom("<>")),
												context,
											),
										(context) =>
											Erlang["=:=/2"](context.vars.op_0, Type.atom("+++")),
										context,
									),
								(context) =>
									Erlang["=:=/2"](context.vars.op_0, Type.atom("---")),
								context,
							),
						body: (context) => {
							return Type.tuple([Type.atom("right"), Type.integer(200n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["orelse/2"](
								(context) => Erlang["=:=/2"](context.vars.op_0, Type.atom("+")),
								(context) => Erlang["=:=/2"](context.vars.op_0, Type.atom("-")),
								context,
							),
						body: (context) => {
							return Type.tuple([Type.atom("left"), Type.integer(210n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["orelse/2"](
								(context) => Erlang["=:=/2"](context.vars.op_0, Type.atom("*")),
								(context) => Erlang["=:=/2"](context.vars.op_0, Type.atom("/")),
								context,
							),
						body: (context) => {
							return Type.tuple([Type.atom("left"), Type.integer(220n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["=:=/2"](context.vars.op_0, Type.atom("**")),
						body: (context) => {
							return Type.tuple([Type.atom("left"), Type.integer(230n)]);
						},
					},
					{
						condition: (context) =>
							Erlang["=:=/2"](context.vars.op_0, Type.atom(".")),
						body: (context) => {
							return Type.tuple([Type.atom("left"), Type.integer(310n)]);
						},
					},
					{
						condition: (context) => Type.atom("true"),
						body: (context) => {
							return Type.atom("error");
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Code.Identifier", "decrement", 1, "private", [
	{
		params: (context) => [Type.atom("infinity")],
		guards: [],
		body: (context) => {
			return Type.atom("infinity");
		},
	},
	{
		params: (context) => [Type.variablePattern("counter_0")],
		guards: [],
		body: (context) => {
			return Erlang["-/2"](context.vars.counter_0, Type.integer(1n));
		},
	},
]);

Interpreter.defineElixirFunction("Code.Identifier", "escape", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("x0_0"),
			Type.variablePattern("x1_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Code_Identifier["escape/4"](
				context.vars.x0_0,
				context.vars.x1_1,
				Type.atom("infinity"),
				Type.functionCapture(
					"Code.Identifier",
					"escape_map",
					1,
					[
						{
							params: (context) => [Type.variablePattern("$1")],
							guards: [],
							body: (context) => {
								return Elixir_Code_Identifier["escape_map/1"](
									context.vars["$1"],
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Code.Identifier", "escape", 4, "public", [
	{
		params: (context) => [
			Type.variablePattern("binary_0"),
			Type.variablePattern("char_1"),
			Type.variablePattern("limit_2"),
			Type.variablePattern("fun_3"),
		],
		guards: [
			(context) =>
				Erlang["orelse/2"](
					(context) =>
						Erlang["andalso/2"](
							(context) => Erlang["is_binary/1"](context.vars.binary_0),
							(context) =>
								Erlang["andalso/2"](
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["andalso/2"](
													(context) =>
														Erlang["is_integer/1"](context.vars.char_1),
													(context) =>
														Erlang["andalso/2"](
															(context) =>
																Erlang[">=/2"](
																	context.vars.char_1,
																	Type.integer(0n),
																),
															(context) =>
																Erlang["=</2"](
																	context.vars.char_1,
																	Type.integer(1114111n),
																),
															context,
														),
													context,
												),
											(context) =>
												Erlang["==/2"](context.vars.char_1, Type.atom("nil")),
											context,
										),
									(context) =>
										Erlang["==/2"](context.vars.limit_2, Type.atom("infinity")),
									context,
								),
							context,
						),
					(context) =>
						Erlang["andalso/2"](
							(context) => Erlang["is_integer/1"](context.vars.limit_2),
							(context) =>
								Erlang[">=/2"](context.vars.limit_2, Type.integer(0n)),
							context,
						),
					context,
				),
		],
		body: (context) => {
			return Elixir_Code_Identifier["escape/5"](
				context.vars.binary_0,
				context.vars.char_1,
				context.vars.limit_2,
				Type.bitstring([]),
				context.vars.fun_3,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Code.Identifier", "escape", 5, "private", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("binary_0"),
				Type.bitstringPattern([
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "integer" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
				context,
			),
			Type.matchPlaceholder(),
			Type.integer(0n),
			Type.variablePattern("acc_2"),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([context.vars.acc_2, context.vars.binary_0]);
		},
	},
	{
		params: (context) => [
			Type.bitstringPattern([
				Type.bitstringSegment(Type.variablePattern("char_0"), {
					type: "integer",
				}),
				Type.bitstringSegment(Type.variablePattern("t_1"), { type: "binary" }),
			]),
			Type.variablePattern("char_0"),
			Type.variablePattern("count_2"),
			Type.variablePattern("acc_3"),
			Type.variablePattern("fun_4"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Code_Identifier["escape/5"](
				context.vars.t_1,
				context.vars.char_0,
				Elixir_Code_Identifier["decrement/1"](context.vars.count_2),
				Type.bitstring([
					Type.bitstringSegment(context.vars.acc_3, { type: "binary" }),
					Type.bitstringSegment(Type.integer(92n), { type: "integer" }),
					Type.bitstringSegment(context.vars.char_0, { type: "integer" }),
				]),
				context.vars.fun_4,
			);
		},
	},
	{
		params: (context) => [
			Type.bitstringPattern([
				Type.bitstringSegment(Type.integer(35n), { type: "integer" }),
				Type.bitstringSegment(Type.integer(123n), { type: "integer" }),
				Type.bitstringSegment(Type.variablePattern("t_0"), { type: "binary" }),
			]),
			Type.variablePattern("char_1"),
			Type.variablePattern("count_2"),
			Type.variablePattern("acc_3"),
			Type.variablePattern("fun_4"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Code_Identifier["escape/5"](
				context.vars.t_0,
				context.vars.char_1,
				Elixir_Code_Identifier["decrement/1"](context.vars.count_2),
				Type.bitstring([
					Type.bitstringSegment(context.vars.acc_3, { type: "binary" }),
					Type.bitstringSegment(Type.integer(92n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(35n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(123n), { type: "integer" }),
				]),
				context.vars.fun_4,
			);
		},
	},
	{
		params: (context) => [
			Type.bitstringPattern([
				Type.bitstringSegment(Type.variablePattern("h_0"), { type: "utf8" }),
				Type.bitstringSegment(Type.variablePattern("t_1"), { type: "binary" }),
			]),
			Type.variablePattern("char_2"),
			Type.variablePattern("count_3"),
			Type.variablePattern("acc_4"),
			Type.variablePattern("fun_5"),
		],
		guards: [],
		body: (context) => {
			globalThis.Hologram.return = Interpreter.case(
				Interpreter.matchOperator(
					Interpreter.callAnonymousFunction(context.vars.fun_5, [
						context.vars.h_0,
					]),
					Type.variablePattern("value_6"),
					context,
				),
				[
					{
						match: Type.variablePattern("x_7"),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["=:=/2"](context.vars.x_7, Type.atom("false")),
									(context) =>
										Erlang["=:=/2"](context.vars.x_7, Type.atom("nil")),
									context,
								),
						],
						body: (context) => {
							return Elixir_Code_Identifier["escape/5"](
								context.vars.t_1,
								context.vars.char_2,
								Elixir_Code_Identifier["decrement/1"](context.vars.count_3),
								Elixir_Code_Identifier["escape_char/2"](
									context.vars.h_0,
									context.vars.acc_4,
								),
								context.vars.fun_5,
							);
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							Interpreter.matchOperator(
								Erlang["iolist_to_binary/1"](context.vars.value_6),
								Type.variablePattern("value_8"),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							return Elixir_Code_Identifier["escape/5"](
								context.vars.t_1,
								context.vars.char_2,
								Elixir_Code_Identifier["decrement/1"](context.vars.count_3),
								Type.bitstring([
									Type.bitstringSegment(context.vars.acc_4, { type: "binary" }),
									Type.bitstringSegment(context.vars.value_8, {
										type: "binary",
									}),
								]),
								context.vars.fun_5,
							);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
	{
		params: (context) => [
			Type.bitstringPattern([
				Type.bitstringSegment(Type.variablePattern("a_0"), {
					size: Type.integer(4n),
					type: "integer",
				}),
				Type.bitstringSegment(Type.variablePattern("b_1"), {
					size: Type.integer(4n),
					type: "integer",
				}),
				Type.bitstringSegment(Type.variablePattern("t_2"), { type: "binary" }),
			]),
			Type.variablePattern("char_3"),
			Type.variablePattern("count_4"),
			Type.variablePattern("acc_5"),
			Type.variablePattern("fun_6"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Code_Identifier["escape/5"](
				context.vars.t_2,
				context.vars.char_3,
				Elixir_Code_Identifier["decrement/1"](context.vars.count_4),
				Type.bitstring([
					Type.bitstringSegment(context.vars.acc_5, { type: "binary" }),
					Type.bitstringSegment(Type.integer(92n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(120n), { type: "integer" }),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.a_0),
						{ type: "integer" },
					),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.b_1),
						{ type: "integer" },
					),
				]),
				context.vars.fun_6,
			);
		},
	},
	{
		params: (context) => [
			Type.bitstringPattern([]),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
			Type.variablePattern("acc_2"),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([context.vars.acc_2, Type.bitstring([])]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Code.Identifier",
	"escape_char",
	2,
	"private",
	[
		{
			params: (context) => [Type.integer(0n), Type.variablePattern("acc_0")],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(context.vars.acc_0, { type: "binary" }),
					Type.bitstringSegment(Type.integer(92n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(48n), { type: "integer" }),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("char_0"),
				Type.variablePattern("acc_1"),
			],
			guards: [
				(context) => Erlang["==/2"](context.vars.char_0, Type.integer(65279n)),
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang["is_integer/1"](context.vars.char_0),
						(context) =>
							Erlang["andalso/2"](
								(context) =>
									Erlang[">=/2"](context.vars.char_0, Type.integer(8289n)),
								(context) =>
									Erlang["=</2"](context.vars.char_0, Type.integer(8292n)),
								context,
							),
						context,
					),
				(context) =>
					Erlang["orelse/2"](
						(context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["=:=/2"](context.vars.char_0, Type.integer(1564n)),
								(context) =>
									Erlang["=:=/2"](context.vars.char_0, Type.integer(8206n)),
								context,
							),
						(context) =>
							Erlang["=:=/2"](context.vars.char_0, Type.integer(8207n)),
						context,
					),
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang["is_integer/1"](context.vars.char_0),
						(context) =>
							Erlang["andalso/2"](
								(context) =>
									Erlang[">=/2"](context.vars.char_0, Type.integer(8234n)),
								(context) =>
									Erlang["=</2"](context.vars.char_0, Type.integer(8238n)),
								context,
							),
						context,
					),
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang["is_integer/1"](context.vars.char_0),
						(context) =>
							Erlang["andalso/2"](
								(context) =>
									Erlang[">=/2"](context.vars.char_0, Type.integer(8294n)),
								(context) =>
									Erlang["=</2"](context.vars.char_0, Type.integer(8297n)),
								context,
							),
						context,
					),
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang["is_integer/1"](context.vars.char_0),
						(context) =>
							Erlang["andalso/2"](
								(context) =>
									Erlang[">=/2"](context.vars.char_0, Type.integer(65529n)),
								(context) =>
									Erlang["=</2"](context.vars.char_0, Type.integer(65532n)),
								context,
							),
						context,
					),
				(context) =>
					Erlang["orelse/2"](
						(context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["=:=/2"](context.vars.char_0, Type.integer(8204n)),
								(context) =>
									Erlang["=:=/2"](context.vars.char_0, Type.integer(8205n)),
								context,
							),
						(context) =>
							Erlang["=:=/2"](context.vars.char_0, Type.integer(847n)),
						context,
					),
				(context) =>
					Erlang["orelse/2"](
						(context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["=:=/2"](context.vars.char_0, Type.integer(160n)),
								(context) =>
									Erlang["=:=/2"](context.vars.char_0, Type.integer(8203n)),
								context,
							),
						(context) =>
							Erlang["=:=/2"](context.vars.char_0, Type.integer(8288n)),
						context,
					),
				(context) =>
					Erlang["orelse/2"](
						(context) =>
							Erlang["=:=/2"](context.vars.char_0, Type.integer(8232n)),
						(context) =>
							Erlang["=:=/2"](context.vars.char_0, Type.integer(8233n)),
						context,
					),
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang["is_integer/1"](context.vars.char_0),
						(context) =>
							Erlang["andalso/2"](
								(context) =>
									Erlang[">=/2"](context.vars.char_0, Type.integer(8192n)),
								(context) =>
									Erlang["=</2"](context.vars.char_0, Type.integer(8202n)),
								context,
							),
						context,
					),
				(context) => Erlang["==/2"](context.vars.char_0, Type.integer(8287n)),
			],
			body: (context) => {
				Interpreter.matchOperator(
					Type.bitstring([
						Type.bitstringSegment(context.vars.char_0, {
							size: Type.integer(16n),
							type: "integer",
						}),
					]),
					Type.bitstringPattern([
						Type.bitstringSegment(Type.variablePattern("a_2"), {
							size: Type.integer(4n),
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("b_3"), {
							size: Type.integer(4n),
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("c_4"), {
							size: Type.integer(4n),
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("d_5"), {
							size: Type.integer(4n),
							type: "integer",
						}),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Type.bitstring([
					Type.bitstringSegment(context.vars.acc_1, { type: "binary" }),
					Type.bitstringSegment(Type.integer(92n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(117n), { type: "integer" }),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.a_2),
						{ type: "integer" },
					),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.b_3),
						{ type: "integer" },
					),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.c_4),
						{ type: "integer" },
					),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.d_5),
						{ type: "integer" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("char_0"),
				Type.variablePattern("acc_1"),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang["is_integer/1"](context.vars.char_0),
						(context) =>
							Erlang["andalso/2"](
								(context) =>
									Erlang[">=/2"](context.vars.char_0, Type.integer(32n)),
								(context) =>
									Erlang["=</2"](context.vars.char_0, Type.integer(126n)),
								context,
							),
						context,
					),
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang["is_integer/1"](context.vars.char_0),
						(context) =>
							Erlang["andalso/2"](
								(context) =>
									Erlang[">=/2"](context.vars.char_0, Type.integer(160n)),
								(context) =>
									Erlang["=</2"](context.vars.char_0, Type.integer(55295n)),
								context,
							),
						context,
					),
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang["is_integer/1"](context.vars.char_0),
						(context) =>
							Erlang["andalso/2"](
								(context) =>
									Erlang[">=/2"](context.vars.char_0, Type.integer(57344n)),
								(context) =>
									Erlang["=</2"](context.vars.char_0, Type.integer(65533n)),
								context,
							),
						context,
					),
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang["is_integer/1"](context.vars.char_0),
						(context) =>
							Erlang["andalso/2"](
								(context) =>
									Erlang[">=/2"](context.vars.char_0, Type.integer(65536n)),
								(context) =>
									Erlang["=</2"](context.vars.char_0, Type.integer(1114111n)),
								context,
							),
						context,
					),
			],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(context.vars.acc_1, { type: "binary" }),
					Type.bitstringSegment(context.vars.char_0, { type: "utf8" }),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("char_0"),
				Type.variablePattern("acc_1"),
			],
			guards: [
				(context) => Erlang["</2"](context.vars.char_0, Type.integer(256n)),
			],
			body: (context) => {
				Interpreter.matchOperator(
					Type.bitstring([
						Type.bitstringSegment(context.vars.char_0, {
							size: Type.integer(8n),
							type: "integer",
						}),
					]),
					Type.bitstringPattern([
						Type.bitstringSegment(Type.variablePattern("a_2"), {
							size: Type.integer(4n),
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("b_3"), {
							size: Type.integer(4n),
							type: "integer",
						}),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Type.bitstring([
					Type.bitstringSegment(context.vars.acc_1, { type: "binary" }),
					Type.bitstringSegment(Type.integer(92n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(120n), { type: "integer" }),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.a_2),
						{ type: "integer" },
					),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.b_3),
						{ type: "integer" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("char_0"),
				Type.variablePattern("acc_1"),
			],
			guards: [
				(context) => Erlang["</2"](context.vars.char_0, Type.integer(65536n)),
			],
			body: (context) => {
				Interpreter.matchOperator(
					Type.bitstring([
						Type.bitstringSegment(context.vars.char_0, {
							size: Type.integer(16n),
							type: "integer",
						}),
					]),
					Type.bitstringPattern([
						Type.bitstringSegment(Type.variablePattern("a_2"), {
							size: Type.integer(4n),
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("b_3"), {
							size: Type.integer(4n),
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("c_4"), {
							size: Type.integer(4n),
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("d_5"), {
							size: Type.integer(4n),
							type: "integer",
						}),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Type.bitstring([
					Type.bitstringSegment(context.vars.acc_1, { type: "binary" }),
					Type.bitstringSegment(Type.integer(92n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(120n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(123n), { type: "integer" }),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.a_2),
						{ type: "integer" },
					),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.b_3),
						{ type: "integer" },
					),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.c_4),
						{ type: "integer" },
					),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.d_5),
						{ type: "integer" },
					),
					Type.bitstringSegment(Type.integer(125n), { type: "integer" }),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("char_0"),
				Type.variablePattern("acc_1"),
			],
			guards: [
				(context) =>
					Erlang["</2"](context.vars.char_0, Type.integer(16777216n)),
			],
			body: (context) => {
				Interpreter.matchOperator(
					Type.bitstring([
						Type.bitstringSegment(context.vars.char_0, {
							size: Type.integer(24n),
							type: "integer",
						}),
					]),
					Type.bitstringPattern([
						Type.bitstringSegment(Type.variablePattern("a_2"), {
							size: Type.integer(4n),
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("b_3"), {
							size: Type.integer(4n),
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("c_4"), {
							size: Type.integer(4n),
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("d_5"), {
							size: Type.integer(4n),
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("e_6"), {
							size: Type.integer(4n),
							type: "integer",
						}),
						Type.bitstringSegment(Type.variablePattern("f_7"), {
							size: Type.integer(4n),
							type: "integer",
						}),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Type.bitstring([
					Type.bitstringSegment(context.vars.acc_1, { type: "binary" }),
					Type.bitstringSegment(Type.integer(92n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(120n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(123n), { type: "integer" }),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.a_2),
						{ type: "integer" },
					),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.b_3),
						{ type: "integer" },
					),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.c_4),
						{ type: "integer" },
					),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.d_5),
						{ type: "integer" },
					),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.e_6),
						{ type: "integer" },
					),
					Type.bitstringSegment(
						Elixir_Code_Identifier["to_hex/1"](context.vars.f_7),
						{ type: "integer" },
					),
					Type.bitstringSegment(Type.integer(125n), { type: "integer" }),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Code.Identifier",
	"escape_map",
	1,
	"private",
	[
		{
			params: (context) => [Type.integer(7n)],
			guards: [],
			body: (context) => {
				return Type.bitstring("\\a");
			},
		},
		{
			params: (context) => [Type.integer(8n)],
			guards: [],
			body: (context) => {
				return Type.bitstring("\\b");
			},
		},
		{
			params: (context) => [Type.integer(127n)],
			guards: [],
			body: (context) => {
				return Type.bitstring("\\d");
			},
		},
		{
			params: (context) => [Type.integer(27n)],
			guards: [],
			body: (context) => {
				return Type.bitstring("\\e");
			},
		},
		{
			params: (context) => [Type.integer(12n)],
			guards: [],
			body: (context) => {
				return Type.bitstring("\\f");
			},
		},
		{
			params: (context) => [Type.integer(10n)],
			guards: [],
			body: (context) => {
				return Type.bitstring("\\n");
			},
		},
		{
			params: (context) => [Type.integer(13n)],
			guards: [],
			body: (context) => {
				return Type.bitstring("\\r");
			},
		},
		{
			params: (context) => [Type.integer(9n)],
			guards: [],
			body: (context) => {
				return Type.bitstring("\\t");
			},
		},
		{
			params: (context) => [Type.integer(11n)],
			guards: [],
			body: (context) => {
				return Type.bitstring("\\v");
			},
		},
		{
			params: (context) => [Type.integer(92n)],
			guards: [],
			body: (context) => {
				return Type.bitstring("\\\\");
			},
		},
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.atom("false");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Code.Identifier",
	"extract_anonymous_fun_parent",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("atom_0")],
			guards: [(context) => Erlang["is_atom/1"](context.vars.atom_0)],
			body: (context) => {
				return Interpreter.with();
			},
		},
	],
);

Interpreter.defineElixirFunction("Code.Identifier", "to_hex", 1, "private", [
	{
		params: (context) => [Type.variablePattern("c_0")],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_integer/1"](context.vars.c_0),
					(context) =>
						Erlang["andalso/2"](
							(context) => Erlang[">=/2"](context.vars.c_0, Type.integer(0n)),
							(context) => Erlang["=</2"](context.vars.c_0, Type.integer(9n)),
							context,
						),
					context,
				),
		],
		body: (context) => {
			return Erlang["+/2"](Type.integer(48n), context.vars.c_0);
		},
	},
	{
		params: (context) => [Type.variablePattern("c_0")],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_integer/1"](context.vars.c_0),
					(context) =>
						Erlang["andalso/2"](
							(context) => Erlang[">=/2"](context.vars.c_0, Type.integer(10n)),
							(context) => Erlang["=</2"](context.vars.c_0, Type.integer(15n)),
							context,
						),
					context,
				),
		],
		body: (context) => {
			return Erlang["-/2"](
				Erlang["+/2"](Type.integer(65n), context.vars.c_0),
				Type.integer(10n),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Code.Identifier", "unary_op", 1, "public", [
	{
		params: (context) => [Type.variablePattern("op_0")],
		guards: [],
		body: (context) => {
			return Interpreter.cond(
				[
					{
						condition: (context) =>
							Erlang["orelse/2"](
								(context) => Erlang["=:=/2"](context.vars.op_0, Type.atom("&")),
								(context) =>
									Erlang["=:=/2"](context.vars.op_0, Type.atom("...")),
								context,
							),
						body: (context) => {
							return Type.tuple([
								Type.atom("non_associative"),
								Type.integer(90n),
							]);
						},
					},
					{
						condition: (context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["orelse/2"](
												(context) =>
													Erlang["orelse/2"](
														(context) =>
															Erlang["orelse/2"](
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.op_0,
																		Type.atom("!"),
																	),
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.op_0,
																		Type.atom("^"),
																	),
																context,
															),
														(context) =>
															Erlang["=:=/2"](
																context.vars.op_0,
																Type.atom("not"),
															),
														context,
													),
												(context) =>
													Erlang["=:=/2"](context.vars.op_0, Type.atom("+")),
												context,
											),
										(context) =>
											Erlang["=:=/2"](context.vars.op_0, Type.atom("-")),
										context,
									),
								(context) =>
									Erlang["=:=/2"](context.vars.op_0, Type.atom("~~~")),
								context,
							),
						body: (context) => {
							return Type.tuple([
								Type.atom("non_associative"),
								Type.integer(300n),
							]);
						},
					},
					{
						condition: (context) =>
							Erlang["=:=/2"](context.vars.op_0, Type.atom("@")),
						body: (context) => {
							return Type.tuple([
								Type.atom("non_associative"),
								Type.integer(320n),
							]);
						},
					},
					{
						condition: (context) => Type.atom("true"),
						body: (context) => {
							return Type.atom("error");
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Collectable", "impl_for", 1, "public", [
	{
		params: (context) => [
			Type.map([[Type.atom("__struct__"), Type.variablePattern("struct_0")]]),
		],
		guards: [],
		body: (context) => {
			return Elixir_Collectable["struct_impl_for/1"](context.vars.struct_0);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_tuple/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.Tuple")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_atom/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.Atom")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.List")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_map/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.Map")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_bitstring/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.Collectable.BitString"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_integer/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.Collectable.Integer"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_float/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.Float")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_function/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.Collectable.Function"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_pid/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.PID")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_port/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.Port")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_reference/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.Collectable.Reference"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.atom("nil");
		},
	},
]);

Interpreter.defineElixirFunction("Collectable", "impl_for!", 1, "public", [
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Elixir_Collectable["impl_for/1"](context.vars.data_0),
				[
					{
						match: Type.variablePattern("x_1"),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["=:=/2"](context.vars.x_1, Type.atom("false")),
									(context) =>
										Erlang["=:=/2"](context.vars.x_1, Type.atom("nil")),
									context,
								),
						],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_Protocol_UndefinedError["exception/1"](
									Type.list([
										Type.tuple([
											Type.atom("protocol"),
											Type.atom("Elixir.Collectable"),
										]),
										Type.tuple([Type.atom("value"), context.vars.data_0]),
										Type.tuple([Type.atom("description"), Type.bitstring("")]),
									]),
								),
							);
						},
					},
					{
						match: Type.variablePattern("x_2"),
						guards: [],
						body: (context) => {
							return context.vars.x_2;
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Collectable", "into", 1, "public", [
	{
		params: (context) => [Type.variablePattern("term_0")],
		guards: [],
		body: (context) => {
			return Interpreter.callNamedFunction(
				Elixir_Collectable["impl_for!/1"](context.vars.term_0),
				Type.atom("into"),
				Type.list([context.vars.term_0]),
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Collectable",
	"struct_impl_for",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("struct_0")],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Elixir_Protocol["__concat__/2"](
							Type.atom("Elixir.Collectable"),
							context.vars.struct_0,
						),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Collectable.BitString",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.BitString");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Collectable");
			},
		},
	],
);

Interpreter.defineElixirFunction("Collectable.BitString", "into", 1, "public", [
	{
		params: (context) => [Type.variablePattern("binary_0")],
		guards: [(context) => Erlang["is_binary/1"](context.vars.binary_0)],
		body: (context) => {
			Interpreter.matchOperator(
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.variablePattern("acc_1"),
								Type.tuple([Type.atom("cont"), Type.variablePattern("x_2")]),
							],
							guards: [
								(context) =>
									Erlang["andalso/2"](
										(context) => Erlang["is_binary/1"](context.vars.x_2),
										(context) => Erlang["is_list/1"](context.vars.acc_1),
										context,
									),
							],
							body: (context) => {
								return Interpreter.consOperator(
									context.vars.acc_1,
									context.vars.x_2,
								);
							},
						},
						{
							params: (context) => [
								Type.variablePattern("acc_3"),
								Type.tuple([Type.atom("cont"), Type.variablePattern("x_4")]),
							],
							guards: [
								(context) =>
									Erlang["andalso/2"](
										(context) => Erlang["is_bitstring/1"](context.vars.x_4),
										(context) => Erlang["is_bitstring/1"](context.vars.acc_3),
										context,
									),
							],
							body: (context) => {
								return Type.bitstring([
									Type.bitstringSegment(context.vars.acc_3, {
										type: "bitstring",
									}),
									Type.bitstringSegment(context.vars.x_4, {
										type: "bitstring",
									}),
								]);
							},
						},
						{
							params: (context) => [
								Type.variablePattern("acc_5"),
								Type.tuple([Type.atom("cont"), Type.variablePattern("x_6")]),
							],
							guards: [(context) => Erlang["is_bitstring/1"](context.vars.x_6)],
							body: (context) => {
								return Type.bitstring([
									Type.bitstringSegment(
										Erlang["iolist_to_binary/1"](context.vars.acc_5),
										{ type: "bitstring" },
									),
									Type.bitstringSegment(context.vars.x_6, {
										type: "bitstring",
									}),
								]);
							},
						},
						{
							params: (context) => [
								Type.variablePattern("acc_7"),
								Type.atom("done"),
							],
							guards: [
								(context) => Erlang["is_bitstring/1"](context.vars.acc_7),
							],
							body: (context) => {
								return context.vars.acc_7;
							},
						},
						{
							params: (context) => [
								Type.variablePattern("acc_8"),
								Type.atom("done"),
							],
							guards: [],
							body: (context) => {
								return Erlang["iolist_to_binary/1"](context.vars.acc_8);
							},
						},
						{
							params: (context) => [Type.matchPlaceholder(), Type.atom("halt")],
							guards: [],
							body: (context) => {
								return Type.atom("ok");
							},
						},
						{
							params: (context) => [
								Type.matchPlaceholder(),
								Type.tuple([
									Type.atom("cont"),
									Type.variablePattern("other_11"),
								]),
							],
							guards: [],
							body: (context) => {
								return Erlang["error/1"](
									Elixir_ArgumentError["exception/1"](
										Type.bitstring([
											Type.bitstringSegment(
												Type.string(
													"collecting into a binary requires a bitstring, got: ",
												),
												{ type: "binary" },
											),
											Type.bitstringSegment(
												Elixir_Kernel["inspect/1"](context.vars.other_11),
												{ type: "binary" },
											),
										]),
									),
								);
							},
						},
					],
					context,
				),
				Type.variablePattern("fun_12"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([
				Type.list([context.vars.binary_0]),
				context.vars.fun_12,
			]);
		},
	},
	{
		params: (context) => [Type.variablePattern("bitstring_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.variablePattern("acc_1"),
								Type.tuple([Type.atom("cont"), Type.variablePattern("x_2")]),
							],
							guards: [(context) => Erlang["is_bitstring/1"](context.vars.x_2)],
							body: (context) => {
								return Type.bitstring([
									Type.bitstringSegment(context.vars.acc_1, {
										type: "bitstring",
									}),
									Type.bitstringSegment(context.vars.x_2, {
										type: "bitstring",
									}),
								]);
							},
						},
						{
							params: (context) => [
								Type.variablePattern("acc_3"),
								Type.atom("done"),
							],
							guards: [],
							body: (context) => {
								return context.vars.acc_3;
							},
						},
						{
							params: (context) => [Type.matchPlaceholder(), Type.atom("halt")],
							guards: [],
							body: (context) => {
								return Type.atom("ok");
							},
						},
						{
							params: (context) => [
								Type.matchPlaceholder(),
								Type.tuple([
									Type.atom("cont"),
									Type.variablePattern("other_6"),
								]),
							],
							guards: [],
							body: (context) => {
								return Erlang["error/1"](
									Elixir_ArgumentError["exception/1"](
										Type.bitstring([
											Type.bitstringSegment(
												Type.string(
													"collecting into a bitstring requires a bitstring, got: ",
												),
												{ type: "binary" },
											),
											Type.bitstringSegment(
												Elixir_Kernel["inspect/1"](context.vars.other_6),
												{ type: "binary" },
											),
										]),
									),
								);
							},
						},
					],
					context,
				),
				Type.variablePattern("fun_7"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([context.vars.bitstring_0, context.vars.fun_7]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Collectable.Ecto.Adapters.SQL.Stream",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Ecto.Adapters.SQL.Stream");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Collectable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Collectable.Ecto.Adapters.SQL.Stream",
	"into",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("stream_0")],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					context.vars.stream_0,
					Type.map([
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.Ecto.Adapters.SQL.Stream"),
						],
						[Type.atom("meta"), Type.variablePattern("meta_1")],
						[Type.atom("opts"), Type.variablePattern("opts_4")],
						[Type.atom("params"), Type.variablePattern("params_3")],
						[Type.atom("statement"), Type.variablePattern("statement_2")],
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Elixir_Ecto_Adapters_SQL["into/4"](
						context.vars.meta_1,
						context.vars.statement_2,
						context.vars.params_3,
						context.vars.opts_4,
					),
					Type.tuple([
						Type.variablePattern("state_5"),
						Type.variablePattern("fun_6"),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Type.tuple([
					context.vars.state_5,
					Elixir_Collectable_Ecto_Adapters_SQL_Stream["make_into/2"](
						context.vars.fun_6,
						context.vars.stream_0,
					),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Collectable.Ecto.Adapters.SQL.Stream",
	"make_into",
	2,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("fun_0"),
				Type.variablePattern("stream_1"),
			],
			guards: [],
			body: (context) => {
				return Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.variablePattern("state_2"),
								Type.atom("done"),
							],
							guards: [],
							body: (context) => {
								Interpreter.callAnonymousFunction(context.vars.fun_0, [
									context.vars.state_2,
									Type.atom("done"),
								]);
								return context.vars.stream_1;
							},
						},
						{
							params: (context) => [
								Type.variablePattern("state_3"),
								Type.variablePattern("acc_4"),
							],
							guards: [],
							body: (context) => {
								return Interpreter.callAnonymousFunction(context.vars.fun_0, [
									context.vars.state_3,
									context.vars.acc_4,
								]);
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Collectable.File.Stream",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.File.Stream");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Collectable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Collectable.File.Stream",
	"into",
	1,
	"public",
	[
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("stream_2"),
					Type.map([
						[Type.atom("modes"), Type.variablePattern("modes_0")],
						[Type.atom("raw"), Type.variablePattern("raw_1")],
					]),
					context,
				),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.comprehension(
						[
							{
								match: Type.variablePattern("mode_3"),
								guards: [],
								body: (context) => context.vars.modes_0,
							},
						],
						[
							(context) =>
								Erlang["not/1"](
									Erlang["=:=/2"](context.vars.mode_3, Type.atom("read")),
								),
						],
						Type.list([]),
						false,
						(context) => {
							return context.vars.mode_3;
						},
						context,
					),
					Type.variablePattern("modes_4"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Interpreter.case(
					Elixir_File_Stream["__open__/2"](
						context.vars.stream_2,
						Interpreter.consOperator(Type.atom("write"), context.vars.modes_4),
					),
					[
						{
							match: Type.tuple([
								Type.atom("ok"),
								Type.variablePattern("device_5"),
							]),
							guards: [],
							body: (context) => {
								return Type.tuple([
									Type.atom("ok"),
									Elixir_Collectable_File_Stream["into/3"](
										context.vars.device_5,
										context.vars.stream_2,
										context.vars.raw_1,
									),
								]);
							},
						},
						{
							match: Type.tuple([
								Type.atom("error"),
								Type.variablePattern("reason_6"),
							]),
							guards: [],
							body: (context) => {
								return Erlang["error/1"](
									Elixir_File_Error["exception/1"](
										Type.list([
											Type.tuple([Type.atom("reason"), context.vars.reason_6]),
											Type.tuple([
												Type.atom("action"),
												Type.bitstring("stream"),
											]),
											Type.tuple([
												Type.atom("path"),
												Interpreter.dotOperator(
													context.vars.stream_2,
													Type.atom("path"),
												),
											]),
										]),
									),
								);
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Collectable.File.Stream",
	"into",
	3,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("device_0"),
				Type.variablePattern("stream_1"),
				Type.variablePattern("raw_2"),
			],
			guards: [],
			body: (context) => {
				globalThis.Hologram.return = Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.atom("ok"),
								Type.tuple([Type.atom("cont"), Type.variablePattern("x_3")]),
							],
							guards: [],
							body: (context) => {
								return Interpreter.case(
									context.vars.raw_2,
									[
										{
											match: Type.atom("true"),
											guards: [],
											body: (context) => {
												return Elixir_IO["binwrite/2"](
													context.vars.device_0,
													context.vars.x_3,
												);
											},
										},
										{
											match: Type.atom("false"),
											guards: [],
											body: (context) => {
												return Elixir_IO["write/2"](
													context.vars.device_0,
													context.vars.x_3,
												);
											},
										},
									],
									context,
								);
							},
						},
						{
							params: (context) => [Type.atom("ok"), Type.atom("done")],
							guards: [],
							body: (context) => {
								Interpreter.matchOperator(
									Erlang_File["close/1"](context.vars.device_0),
									Type.atom("ok"),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								return context.vars.stream_1;
							},
						},
						{
							params: (context) => [Type.atom("ok"), Type.atom("halt")],
							guards: [],
							body: (context) => {
								globalThis.Hologram.return = Interpreter.matchOperator(
									Erlang_File["close/1"](context.vars.device_0),
									Type.atom("ok"),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								return globalThis.Hologram.return;
							},
						},
					],
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return globalThis.Hologram.return;
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Collectable.HashDict",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.HashDict");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Collectable");
			},
		},
	],
);

Interpreter.defineElixirFunction("Collectable.HashDict", "into", 1, "public", [
	{
		params: (context) => [Type.variablePattern("original_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["binary_to_atom/1"](Type.bitstring("HashDict")),
				Type.variablePattern("module_1"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.variablePattern("dict_2"),
								Type.tuple([
									Type.atom("cont"),
									Type.tuple([
										Type.variablePattern("key_3"),
										Type.variablePattern("value_4"),
									]),
								]),
							],
							guards: [],
							body: (context) => {
								return Interpreter.callNamedFunction(
									context.vars.module_1,
									Type.atom("put"),
									Type.list([
										context.vars.dict_2,
										context.vars.key_3,
										context.vars.value_4,
									]),
									context,
								);
							},
						},
						{
							params: (context) => [
								Type.variablePattern("dict_5"),
								Type.atom("done"),
							],
							guards: [],
							body: (context) => {
								return context.vars.dict_5;
							},
						},
						{
							params: (context) => [Type.matchPlaceholder(), Type.atom("halt")],
							guards: [],
							body: (context) => {
								return Type.atom("ok");
							},
						},
					],
					context,
				),
				Type.variablePattern("collector_fun_6"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([
				context.vars.original_0,
				context.vars.collector_fun_6,
			]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Collectable.HashSet",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.HashSet");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Collectable");
			},
		},
	],
);

Interpreter.defineElixirFunction("Collectable.HashSet", "into", 1, "public", [
	{
		params: (context) => [Type.variablePattern("original_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["binary_to_atom/1"](Type.bitstring("HashSet")),
				Type.variablePattern("module_1"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.variablePattern("set_2"),
								Type.tuple([Type.atom("cont"), Type.variablePattern("term_3")]),
							],
							guards: [],
							body: (context) => {
								return Interpreter.callNamedFunction(
									context.vars.module_1,
									Type.atom("put"),
									Type.list([context.vars.set_2, context.vars.term_3]),
									context,
								);
							},
						},
						{
							params: (context) => [
								Type.variablePattern("set_4"),
								Type.atom("done"),
							],
							guards: [],
							body: (context) => {
								return context.vars.set_4;
							},
						},
						{
							params: (context) => [Type.matchPlaceholder(), Type.atom("halt")],
							guards: [],
							body: (context) => {
								return Type.atom("ok");
							},
						},
					],
					context,
				),
				Type.variablePattern("collector_fun_5"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([
				context.vars.original_0,
				context.vars.collector_fun_5,
			]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Collectable.IO.Stream",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.IO.Stream");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Collectable");
			},
		},
	],
);

Interpreter.defineElixirFunction("Collectable.IO.Stream", "into", 1, "public", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("stream_2"),
				Type.map([
					[Type.atom("device"), Type.variablePattern("device_0")],
					[Type.atom("raw"), Type.variablePattern("raw_1")],
				]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("ok"),
				Elixir_Collectable_IO_Stream["into/3"](
					context.vars.stream_2,
					context.vars.device_0,
					context.vars.raw_1,
				),
			]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Collectable.IO.Stream",
	"into",
	3,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("stream_0"),
				Type.variablePattern("device_1"),
				Type.variablePattern("raw_2"),
			],
			guards: [],
			body: (context) => {
				return Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.atom("ok"),
								Type.tuple([Type.atom("cont"), Type.variablePattern("x_3")]),
							],
							guards: [],
							body: (context) => {
								return Interpreter.case(
									context.vars.raw_2,
									[
										{
											match: Type.atom("true"),
											guards: [],
											body: (context) => {
												return Elixir_IO["binwrite/2"](
													context.vars.device_1,
													context.vars.x_3,
												);
											},
										},
										{
											match: Type.atom("false"),
											guards: [],
											body: (context) => {
												return Elixir_IO["write/2"](
													context.vars.device_1,
													context.vars.x_3,
												);
											},
										},
									],
									context,
								);
							},
						},
						{
							params: (context) => [Type.atom("ok"), Type.matchPlaceholder()],
							guards: [],
							body: (context) => {
								return context.vars.stream_0;
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Collectable.List", "__impl__", 1, "public", [
	{
		params: (context) => [Type.atom("for")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.List");
		},
	},
	{
		params: (context) => [Type.atom("protocol")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Collectable");
		},
	},
]);

Interpreter.defineElixirFunction("Collectable.List", "into", 1, "public", [
	{
		params: (context) => [Type.variablePattern("list_0")],
		guards: [],
		body: (context) => {
			Interpreter.case(
				Erlang["/=/2"](context.vars.list_0, Type.list([])),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Elixir_IO["warn/1"](
								Type.bitstring([
									Type.bitstringSegment(
										Type.string(
											"the Collectable protocol is deprecated for non-empty lists. The behavior of ",
										),
										{ type: "binary" },
									),
									Type.bitstringSegment(
										Type.string(
											'Enum.into/2 and "for" comprehensions with an :into option is incorrect ',
										),
										{ type: "binary" },
									),
									Type.bitstringSegment(
										Type.string(
											"when collecting into non-empty lists. If you're collecting into a non-empty keyword ",
										),
										{ type: "binary" },
									),
									Type.bitstringSegment(
										Type.string(
											"list, consider using Keyword.merge/2 instead. If you're collecting into a non-empty ",
										),
										{ type: "binary" },
									),
									Type.bitstringSegment(
										Type.string(
											"list, consider concatenating the two lists with the ++ operator.",
										),
										{ type: "binary" },
									),
								]),
							);
						},
					},
				],
				context,
			);
			Interpreter.matchOperator(
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.variablePattern("list_acc_1"),
								Type.tuple([Type.atom("cont"), Type.variablePattern("elem_2")]),
							],
							guards: [],
							body: (context) => {
								return Interpreter.consOperator(
									context.vars.elem_2,
									context.vars.list_acc_1,
								);
							},
						},
						{
							params: (context) => [
								Type.variablePattern("list_acc_3"),
								Type.atom("done"),
							],
							guards: [],
							body: (context) => {
								return Erlang["++/2"](
									context.vars.list_0,
									Erlang_Lists["reverse/1"](context.vars.list_acc_3),
								);
							},
						},
						{
							params: (context) => [Type.matchPlaceholder(), Type.atom("halt")],
							guards: [],
							body: (context) => {
								return Type.atom("ok");
							},
						},
					],
					context,
				),
				Type.variablePattern("fun_5"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([Type.list([]), context.vars.fun_5]);
		},
	},
]);

Interpreter.defineElixirFunction("Collectable.Map", "__impl__", 1, "public", [
	{
		params: (context) => [Type.atom("for")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Map");
		},
	},
	{
		params: (context) => [Type.atom("protocol")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Collectable");
		},
	},
]);

Interpreter.defineElixirFunction("Collectable.Map", "into", 1, "public", [
	{
		params: (context) => [Type.variablePattern("map_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.variablePattern("map_acc_1"),
								Type.tuple([
									Type.atom("cont"),
									Type.tuple([
										Type.variablePattern("key_2"),
										Type.variablePattern("value_3"),
									]),
								]),
							],
							guards: [],
							body: (context) => {
								return Erlang_Maps["put/3"](
									context.vars.key_2,
									context.vars.value_3,
									context.vars.map_acc_1,
								);
							},
						},
						{
							params: (context) => [
								Type.variablePattern("map_acc_4"),
								Type.atom("done"),
							],
							guards: [],
							body: (context) => {
								return context.vars.map_acc_4;
							},
						},
						{
							params: (context) => [Type.matchPlaceholder(), Type.atom("halt")],
							guards: [],
							body: (context) => {
								return Type.atom("ok");
							},
						},
						{
							params: (context) => [
								Type.matchPlaceholder(),
								Type.tuple([
									Type.atom("cont"),
									Type.variablePattern("other_7"),
								]),
							],
							guards: [],
							body: (context) => {
								return Erlang["error/1"](
									Elixir_ArgumentError["exception/1"](
										Type.bitstring([
											Type.bitstringSegment(
												Type.string(
													"collecting into a map requires {key, value} tuples, got: ",
												),
												{ type: "binary" },
											),
											Type.bitstringSegment(
												Elixir_Kernel["inspect/1"](context.vars.other_7),
												{ type: "binary" },
											),
										]),
									),
								);
							},
						},
					],
					context,
				),
				Type.variablePattern("fun_8"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([context.vars.map_0, context.vars.fun_8]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Collectable.MapSet",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.MapSet");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Collectable");
			},
		},
	],
);

Interpreter.defineElixirFunction("Collectable.MapSet", "into", 1, "public", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("map_set_1"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.MapSet")],
					[Type.atom("map"), Type.variablePattern("set_0")],
				]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.variablePattern("list_2"),
								Type.tuple([Type.atom("cont"), Type.variablePattern("x_3")]),
							],
							guards: [],
							body: (context) => {
								return Interpreter.consOperator(
									context.vars.x_3,
									context.vars.list_2,
								);
							},
						},
						{
							params: (context) => [
								Type.variablePattern("list_4"),
								Type.atom("done"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_set_1,
									Type.map([
										[
											Type.atom("map"),
											Erlang_Sets["union/2"](
												context.vars.set_0,
												Erlang_Sets["from_list/2"](
													context.vars.list_4,
													Type.list([
														Type.tuple([
															Type.atom("version"),
															Type.integer(2n),
														]),
													]),
												),
											),
										],
									]),
								);
							},
						},
						{
							params: (context) => [Type.matchPlaceholder(), Type.atom("halt")],
							guards: [],
							body: (context) => {
								return Type.atom("ok");
							},
						},
					],
					context,
				),
				Type.variablePattern("fun_5"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([Type.list([]), context.vars.fun_5]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Collectable.Mix.Shell",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Mix.Shell");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Collectable");
			},
		},
	],
);

Interpreter.defineElixirFunction("Collectable.Mix.Shell", "into", 1, "public", [
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Mix.Shell")],
				[Type.atom("callback"), Type.variablePattern("fun_0")],
			]),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("ok"),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.matchPlaceholder(),
								Type.tuple([Type.atom("cont"), Type.variablePattern("data_1")]),
							],
							guards: [],
							body: (context) => {
								return Interpreter.callAnonymousFunction(context.vars.fun_0, [
									context.vars.data_1,
								]);
							},
						},
						{
							params: (context) => [
								Type.matchPlaceholder(),
								Type.matchPlaceholder(),
							],
							guards: [],
							body: (context) => {
								return Type.atom("ok");
							},
						},
					],
					context,
				),
			]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Collectable.Req.Utils.CollectWithHash",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Req.Utils.CollectWithHash");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Collectable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Collectable.Req.Utils.CollectWithHash",
	"hash_init",
	1,
	"private",
	[
		{
			params: (context) => [Type.atom("sha1")],
			guards: [],
			body: (context) => {
				return Erlang_Crypto["hash_init/1"](Type.atom("sha"));
			},
		},
		{
			params: (context) => [Type.variablePattern("type_0")],
			guards: [],
			body: (context) => {
				return Erlang_Crypto["hash_init/1"](context.vars.type_0);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Collectable.Req.Utils.CollectWithHash",
	"into",
	1,
	"public",
	[
		{
			params: (context) => [
				Type.map([
					[Type.atom("collectable"), Type.variablePattern("collectable_0")],
					[Type.atom("type"), Type.variablePattern("type_1")],
				]),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Elixir_Collectable["into/1"](context.vars.collectable_0),
					Type.tuple([
						Type.variablePattern("acc_2"),
						Type.variablePattern("collector_3"),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("acc_4"),
										Type.variablePattern("hash_5"),
									]),
									Type.tuple([
										Type.atom("cont"),
										Type.variablePattern("element_6"),
									]),
								],
								guards: [],
								body: (context) => {
									Interpreter.matchOperator(
										Erlang_Crypto["hash_update/2"](
											context.vars.hash_5,
											context.vars.element_6,
										),
										Type.variablePattern("hash_7"),
										context,
									);
									Interpreter.updateVarsToMatchedValues(context);
									return Type.tuple([
										Interpreter.callAnonymousFunction(
											context.vars.collector_3,
											[
												context.vars.acc_4,
												Type.tuple([Type.atom("cont"), context.vars.element_6]),
											],
										),
										context.vars.hash_7,
									]);
								},
							},
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("acc_8"),
										Type.variablePattern("hash_9"),
									]),
									Type.atom("done"),
								],
								guards: [],
								body: (context) => {
									Interpreter.matchOperator(
										Erlang_Crypto["hash_final/1"](context.vars.hash_9),
										Type.variablePattern("hash_10"),
										context,
									);
									Interpreter.updateVarsToMatchedValues(context);
									return Type.tuple([
										Interpreter.callAnonymousFunction(
											context.vars.collector_3,
											[context.vars.acc_8, Type.atom("done")],
										),
										context.vars.hash_10,
									]);
								},
							},
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("acc_11"),
										Type.variablePattern("hash_12"),
									]),
									Type.atom("halt"),
								],
								guards: [],
								body: (context) => {
									return Type.tuple([
										Interpreter.callAnonymousFunction(
											context.vars.collector_3,
											[context.vars.acc_11, Type.atom("halt")],
										),
										context.vars.hash_12,
									]);
								},
							},
						],
						context,
					),
					Type.variablePattern("new_collector_13"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Elixir_Collectable_Req_Utils_CollectWithHash["hash_init/1"](
						context.vars.type_1,
					),
					Type.variablePattern("hash_14"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Type.tuple([
					Type.tuple([context.vars.acc_2, context.vars.hash_14]),
					context.vars.new_collector_13,
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction("CondClauseError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.CondClauseError")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("CondClauseError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.CondClauseError")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.DBConnection")],
				[Type.atom("conn_mode"), Type.atom("nil")],
				[Type.atom("conn_ref"), Type.atom("nil")],
				[Type.atom("pool_ref"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.DBConnection")],
					[Type.atom("conn_mode"), Type.atom("nil")],
					[Type.atom("conn_ref"), Type.atom("nil")],
					[Type.atom("pool_ref"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "bad_return!", 3, "private", [
	{
		params: (context) => [
			Type.variablePattern("other_0"),
			Type.variablePattern("conn_1"),
			Type.variablePattern("meter_2"),
		],
		guards: [],
		body: (context) => {
			globalThis.Hologram.return = Interpreter.try(
				(context) => {
					return Erlang["error/1"](
						Elixir_DBConnection_ConnectionError["exception/1"](
							Type.bitstring([
								Type.bitstringSegment(Type.string("bad return value: "), {
									type: "binary",
								}),
								Type.bitstringSegment(
									Elixir_Kernel["inspect/1"](context.vars.other_0),
									{ type: "binary" },
								),
							]),
						),
					);
				},
				[],
				[],
				[],
				null,
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "cleanup", 4, "private", [
	{
		params: (context) => [
			Type.variablePattern("conn_0"),
			Type.variablePattern("fun_1"),
			Type.variablePattern("args_2"),
			Type.variablePattern("opts_3"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				context.vars.conn_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.DBConnection")],
					[Type.atom("pool_ref"), Type.variablePattern("pool_ref_4")],
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_DBConnection_Holder["cleanup/4"](
				context.vars.pool_ref_4,
				context.vars.fun_1,
				context.vars.args_2,
				context.vars.opts_3,
			);
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "crash_reason", 2, "private", [
	{
		params: (context) => [Type.atom("throw"), Type.variablePattern("value_0")],
		guards: [],
		body: (context) => {
			return Type.tuple([Type.atom("nocatch"), context.vars.value_0]);
		},
	},
	{
		params: (context) => [
			Type.matchPlaceholder(),
			Type.variablePattern("value_0"),
		],
		guards: [],
		body: (context) => {
			return context.vars.value_0;
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "declare", 4, "private", [
	{
		params: (context) => [
			Type.variablePattern("conn_0"),
			Type.variablePattern("query_1"),
			Type.variablePattern("params_2"),
			Type.variablePattern("opts_3"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Interpreter.case(
					Elixir_DBConnection["maybe_encode/4"](
						context.vars.query_1,
						context.vars.params_2,
						Elixir_DBConnection["meter/1"](context.vars.opts_3),
						context.vars.opts_3,
					),
					[
						{
							match: Type.tuple([
								Type.atom("prepare"),
								Type.variablePattern("meter_4"),
							]),
							guards: [],
							body: (context) => {
								return Elixir_DBConnection["run/4"](
									context.vars.conn_0,
									Type.anonymousFunction(
										3,
										[
											{
												params: (context) => [
													Type.variablePattern("$250"),
													Type.variablePattern("$251"),
													Type.variablePattern("$252"),
												],
												guards: [],
												body: (context) => {
													return Elixir_DBConnection["run_prepare_declare/5"](
														context.vars["$250"],
														context.vars.query_1,
														context.vars.params_2,
														context.vars["$251"],
														context.vars["$252"],
													);
												},
											},
										],
										context,
									),
									context.vars.meter_4,
									context.vars.opts_3,
								);
							},
						},
						{
							match: Type.tuple([
								Type.atom("ok"),
								Type.variablePattern("params_8"),
								Type.variablePattern("meter_9"),
							]),
							guards: [],
							body: (context) => {
								return Elixir_DBConnection["run/4"](
									context.vars.conn_0,
									Type.anonymousFunction(
										3,
										[
											{
												params: (context) => [
													Type.variablePattern("$253"),
													Type.variablePattern("$254"),
													Type.variablePattern("$255"),
												],
												guards: [],
												body: (context) => {
													return Elixir_DBConnection["run_declare/5"](
														context.vars["$253"],
														context.vars.query_1,
														context.vars.params_8,
														context.vars["$254"],
														context.vars["$255"],
													);
												},
											},
										],
										context,
									),
									context.vars.meter_9,
									context.vars.opts_3,
								);
							},
						},
						{
							match: Interpreter.matchOperator(
								Type.variablePattern("error_13"),
								Type.tuple([
									Type.matchPlaceholder(),
									Type.matchPlaceholder(),
									Type.matchPlaceholder(),
									Type.matchPlaceholder(),
								]),
								context,
							),
							guards: [],
							body: (context) => {
								return context.vars.error_13;
							},
						},
					],
					context,
				),
				Type.variablePattern("result_14"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_DBConnection["log/4"](
				context.vars.result_14,
				Type.atom("declare"),
				context.vars.query_1,
				context.vars.params_2,
			);
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "disconnect", 2, "private", [
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.DBConnection")],
				[Type.atom("pool_ref"), Type.variablePattern("pool_ref_0")],
			]),
			Type.variablePattern("err_1"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_DBConnection_Holder["disconnect/2"](
					context.vars.pool_ref_0,
					context.vars.err_1,
				),
				Type.matchPlaceholder(),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.atom("ok");
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "entry_result", 1, "private", [
	{
		params: (context) => [
			Type.tuple([
				Type.variablePattern("kind_0"),
				Type.variablePattern("reason_1"),
				Type.variablePattern("stack_2"),
			]),
		],
		guards: [
			(context) =>
				Erlang["orelse/2"](
					(context) =>
						Erlang["orelse/2"](
							(context) =>
								Erlang["=:=/2"](context.vars.kind_0, Type.atom("error")),
							(context) =>
								Erlang["=:=/2"](context.vars.kind_0, Type.atom("exit")),
							context,
						),
					(context) => Erlang["=:=/2"](context.vars.kind_0, Type.atom("throw")),
					context,
				),
		],
		body: (context) => {
			Interpreter.matchOperator(
				Type.bitstring([
					Type.bitstringSegment(Type.string("an exception was raised: "), {
						type: "binary",
					}),
					Type.bitstringSegment(
						Elixir_Exception["format/3"](
							context.vars.kind_0,
							context.vars.reason_1,
							context.vars.stack_2,
						),
						{ type: "binary" },
					),
				]),
				Type.variablePattern("msg_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([
				Type.atom("error"),
				Elixir_DBConnection_ConnectionError["__struct__/1"](
					Type.list([
						Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
						Type.tuple([Type.atom("reason"), Type.atom("error")]),
						Type.tuple([Type.atom("severity"), Type.atom("error")]),
						Type.tuple([Type.atom("message"), context.vars.msg_3]),
					]),
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([Type.variablePattern("ok_0"), Type.variablePattern("res_1")]),
		],
		guards: [
			(context) =>
				Erlang["orelse/2"](
					(context) => Erlang["=:=/2"](context.vars.ok_0, Type.atom("cont")),
					(context) => Erlang["=:=/2"](context.vars.ok_0, Type.atom("halt")),
					context,
				),
		],
		body: (context) => {
			return Type.tuple([Type.atom("ok"), context.vars.res_1]);
		},
	},
	{
		params: (context) => [Type.variablePattern("other_0")],
		guards: [],
		body: (context) => {
			return context.vars.other_0;
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "event", 2, "private", [
	{
		params: (context) => [Type.atom("nil"), Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.atom("nil");
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.variablePattern("log_0"),
				Type.variablePattern("events_1"),
			]),
			Type.variablePattern("event_2"),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([
				context.vars.log_0,
				Interpreter.consOperator(
					Type.tuple([context.vars.event_2, Erlang["monotonic_time/0"]()]),
					context.vars.events_1,
				),
			]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"DBConnection",
	"handle_common_result",
	3,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("return_0"),
				Type.variablePattern("conn_1"),
				Type.variablePattern("meter_2"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					context.vars.return_0,
					[
						{
							match: Type.tuple([
								Type.atom("ok"),
								Type.variablePattern("result_3"),
								Type.matchPlaceholder(),
							]),
							guards: [],
							body: (context) => {
								return Type.tuple([
									Type.atom("ok"),
									context.vars.result_3,
									context.vars.meter_2,
								]);
							},
						},
						{
							match: Type.tuple([
								Type.atom("error"),
								Type.variablePattern("err_5"),
								Type.matchPlaceholder(),
							]),
							guards: [],
							body: (context) => {
								return Type.tuple([
									Type.atom("error"),
									context.vars.err_5,
									context.vars.meter_2,
								]);
							},
						},
						{
							match: Type.tuple([
								Type.atom("disconnect"),
								Type.variablePattern("err_7"),
								Type.matchPlaceholder(),
							]),
							guards: [],
							body: (context) => {
								Elixir_DBConnection["disconnect/2"](
									context.vars.conn_1,
									context.vars.err_7,
								);
								return Type.tuple([
									Type.atom("error"),
									context.vars.err_7,
									context.vars.meter_2,
								]);
							},
						},
						{
							match: Type.tuple([
								Type.atom("catch"),
								Type.variablePattern("kind_9"),
								Type.variablePattern("reason_10"),
								Type.variablePattern("stack_11"),
							]),
							guards: [],
							body: (context) => {
								Elixir_DBConnection["stop/4"](
									context.vars.conn_1,
									context.vars.kind_9,
									context.vars.reason_10,
									context.vars.stack_11,
								);
								return Type.tuple([
									context.vars.kind_9,
									context.vars.reason_10,
									context.vars.stack_11,
									context.vars.meter_2,
								]);
							},
						},
						{
							match: Type.variablePattern("other_12"),
							guards: [],
							body: (context) => {
								return Elixir_DBConnection["bad_return!/3"](
									context.vars.other_12,
									context.vars.conn_1,
									context.vars.meter_2,
								);
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("DBConnection", "log", 2, "private", [
	{
		params: (context) => [
			Type.tuple([
				Type.variablePattern("mod_0"),
				Type.variablePattern("fun_1"),
				Type.variablePattern("args_2"),
			]),
			Type.variablePattern("entry_3"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.callNamedFunction(
				context.vars.mod_0,
				context.vars.fun_1,
				Interpreter.consOperator(context.vars.entry_3, context.vars.args_2),
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("fun_0"),
			Type.variablePattern("entry_1"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.callAnonymousFunction(context.vars.fun_0, [
				context.vars.entry_1,
			]);
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "log", 4, "private", [
	{
		params: (context) => [
			Type.tuple([
				Type.atom("ok"),
				Type.variablePattern("res_0"),
				Type.variablePattern("meter_1"),
			]),
			Type.variablePattern("call_2"),
			Type.variablePattern("query_3"),
			Type.variablePattern("params_4"),
		],
		guards: [],
		body: (context) => {
			return Elixir_DBConnection["log/5"](
				context.vars.meter_1,
				context.vars.call_2,
				context.vars.query_3,
				context.vars.params_4,
				Type.tuple([Type.atom("ok"), context.vars.res_0]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("ok"),
				Type.variablePattern("res1_0"),
				Type.variablePattern("res2_1"),
				Type.variablePattern("meter_2"),
			]),
			Type.variablePattern("call_3"),
			Type.variablePattern("query_4"),
			Type.variablePattern("params_5"),
		],
		guards: [],
		body: (context) => {
			return Elixir_DBConnection["log/5"](
				context.vars.meter_2,
				context.vars.call_3,
				context.vars.query_4,
				context.vars.params_5,
				Type.tuple([Type.atom("ok"), context.vars.res1_0, context.vars.res2_1]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.variablePattern("ok_0"),
				Type.variablePattern("res_1"),
				Type.variablePattern("meter_2"),
			]),
			Type.variablePattern("call_3"),
			Type.variablePattern("query_4"),
			Type.variablePattern("cursor_5"),
		],
		guards: [
			(context) =>
				Erlang["orelse/2"](
					(context) => Erlang["=:=/2"](context.vars.ok_0, Type.atom("cont")),
					(context) => Erlang["=:=/2"](context.vars.ok_0, Type.atom("halt")),
					context,
				),
		],
		body: (context) => {
			return Elixir_DBConnection["log/5"](
				context.vars.meter_2,
				context.vars.call_3,
				context.vars.query_4,
				context.vars.cursor_5,
				Type.tuple([context.vars.ok_0, context.vars.res_1]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("error"),
				Type.variablePattern("err_0"),
				Type.variablePattern("meter_1"),
			]),
			Type.variablePattern("call_2"),
			Type.variablePattern("query_3"),
			Type.variablePattern("params_4"),
		],
		guards: [],
		body: (context) => {
			return Elixir_DBConnection["log/5"](
				context.vars.meter_1,
				context.vars.call_2,
				context.vars.query_3,
				context.vars.params_4,
				Type.tuple([Type.atom("error"), context.vars.err_0]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.variablePattern("kind_0"),
				Type.variablePattern("reason_1"),
				Type.variablePattern("stack_2"),
				Type.variablePattern("meter_3"),
			]),
			Type.variablePattern("call_4"),
			Type.variablePattern("query_5"),
			Type.variablePattern("params_6"),
		],
		guards: [],
		body: (context) => {
			return Elixir_DBConnection["log/5"](
				context.vars.meter_3,
				context.vars.call_4,
				context.vars.query_5,
				context.vars.params_6,
				Type.tuple([
					context.vars.kind_0,
					context.vars.reason_1,
					context.vars.stack_2,
				]),
			);
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "log", 5, "private", [
	{
		params: (context) => [
			Type.atom("nil"),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
			Type.variablePattern("result_0"),
		],
		guards: [],
		body: (context) => {
			return Elixir_DBConnection["log_result/1"](context.vars.result_0);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.variablePattern("log_0"),
				Type.variablePattern("times_1"),
			]),
			Type.variablePattern("call_2"),
			Type.variablePattern("query_3"),
			Type.variablePattern("params_4"),
			Type.variablePattern("result_5"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_DBConnection_LogEntry["new/5"](
					context.vars.call_2,
					context.vars.query_3,
					context.vars.params_4,
					context.vars.times_1,
					Elixir_DBConnection["entry_result/1"](context.vars.result_5),
				),
				Type.variablePattern("entry_6"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.try(
				(context) => {
					return Elixir_DBConnection["log/2"](
						context.vars.log_0,
						context.vars.entry_6,
					);
				},
				[],
				[],
				[],
				null,
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_DBConnection["log_result/1"](context.vars.result_5);
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "log_raised", 4, "private", [
	{
		params: (context) => [
			Type.variablePattern("entry_0"),
			Type.variablePattern("kind_1"),
			Type.variablePattern("reason_2"),
			Type.variablePattern("stack_3"),
		],
		guards: [],
		body: (context) => {
			globalThis.Hologram.return = Interpreter.try(
				(context) => {
					Interpreter.matchOperator(
						Elixir_Exception["normalize/3"](
							context.vars.kind_1,
							context.vars.reason_2,
							context.vars.stack_3,
						),
						Type.variablePattern("reason_4"),
						context,
					);
					Interpreter.updateVarsToMatchedValues(context);
					return Interpreter.case(
						Elixir_Logger["__should_log__/2"](
							Type.atom("error"),
							Type.atom("Elixir.DBConnection"),
						),
						[
							{
								match: Type.atom("nil"),
								guards: [],
								body: (context) => {
									return Type.atom("ok");
								},
							},
							{
								match: Type.variablePattern("level_5"),
								guards: [],
								body: (context) => {
									return Elixir_Logger["__do_log__/4"](
										context.vars.level_5,
										Type.anonymousFunction(
											0,
											[
												{
													params: (context) => [],
													guards: [],
													body: (context) => {
														return Type.bitstring([
															Type.bitstringSegment(
																Type.string("an exception was raised logging "),
																{ type: "binary" },
															),
															Type.bitstringSegment(
																Elixir_Kernel["inspect/1"](
																	context.vars.entry_0,
																),
																{ type: "binary" },
															),
															Type.bitstringSegment(Type.string(": "), {
																type: "binary",
															}),
															Type.bitstringSegment(
																Elixir_Exception["format/3"](
																	context.vars.kind_1,
																	context.vars.reason_4,
																	context.vars.stack_3,
																),
																{ type: "binary" },
															),
														]);
													},
												},
											],
											context,
										),
										Type.map([
											[
												Type.atom("file"),
												Type.list([
													Type.integer(108n),
													Type.integer(105n),
													Type.integer(98n),
													Type.integer(47n),
													Type.integer(100n),
													Type.integer(98n),
													Type.integer(95n),
													Type.integer(99n),
													Type.integer(111n),
													Type.integer(110n),
													Type.integer(110n),
													Type.integer(101n),
													Type.integer(99n),
													Type.integer(116n),
													Type.integer(105n),
													Type.integer(111n),
													Type.integer(110n),
													Type.integer(46n),
													Type.integer(101n),
													Type.integer(120n),
												]),
											],
											[Type.atom("line"), Type.integer(1728n)],
											[
												Type.atom("mfa"),
												Type.tuple([
													Type.atom("Elixir.DBConnection"),
													Type.atom("log_raised"),
													Type.integer(4n),
												]),
											],
										]),
										Type.map([
											[Type.atom("application"), Type.atom("db_connection")],
											[
												Type.atom("crash_reason"),
												Type.tuple([
													Elixir_DBConnection["crash_reason/2"](
														context.vars.kind_1,
														context.vars.reason_4,
													),
													context.vars.stack_3,
												]),
											],
										]),
									);
								},
							},
						],
						context,
					);
				},
				[],
				[],
				[],
				null,
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "log_result", 1, "private", [
	{
		params: (context) => [
			Type.tuple([
				Type.variablePattern("kind_0"),
				Type.variablePattern("reason_1"),
				Type.variablePattern("stack_2"),
			]),
		],
		guards: [
			(context) =>
				Erlang["orelse/2"](
					(context) =>
						Erlang["orelse/2"](
							(context) =>
								Erlang["=:=/2"](context.vars.kind_0, Type.atom("error")),
							(context) =>
								Erlang["=:=/2"](context.vars.kind_0, Type.atom("exit")),
							context,
						),
					(context) => Erlang["=:=/2"](context.vars.kind_0, Type.atom("throw")),
					context,
				),
		],
		body: (context) => {
			return Erlang["raise/3"](
				context.vars.kind_0,
				context.vars.reason_1,
				context.vars.stack_2,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("other_0")],
		guards: [],
		body: (context) => {
			return context.vars.other_0;
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "maybe_encode", 4, "private", [
	{
		params: (context) => [
			Type.variablePattern("query_0"),
			Type.variablePattern("params_1"),
			Type.variablePattern("meter_2"),
			Type.variablePattern("opts_3"),
		],
		guards: [],
		body: (context) => {
			globalThis.Hologram.return = Interpreter.try(
				(context) => {
					return Elixir_DBConnection_Query["encode/3"](
						context.vars.query_0,
						context.vars.params_1,
						context.vars.opts_3,
					);
				},
				[
					{
						variable: Type.matchPlaceholder(),
						modules: [Type.atom("Elixir.DBConnection.EncodeError")],
						body: (context) => {
							return Type.tuple([Type.atom("prepare"), context.vars.meter_2]);
						},
					},
				],
				[],
				[],
				null,
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "meter", 1, "private", [
	{
		params: (context) => [Type.variablePattern("opts_0")],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Elixir_Keyword["get/2"](context.vars.opts_0, Type.atom("log")),
				[
					{
						match: Type.atom("nil"),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
					{
						match: Type.variablePattern("log_1"),
						guards: [],
						body: (context) => {
							return Type.tuple([context.vars.log_1, Type.list([])]);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"DBConnection",
	"prepare_declare",
	4,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("conn_0"),
				Type.variablePattern("query_1"),
				Type.variablePattern("params_2"),
				Type.variablePattern("opts_3"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.with(),
					Type.variablePattern("result_9"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_DBConnection["log/4"](
					context.vars.result_9,
					Type.atom("prepare_declare"),
					context.vars.query_1,
					context.vars.params_2,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection",
	"prepare_declare!",
	4,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("conn_0"),
				Type.variablePattern("query_1"),
				Type.variablePattern("params_2"),
				Type.variablePattern("opts_3"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Elixir_DBConnection["prepare_declare/4"](
						context.vars.conn_0,
						context.vars.query_1,
						context.vars.params_2,
						context.vars.opts_3,
					),
					[
						{
							match: Type.tuple([
								Type.atom("ok"),
								Type.variablePattern("query_4"),
								Type.variablePattern("cursor_5"),
							]),
							guards: [],
							body: (context) => {
								return Type.tuple([
									context.vars.query_4,
									context.vars.cursor_5,
								]);
							},
						},
						{
							match: Type.tuple([
								Type.atom("error"),
								Type.variablePattern("err_6"),
							]),
							guards: [],
							body: (context) => {
								return Erlang["error/3"](
									Elixir_Kernel_Utils["raise/1"](context.vars.err_6),
									Type.atom("none"),
									Type.list([
										Type.tuple([
											Type.atom("error_info"),
											Type.map([
												[Type.atom("module"), Type.atom("Elixir.Exception")],
											]),
										]),
									]),
								);
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("DBConnection", "reduce", 3, "public", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("stream_0"),
				Type.map([
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.DBConnection.PrepareStream"),
					],
				]),
				context,
			),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				context.vars.stream_0,
				Type.map([
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.DBConnection.PrepareStream"),
					],
					[Type.atom("conn"), Type.variablePattern("conn_3")],
					[Type.atom("opts"), Type.variablePattern("opts_6")],
					[Type.atom("params"), Type.variablePattern("params_5")],
					[Type.atom("query"), Type.variablePattern("query_4")],
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.variablePattern("conn_7"),
								Type.variablePattern("opts_8"),
							],
							guards: [],
							body: (context) => {
								Interpreter.matchOperator(
									Elixir_DBConnection["prepare_declare!/4"](
										context.vars.conn_7,
										context.vars.query_4,
										context.vars.params_5,
										context.vars.opts_8,
									),
									Type.tuple([
										Type.variablePattern("query_9"),
										Type.variablePattern("cursor_10"),
									]),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								return Type.tuple([
									Type.atom("cont"),
									context.vars.query_9,
									context.vars.cursor_10,
								]);
							},
						},
					],
					context,
				),
				Type.variablePattern("declare_11"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_DBConnection["resource/5"](
					context.vars.conn_3,
					context.vars.declare_11,
					Type.functionCapture(
						"DBConnection",
						"stream_fetch",
						3,
						[
							{
								params: (context) => [
									Type.variablePattern("$1"),
									Type.variablePattern("$2"),
									Type.variablePattern("$3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_DBConnection["stream_fetch/3"](
										context.vars["$1"],
										context.vars["$2"],
										context.vars["$3"],
									);
								},
							},
						],
						context,
					),
					Type.functionCapture(
						"DBConnection",
						"stream_deallocate",
						3,
						[
							{
								params: (context) => [
									Type.variablePattern("$1"),
									Type.variablePattern("$2"),
									Type.variablePattern("$3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_DBConnection["stream_deallocate/3"](
										context.vars["$1"],
										context.vars["$2"],
										context.vars["$3"],
									);
								},
							},
						],
						context,
					),
					context.vars.opts_6,
				),
				Type.variablePattern("enum_12"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.callAnonymousFunction(context.vars.enum_12, [
				context.vars.acc_1,
				context.vars.fun_2,
			]);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("stream_0"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.DBConnection.Stream")],
				]),
				context,
			),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				context.vars.stream_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.DBConnection.Stream")],
					[Type.atom("conn"), Type.variablePattern("conn_3")],
					[Type.atom("opts"), Type.variablePattern("opts_6")],
					[Type.atom("params"), Type.variablePattern("params_5")],
					[Type.atom("query"), Type.variablePattern("query_4")],
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.variablePattern("conn_7"),
								Type.variablePattern("opts_8"),
							],
							guards: [],
							body: (context) => {
								return Interpreter.case(
									Elixir_DBConnection["declare/4"](
										context.vars.conn_7,
										context.vars.query_4,
										context.vars.params_5,
										context.vars.opts_8,
									),
									[
										{
											match: Type.tuple([
												Type.atom("ok"),
												Type.variablePattern("query_9"),
												Type.variablePattern("cursor_10"),
											]),
											guards: [],
											body: (context) => {
												return Type.tuple([
													Type.atom("cont"),
													context.vars.query_9,
													context.vars.cursor_10,
												]);
											},
										},
										{
											match: Type.tuple([
												Type.atom("ok"),
												Type.variablePattern("cursor_11"),
											]),
											guards: [],
											body: (context) => {
												return Type.tuple([
													Type.atom("cont"),
													context.vars.query_4,
													context.vars.cursor_11,
												]);
											},
										},
										{
											match: Type.tuple([
												Type.atom("error"),
												Type.variablePattern("err_12"),
											]),
											guards: [],
											body: (context) => {
												return Erlang["error/3"](
													Elixir_Kernel_Utils["raise/1"](context.vars.err_12),
													Type.atom("none"),
													Type.list([
														Type.tuple([
															Type.atom("error_info"),
															Type.map([
																[
																	Type.atom("module"),
																	Type.atom("Elixir.Exception"),
																],
															]),
														]),
													]),
												);
											},
										},
									],
									context,
								);
							},
						},
					],
					context,
				),
				Type.variablePattern("declare_13"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_DBConnection["resource/5"](
					context.vars.conn_3,
					context.vars.declare_13,
					Type.functionCapture(
						"DBConnection",
						"stream_fetch",
						3,
						[
							{
								params: (context) => [
									Type.variablePattern("$1"),
									Type.variablePattern("$2"),
									Type.variablePattern("$3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_DBConnection["stream_fetch/3"](
										context.vars["$1"],
										context.vars["$2"],
										context.vars["$3"],
									);
								},
							},
						],
						context,
					),
					Type.functionCapture(
						"DBConnection",
						"stream_deallocate",
						3,
						[
							{
								params: (context) => [
									Type.variablePattern("$1"),
									Type.variablePattern("$2"),
									Type.variablePattern("$3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_DBConnection["stream_deallocate/3"](
										context.vars["$1"],
										context.vars["$2"],
										context.vars["$3"],
									);
								},
							},
						],
						context,
					),
					context.vars.opts_6,
				),
				Type.variablePattern("enum_14"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.callAnonymousFunction(context.vars.enum_14, [
				context.vars.acc_1,
				context.vars.fun_2,
			]);
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "resource", 5, "private", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("conn_0"),
				Type.map([[Type.atom("__struct__"), Type.atom("Elixir.DBConnection")]]),
				context,
			),
			Type.variablePattern("start_1"),
			Type.variablePattern("next_2"),
			Type.variablePattern("stop_3"),
			Type.variablePattern("opts_4"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Type.anonymousFunction(
					0,
					[
						{
							params: (context) => [],
							guards: [],
							body: (context) => {
								return Interpreter.callAnonymousFunction(context.vars.start_1, [
									context.vars.conn_0,
									context.vars.opts_4,
								]);
							},
						},
					],
					context,
				),
				Type.variablePattern("start_5"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Type.anonymousFunction(
					1,
					[
						{
							params: (context) => [Type.variablePattern("state_6")],
							guards: [],
							body: (context) => {
								return Interpreter.callAnonymousFunction(context.vars.next_2, [
									context.vars.conn_0,
									context.vars.state_6,
									context.vars.opts_4,
								]);
							},
						},
					],
					context,
				),
				Type.variablePattern("next_7"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Type.anonymousFunction(
					1,
					[
						{
							params: (context) => [Type.variablePattern("state_8")],
							guards: [],
							body: (context) => {
								return Interpreter.callAnonymousFunction(context.vars.stop_3, [
									context.vars.conn_0,
									context.vars.state_8,
									context.vars.opts_4,
								]);
							},
						},
					],
					context,
				),
				Type.variablePattern("stop_9"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_Stream["resource/3"](
				context.vars.start_5,
				context.vars.next_7,
				context.vars.stop_9,
			);
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "run", 4, "private", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("conn_0"),
				Type.map([[Type.atom("__struct__"), Type.atom("Elixir.DBConnection")]]),
				context,
			),
			Type.variablePattern("fun_1"),
			Type.variablePattern("meter_2"),
			Type.variablePattern("opts_3"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.with();
		},
	},
	{
		params: (context) => [
			Type.variablePattern("pool_0"),
			Type.variablePattern("fun_1"),
			Type.variablePattern("meter_2"),
			Type.variablePattern("opts_3"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Keyword["get/3"](
					context.vars.opts_3,
					Type.atom("checkout_retries"),
					Type.integer(3n),
				),
				Type.variablePattern("retries_4"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_DBConnection["run_with_retries/5"](
				context.vars.retries_4,
				context.vars.pool_0,
				context.vars.fun_1,
				context.vars.meter_2,
				context.vars.opts_3,
			);
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection", "run_declare", 5, "private", [
	{
		params: (context) => [
			Type.variablePattern("conn_0"),
			Type.variablePattern("query_1"),
			Type.variablePattern("params_2"),
			Type.variablePattern("meter_3"),
			Type.variablePattern("opts_4"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				context.vars.conn_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.DBConnection")],
					[Type.atom("pool_ref"), Type.variablePattern("pool_ref_5")],
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_DBConnection["event/2"](
					context.vars.meter_3,
					Type.atom("declare"),
				),
				Type.variablePattern("meter_6"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			globalThis.Hologram.return = Interpreter.case(
				Elixir_DBConnection_Holder["handle/4"](
					context.vars.pool_ref_5,
					Type.atom("handle_declare"),
					Type.list([context.vars.query_1, context.vars.params_2]),
					context.vars.opts_4,
				),
				[
					{
						match: Type.tuple([
							Type.atom("ok"),
							Type.variablePattern("query_7"),
							Type.variablePattern("result_8"),
							Type.matchPlaceholder(),
						]),
						guards: [],
						body: (context) => {
							return Type.tuple([
								Type.atom("ok"),
								context.vars.query_7,
								context.vars.result_8,
								context.vars.meter_6,
							]);
						},
					},
					{
						match: Interpreter.matchOperator(
							Type.variablePattern("other_10"),
							Type.tuple([
								Type.atom("ok"),
								Type.matchPlaceholder(),
								Type.matchPlaceholder(),
							]),
							context,
						),
						guards: [],
						body: (context) => {
							return Elixir_DBConnection["bad_return!/3"](
								context.vars.other_10,
								context.vars.conn_0,
								context.vars.meter_6,
							);
						},
					},
					{
						match: Type.variablePattern("other_11"),
						guards: [],
						body: (context) => {
							return Elixir_DBConnection["handle_common_result/3"](
								context.vars.other_11,
								context.vars.conn_0,
								context.vars.meter_6,
							);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction(
	"DBConnection",
	"run_prepare_declare",
	5,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("conn_0"),
				Type.variablePattern("query_1"),
				Type.variablePattern("params_2"),
				Type.variablePattern("meter_3"),
				Type.variablePattern("opts_4"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.with();
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection",
	"run_with_retries",
	5,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("retries_0"),
				Type.variablePattern("pool_1"),
				Type.variablePattern("fun_2"),
				Type.variablePattern("meter_3"),
				Type.variablePattern("opts_4"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.with();
			},
		},
	],
);

Interpreter.defineElixirFunction("DBConnection", "stop", 4, "private", [
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.DBConnection")],
				[Type.atom("pool_ref"), Type.variablePattern("pool_ref_0")],
			]),
			Type.variablePattern("kind_1"),
			Type.variablePattern("reason_2"),
			Type.variablePattern("stack_3"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Type.bitstring([
					Type.bitstringSegment(Type.string("client "), { type: "binary" }),
					Type.bitstringSegment(
						Elixir_String_Chars["to_string/1"](
							Elixir_DBConnection_Util["inspect_pid/1"](Erlang["self/0"]()),
						),
						{ type: "binary" },
					),
					Type.bitstringSegment(Type.string(" stopped: "), { type: "binary" }),
					Type.bitstringSegment(
						Elixir_Exception["format/3"](
							context.vars.kind_1,
							context.vars.reason_2,
							context.vars.stack_3,
						),
						{ type: "binary" },
					),
				]),
				Type.variablePattern("msg_4"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_DBConnection_ConnectionError["exception/1"](context.vars.msg_4),
				Type.variablePattern("exception_5"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_DBConnection_Holder["stop/2"](
					context.vars.pool_ref_0,
					context.vars.exception_5,
				),
				Type.matchPlaceholder(),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.atom("ok");
		},
	},
]);

Interpreter.defineElixirFunction(
	"DBConnection",
	"stream_deallocate",
	3,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("conn_0"),
				Type.tuple([
					Type.matchPlaceholder(),
					Type.variablePattern("query_2"),
					Type.variablePattern("cursor_3"),
				]),
				Type.variablePattern("opts_4"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Elixir_DBConnection["event/2"](
						Elixir_DBConnection["meter/1"](context.vars.opts_4),
						Type.atom("deallocate"),
					),
					Type.variablePattern("meter_5"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_DBConnection["log/4"](
					Elixir_DBConnection["handle_common_result/3"](
						Elixir_DBConnection["cleanup/4"](
							context.vars.conn_0,
							Type.atom("handle_deallocate"),
							Type.list([context.vars.query_2, context.vars.cursor_3]),
							context.vars.opts_4,
						),
						context.vars.conn_0,
						context.vars.meter_5,
					),
					Type.atom("deallocate"),
					context.vars.query_2,
					context.vars.cursor_3,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("DBConnection", "stream_fetch", 3, "private", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("conn_0"),
				Type.map([[Type.atom("__struct__"), Type.atom("Elixir.DBConnection")]]),
				context,
			),
			Type.tuple([
				Type.atom("cont"),
				Type.variablePattern("query_1"),
				Type.variablePattern("cursor_2"),
			]),
			Type.variablePattern("opts_3"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Elixir_DBConnection["log/4"](
					Interpreter.with(),
					Type.atom("fetch"),
					context.vars.query_1,
					context.vars.cursor_2,
				),
				[
					{
						match: Type.tuple([
							Type.variablePattern("ok_9"),
							Type.variablePattern("result_10"),
						]),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["=:=/2"](context.vars.ok_9, Type.atom("cont")),
									(context) =>
										Erlang["=:=/2"](context.vars.ok_9, Type.atom("halt")),
									context,
								),
						],
						body: (context) => {
							return Type.tuple([
								Type.list([context.vars.result_10]),
								Type.tuple([
									context.vars.ok_9,
									context.vars.query_1,
									context.vars.cursor_2,
								]),
							]);
						},
					},
					{
						match: Type.tuple([
							Type.atom("error"),
							Type.variablePattern("err_11"),
						]),
						guards: [],
						body: (context) => {
							return Erlang["error/3"](
								Elixir_Kernel_Utils["raise/1"](context.vars.err_11),
								Type.atom("none"),
								Type.list([
									Type.tuple([
										Type.atom("error_info"),
										Type.map([
											[Type.atom("module"), Type.atom("Elixir.Exception")],
										]),
									]),
								]),
							);
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.matchPlaceholder(),
			Interpreter.matchOperator(
				Type.variablePattern("state_0"),
				Type.tuple([
					Type.atom("halt"),
					Type.matchPlaceholder(),
					Type.matchPlaceholder(),
				]),
				context,
			),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([Type.atom("halt"), context.vars.state_0]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"DBConnection.ConnectionError",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.DBConnection.ConnectionError"),
					],
					[Type.atom("message"), Type.atom("nil")],
					[Type.atom("reason"), Type.atom("error")],
					[Type.atom("severity"), Type.atom("error")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.ConnectionError",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[Type.atom("__exception__"), Type.atom("true")],
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.DBConnection.ConnectionError"),
						],
						[Type.atom("message"), Type.atom("nil")],
						[Type.atom("reason"), Type.atom("error")],
						[Type.atom("severity"), Type.atom("error")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.ConnectionError",
	"exception",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("msg_0")],
			guards: [(context) => Erlang["is_binary/1"](context.vars.msg_0)],
			body: (context) => {
				return Elixir_DBConnection_ConnectionError["exception/1"](
					Type.list([Type.tuple([Type.atom("message"), context.vars.msg_0])]),
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("args_0")],
			guards: [(context) => Erlang["is_list/1"](context.vars.args_0)],
			body: (context) => {
				return Elixir_Kernel["struct!/2"](
					Type.atom("Elixir.DBConnection.ConnectionError"),
					context.vars.args_0,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.EncodeError",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.DBConnection.EncodeError"),
					],
					[Type.atom("message"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.EncodeError",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[Type.atom("__exception__"), Type.atom("true")],
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.DBConnection.EncodeError"),
						],
						[Type.atom("message"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.Holder",
	"augment_disconnect",
	1,
	"private",
	[
		{
			params: (context) => [
				Type.tuple([
					Type.atom("disconnect"),
					Interpreter.matchOperator(
						Type.variablePattern("err_0"),
						Type.map([
							[
								Type.atom("__struct__"),
								Type.atom("Elixir.DBConnection.ConnectionError"),
							],
						]),
						context,
					),
					Type.variablePattern("state_1"),
				]),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					context.vars.err_0,
					Type.map([[Type.atom("message"), Type.variablePattern("message_2")]]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Type.bitstring([
						Type.bitstringSegment(context.vars.message_2, { type: "binary" }),
						Type.bitstringSegment(
							Type.string(" (the connection was closed by the pool, "),
							{ type: "binary" },
						),
						Type.bitstringSegment(
							Type.string(
								"possibly due to a timeout or because the pool has been terminated)",
							),
							{ type: "binary" },
						),
					]),
					Type.variablePattern("message_3"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Type.tuple([
					Type.atom("disconnect"),
					Elixir_Map["merge/2"](
						context.vars.err_0,
						Type.map([[Type.atom("message"), context.vars.message_3]]),
					),
					context.vars.state_1,
				]);
			},
		},
		{
			params: (context) => [Type.variablePattern("result_0")],
			guards: [],
			body: (context) => {
				return context.vars.result_0;
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.Holder",
	"cancel_deadline",
	1,
	"private",
	[
		{
			params: (context) => [Type.atom("nil")],
			guards: [],
			body: (context) => {
				return Type.atom("ok");
			},
		},
		{
			params: (context) => [Type.variablePattern("deadline_0")],
			guards: [],
			body: (context) => {
				return Erlang["cancel_timer/2"](
					context.vars.deadline_0,
					Type.list([
						Type.tuple([Type.atom("async"), Type.atom("true")]),
						Type.tuple([Type.atom("info"), Type.atom("false")]),
					]),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.Holder",
	"cleanup",
	4,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("pool_ref_0"),
				Type.variablePattern("fun_1"),
				Type.variablePattern("args_2"),
				Type.variablePattern("opts_3"),
			],
			guards: [],
			body: (context) => {
				return Elixir_DBConnection_Holder["handle_or_cleanup/5"](
					Type.atom("cleanup"),
					context.vars.pool_ref_0,
					context.vars.fun_1,
					context.vars.args_2,
					context.vars.opts_3,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.Holder",
	"disconnect",
	2,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("pool_ref_0"),
				Type.variablePattern("err_1"),
			],
			guards: [],
			body: (context) => {
				return Elixir_DBConnection_Holder["done/4"](
					context.vars.pool_ref_0,
					Type.list([
						Type.tuple([
							Erlang["+/2"](Type.integer(7n), Type.integer(1n)),
							Type.atom("error"),
						]),
					]),
					Type.atom("disconnect"),
					context.vars.err_1,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("DBConnection.Holder", "done", 4, "private", [
	{
		params: (context) => [
			Type.variablePattern("pool_ref_0"),
			Type.variablePattern("ops_1"),
			Type.variablePattern("tag_2"),
			Type.variablePattern("info_3"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				context.vars.pool_ref_0,
				Type.tuple([
					Type.atom("pool_ref"),
					Type.variablePattern("pool_4"),
					Type.variablePattern("ref_5"),
					Type.variablePattern("deadline_6"),
					Type.variablePattern("holder_7"),
					Type.matchPlaceholder(),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Elixir_DBConnection_Holder["cancel_deadline/1"](context.vars.deadline_6);
			return Interpreter.try(
				(context) => {
					Erlang_Ets["update_element/3"](
						context.vars.holder_7,
						Type.atom("conn"),
						Interpreter.consOperator(
							Type.tuple([
								Erlang["+/2"](Type.integer(6n), Type.integer(1n)),
								Type.atom("nil"),
							]),
							context.vars.ops_1,
						),
					);
					return Erlang_Ets["give_away/3"](
						context.vars.holder_7,
						context.vars.pool_4,
						Type.tuple([
							context.vars.tag_2,
							context.vars.ref_5,
							context.vars.info_3,
						]),
					);
				},
				[
					{
						variable: Type.matchPlaceholder(),
						modules: [Type.atom("Elixir.ArgumentError")],
						body: (context) => {
							return Type.atom("ok");
						},
					},
				],
				[],
				[],
				null,
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("DBConnection.Holder", "handle", 4, "public", [
	{
		params: (context) => [
			Type.variablePattern("pool_ref_0"),
			Type.variablePattern("fun_1"),
			Type.variablePattern("args_2"),
			Type.variablePattern("opts_3"),
		],
		guards: [],
		body: (context) => {
			return Elixir_DBConnection_Holder["handle_or_cleanup/5"](
				Type.atom("handle"),
				context.vars.pool_ref_0,
				context.vars.fun_1,
				context.vars.args_2,
				context.vars.opts_3,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"DBConnection.Holder",
	"handle_or_cleanup",
	5,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("type_0"),
				Type.variablePattern("pool_ref_1"),
				Type.variablePattern("fun_2"),
				Type.variablePattern("args_3"),
				Type.variablePattern("opts_4"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					context.vars.pool_ref_1,
					Type.tuple([
						Type.atom("pool_ref"),
						Type.matchPlaceholder(),
						Type.matchPlaceholder(),
						Type.matchPlaceholder(),
						Type.variablePattern("holder_5"),
						Type.variablePattern("lock_6"),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				globalThis.Hologram.return = Interpreter.try(
					(context) => {
						return Erlang_Ets["lookup/2"](
							context.vars.holder_5,
							Type.atom("conn"),
						);
					},
					[
						{
							variable: Type.matchPlaceholder(),
							modules: [Type.atom("Elixir.ArgumentError")],
							body: (context) => {
								Interpreter.matchOperator(
									Type.bitstring(
										"connection is closed because of an error, disconnect or timeout",
									),
									Type.variablePattern("msg_7"),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								globalThis.Hologram.return = Type.tuple([
									Type.atom("disconnect"),
									Elixir_DBConnection_ConnectionError["exception/1"](
										context.vars.msg_7,
									),
									Interpreter.matchOperator(
										Type.atom("unused"),
										Type.matchPlaceholder(),
										context,
									),
								]);
								Interpreter.updateVarsToMatchedValues(context);
								return globalThis.Hologram.return;
							},
						},
					],
					[],
					[],
					null,
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return globalThis.Hologram.return;
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.Holder",
	"holder_apply",
	4,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("holder_0"),
				Type.variablePattern("module_1"),
				Type.variablePattern("fun_2"),
				Type.variablePattern("args_3"),
			],
			guards: [],
			body: (context) => {
				globalThis.Hologram.return = Interpreter.try(
					(context) => {
						return Interpreter.callNamedFunction(
							context.vars.module_1,
							context.vars.fun_2,
							context.vars.args_3,
							context,
						);
					},
					[],
					[],
					[],
					null,
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return globalThis.Hologram.return;
			},
		},
	],
);

Interpreter.defineElixirFunction("DBConnection.Holder", "stop", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("pool_ref_0"),
			Type.variablePattern("err_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_DBConnection_Holder["done/4"](
				context.vars.pool_ref_0,
				Type.list([
					Type.tuple([
						Erlang["+/2"](Type.integer(7n), Type.integer(1n)),
						Type.atom("error"),
					]),
				]),
				Type.atom("stop"),
				context.vars.err_1,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"DBConnection.LogEntry",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.DBConnection.LogEntry")],
					[Type.atom("call"), Type.atom("nil")],
					[Type.atom("connection_time"), Type.atom("nil")],
					[Type.atom("decode_time"), Type.atom("nil")],
					[Type.atom("idle_time"), Type.atom("nil")],
					[Type.atom("params"), Type.atom("nil")],
					[Type.atom("pool_time"), Type.atom("nil")],
					[Type.atom("query"), Type.atom("nil")],
					[Type.atom("result"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.LogEntry",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.DBConnection.LogEntry"),
						],
						[Type.atom("call"), Type.atom("nil")],
						[Type.atom("connection_time"), Type.atom("nil")],
						[Type.atom("decode_time"), Type.atom("nil")],
						[Type.atom("idle_time"), Type.atom("nil")],
						[Type.atom("params"), Type.atom("nil")],
						[Type.atom("pool_time"), Type.atom("nil")],
						[Type.atom("query"), Type.atom("nil")],
						[Type.atom("result"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("DBConnection.LogEntry", "new", 5, "public", [
	{
		params: (context) => [
			Type.variablePattern("call_0"),
			Type.variablePattern("query_1"),
			Type.variablePattern("params_2"),
			Type.variablePattern("times_3"),
			Type.variablePattern("result_4"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_DBConnection_LogEntry["__struct__/1"](
					Type.list([
						Type.tuple([Type.atom("connection_time"), Type.atom("nil")]),
						Type.tuple([Type.atom("decode_time"), Type.atom("nil")]),
						Type.tuple([Type.atom("idle_time"), Type.atom("nil")]),
						Type.tuple([Type.atom("pool_time"), Type.atom("nil")]),
						Type.tuple([Type.atom("call"), context.vars.call_0]),
						Type.tuple([Type.atom("query"), context.vars.query_1]),
						Type.tuple([Type.atom("params"), context.vars.params_2]),
						Type.tuple([Type.atom("result"), context.vars.result_4]),
					]),
				),
				Type.variablePattern("entry_5"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_DBConnection_LogEntry["parse_times/2"](
				context.vars.times_3,
				context.vars.entry_5,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"DBConnection.LogEntry",
	"parse_time",
	2,
	"private",
	[
		{
			params: (context) => [
				Type.tuple([Type.atom("decode"), Type.variablePattern("start_0")]),
				Type.tuple([
					Type.variablePattern("stop_1"),
					Type.variablePattern("entry_2"),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([
					context.vars.start_0,
					Elixir_Map["merge/2"](
						context.vars.entry_2,
						Type.map([
							[
								Type.atom("decode_time"),
								Erlang["-/2"](context.vars.stop_1, context.vars.start_0),
							],
						]),
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([Type.atom("checkout"), Type.variablePattern("start_0")]),
				Type.tuple([
					Type.variablePattern("stop_1"),
					Type.variablePattern("entry_2"),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([
					context.vars.start_0,
					Elixir_Map["merge/2"](
						context.vars.entry_2,
						Type.map([
							[
								Type.atom("pool_time"),
								Erlang["-/2"](context.vars.stop_1, context.vars.start_0),
							],
						]),
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([Type.atom("checkin"), Type.variablePattern("start_0")]),
				Type.tuple([
					Type.variablePattern("stop_1"),
					Type.variablePattern("entry_2"),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([
					context.vars.stop_1,
					Elixir_Map["merge/2"](
						context.vars.entry_2,
						Type.map([
							[
								Type.atom("idle_time"),
								Erlang["max/2"](
									Erlang["-/2"](context.vars.stop_1, context.vars.start_0),
									Type.integer(0n),
								),
							],
						]),
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([Type.matchPlaceholder(), Type.variablePattern("start_0")]),
				Type.tuple([
					Type.variablePattern("stop_1"),
					Type.variablePattern("entry_2"),
				]),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					context.vars.entry_2,
					Type.map([
						[
							Type.atom("connection_time"),
							Type.variablePattern("connection_time_3"),
						],
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Type.tuple([
					context.vars.start_0,
					Elixir_Map["merge/2"](
						context.vars.entry_2,
						Type.map([
							[
								Type.atom("connection_time"),
								Erlang["+/2"](
									Interpreter.case(
										context.vars.connection_time_3,
										[
											{
												match: Type.variablePattern("x_4"),
												guards: [
													(context) =>
														Erlang["orelse/2"](
															(context) =>
																Erlang["=:=/2"](
																	context.vars.x_4,
																	Type.atom("false"),
																),
															(context) =>
																Erlang["=:=/2"](
																	context.vars.x_4,
																	Type.atom("nil"),
																),
															context,
														),
												],
												body: (context) => {
													return Type.integer(0n);
												},
											},
											{
												match: Type.variablePattern("x_5"),
												guards: [],
												body: (context) => {
													return context.vars.x_5;
												},
											},
										],
										context,
									),
									Erlang["-/2"](context.vars.stop_1, context.vars.start_0),
								),
							],
						]),
					),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.LogEntry",
	"parse_times",
	2,
	"private",
	[
		{
			params: (context) => [Type.list([]), Type.variablePattern("entry_0")],
			guards: [],
			body: (context) => {
				return context.vars.entry_0;
			},
		},
		{
			params: (context) => [
				Type.variablePattern("times_0"),
				Type.variablePattern("entry_1"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Erlang["monotonic_time/0"](),
					Type.variablePattern("stop_2"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Elixir_Enum["reduce/3"](
						context.vars.times_0,
						Type.tuple([context.vars.stop_2, context.vars.entry_1]),
						Type.functionCapture(
							"DBConnection.LogEntry",
							"parse_time",
							2,
							[
								{
									params: (context) => [
										Type.variablePattern("$1"),
										Type.variablePattern("$2"),
									],
									guards: [],
									body: (context) => {
										return Elixir_DBConnection_LogEntry["parse_time/2"](
											context.vars["$1"],
											context.vars["$2"],
										);
									},
								},
							],
							context,
						),
					),
					Type.tuple([
						Type.matchPlaceholder(),
						Type.variablePattern("entry_3"),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return context.vars.entry_3;
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.PrepareStream",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.DBConnection.PrepareStream"),
					],
					[Type.atom("conn"), Type.atom("nil")],
					[Type.atom("opts"), Type.atom("nil")],
					[Type.atom("params"), Type.atom("nil")],
					[Type.atom("query"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.PrepareStream",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.DBConnection.PrepareStream"),
						],
						[Type.atom("conn"), Type.atom("nil")],
						[Type.atom("opts"), Type.atom("nil")],
						[Type.atom("params"), Type.atom("nil")],
						[Type.atom("query"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("DBConnection.Query", "encode", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("term_0"),
			Type.variablePattern("arg2_1"),
			Type.variablePattern("arg3_2"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.callNamedFunction(
				Elixir_DBConnection_Query["impl_for!/1"](context.vars.term_0),
				Type.atom("encode"),
				Type.list([
					context.vars.term_0,
					context.vars.arg2_1,
					context.vars.arg3_2,
				]),
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"DBConnection.Query",
	"impl_for",
	1,
	"public",
	[
		{
			params: (context) => [
				Type.map([[Type.atom("__struct__"), Type.variablePattern("struct_0")]]),
			],
			guards: [],
			body: (context) => {
				return Elixir_DBConnection_Query["struct_impl_for/1"](
					context.vars.struct_0,
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("data_0")],
			guards: [(context) => Erlang["is_tuple/1"](context.vars.data_0)],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Type.atom("Elixir.DBConnection.Query.Tuple"),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("data_0")],
			guards: [(context) => Erlang["is_atom/1"](context.vars.data_0)],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Type.atom("Elixir.DBConnection.Query.Atom"),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("data_0")],
			guards: [(context) => Erlang["is_list/1"](context.vars.data_0)],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Type.atom("Elixir.DBConnection.Query.List"),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("data_0")],
			guards: [(context) => Erlang["is_map/1"](context.vars.data_0)],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Type.atom("Elixir.DBConnection.Query.Map"),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("data_0")],
			guards: [(context) => Erlang["is_bitstring/1"](context.vars.data_0)],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Type.atom("Elixir.DBConnection.Query.BitString"),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("data_0")],
			guards: [(context) => Erlang["is_integer/1"](context.vars.data_0)],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Type.atom("Elixir.DBConnection.Query.Integer"),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("data_0")],
			guards: [(context) => Erlang["is_float/1"](context.vars.data_0)],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Type.atom("Elixir.DBConnection.Query.Float"),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("data_0")],
			guards: [(context) => Erlang["is_function/1"](context.vars.data_0)],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Type.atom("Elixir.DBConnection.Query.Function"),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("data_0")],
			guards: [(context) => Erlang["is_pid/1"](context.vars.data_0)],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Type.atom("Elixir.DBConnection.Query.PID"),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("data_0")],
			guards: [(context) => Erlang["is_port/1"](context.vars.data_0)],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Type.atom("Elixir.DBConnection.Query.Port"),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("data_0")],
			guards: [(context) => Erlang["is_reference/1"](context.vars.data_0)],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Type.atom("Elixir.DBConnection.Query.Reference"),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.atom("nil");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.Query",
	"impl_for!",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("data_0")],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Elixir_DBConnection_Query["impl_for/1"](context.vars.data_0),
					[
						{
							match: Type.variablePattern("x_1"),
							guards: [
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](context.vars.x_1, Type.atom("false")),
										(context) =>
											Erlang["=:=/2"](context.vars.x_1, Type.atom("nil")),
										context,
									),
							],
							body: (context) => {
								return Erlang["error/1"](
									Elixir_Protocol_UndefinedError["exception/1"](
										Type.list([
											Type.tuple([
												Type.atom("protocol"),
												Type.atom("Elixir.DBConnection.Query"),
											]),
											Type.tuple([Type.atom("value"), context.vars.data_0]),
											Type.tuple([
												Type.atom("description"),
												Type.bitstring(""),
											]),
										]),
									),
								);
							},
						},
						{
							match: Type.variablePattern("x_2"),
							guards: [],
							body: (context) => {
								return context.vars.x_2;
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.Query",
	"struct_impl_for",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("struct_0")],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Elixir_Protocol["__concat__/2"](
							Type.atom("Elixir.DBConnection.Query"),
							context.vars.struct_0,
						),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.Query.Exqlite.Query",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Exqlite.Query");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.DBConnection.Query");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.Query.Exqlite.Query",
	"encode",
	3,
	"public",
	[
		{
			params: (context) => [
				Type.matchPlaceholder(),
				Type.variablePattern("params_1"),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				return context.vars.params_1;
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.Stream",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.DBConnection.Stream")],
					[Type.atom("conn"), Type.atom("nil")],
					[Type.atom("opts"), Type.atom("nil")],
					[Type.atom("params"), Type.atom("nil")],
					[Type.atom("query"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.Stream",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.DBConnection.Stream")],
						[Type.atom("conn"), Type.atom("nil")],
						[Type.atom("opts"), Type.atom("nil")],
						[Type.atom("params"), Type.atom("nil")],
						[Type.atom("query"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"DBConnection.Util",
	"inspect_pid",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("pid_0")],
			guards: [(context) => Erlang["is_pid/1"](context.vars.pid_0)],
			body: (context) => {
				return Interpreter.with();
			},
		},
		{
			params: (context) => [Type.variablePattern("other_0")],
			guards: [],
			body: (context) => {
				return Elixir_Kernel["inspect/1"](context.vars.other_0);
			},
		},
	],
);

Interpreter.defineElixirFunction("Date", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Date")],
				[Type.atom("calendar"), Type.atom("Elixir.Calendar.ISO")],
				[Type.atom("day"), Type.atom("nil")],
				[Type.atom("month"), Type.atom("nil")],
				[Type.atom("year"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Date", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.tuple([
						Type.map([
							[Type.atom("__struct__"), Type.atom("Elixir.Date")],
							[Type.atom("calendar"), Type.atom("Elixir.Calendar.ISO")],
							[Type.atom("day"), Type.atom("nil")],
							[Type.atom("month"), Type.atom("nil")],
							[Type.atom("year"), Type.atom("nil")],
						]),
						Type.list([
							Type.atom("year"),
							Type.atom("month"),
							Type.atom("day"),
						]),
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.tuple([
										Type.variablePattern("map_3"),
										Type.variablePattern("keys_4"),
									]),
								],
								guards: [],
								body: (context) => {
									return Type.tuple([
										Elixir_Map["merge/2"](
											context.vars.map_3,
											Type.map([[context.vars.key_1, context.vars.val_2]]),
										),
										Elixir_List["delete/2"](
											context.vars.keys_4,
											context.vars.key_1,
										),
									]);
								},
							},
						],
						context,
					),
				),
				Type.tuple([
					Type.variablePattern("map_5"),
					Type.variablePattern("keys_6"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				context.vars.keys_6,
				[
					{
						match: Type.list([]),
						guards: [],
						body: (context) => {
							return context.vars.map_5;
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_ArgumentError["exception/1"](
									Type.bitstring([
										Type.bitstringSegment(
											Type.string(
												"the following keys must also be given when building ",
											),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string("struct "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](Type.atom("Elixir.Date")),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string(": "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](context.vars.keys_6),
											{ type: "binary" },
										),
									]),
								),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Date", "to_iso_days", 1, "public", [
	{
		params: (context) => [
			Type.map([
				[Type.atom("calendar"), Type.atom("Elixir.Calendar.ISO")],
				[Type.atom("day"), Type.variablePattern("day_2")],
				[Type.atom("month"), Type.variablePattern("month_1")],
				[Type.atom("year"), Type.variablePattern("year_0")],
			]),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Elixir_Calendar_ISO["date_to_iso_days/3"](
					context.vars.year_0,
					context.vars.month_1,
					context.vars.day_2,
				),
				Type.tuple([Type.integer(0n), Type.integer(86400000000n)]),
			]);
		},
	},
	{
		params: (context) => [
			Type.map([
				[Type.atom("calendar"), Type.variablePattern("calendar_0")],
				[Type.atom("day"), Type.variablePattern("day_3")],
				[Type.atom("month"), Type.variablePattern("month_2")],
				[Type.atom("year"), Type.variablePattern("year_1")],
			]),
		],
		guards: [],
		body: (context) => {
			return Interpreter.callNamedFunction(
				context.vars.calendar_0,
				Type.atom("naive_datetime_to_iso_days"),
				Type.list([
					context.vars.year_1,
					context.vars.month_2,
					context.vars.day_3,
					Type.integer(0n),
					Type.integer(0n),
					Type.integer(0n),
					Type.tuple([Type.integer(0n), Type.integer(0n)]),
				]),
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Date.Range", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
				[Type.atom("first"), Type.atom("nil")],
				[Type.atom("first_in_iso_days"), Type.atom("nil")],
				[Type.atom("last"), Type.atom("nil")],
				[Type.atom("last_in_iso_days"), Type.atom("nil")],
				[Type.atom("step"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Date.Range", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.tuple([
						Type.map([
							[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
							[Type.atom("first"), Type.atom("nil")],
							[Type.atom("first_in_iso_days"), Type.atom("nil")],
							[Type.atom("last"), Type.atom("nil")],
							[Type.atom("last_in_iso_days"), Type.atom("nil")],
							[Type.atom("step"), Type.atom("nil")],
						]),
						Type.list([
							Type.atom("first"),
							Type.atom("last"),
							Type.atom("first_in_iso_days"),
							Type.atom("last_in_iso_days"),
							Type.atom("step"),
						]),
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.tuple([
										Type.variablePattern("map_3"),
										Type.variablePattern("keys_4"),
									]),
								],
								guards: [],
								body: (context) => {
									return Type.tuple([
										Elixir_Map["merge/2"](
											context.vars.map_3,
											Type.map([[context.vars.key_1, context.vars.val_2]]),
										),
										Elixir_List["delete/2"](
											context.vars.keys_4,
											context.vars.key_1,
										),
									]);
								},
							},
						],
						context,
					),
				),
				Type.tuple([
					Type.variablePattern("map_5"),
					Type.variablePattern("keys_6"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				context.vars.keys_6,
				[
					{
						match: Type.list([]),
						guards: [],
						body: (context) => {
							return context.vars.map_5;
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_ArgumentError["exception/1"](
									Type.bitstring([
										Type.bitstringSegment(
											Type.string(
												"the following keys must also be given when building ",
											),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string("struct "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](
												Type.atom("Elixir.Date.Range"),
											),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string(": "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](context.vars.keys_6),
											{ type: "binary" },
										),
									]),
								),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("DateTime", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.DateTime")],
				[Type.atom("calendar"), Type.atom("Elixir.Calendar.ISO")],
				[Type.atom("day"), Type.atom("nil")],
				[Type.atom("hour"), Type.atom("nil")],
				[
					Type.atom("microsecond"),
					Type.tuple([Type.integer(0n), Type.integer(0n)]),
				],
				[Type.atom("minute"), Type.atom("nil")],
				[Type.atom("month"), Type.atom("nil")],
				[Type.atom("second"), Type.atom("nil")],
				[Type.atom("std_offset"), Type.atom("nil")],
				[Type.atom("time_zone"), Type.atom("nil")],
				[Type.atom("utc_offset"), Type.atom("nil")],
				[Type.atom("year"), Type.atom("nil")],
				[Type.atom("zone_abbr"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("DateTime", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.tuple([
						Type.map([
							[Type.atom("__struct__"), Type.atom("Elixir.DateTime")],
							[Type.atom("calendar"), Type.atom("Elixir.Calendar.ISO")],
							[Type.atom("day"), Type.atom("nil")],
							[Type.atom("hour"), Type.atom("nil")],
							[
								Type.atom("microsecond"),
								Type.tuple([Type.integer(0n), Type.integer(0n)]),
							],
							[Type.atom("minute"), Type.atom("nil")],
							[Type.atom("month"), Type.atom("nil")],
							[Type.atom("second"), Type.atom("nil")],
							[Type.atom("std_offset"), Type.atom("nil")],
							[Type.atom("time_zone"), Type.atom("nil")],
							[Type.atom("utc_offset"), Type.atom("nil")],
							[Type.atom("year"), Type.atom("nil")],
							[Type.atom("zone_abbr"), Type.atom("nil")],
						]),
						Type.list([
							Type.atom("year"),
							Type.atom("month"),
							Type.atom("day"),
							Type.atom("hour"),
							Type.atom("minute"),
							Type.atom("second"),
							Type.atom("time_zone"),
							Type.atom("zone_abbr"),
							Type.atom("utc_offset"),
							Type.atom("std_offset"),
						]),
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.tuple([
										Type.variablePattern("map_3"),
										Type.variablePattern("keys_4"),
									]),
								],
								guards: [],
								body: (context) => {
									return Type.tuple([
										Elixir_Map["merge/2"](
											context.vars.map_3,
											Type.map([[context.vars.key_1, context.vars.val_2]]),
										),
										Elixir_List["delete/2"](
											context.vars.keys_4,
											context.vars.key_1,
										),
									]);
								},
							},
						],
						context,
					),
				),
				Type.tuple([
					Type.variablePattern("map_5"),
					Type.variablePattern("keys_6"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				context.vars.keys_6,
				[
					{
						match: Type.list([]),
						guards: [],
						body: (context) => {
							return context.vars.map_5;
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_ArgumentError["exception/1"](
									Type.bitstring([
										Type.bitstringSegment(
											Type.string(
												"the following keys must also be given when building ",
											),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string("struct "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](Type.atom("Elixir.DateTime")),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string(": "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](context.vars.keys_6),
											{ type: "binary" },
										),
									]),
								),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Decimal", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Decimal")],
				[Type.atom("coef"), Type.integer(0n)],
				[Type.atom("exp"), Type.integer(0n)],
				[Type.atom("sign"), Type.integer(1n)],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Decimal", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Decimal")],
					[Type.atom("coef"), Type.integer(0n)],
					[Type.atom("exp"), Type.integer(0n)],
					[Type.atom("sign"), Type.integer(1n)],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Decimal", "canonical_xsd", 1, "private", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("decimal_0"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Decimal")],
					[Type.atom("coef"), Type.integer(0n)],
				]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return Elixir_Map["merge/2"](
				context.vars.decimal_0,
				Type.map([[Type.atom("exp"), Type.integer(-1n)]]),
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("decimal_1"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Decimal")],
					[Type.atom("coef"), Type.variablePattern("coef_0")],
					[Type.atom("exp"), Type.integer(0n)],
				]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return Elixir_Map["merge/2"](
				context.vars.decimal_1,
				Type.map([
					[
						Type.atom("coef"),
						Erlang["*/2"](context.vars.coef_0, Type.integer(10n)),
					],
					[Type.atom("exp"), Type.integer(-1n)],
				]),
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("decimal_2"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Decimal")],
					[Type.atom("coef"), Type.variablePattern("coef_0")],
					[Type.atom("exp"), Type.variablePattern("exp_1")],
				]),
				context,
			),
		],
		guards: [(context) => Erlang[">/2"](context.vars.exp_1, Type.integer(0n))],
		body: (context) => {
			return Elixir_Decimal["canonical_xsd/1"](
				Elixir_Map["merge/2"](
					context.vars.decimal_2,
					Type.map([
						[
							Type.atom("coef"),
							Erlang["*/2"](context.vars.coef_0, Type.integer(10n)),
						],
						[
							Type.atom("exp"),
							Erlang["-/2"](context.vars.exp_1, Type.integer(1n)),
						],
					]),
				),
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("decimal_1"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Decimal")],
					[Type.atom("coef"), Type.variablePattern("coef_0")],
				]),
				context,
			),
		],
		guards: [
			(context) =>
				Erlang["/=/2"](
					Erlang["rem/2"](context.vars.coef_0, Type.integer(10n)),
					Type.integer(0n),
				),
		],
		body: (context) => {
			return context.vars.decimal_1;
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("decimal_2"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Decimal")],
					[Type.atom("coef"), Type.variablePattern("coef_0")],
					[Type.atom("exp"), Type.variablePattern("exp_1")],
				]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return Elixir_Decimal["canonical_xsd/1"](
				Elixir_Map["merge/2"](
					context.vars.decimal_2,
					Type.map([
						[
							Type.atom("coef"),
							Erlang["div/2"](context.vars.coef_0, Type.integer(10n)),
						],
						[
							Type.atom("exp"),
							Erlang["+/2"](context.vars.exp_1, Type.integer(1n)),
						],
					]),
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Decimal",
	"integer_to_charlist",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("string_0")],
			guards: [],
			body: (context) => {
				return Erlang["integer_to_list/1"](context.vars.string_0);
			},
		},
	],
);

Interpreter.defineElixirFunction("Decimal", "to_string", 1, "public", [
	{
		params: (context) => [Type.variablePattern("x0_0")],
		guards: [],
		body: (context) => {
			return Elixir_Decimal["to_string/2"](
				context.vars.x0_0,
				Type.atom("scientific"),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Decimal", "to_string", 2, "public", [
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Decimal")],
				[Type.atom("coef"), Type.atom("NaN")],
				[Type.atom("sign"), Type.variablePattern("sign_0")],
			]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Erlang["==/2"](context.vars.sign_0, Type.integer(1n)),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Type.bitstring("-NaN");
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Type.bitstring("NaN");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Decimal")],
				[Type.atom("coef"), Type.atom("inf")],
				[Type.atom("sign"), Type.variablePattern("sign_0")],
			]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Erlang["==/2"](context.vars.sign_0, Type.integer(1n)),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Type.bitstring("-Infinity");
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Type.bitstring("Infinity");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Decimal")],
				[Type.atom("coef"), Type.variablePattern("coef_1")],
				[Type.atom("exp"), Type.variablePattern("exp_2")],
				[Type.atom("sign"), Type.variablePattern("sign_0")],
			]),
			Type.atom("normal"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Decimal["integer_to_charlist/1"](context.vars.coef_1),
				Type.variablePattern("list_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Interpreter.case(
					Erlang[">=/2"](context.vars.exp_2, Type.integer(0n)),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								Interpreter.matchOperator(
									Erlang["+/2"](
										Erlang["length/1"](context.vars.list_3),
										context.vars.exp_2,
									),
									Type.variablePattern("diff_4"),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								return Interpreter.case(
									Erlang[">/2"](context.vars.diff_4, Type.integer(0n)),
									[
										{
											match: Type.atom("false"),
											guards: [],
											body: (context) => {
												return Erlang["++/2"](
													Type.list([Type.integer(48n), Type.integer(46n)]),
													Erlang["++/2"](
														Erlang_Lists["duplicate/2"](
															Erlang["-/1"](context.vars.diff_4),
															Type.integer(48n),
														),
														context.vars.list_3,
													),
												);
											},
										},
										{
											match: Type.atom("true"),
											guards: [],
											body: (context) => {
												return Elixir_List["insert_at/3"](
													context.vars.list_3,
													context.vars.diff_4,
													Type.integer(46n),
												);
											},
										},
									],
									context,
								);
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Erlang["++/2"](
									context.vars.list_3,
									Erlang_Lists["duplicate/2"](
										context.vars.exp_2,
										Type.integer(48n),
									),
								);
							},
						},
					],
					context,
				),
				Type.variablePattern("list_5"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Interpreter.case(
					Erlang["==/2"](context.vars.sign_0, Type.integer(-1n)),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return context.vars.list_5;
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Interpreter.consOperator(
									Type.integer(45n),
									context.vars.list_5,
								);
							},
						},
					],
					context,
				),
				Type.variablePattern("list_6"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Erlang["iolist_to_binary/1"](context.vars.list_6);
		},
	},
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Decimal")],
				[Type.atom("coef"), Type.variablePattern("coef_1")],
				[Type.atom("exp"), Type.variablePattern("exp_2")],
				[Type.atom("sign"), Type.variablePattern("sign_0")],
			]),
			Type.atom("scientific"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Decimal["integer_to_charlist/1"](context.vars.coef_1),
				Type.variablePattern("list_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Erlang["length/1"](context.vars.list_3),
				Type.variablePattern("length_4"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Erlang["-/2"](
					Erlang["+/2"](context.vars.exp_2, context.vars.length_4),
					Type.integer(1n),
				),
				Type.variablePattern("adjusted_5"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Interpreter.cond(
					[
						{
							condition: (context) =>
								Erlang["==/2"](context.vars.exp_2, Type.integer(0n)),
							body: (context) => {
								return context.vars.list_3;
							},
						},
						{
							condition: (context) =>
								Interpreter.case(
									Erlang["</2"](context.vars.exp_2, Type.integer(0n)),
									[
										{
											match: Type.atom("false"),
											guards: [],
											body: (context) => {
												return Type.atom("false");
											},
										},
										{
											match: Type.atom("true"),
											guards: [],
											body: (context) => {
												return Erlang[">=/2"](
													context.vars.adjusted_5,
													Type.integer(-6n),
												);
											},
										},
									],
									context,
								),
							body: (context) => {
								Interpreter.matchOperator(
									Erlang["abs/1"](context.vars.exp_2),
									Type.variablePattern("abs_exp_6"),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								Interpreter.matchOperator(
									Erlang["+/2"](
										Erlang["+/2"](
											Erlang["-/1"](context.vars.length_4),
											context.vars.abs_exp_6,
										),
										Type.integer(1n),
									),
									Type.variablePattern("diff_7"),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								globalThis.Hologram.return = Interpreter.case(
									Erlang[">/2"](context.vars.diff_7, Type.integer(0n)),
									[
										{
											match: Type.atom("false"),
											guards: [],
											body: (context) => {
												return Elixir_List["insert_at/3"](
													context.vars.list_3,
													Erlang["-/2"](context.vars.exp_2, Type.integer(1n)),
													Type.integer(46n),
												);
											},
										},
										{
											match: Type.atom("true"),
											guards: [],
											body: (context) => {
												Interpreter.matchOperator(
													Erlang["++/2"](
														Erlang_Lists["duplicate/2"](
															context.vars.diff_7,
															Type.integer(48n),
														),
														context.vars.list_3,
													),
													Type.variablePattern("list_8"),
													context,
												);
												Interpreter.updateVarsToMatchedValues(context);
												return Elixir_List["insert_at/3"](
													context.vars.list_8,
													Type.integer(1n),
													Type.integer(46n),
												);
											},
										},
									],
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								return globalThis.Hologram.return;
							},
						},
						{
							condition: (context) => Type.atom("true"),
							body: (context) => {
								Interpreter.matchOperator(
									Interpreter.case(
										Erlang[">/2"](context.vars.length_4, Type.integer(1n)),
										[
											{
												match: Type.atom("false"),
												guards: [],
												body: (context) => {
													return context.vars.list_3;
												},
											},
											{
												match: Type.atom("true"),
												guards: [],
												body: (context) => {
													return Elixir_List["insert_at/3"](
														context.vars.list_3,
														Type.integer(1n),
														Type.integer(46n),
													);
												},
											},
										],
										context,
									),
									Type.variablePattern("list_9"),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								Interpreter.matchOperator(
									Erlang["++/2"](
										context.vars.list_9,
										Type.list([Type.integer(69n)]),
									),
									Type.variablePattern("list_10"),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								Interpreter.matchOperator(
									Interpreter.case(
										Erlang[">=/2"](context.vars.exp_2, Type.integer(0n)),
										[
											{
												match: Type.atom("false"),
												guards: [],
												body: (context) => {
													return context.vars.list_10;
												},
											},
											{
												match: Type.atom("true"),
												guards: [],
												body: (context) => {
													return Erlang["++/2"](
														context.vars.list_10,
														Type.list([Type.integer(43n)]),
													);
												},
											},
										],
										context,
									),
									Type.variablePattern("list_11"),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								return Erlang["++/2"](
									context.vars.list_11,
									Elixir_Decimal["integer_to_charlist/1"](
										context.vars.adjusted_5,
									),
								);
							},
						},
					],
					context,
				),
				Type.variablePattern("list_12"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Interpreter.case(
					Erlang["==/2"](context.vars.sign_0, Type.integer(-1n)),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return context.vars.list_12;
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Interpreter.consOperator(
									Type.integer(45n),
									context.vars.list_12,
								);
							},
						},
					],
					context,
				),
				Type.variablePattern("list_13"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Erlang["iolist_to_binary/1"](context.vars.list_13);
		},
	},
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Decimal")],
				[Type.atom("coef"), Type.variablePattern("coef_1")],
				[Type.atom("exp"), Type.variablePattern("exp_2")],
				[Type.atom("sign"), Type.variablePattern("sign_0")],
			]),
			Type.atom("raw"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["integer_to_binary/1"](context.vars.coef_1),
				Type.variablePattern("str_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Interpreter.case(
					Erlang["==/2"](context.vars.sign_0, Type.integer(-1n)),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return context.vars.str_3;
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Interpreter.consOperator(
									Type.integer(45n),
									context.vars.str_3,
								);
							},
						},
					],
					context,
				),
				Type.variablePattern("str_4"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Interpreter.case(
					Erlang["/=/2"](context.vars.exp_2, Type.integer(0n)),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return context.vars.str_4;
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Type.list([
									context.vars.str_4,
									Type.bitstring("E"),
									Erlang["integer_to_binary/1"](context.vars.exp_2),
								]);
							},
						},
					],
					context,
				),
				Type.variablePattern("str_5"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Erlang["iolist_to_binary/1"](context.vars.str_5);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("decimal_0"),
				Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Decimal")]]),
				context,
			),
			Type.atom("xsd"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Decimal["to_string/2"](
				Elixir_Decimal["canonical_xsd/1"](context.vars.decimal_0),
				Type.atom("normal"),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Ecto.Adapters.SQL",
	"get_conn",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("pool_0")],
			guards: [],
			body: (context) => {
				return Elixir_Process["get/1"](
					Elixir_Ecto_Adapters_SQL["key/1"](context.vars.pool_0),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Ecto.Adapters.SQL", "into", 4, "public", [
	{
		params: (context) => [
			Type.variablePattern("adapter_meta_0"),
			Type.variablePattern("statement_1"),
			Type.variablePattern("params_2"),
			Type.variablePattern("opts_3"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				context.vars.adapter_meta_0,
				Type.map([
					[
						Type.atom("log_stacktrace_mfa"),
						Type.variablePattern("log_stacktrace_mfa_8"),
					],
					[Type.atom("opts"), Type.variablePattern("default_opts_7")],
					[Type.atom("pid"), Type.variablePattern("pool_4")],
					[Type.atom("sql"), Type.variablePattern("sql_6")],
					[Type.atom("telemetry"), Type.variablePattern("telemetry_5")],
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_Ecto_Adapters_SQL["with_log/4"](
					context.vars.telemetry_5,
					context.vars.log_stacktrace_mfa_8,
					context.vars.params_2,
					Erlang["++/2"](context.vars.opts_3, context.vars.default_opts_7),
				),
				Type.variablePattern("opts_9"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			globalThis.Hologram.return = Interpreter.case(
				Elixir_Ecto_Adapters_SQL["get_conn/1"](context.vars.pool_4),
				[
					{
						match: Interpreter.matchOperator(
							Type.variablePattern("conn_10"),
							Type.map([
								[Type.atom("__struct__"), Type.atom("Elixir.DBConnection")],
								[Type.atom("conn_mode"), Type.atom("transaction")],
							]),
							context,
						),
						guards: [],
						body: (context) => {
							return Elixir_Collectable["into/1"](
								Interpreter.callNamedFunction(
									context.vars.sql_6,
									Type.atom("stream"),
									Type.list([
										context.vars.conn_10,
										context.vars.statement_1,
										context.vars.params_2,
										context.vars.opts_9,
									]),
									context,
								),
							);
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Erlang["error/3"](
								Elixir_RuntimeError["exception/1"](
									Type.bitstring(
										"cannot collect into stream outside of transaction",
									),
								),
								Type.atom("none"),
								Type.list([
									Type.tuple([
										Type.atom("error_info"),
										Type.map([
											[Type.atom("module"), Type.atom("Elixir.Exception")],
										]),
									]),
								]),
							);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("Ecto.Adapters.SQL", "key", 1, "private", [
	{
		params: (context) => [Type.variablePattern("pool_0")],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("Elixir.Ecto.Adapters.SQL"),
				context.vars.pool_0,
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Ecto.Adapters.SQL", "log", 5, "private", [
	{
		params: (context) => [
			Type.tuple([
				Type.variablePattern("repo_0"),
				Type.variablePattern("log_1"),
				Type.variablePattern("event_name_2"),
			]),
			Type.variablePattern("log_stacktrace_mfa_3"),
			Type.variablePattern("params_4"),
			Type.variablePattern("entry_5"),
			Type.variablePattern("opts_6"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				context.vars.entry_5,
				Type.map([
					[Type.atom("connection_time"), Type.variablePattern("query_time_7")],
					[Type.atom("decode_time"), Type.variablePattern("decode_time_8")],
					[Type.atom("idle_time"), Type.variablePattern("idle_time_10")],
					[Type.atom("pool_time"), Type.variablePattern("queue_time_9")],
					[Type.atom("query"), Type.variablePattern("query_12")],
					[Type.atom("result"), Type.variablePattern("result_11")],
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_Keyword["get/2"](context.vars.opts_6, Type.atom("source")),
				Type.variablePattern("source_13"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_String_Chars["to_string/1"](context.vars.query_12),
				Type.variablePattern("query_14"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Interpreter.with(),
				Type.variablePattern("result_17"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_Keyword["get/2"](context.vars.opts_6, Type.atom("stacktrace")),
				Type.variablePattern("stacktrace_18"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Interpreter.case(
					Elixir_Access["get/2"](context.vars.opts_6, Type.atom("cast_params")),
					[
						{
							match: Type.variablePattern("x_19"),
							guards: [
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](context.vars.x_19, Type.atom("false")),
										(context) =>
											Erlang["=:=/2"](context.vars.x_19, Type.atom("nil")),
										context,
									),
							],
							body: (context) => {
								return context.vars.params_4;
							},
						},
						{
							match: Type.variablePattern("x_20"),
							guards: [],
							body: (context) => {
								return context.vars.x_20;
							},
						},
					],
					context,
				),
				Type.variablePattern("log_params_21"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Interpreter.case(
					context.vars.idle_time_10,
					[
						{
							match: Type.variablePattern("x_22"),
							guards: [
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](context.vars.x_22, Type.atom("false")),
										(context) =>
											Erlang["=:=/2"](context.vars.x_22, Type.atom("nil")),
										context,
									),
							],
							body: (context) => {
								return Type.list([]);
							},
						},
						{
							match: Type.matchPlaceholder(),
							guards: [],
							body: (context) => {
								return Type.list([
									Type.tuple([
										Type.atom("idle_time"),
										context.vars.idle_time_10,
									]),
								]);
							},
						},
					],
					context,
				),
				Type.variablePattern("acc_23"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_Ecto_Adapters_SQL["log_measurements/3"](
					Type.list([
						Type.tuple([Type.atom("query_time"), context.vars.query_time_7]),
						Type.tuple([Type.atom("decode_time"), context.vars.decode_time_8]),
						Type.tuple([Type.atom("queue_time"), context.vars.queue_time_9]),
					]),
					Type.integer(0n),
					context.vars.acc_23,
				),
				Type.variablePattern("measurements_24"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Type.map([
					[
						Type.atom("cast_params"),
						Elixir_Access["get/2"](
							context.vars.opts_6,
							Type.atom("cast_params"),
						),
					],
					[
						Type.atom("options"),
						Elixir_Keyword["get/3"](
							context.vars.opts_6,
							Type.atom("telemetry_options"),
							Type.list([]),
						),
					],
					[Type.atom("params"), context.vars.params_4],
					[Type.atom("query"), context.vars.query_14],
					[Type.atom("repo"), context.vars.repo_0],
					[Type.atom("result"), context.vars.result_17],
					[Type.atom("source"), context.vars.source_13],
					[Type.atom("stacktrace"), context.vars.stacktrace_18],
					[Type.atom("type"), Type.atom("ecto_sql_query")],
				]),
				Type.variablePattern("metadata_25"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.case(
				Interpreter.matchOperator(
					Elixir_Keyword["get/3"](
						context.vars.opts_6,
						Type.atom("telemetry_event"),
						context.vars.event_name_2,
					),
					Type.variablePattern("event_name_26"),
					context,
				),
				[
					{
						match: Type.variablePattern("x_27"),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["=:=/2"](context.vars.x_27, Type.atom("false")),
									(context) =>
										Erlang["=:=/2"](context.vars.x_27, Type.atom("nil")),
									context,
								),
						],
						body: (context) => {
							return Type.atom("nil");
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Erlang_Telemetry["execute/3"](
								context.vars.event_name_26,
								context.vars.measurements_24,
								context.vars.metadata_25,
							);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.case(
				Type.tuple([
					Elixir_Access["get/2"](context.vars.opts_6, Type.atom("log")),
					context.vars.log_1,
				]),
				[
					{
						match: Type.tuple([Type.atom("false"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("ok");
						},
					},
					{
						match: Type.tuple([
							Type.variablePattern("opts_level_29"),
							Type.atom("false"),
						]),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["=:=/2"](
											context.vars.opts_level_29,
											Type.atom("nil"),
										),
									(context) =>
										Erlang["=:=/2"](
											context.vars.opts_level_29,
											Type.atom("true"),
										),
									context,
								),
						],
						body: (context) => {
							return Type.atom("ok");
						},
					},
					{
						match: Type.tuple([
							Type.atom("true"),
							Type.variablePattern("level_30"),
						]),
						guards: [],
						body: (context) => {
							return Interpreter.case(
								Elixir_Logger["__should_log__/2"](
									context.vars.level_30,
									Type.atom("Elixir.Ecto.Adapters.SQL"),
								),
								[
									{
										match: Type.atom("nil"),
										guards: [],
										body: (context) => {
											return Type.atom("ok");
										},
									},
									{
										match: Type.variablePattern("level_31"),
										guards: [],
										body: (context) => {
											return Elixir_Logger["__do_log__/4"](
												context.vars.level_31,
												Type.anonymousFunction(
													0,
													[
														{
															params: (context) => [],
															guards: [],
															body: (context) => {
																return Elixir_Ecto_Adapters_SQL["log_iodata/8"](
																	context.vars.measurements_24,
																	context.vars.repo_0,
																	context.vars.source_13,
																	context.vars.query_14,
																	context.vars.log_params_21,
																	context.vars.result_17,
																	context.vars.stacktrace_18,
																	Interpreter.case(
																		Elixir_Access["get/2"](
																			context.vars.opts_6,
																			Type.atom("log_stacktrace_mfa"),
																		),
																		[
																			{
																				match: Type.variablePattern("x_32"),
																				guards: [
																					(context) =>
																						Erlang["orelse/2"](
																							(context) =>
																								Erlang["=:=/2"](
																									context.vars.x_32,
																									Type.atom("false"),
																								),
																							(context) =>
																								Erlang["=:=/2"](
																									context.vars.x_32,
																									Type.atom("nil"),
																								),
																							context,
																						),
																				],
																				body: (context) => {
																					return context.vars
																						.log_stacktrace_mfa_3;
																				},
																			},
																			{
																				match: Type.variablePattern("x_33"),
																				guards: [],
																				body: (context) => {
																					return context.vars.x_33;
																				},
																			},
																		],
																		context,
																	),
																);
															},
														},
													],
													context,
												),
												Type.map([
													[
														Type.atom("file"),
														Type.list([
															Type.integer(108n),
															Type.integer(105n),
															Type.integer(98n),
															Type.integer(47n),
															Type.integer(101n),
															Type.integer(99n),
															Type.integer(116n),
															Type.integer(111n),
															Type.integer(47n),
															Type.integer(97n),
															Type.integer(100n),
															Type.integer(97n),
															Type.integer(112n),
															Type.integer(116n),
															Type.integer(101n),
															Type.integer(114n),
															Type.integer(115n),
															Type.integer(47n),
															Type.integer(115n),
															Type.integer(113n),
															Type.integer(108n),
															Type.integer(46n),
															Type.integer(101n),
															Type.integer(120n),
														]),
													],
													[Type.atom("line"), Type.integer(1318n)],
													[
														Type.atom("mfa"),
														Type.tuple([
															Type.atom("Elixir.Ecto.Adapters.SQL"),
															Type.atom("log"),
															Type.integer(5n),
														]),
													],
												]),
												Type.map([
													[
														Type.atom("ansi_color"),
														Elixir_Ecto_Adapters_SQL["sql_color/1"](
															context.vars.query_14,
														),
													],
													[Type.atom("application"), Type.atom("ecto_sql")],
												]),
											);
										},
									},
								],
								context,
							);
						},
					},
					{
						match: Type.tuple([
							Type.variablePattern("opts_level_34"),
							Type.variablePattern("args_level_35"),
						]),
						guards: [],
						body: (context) => {
							return Interpreter.case(
								Elixir_Logger["__should_log__/2"](
									Interpreter.case(
										context.vars.opts_level_34,
										[
											{
												match: Type.variablePattern("x_36"),
												guards: [
													(context) =>
														Erlang["orelse/2"](
															(context) =>
																Erlang["=:=/2"](
																	context.vars.x_36,
																	Type.atom("false"),
																),
															(context) =>
																Erlang["=:=/2"](
																	context.vars.x_36,
																	Type.atom("nil"),
																),
															context,
														),
												],
												body: (context) => {
													return context.vars.args_level_35;
												},
											},
											{
												match: Type.variablePattern("x_37"),
												guards: [],
												body: (context) => {
													return context.vars.x_37;
												},
											},
										],
										context,
									),
									Type.atom("Elixir.Ecto.Adapters.SQL"),
								),
								[
									{
										match: Type.atom("nil"),
										guards: [],
										body: (context) => {
											return Type.atom("ok");
										},
									},
									{
										match: Type.variablePattern("level_38"),
										guards: [],
										body: (context) => {
											return Elixir_Logger["__do_log__/4"](
												context.vars.level_38,
												Type.anonymousFunction(
													0,
													[
														{
															params: (context) => [],
															guards: [],
															body: (context) => {
																return Elixir_Ecto_Adapters_SQL["log_iodata/8"](
																	context.vars.measurements_24,
																	context.vars.repo_0,
																	context.vars.source_13,
																	context.vars.query_14,
																	context.vars.log_params_21,
																	context.vars.result_17,
																	context.vars.stacktrace_18,
																	Interpreter.case(
																		Elixir_Access["get/2"](
																			context.vars.opts_6,
																			Type.atom("log_stacktrace_mfa"),
																		),
																		[
																			{
																				match: Type.variablePattern("x_39"),
																				guards: [
																					(context) =>
																						Erlang["orelse/2"](
																							(context) =>
																								Erlang["=:=/2"](
																									context.vars.x_39,
																									Type.atom("false"),
																								),
																							(context) =>
																								Erlang["=:=/2"](
																									context.vars.x_39,
																									Type.atom("nil"),
																								),
																							context,
																						),
																				],
																				body: (context) => {
																					return context.vars
																						.log_stacktrace_mfa_3;
																				},
																			},
																			{
																				match: Type.variablePattern("x_40"),
																				guards: [],
																				body: (context) => {
																					return context.vars.x_40;
																				},
																			},
																		],
																		context,
																	),
																);
															},
														},
													],
													context,
												),
												Type.map([
													[
														Type.atom("file"),
														Type.list([
															Type.integer(108n),
															Type.integer(105n),
															Type.integer(98n),
															Type.integer(47n),
															Type.integer(101n),
															Type.integer(99n),
															Type.integer(116n),
															Type.integer(111n),
															Type.integer(47n),
															Type.integer(97n),
															Type.integer(100n),
															Type.integer(97n),
															Type.integer(112n),
															Type.integer(116n),
															Type.integer(101n),
															Type.integer(114n),
															Type.integer(115n),
															Type.integer(47n),
															Type.integer(115n),
															Type.integer(113n),
															Type.integer(108n),
															Type.integer(46n),
															Type.integer(101n),
															Type.integer(120n),
														]),
													],
													[Type.atom("line"), Type.integer(1336n)],
													[
														Type.atom("mfa"),
														Type.tuple([
															Type.atom("Elixir.Ecto.Adapters.SQL"),
															Type.atom("log"),
															Type.integer(5n),
														]),
													],
												]),
												Type.map([
													[
														Type.atom("ansi_color"),
														Elixir_Ecto_Adapters_SQL["sql_color/1"](
															context.vars.query_14,
														),
													],
													[Type.atom("application"), Type.atom("ecto_sql")],
												]),
											);
										},
									},
								],
								context,
							);
						},
					},
				],
				context,
			);
			return Type.atom("ok");
		},
	},
]);

Interpreter.defineElixirFunction(
	"Ecto.Adapters.SQL",
	"log_iodata",
	8,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("measurements_0"),
				Type.variablePattern("repo_1"),
				Type.variablePattern("source_2"),
				Type.variablePattern("query_3"),
				Type.variablePattern("params_4"),
				Type.variablePattern("result_5"),
				Type.variablePattern("stacktrace_6"),
				Type.variablePattern("stacktrace_mfa_7"),
			],
			guards: [],
			body: (context) => {
				return Type.list([
					Type.bitstring("QUERY"),
					Type.integer(32n),
					Elixir_Ecto_Adapters_SQL["log_ok_error/1"](context.vars.result_5),
					Elixir_Ecto_Adapters_SQL["log_ok_source/1"](context.vars.source_2),
					Elixir_Ecto_Adapters_SQL["log_time/4"](
						Type.bitstring("db"),
						context.vars.measurements_0,
						Type.atom("query_time"),
						Type.atom("true"),
					),
					Elixir_Ecto_Adapters_SQL["log_time/4"](
						Type.bitstring("decode"),
						context.vars.measurements_0,
						Type.atom("decode_time"),
						Type.atom("false"),
					),
					Elixir_Ecto_Adapters_SQL["log_time/4"](
						Type.bitstring("queue"),
						context.vars.measurements_0,
						Type.atom("queue_time"),
						Type.atom("false"),
					),
					Elixir_Ecto_Adapters_SQL["log_time/4"](
						Type.bitstring("idle"),
						context.vars.measurements_0,
						Type.atom("idle_time"),
						Type.atom("true"),
					),
					Type.integer(10n),
					context.vars.query_3,
					Type.integer(32n),
					Elixir_Kernel["inspect/2"](
						context.vars.params_4,
						Type.list([
							Type.tuple([Type.atom("charlists"), Type.atom("false")]),
						]),
					),
					Elixir_Ecto_Adapters_SQL["log_stacktrace/3"](
						context.vars.stacktrace_6,
						context.vars.repo_1,
						context.vars.stacktrace_mfa_7,
					),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Ecto.Adapters.SQL",
	"log_measurements",
	3,
	"private",
	[
		{
			params: (context) => [
				Type.consPattern(
					Type.tuple([Type.matchPlaceholder(), Type.atom("nil")]),
					Type.variablePattern("rest_0"),
				),
				Type.variablePattern("total_1"),
				Type.variablePattern("acc_2"),
			],
			guards: [],
			body: (context) => {
				return Elixir_Ecto_Adapters_SQL["log_measurements/3"](
					context.vars.rest_0,
					context.vars.total_1,
					context.vars.acc_2,
				);
			},
		},
		{
			params: (context) => [
				Type.consPattern(
					Type.tuple([
						Type.variablePattern("key_0"),
						Type.variablePattern("value_1"),
					]),
					Type.variablePattern("rest_2"),
				),
				Type.variablePattern("total_3"),
				Type.variablePattern("acc_4"),
			],
			guards: [],
			body: (context) => {
				return Elixir_Ecto_Adapters_SQL["log_measurements/3"](
					context.vars.rest_2,
					Erlang["+/2"](context.vars.total_3, context.vars.value_1),
					Interpreter.consOperator(
						Type.tuple([context.vars.key_0, context.vars.value_1]),
						context.vars.acc_4,
					),
				);
			},
		},
		{
			params: (context) => [
				Type.list([]),
				Type.variablePattern("total_0"),
				Type.variablePattern("acc_1"),
			],
			guards: [],
			body: (context) => {
				return Elixir_Map["new/1"](
					Erlang["++/2"](
						Type.list([
							Type.tuple([Type.atom("total_time"), context.vars.total_0]),
						]),
						context.vars.acc_1,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Ecto.Adapters.SQL",
	"log_ok_error",
	1,
	"private",
	[
		{
			params: (context) => [
				Type.tuple([Type.atom("ok"), Type.matchPlaceholder()]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring("OK");
			},
		},
		{
			params: (context) => [
				Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring("ERROR");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Ecto.Adapters.SQL",
	"log_ok_source",
	1,
	"private",
	[
		{
			params: (context) => [Type.atom("nil")],
			guards: [],
			body: (context) => {
				return Type.bitstring("");
			},
		},
		{
			params: (context) => [Type.variablePattern("source_0")],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(Type.string(" source="), { type: "binary" }),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.source_0),
						{ type: "binary" },
					),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Ecto.Adapters.SQL",
	"log_stacktrace",
	3,
	"private",
	[
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("stacktrace_0"),
					Type.consPattern(Type.matchPlaceholder(), Type.matchPlaceholder()),
					context,
				),
				Type.variablePattern("repo_1"),
				Type.tuple([
					Type.variablePattern("module_2"),
					Type.variablePattern("function_3"),
					Type.variablePattern("args_4"),
				]),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.callNamedFunction(
						context.vars.module_2,
						context.vars.function_3,
						Interpreter.consOperator(
							context.vars.stacktrace_0,
							Interpreter.consOperator(
								Type.map([[Type.atom("repo"), context.vars.repo_1]]),
								context.vars.args_4,
							),
						),
						context,
					),
					Type.variablePattern("entries_5"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Enum["with_index/2"](
					context.vars.entries_5,
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("module_6"),
										Type.variablePattern("function_7"),
										Type.variablePattern("arity_8"),
										Type.variablePattern("info_9"),
									]),
									Type.variablePattern("idx_10"),
								],
								guards: [],
								body: (context) => {
									return Type.list([
										Type.integer(10n),
										Elixir_IO_ANSI["light_black/0"](),
										Interpreter.case(
											Erlang["==/2"](context.vars.idx_10, Type.integer(0n)),
											[
												{
													match: Type.atom("false"),
													guards: [],
													body: (context) => {
														return Type.bitstring("  ");
													},
												},
												{
													match: Type.atom("true"),
													guards: [],
													body: (context) => {
														return Type.bitstring("↳ ");
													},
												},
											],
											context,
										),
										Elixir_Exception["format_mfa/3"](
											context.vars.module_6,
											context.vars.function_7,
											context.vars.arity_8,
										),
										Elixir_Ecto_Adapters_SQL["log_stacktrace_info/1"](
											context.vars.info_9,
										),
										Elixir_IO_ANSI["reset/0"](),
									]);
								},
							},
						],
						context,
					),
				);
			},
		},
		{
			params: (context) => [
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				return Type.list([]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Ecto.Adapters.SQL",
	"log_stacktrace_info",
	1,
	"private",
	[
		{
			params: (context) => [
				Type.consPattern(
					Type.tuple([Type.atom("file"), Type.variablePattern("file_0")]),
					Type.consPattern(
						Type.tuple([Type.atom("line"), Type.variablePattern("line_1")]),
						Type.matchPlaceholder(),
					),
				),
			],
			guards: [],
			body: (context) => {
				return Type.list([
					Type.bitstring(", at: "),
					context.vars.file_0,
					Type.integer(58n),
					Erlang["integer_to_binary/1"](context.vars.line_1),
				]);
			},
		},
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.list([]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Ecto.Adapters.SQL",
	"log_time",
	4,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("label_0"),
				Type.variablePattern("measurements_1"),
				Type.variablePattern("key_2"),
				Type.variablePattern("force_3"),
			],
			guards: [],
			body: (context) => {
				globalThis.Hologram.return = Interpreter.case(
					context.vars.measurements_1,
					[
						{
							match: Type.map([
								[context.vars.key_2, Type.variablePattern("time_4")],
							]),
							guards: [],
							body: (context) => {
								Interpreter.matchOperator(
									Elixir_System["convert_time_unit/3"](
										context.vars.time_4,
										Type.atom("native"),
										Type.atom("microsecond"),
									),
									Type.variablePattern("us_5"),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								Interpreter.matchOperator(
									Erlang["//2"](
										Erlang["div/2"](context.vars.us_5, Type.integer(100n)),
										Type.integer(10n),
									),
									Type.variablePattern("ms_6"),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								return Interpreter.case(
									Interpreter.case(
										context.vars.force_3,
										[
											{
												match: Type.atom("false"),
												guards: [],
												body: (context) => {
													return Erlang[">/2"](
														context.vars.ms_6,
														Type.integer(0n),
													);
												},
											},
											{
												match: Type.atom("true"),
												guards: [],
												body: (context) => {
													return Type.atom("true");
												},
											},
											{
												match: Type.variablePattern("other_7"),
												guards: [],
												body: (context) => {
													return Erlang["error/1"](
														Type.tuple([
															Type.atom("badbool"),
															Type.atom("or"),
															context.vars.other_7,
														]),
													);
												},
											},
										],
										context,
									),
									[
										{
											match: Type.variablePattern("x_8"),
											guards: [
												(context) =>
													Erlang["orelse/2"](
														(context) =>
															Erlang["=:=/2"](
																context.vars.x_8,
																Type.atom("false"),
															),
														(context) =>
															Erlang["=:=/2"](
																context.vars.x_8,
																Type.atom("nil"),
															),
														context,
													),
											],
											body: (context) => {
												return Type.list([]);
											},
										},
										{
											match: Type.matchPlaceholder(),
											guards: [],
											body: (context) => {
												return Type.list([
													Type.integer(32n),
													context.vars.label_0,
													Type.integer(61n),
													Erlang_Io_Lib_Format["fwrite_g/1"](context.vars.ms_6),
													Type.integer(109n),
													Type.integer(115n),
												]);
											},
										},
									],
									context,
								);
							},
						},
						{
							match: Type.map([]),
							guards: [],
							body: (context) => {
								return Type.list([]);
							},
						},
					],
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return globalThis.Hologram.return;
			},
		},
	],
);

Interpreter.defineElixirFunction("Ecto.Adapters.SQL", "reduce", 6, "public", [
	{
		params: (context) => [
			Type.variablePattern("adapter_meta_0"),
			Type.variablePattern("statement_1"),
			Type.variablePattern("params_2"),
			Type.variablePattern("opts_3"),
			Type.variablePattern("acc_4"),
			Type.variablePattern("fun_5"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				context.vars.adapter_meta_0,
				Type.map([
					[
						Type.atom("log_stacktrace_mfa"),
						Type.variablePattern("log_stacktrace_mfa_9"),
					],
					[Type.atom("opts"), Type.variablePattern("default_opts_10")],
					[Type.atom("pid"), Type.variablePattern("pool_6")],
					[Type.atom("sql"), Type.variablePattern("sql_8")],
					[Type.atom("telemetry"), Type.variablePattern("telemetry_7")],
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_Ecto_Adapters_SQL["with_log/4"](
					context.vars.telemetry_7,
					context.vars.log_stacktrace_mfa_9,
					context.vars.params_2,
					Erlang["++/2"](context.vars.opts_3, context.vars.default_opts_10),
				),
				Type.variablePattern("opts_11"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			globalThis.Hologram.return = Interpreter.case(
				Elixir_Ecto_Adapters_SQL["get_conn/1"](context.vars.pool_6),
				[
					{
						match: Interpreter.matchOperator(
							Type.variablePattern("conn_12"),
							Type.map([
								[Type.atom("__struct__"), Type.atom("Elixir.DBConnection")],
								[Type.atom("conn_mode"), Type.atom("transaction")],
							]),
							context,
						),
						guards: [],
						body: (context) => {
							return Elixir_Enumerable["reduce/3"](
								Interpreter.callNamedFunction(
									context.vars.sql_8,
									Type.atom("stream"),
									Type.list([
										context.vars.conn_12,
										context.vars.statement_1,
										context.vars.params_2,
										context.vars.opts_11,
									]),
									context,
								),
								context.vars.acc_4,
								context.vars.fun_5,
							);
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Erlang["error/3"](
								Elixir_RuntimeError["exception/1"](
									Type.bitstring("cannot reduce stream outside of transaction"),
								),
								Type.atom("none"),
								Type.list([
									Type.tuple([
										Type.atom("error_info"),
										Type.map([
											[Type.atom("module"), Type.atom("Elixir.Exception")],
										]),
									]),
								]),
							);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction(
	"Ecto.Adapters.SQL",
	"sql_color",
	1,
	"private",
	[
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.string("SELECT"), { type: "binary" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.atom("cyan");
			},
		},
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.string("ROLLBACK"), { type: "binary" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.atom("red");
			},
		},
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.string("LOCK"), { type: "binary" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.atom("white");
			},
		},
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.string("INSERT"), { type: "binary" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.atom("green");
			},
		},
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.string("UPDATE"), { type: "binary" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.atom("yellow");
			},
		},
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.string("DELETE"), { type: "binary" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.atom("red");
			},
		},
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.string("begin"), { type: "binary" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.atom("magenta");
			},
		},
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.string("commit"), { type: "binary" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.atom("magenta");
			},
		},
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.atom("nil");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Ecto.Adapters.SQL",
	"with_log",
	4,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("telemetry_0"),
				Type.variablePattern("log_stacktrace_mfa_1"),
				Type.variablePattern("params_2"),
				Type.variablePattern("opts_3"),
			],
			guards: [],
			body: (context) => {
				return Erlang["++/2"](
					Type.list([
						Type.tuple([
							Type.atom("log"),
							Type.anonymousFunction(
								1,
								[
									{
										params: (context) => [Type.variablePattern("$262")],
										guards: [],
										body: (context) => {
											return Elixir_Ecto_Adapters_SQL["log/5"](
												context.vars.telemetry_0,
												context.vars.log_stacktrace_mfa_1,
												context.vars.params_2,
												context.vars["$262"],
												context.vars.opts_3,
											);
										},
									},
								],
								context,
							),
						]),
					]),
					context.vars.opts_3,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Ecto.Adapters.SQL.Stream",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.Ecto.Adapters.SQL.Stream"),
					],
					[Type.atom("meta"), Type.atom("nil")],
					[Type.atom("opts"), Type.atom("nil")],
					[Type.atom("params"), Type.atom("nil")],
					[Type.atom("statement"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Ecto.Adapters.SQL.Stream",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.Ecto.Adapters.SQL.Stream"),
						],
						[Type.atom("meta"), Type.atom("nil")],
						[Type.atom("opts"), Type.atom("nil")],
						[Type.atom("params"), Type.atom("nil")],
						[Type.atom("statement"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Enum", "drop", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("amount_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) =>
						Erlang["andalso/2"](
							(context) => Erlang["is_list/1"](context.vars.enumerable_0),
							(context) => Erlang["is_integer/1"](context.vars.amount_1),
							context,
						),
					(context) => Erlang[">=/2"](context.vars.amount_1, Type.integer(0n)),
					context,
				),
		],
		body: (context) => {
			return Elixir_Enum["drop_list/2"](
				context.vars.enumerable_0,
				context.vars.amount_1,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.integer(0n),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["to_list/1"](context.vars.enumerable_0);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("amount_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_integer/1"](context.vars.amount_1),
					(context) => Erlang[">/2"](context.vars.amount_1, Type.integer(0n)),
					context,
				),
		],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Enum["reduce/3"](
					context.vars.enumerable_0,
					Type.tuple([Type.list([]), context.vars.amount_1]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.matchPlaceholder(),
									Type.tuple([
										Type.variablePattern("head_3"),
										Type.variablePattern("amount_4"),
									]),
								],
								guards: [
									(context) =>
										Erlang[">/2"](context.vars.amount_4, Type.integer(0n)),
								],
								body: (context) => {
									return Type.tuple([
										context.vars.head_3,
										Erlang["-/2"](context.vars.amount_4, Type.integer(1n)),
									]);
								},
							},
							{
								params: (context) => [
									Type.variablePattern("entry_5"),
									Type.tuple([
										Type.variablePattern("head_6"),
										Type.variablePattern("amount_7"),
									]),
								],
								guards: [],
								body: (context) => {
									return Type.tuple([
										Interpreter.consOperator(
											context.vars.entry_5,
											context.vars.head_6,
										),
										context.vars.amount_7,
									]);
								},
							},
						],
						context,
					),
				),
				Type.tuple([Type.variablePattern("result_8"), Type.matchPlaceholder()]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				Erlang["is_list/1"](context.vars.result_8),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Type.list([]);
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Erlang_Lists["reverse/1"](context.vars.result_8);
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("amount_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_integer/1"](context.vars.amount_1),
					(context) => Erlang["</2"](context.vars.amount_1, Type.integer(0n)),
					context,
				),
		],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Enum["slice_count_and_fun/2"](
					context.vars.enumerable_0,
					Type.integer(1n),
				),
				Type.tuple([
					Type.variablePattern("count_2"),
					Type.variablePattern("fun_3"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Erlang["min/2"](
					Erlang["+/2"](context.vars.amount_1, context.vars.count_2),
					context.vars.count_2,
				),
				Type.variablePattern("amount_4"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				Erlang[">/2"](context.vars.amount_4, Type.integer(0n)),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Type.list([]);
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Interpreter.callAnonymousFunction(context.vars.fun_3, [
								Type.integer(0n),
								context.vars.amount_4,
								Type.integer(1n),
							]);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "drop_exact", 2, "private", [
	{
		params: (context) => [Type.variablePattern("list_0"), Type.integer(0n)],
		guards: [],
		body: (context) => {
			return context.vars.list_0;
		},
	},
	{
		params: (context) => [
			Type.consPattern(Type.matchPlaceholder(), Type.variablePattern("tail_0")),
			Type.variablePattern("amount_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["drop_exact/2"](
				context.vars.tail_0,
				Erlang["-/2"](context.vars.amount_1, Type.integer(1n)),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "drop_list", 2, "private", [
	{
		params: (context) => [Type.variablePattern("list_0"), Type.integer(0n)],
		guards: [],
		body: (context) => {
			return context.vars.list_0;
		},
	},
	{
		params: (context) => [
			Type.consPattern(Type.matchPlaceholder(), Type.variablePattern("tail_0")),
			Type.variablePattern("counter_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["drop_list/2"](
				context.vars.tail_0,
				Erlang["-/2"](context.vars.counter_1, Type.integer(1n)),
			);
		},
	},
	{
		params: (context) => [Type.list([]), Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "entry_to_string", 1, "private", [
	{
		params: (context) => [Type.variablePattern("entry_0")],
		guards: [(context) => Erlang["is_binary/1"](context.vars.entry_0)],
		body: (context) => {
			return context.vars.entry_0;
		},
	},
	{
		params: (context) => [Type.variablePattern("entry_0")],
		guards: [],
		body: (context) => {
			return Elixir_String_Chars["to_string/1"](context.vars.entry_0);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "into", 2, "public", [
	{
		params: (context) => [Type.variablePattern("enumerable_0"), Type.list([])],
		guards: [],
		body: (context) => {
			return Elixir_Enum["to_list/1"](context.vars.enumerable_0);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("enumerable_0"),
				Type.map([[Type.atom("__struct__"), Type.matchPlaceholder()]]),
				context,
			),
			Type.variablePattern("collectable_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["into_protocol/2"](
				context.vars.enumerable_0,
				context.vars.collectable_1,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Interpreter.matchOperator(
				Type.variablePattern("collectable_1"),
				Type.map([[Type.atom("__struct__"), Type.matchPlaceholder()]]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["into_protocol/2"](
				context.vars.enumerable_0,
				context.vars.collectable_1,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Interpreter.matchOperator(
				Type.variablePattern("collectable_1"),
				Type.map([]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Erlang["==/2"](
					Erlang["map_size/1"](context.vars.collectable_1),
					Type.integer(0n),
				),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Elixir_Enum["into_map/2"](
								context.vars.enumerable_0,
								context.vars.collectable_1,
							);
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Elixir_Enum["into_map/1"](context.vars.enumerable_0);
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("collectable_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["into_protocol/2"](
				context.vars.enumerable_0,
				context.vars.collectable_1,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "into_map", 1, "private", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("enumerable_0"),
				Type.map([]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return context.vars.enumerable_0;
		},
	},
	{
		params: (context) => [Type.variablePattern("enumerable_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)],
		body: (context) => {
			return Erlang_Maps["from_list/1"](context.vars.enumerable_0);
		},
	},
	{
		params: (context) => [Type.variablePattern("enumerable_0")],
		guards: [],
		body: (context) => {
			return Erlang_Maps["from_list/1"](
				Elixir_Enum["to_list/1"](context.vars.enumerable_0),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "into_map", 2, "private", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("enumerable_0"),
				Type.map([]),
				context,
			),
			Type.variablePattern("collectable_1"),
		],
		guards: [],
		body: (context) => {
			return Erlang_Maps["merge/2"](
				context.vars.collectable_1,
				context.vars.enumerable_0,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("collectable_1"),
		],
		guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)],
		body: (context) => {
			return Erlang_Maps["merge/2"](
				context.vars.collectable_1,
				Erlang_Maps["from_list/1"](context.vars.enumerable_0),
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("collectable_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.enumerable_0,
				context.vars.collectable_1,
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_2"),
									Type.variablePattern("val_3"),
								]),
								Type.variablePattern("acc_4"),
							],
							guards: [],
							body: (context) => {
								return Erlang_Maps["put/3"](
									context.vars.key_2,
									context.vars.val_3,
									context.vars.acc_4,
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "into_protocol", 2, "private", [
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("collectable_1"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Collectable["into/1"](context.vars.collectable_1),
				Type.tuple([
					Type.variablePattern("initial_2"),
					Type.variablePattern("fun_3"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.try(
				(context) => {
					return Elixir_Enum["reduce_into_protocol/3"](
						context.vars.enumerable_0,
						context.vars.initial_2,
						context.vars.fun_3,
					);
				},
				[],
				[],
				[],
				null,
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "join", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.bitstring(""),
		],
		guards: [],
		body: (context) => {
			return Erlang["iolist_to_binary/1"](
				Elixir_Enum["map/2"](
					context.vars.enumerable_0,
					Type.functionCapture(
						"Enum",
						"entry_to_string",
						1,
						[
							{
								params: (context) => [Type.variablePattern("$1")],
								guards: [],
								body: (context) => {
									return Elixir_Enum["entry_to_string/1"](context.vars["$1"]);
								},
							},
						],
						context,
					),
				),
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("joiner_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_list/1"](context.vars.enumerable_0),
					(context) => Erlang["is_binary/1"](context.vars.joiner_1),
					context,
				),
		],
		body: (context) => {
			return Elixir_Enum["join_list/2"](
				context.vars.enumerable_0,
				context.vars.joiner_1,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("joiner_1"),
		],
		guards: [(context) => Erlang["is_binary/1"](context.vars.joiner_1)],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Enum["reduce/3"](
					context.vars.enumerable_0,
					Type.atom("first"),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.variablePattern("entry_2"),
									Type.atom("first"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Enum["entry_to_string/1"](context.vars.entry_2);
								},
							},
							{
								params: (context) => [
									Type.variablePattern("entry_3"),
									Type.variablePattern("acc_4"),
								],
								guards: [],
								body: (context) => {
									return Interpreter.consOperator(
										context.vars.acc_4,
										Interpreter.consOperator(
											context.vars.joiner_1,
											Elixir_Enum["entry_to_string/1"](context.vars.entry_3),
										),
									);
								},
							},
						],
						context,
					),
				),
				Type.variablePattern("reduced_5"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				Erlang["==/2"](context.vars.reduced_5, Type.atom("first")),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Erlang["iolist_to_binary/1"](context.vars.reduced_5);
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Type.bitstring("");
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "join_list", 2, "private", [
	{
		params: (context) => [Type.list([]), Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.bitstring("");
		},
	},
	{
		params: (context) => [
			Type.variablePattern("list_0"),
			Type.variablePattern("joiner_1"),
		],
		guards: [],
		body: (context) => {
			return Erlang["iolist_to_binary/1"](
				Erlang_Lists["reverse/1"](
					Elixir_Enum["join_non_empty_list/3"](
						context.vars.list_0,
						context.vars.joiner_1,
						Type.list([]),
					),
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "join_non_empty_list", 3, "private", [
	{
		params: (context) => [
			Type.list([Type.variablePattern("first_0")]),
			Type.matchPlaceholder(),
			Type.variablePattern("acc_2"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.consOperator(
				Elixir_Enum["entry_to_string/1"](context.vars.first_0),
				context.vars.acc_2,
			);
		},
	},
	{
		params: (context) => [
			Type.consPattern(
				Type.variablePattern("first_0"),
				Type.variablePattern("rest_1"),
			),
			Type.variablePattern("joiner_2"),
			Type.variablePattern("acc_3"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["join_non_empty_list/3"](
				context.vars.rest_1,
				context.vars.joiner_2,
				Interpreter.consOperator(
					context.vars.joiner_2,
					Interpreter.consOperator(
						Elixir_Enum["entry_to_string/1"](context.vars.first_0),
						context.vars.acc_3,
					),
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "map", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("fun_1"),
		],
		guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)],
		body: (context) => {
			return Erlang_Lists["map/2"](
				context.vars.fun_1,
				context.vars.enumerable_0,
			);
		},
	},
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Range")],
				[Type.atom("first"), Type.variablePattern("first_0")],
				[Type.atom("last"), Type.variablePattern("last_1")],
				[Type.atom("step"), Type.variablePattern("step_2")],
			]),
			Type.variablePattern("fun_3"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["map_range/4"](
				context.vars.first_0,
				context.vars.last_1,
				context.vars.step_2,
				context.vars.fun_3,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("fun_1"),
		],
		guards: [],
		body: (context) => {
			return Erlang_Lists["reverse/1"](
				Elixir_Enum["reduce/3"](
					context.vars.enumerable_0,
					Type.list([]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.variablePattern("entry_2"),
									Type.variablePattern("acc_3"),
								],
								guards: [],
								body: (context) => {
									return Interpreter.consOperator(
										Interpreter.callAnonymousFunction(context.vars.fun_1, [
											context.vars.entry_2,
										]),
										context.vars.acc_3,
									);
								},
							},
						],
						context,
					),
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "map_intersperse", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("separator_1"),
			Type.variablePattern("mapper_2"),
		],
		guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)],
		body: (context) => {
			return Elixir_Enum["map_intersperse_list/3"](
				context.vars.enumerable_0,
				context.vars.separator_1,
				context.vars.mapper_2,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("separator_1"),
			Type.variablePattern("mapper_2"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Enum["reduce/3"](
					context.vars.enumerable_0,
					Type.atom("first"),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.variablePattern("entry_3"),
									Type.atom("first"),
								],
								guards: [],
								body: (context) => {
									return Type.list([
										Interpreter.callAnonymousFunction(context.vars.mapper_2, [
											context.vars.entry_3,
										]),
									]);
								},
							},
							{
								params: (context) => [
									Type.variablePattern("entry_4"),
									Type.variablePattern("acc_5"),
								],
								guards: [],
								body: (context) => {
									return Interpreter.consOperator(
										Interpreter.callAnonymousFunction(context.vars.mapper_2, [
											context.vars.entry_4,
										]),
										Interpreter.consOperator(
											context.vars.separator_1,
											context.vars.acc_5,
										),
									);
								},
							},
						],
						context,
					),
				),
				Type.variablePattern("reduced_6"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				Erlang["==/2"](context.vars.reduced_6, Type.atom("first")),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Erlang_Lists["reverse/1"](context.vars.reduced_6);
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Type.list([]);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "map_intersperse_list", 3, "private", [
	{
		params: (context) => [
			Type.list([]),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
	{
		params: (context) => [
			Type.list([Type.variablePattern("last_0")]),
			Type.matchPlaceholder(),
			Type.variablePattern("mapper_1"),
		],
		guards: [],
		body: (context) => {
			return Type.list([
				Interpreter.callAnonymousFunction(context.vars.mapper_1, [
					context.vars.last_0,
				]),
			]);
		},
	},
	{
		params: (context) => [
			Type.consPattern(
				Type.variablePattern("head_0"),
				Type.variablePattern("rest_1"),
			),
			Type.variablePattern("separator_2"),
			Type.variablePattern("mapper_3"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.consOperator(
				Interpreter.callAnonymousFunction(context.vars.mapper_3, [
					context.vars.head_0,
				]),
				Interpreter.consOperator(
					context.vars.separator_2,
					Elixir_Enum["map_intersperse_list/3"](
						context.vars.rest_1,
						context.vars.separator_2,
						context.vars.mapper_3,
					),
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "map_join", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("joiner_1"),
			Type.variablePattern("mapper_2"),
		],
		guards: [(context) => Erlang["is_binary/1"](context.vars.joiner_1)],
		body: (context) => {
			return Erlang["iolist_to_binary/1"](
				Elixir_Enum["map_intersperse/3"](
					context.vars.enumerable_0,
					context.vars.joiner_1,
					Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("$797")],
								guards: [],
								body: (context) => {
									return Elixir_Enum["entry_to_string/1"](
										Interpreter.callAnonymousFunction(context.vars.mapper_2, [
											context.vars["$797"],
										]),
									);
								},
							},
						],
						context,
					),
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "map_range", 4, "private", [
	{
		params: (context) => [
			Type.variablePattern("first_0"),
			Type.variablePattern("last_1"),
			Type.variablePattern("step_2"),
			Type.variablePattern("fun_3"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang[">/2"](context.vars.step_2, Type.integer(0n)),
					(context) =>
						Erlang["=</2"](context.vars.first_0, context.vars.last_1),
					context,
				),
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["</2"](context.vars.step_2, Type.integer(0n)),
					(context) =>
						Erlang[">=/2"](context.vars.first_0, context.vars.last_1),
					context,
				),
		],
		body: (context) => {
			return Interpreter.consOperator(
				Interpreter.callAnonymousFunction(context.vars.fun_3, [
					context.vars.first_0,
				]),
				Elixir_Enum["map_range/4"](
					Erlang["+/2"](context.vars.first_0, context.vars.step_2),
					context.vars.last_1,
					context.vars.step_2,
					context.vars.fun_3,
				),
			);
		},
	},
	{
		params: (context) => [
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "map_reduce", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)],
		body: (context) => {
			return Erlang_Lists["mapfoldl/3"](
				context.vars.fun_2,
				context.vars.acc_1,
				context.vars.enumerable_0,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Enum["reduce/3"](
					context.vars.enumerable_0,
					Type.tuple([Type.list([]), context.vars.acc_1]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.variablePattern("entry_3"),
									Type.tuple([
										Type.variablePattern("list_4"),
										Type.variablePattern("acc_5"),
									]),
								],
								guards: [],
								body: (context) => {
									Interpreter.matchOperator(
										Interpreter.callAnonymousFunction(context.vars.fun_2, [
											context.vars.entry_3,
											context.vars.acc_5,
										]),
										Type.tuple([
											Type.variablePattern("new_entry_6"),
											Type.variablePattern("acc_7"),
										]),
										context,
									);
									Interpreter.updateVarsToMatchedValues(context);
									return Type.tuple([
										Interpreter.consOperator(
											context.vars.new_entry_6,
											context.vars.list_4,
										),
										context.vars.acc_7,
									]);
								},
							},
						],
						context,
					),
				),
				Type.tuple([
					Type.variablePattern("list_8"),
					Type.variablePattern("acc_9"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([
				Erlang_Lists["reverse/1"](context.vars.list_8),
				context.vars.acc_9,
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "member?", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("element_1"),
		],
		guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)],
		body: (context) => {
			return Erlang_Lists["member/2"](
				context.vars.element_1,
				context.vars.enumerable_0,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("element_1"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Elixir_Enumerable["member?/2"](
					context.vars.enumerable_0,
					context.vars.element_1,
				),
				[
					{
						match: Type.tuple([
							Type.atom("ok"),
							Type.variablePattern("element_2"),
						]),
						guards: [
							(context) => Erlang["is_boolean/1"](context.vars.element_2),
						],
						body: (context) => {
							return context.vars.element_2;
						},
					},
					{
						match: Type.tuple([
							Type.atom("error"),
							Type.variablePattern("module_3"),
						]),
						guards: [],
						body: (context) => {
							return Erlang["element/2"](
								Type.integer(2n),
								Interpreter.callNamedFunction(
									context.vars.module_3,
									Type.atom("reduce"),
									Type.list([
										context.vars.enumerable_0,
										Type.tuple([Type.atom("cont"), Type.atom("false")]),
										Type.anonymousFunction(
											2,
											[
												{
													params: (context) => [
														Type.variablePattern("v_4"),
														Type.matchPlaceholder(),
													],
													guards: [
														(context) =>
															Erlang["=:=/2"](
																context.vars.v_4,
																context.vars.element_1,
															),
													],
													body: (context) => {
														return Type.tuple([
															Type.atom("halt"),
															Type.atom("true"),
														]);
													},
												},
												{
													params: (context) => [
														Type.matchPlaceholder(),
														Type.matchPlaceholder(),
													],
													guards: [],
													body: (context) => {
														return Type.tuple([
															Type.atom("cont"),
															Type.atom("false"),
														]);
													},
												},
											],
											context,
										),
									]),
									context,
								),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "reduce", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)],
		body: (context) => {
			return Erlang_Lists["foldl/3"](
				context.vars.fun_2,
				context.vars.acc_1,
				context.vars.enumerable_0,
			);
		},
	},
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Range")],
				[Type.atom("first"), Type.variablePattern("first_0")],
				[Type.atom("last"), Type.variablePattern("last_1")],
				[Type.atom("step"), Type.variablePattern("step_2")],
			]),
			Type.variablePattern("acc_3"),
			Type.variablePattern("fun_4"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce_range/5"](
				context.vars.first_0,
				context.vars.last_1,
				context.vars.step_2,
				context.vars.acc_3,
				context.vars.fun_4,
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("enumerable_0"),
				Type.map([[Type.atom("__struct__"), Type.matchPlaceholder()]]),
				context,
			),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce_enumerable/3"](
				context.vars.enumerable_0,
				context.vars.acc_1,
				context.vars.fun_2,
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("enumerable_0"),
				Type.map([]),
				context,
			),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			return Erlang_Maps["fold/3"](
				Type.anonymousFunction(
					3,
					[
						{
							params: (context) => [
								Type.variablePattern("k_3"),
								Type.variablePattern("v_4"),
								Type.variablePattern("acc_5"),
							],
							guards: [],
							body: (context) => {
								return Interpreter.callAnonymousFunction(context.vars.fun_2, [
									Type.tuple([context.vars.k_3, context.vars.v_4]),
									context.vars.acc_5,
								]);
							},
						},
					],
					context,
				),
				context.vars.acc_1,
				context.vars.enumerable_0,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce_enumerable/3"](
				context.vars.enumerable_0,
				context.vars.acc_1,
				context.vars.fun_2,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "reduce_enumerable", 3, "private", [
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			return Erlang["element/2"](
				Type.integer(2n),
				Elixir_Enumerable["reduce/3"](
					context.vars.enumerable_0,
					Type.tuple([Type.atom("cont"), context.vars.acc_1]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.variablePattern("x_3"),
									Type.variablePattern("acc_4"),
								],
								guards: [],
								body: (context) => {
									return Type.tuple([
										Type.atom("cont"),
										Interpreter.callAnonymousFunction(context.vars.fun_2, [
											context.vars.x_3,
											context.vars.acc_4,
										]),
									]);
								},
							},
						],
						context,
					),
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "reduce_into_protocol", 3, "private", [
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("initial_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)],
		body: (context) => {
			return Erlang_Lists["foldl/3"](
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.variablePattern("x_3"),
								Type.variablePattern("acc_4"),
							],
							guards: [],
							body: (context) => {
								return Interpreter.callAnonymousFunction(context.vars.fun_2, [
									context.vars.acc_4,
									Type.tuple([Type.atom("cont"), context.vars.x_3]),
								]);
							},
						},
					],
					context,
				),
				context.vars.initial_1,
				context.vars.enumerable_0,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("initial_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			return Erlang["element/2"](
				Type.integer(2n),
				Elixir_Enumerable["reduce/3"](
					context.vars.enumerable_0,
					Type.tuple([Type.atom("cont"), context.vars.initial_1]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.variablePattern("x_3"),
									Type.variablePattern("acc_4"),
								],
								guards: [],
								body: (context) => {
									return Type.tuple([
										Type.atom("cont"),
										Interpreter.callAnonymousFunction(context.vars.fun_2, [
											context.vars.acc_4,
											Type.tuple([Type.atom("cont"), context.vars.x_3]),
										]),
									]);
								},
							},
						],
						context,
					),
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "reduce_range", 5, "private", [
	{
		params: (context) => [
			Type.variablePattern("first_0"),
			Type.variablePattern("last_1"),
			Type.variablePattern("step_2"),
			Type.variablePattern("acc_3"),
			Type.variablePattern("fun_4"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang[">/2"](context.vars.step_2, Type.integer(0n)),
					(context) =>
						Erlang["=</2"](context.vars.first_0, context.vars.last_1),
					context,
				),
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["</2"](context.vars.step_2, Type.integer(0n)),
					(context) =>
						Erlang[">=/2"](context.vars.first_0, context.vars.last_1),
					context,
				),
		],
		body: (context) => {
			return Elixir_Enum["reduce_range/5"](
				Erlang["+/2"](context.vars.first_0, context.vars.step_2),
				context.vars.last_1,
				context.vars.step_2,
				Interpreter.callAnonymousFunction(context.vars.fun_4, [
					context.vars.first_0,
					context.vars.acc_3,
				]),
				context.vars.fun_4,
			);
		},
	},
	{
		params: (context) => [
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
			Type.variablePattern("acc_3"),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return context.vars.acc_3;
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "reverse", 1, "public", [
	{
		params: (context) => [Type.list([])],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("list_0"),
				Type.list([Type.matchPlaceholder()]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return context.vars.list_0;
		},
	},
	{
		params: (context) => [
			Type.list([
				Type.variablePattern("element1_0"),
				Type.variablePattern("element2_1"),
			]),
		],
		guards: [],
		body: (context) => {
			return Type.list([context.vars.element2_1, context.vars.element1_0]);
		},
	},
	{
		params: (context) => [
			Type.consPattern(
				Type.variablePattern("element1_0"),
				Type.consPattern(
					Type.variablePattern("element2_1"),
					Type.variablePattern("rest_2"),
				),
			),
		],
		guards: [],
		body: (context) => {
			return Erlang_Lists["reverse/2"](
				context.vars.rest_2,
				Type.list([context.vars.element2_1, context.vars.element1_0]),
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("enumerable_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.enumerable_0,
				Type.list([]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.variablePattern("$841"),
								Type.variablePattern("$842"),
							],
							guards: [],
							body: (context) => {
								return Interpreter.consOperator(
									context.vars["$841"],
									context.vars["$842"],
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "slice_count_and_fun", 2, "private", [
	{
		params: (context) => [
			Type.variablePattern("list_0"),
			Type.matchPlaceholder(),
		],
		guards: [(context) => Erlang["is_list/1"](context.vars.list_0)],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["length/1"](context.vars.list_0),
				Type.variablePattern("length_2"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([
				context.vars.length_2,
				Type.anonymousFunction(
					3,
					[
						{
							params: (context) => [
								Type.variablePattern("$1059"),
								Type.variablePattern("$1060"),
								Type.variablePattern("$1061"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Enum["slice_exact/5"](
									context.vars.list_0,
									context.vars["$1059"],
									context.vars["$1060"],
									context.vars["$1061"],
									context.vars.length_2,
								);
							},
						},
					],
					context,
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("step_1"),
		],
		guards: [],
		body: (context) => {
			globalThis.Hologram.return = Interpreter.case(
				Elixir_Enumerable["slice/1"](context.vars.enumerable_0),
				[
					{
						match: Type.tuple([
							Type.atom("ok"),
							Type.variablePattern("count_2"),
							Type.variablePattern("fun_3"),
						]),
						guards: [
							(context) =>
								Erlang["is_function/2"](context.vars.fun_3, Type.integer(1n)),
						],
						body: (context) => {
							return Type.tuple([
								context.vars.count_2,
								Type.anonymousFunction(
									3,
									[
										{
											params: (context) => [
												Type.variablePattern("$1062"),
												Type.variablePattern("$1063"),
												Type.variablePattern("$1064"),
											],
											guards: [],
											body: (context) => {
												return Elixir_Enum["slice_exact/5"](
													Interpreter.callAnonymousFunction(
														context.vars.fun_3,
														[context.vars.enumerable_0],
													),
													context.vars["$1062"],
													context.vars["$1063"],
													context.vars["$1064"],
													context.vars.count_2,
												);
											},
										},
									],
									context,
								),
							]);
						},
					},
					{
						match: Type.tuple([
							Type.atom("ok"),
							Type.variablePattern("count_7"),
							Type.variablePattern("fun_8"),
						]),
						guards: [
							(context) =>
								Erlang["is_function/2"](context.vars.fun_8, Type.integer(3n)),
						],
						body: (context) => {
							return Type.tuple([context.vars.count_7, context.vars.fun_8]);
						},
					},
					{
						match: Type.tuple([
							Type.atom("ok"),
							Type.variablePattern("count_9"),
							Type.variablePattern("fun_10"),
						]),
						guards: [
							(context) =>
								Erlang["is_function/2"](context.vars.fun_10, Type.integer(2n)),
						],
						body: (context) => {
							Elixir_IO["warn/1"](
								Type.bitstring([
									Type.bitstringSegment(
										Elixir_Kernel["inspect/1"](
											Elixir_Enumerable["impl_for/1"](
												context.vars.enumerable_0,
											),
										),
										{ type: "binary" },
									),
									Type.bitstringSegment(
										Type.string(
											" must return a three arity function on slice/1",
										),
										{ type: "binary" },
									),
								]),
							);
							return Interpreter.case(
								Erlang["==/2"](context.vars.step_1, Type.integer(1n)),
								[
									{
										match: Type.atom("false"),
										guards: [],
										body: (context) => {
											return Type.tuple([
												context.vars.count_9,
												Type.anonymousFunction(
													3,
													[
														{
															params: (context) => [
																Type.variablePattern("start_11"),
																Type.variablePattern("amount_12"),
																Type.variablePattern("step_13"),
															],
															guards: [],
															body: (context) => {
																return Elixir_Enum["take_every_list/3"](
																	Interpreter.callAnonymousFunction(
																		context.vars.fun_10,
																		[
																			context.vars.start_11,
																			Erlang["min/2"](
																				Erlang["*/2"](
																					context.vars.amount_12,
																					context.vars.step_13,
																				),
																				Erlang["-/2"](
																					context.vars.count_9,
																					context.vars.start_11,
																				),
																			),
																		],
																	),
																	context.vars.amount_12,
																	Erlang["-/2"](
																		context.vars.step_13,
																		Type.integer(1n),
																	),
																);
															},
														},
													],
													context,
												),
											]);
										},
									},
									{
										match: Type.atom("true"),
										guards: [],
										body: (context) => {
											return Type.tuple([
												context.vars.count_9,
												Type.anonymousFunction(
													3,
													[
														{
															params: (context) => [
																Type.variablePattern("start_14"),
																Type.variablePattern("amount_15"),
																Type.integer(1n),
															],
															guards: [],
															body: (context) => {
																return Interpreter.callAnonymousFunction(
																	context.vars.fun_10,
																	[
																		context.vars.start_14,
																		context.vars.amount_15,
																	],
																);
															},
														},
													],
													context,
												),
											]);
										},
									},
								],
								context,
							);
						},
					},
					{
						match: Type.tuple([
							Type.atom("error"),
							Type.variablePattern("module_16"),
						]),
						guards: [],
						body: (context) => {
							Interpreter.matchOperator(
								Erlang["element/2"](
									Type.integer(2n),
									Interpreter.callNamedFunction(
										context.vars.module_16,
										Type.atom("reduce"),
										Type.list([
											context.vars.enumerable_0,
											Type.tuple([
												Type.atom("cont"),
												Type.tuple([Type.list([]), Type.integer(0n)]),
											]),
											Type.anonymousFunction(
												2,
												[
													{
														params: (context) => [
															Type.variablePattern("elem_17"),
															Type.tuple([
																Type.variablePattern("acc_18"),
																Type.variablePattern("count_19"),
															]),
														],
														guards: [],
														body: (context) => {
															return Type.tuple([
																Type.atom("cont"),
																Type.tuple([
																	Interpreter.consOperator(
																		context.vars.elem_17,
																		context.vars.acc_18,
																	),
																	Erlang["+/2"](
																		context.vars.count_19,
																		Type.integer(1n),
																	),
																]),
															]);
														},
													},
												],
												context,
											),
										]),
										context,
									),
								),
								Type.tuple([
									Type.variablePattern("list_20"),
									Type.variablePattern("count_21"),
								]),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							return Type.tuple([
								context.vars.count_21,
								Type.anonymousFunction(
									3,
									[
										{
											params: (context) => [
												Type.variablePattern("start_22"),
												Type.variablePattern("amount_23"),
												Type.variablePattern("step_24"),
											],
											guards: [],
											body: (context) => {
												return Elixir_Enum["slice_exact/5"](
													Erlang_Lists["reverse/1"](context.vars.list_20),
													context.vars.start_22,
													context.vars.amount_23,
													context.vars.step_24,
													context.vars.count_21,
												);
											},
										},
									],
									context,
								),
							]);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "slice_exact", 5, "private", [
	{
		params: (context) => [
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
			Type.integer(0n),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("list_0"),
			Type.variablePattern("start_1"),
			Type.variablePattern("amount_2"),
			Type.integer(1n),
			Type.variablePattern("size_3"),
		],
		guards: [
			(context) =>
				Erlang["==/2"](
					Erlang["+/2"](context.vars.start_1, context.vars.amount_2),
					context.vars.size_3,
				),
		],
		body: (context) => {
			return Elixir_Enum["drop_exact/2"](
				context.vars.list_0,
				context.vars.start_1,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("list_0"),
			Type.variablePattern("start_1"),
			Type.variablePattern("amount_2"),
			Type.integer(1n),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["take_exact/2"](
				Elixir_Enum["drop_exact/2"](context.vars.list_0, context.vars.start_1),
				context.vars.amount_2,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("list_0"),
			Type.variablePattern("start_1"),
			Type.variablePattern("amount_2"),
			Type.variablePattern("step_3"),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["take_every_list/3"](
				Elixir_Enum["drop_exact/2"](context.vars.list_0, context.vars.start_1),
				context.vars.amount_2,
				Erlang["-/2"](context.vars.step_3, Type.integer(1n)),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "take_every_list", 3, "private", [
	{
		params: (context) => [
			Type.matchPlaceholder(),
			Type.integer(0n),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
	{
		params: (context) => [
			Type.consPattern(
				Type.variablePattern("head_0"),
				Type.variablePattern("tail_1"),
			),
			Type.variablePattern("counter_2"),
			Type.variablePattern("to_drop_3"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.consOperator(
				context.vars.head_0,
				Elixir_Enum["take_every_list/3"](
					Elixir_Enum["drop_list/2"](
						context.vars.tail_1,
						context.vars.to_drop_3,
					),
					Erlang["-/2"](context.vars.counter_2, Type.integer(1n)),
					context.vars.to_drop_3,
				),
			);
		},
	},
	{
		params: (context) => [
			Type.list([]),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "take_exact", 2, "private", [
	{
		params: (context) => [Type.matchPlaceholder(), Type.integer(0n)],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
	{
		params: (context) => [
			Type.consPattern(
				Type.variablePattern("head_0"),
				Type.variablePattern("tail_1"),
			),
			Type.variablePattern("amount_2"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.consOperator(
				context.vars.head_0,
				Elixir_Enum["take_exact/2"](
					context.vars.tail_1,
					Erlang["-/2"](context.vars.amount_2, Type.integer(1n)),
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "to_list", 1, "public", [
	{
		params: (context) => [Type.variablePattern("enumerable_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)],
		body: (context) => {
			return context.vars.enumerable_0;
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("range_0"),
				Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Range")]]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return Elixir_Range["to_list/1"](context.vars.range_0);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("enumerable_0"),
				Type.map([[Type.atom("__struct__"), Type.matchPlaceholder()]]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return Erlang_Lists["reverse/1"](
				Elixir_Enum["reverse/1"](context.vars.enumerable_0),
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("enumerable_0"),
				Type.map([]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return Erlang_Maps["to_list/1"](context.vars.enumerable_0);
		},
	},
	{
		params: (context) => [Type.variablePattern("enumerable_0")],
		guards: [],
		body: (context) => {
			return Erlang_Lists["reverse/1"](
				Elixir_Enum["reverse/1"](context.vars.enumerable_0),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "with_index", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("offset_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_list/1"](context.vars.enumerable_0),
					(context) => Erlang["is_integer/1"](context.vars.offset_1),
					context,
				),
		],
		body: (context) => {
			return Elixir_Enum["with_index_list/2"](
				context.vars.enumerable_0,
				context.vars.offset_1,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("fun_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_list/1"](context.vars.enumerable_0),
					(context) =>
						Erlang["is_function/2"](context.vars.fun_1, Type.integer(2n)),
					context,
				),
		],
		body: (context) => {
			return Elixir_Enum["with_index_list/3"](
				context.vars.enumerable_0,
				Type.integer(0n),
				context.vars.fun_1,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("offset_1"),
		],
		guards: [(context) => Erlang["is_integer/1"](context.vars.offset_1)],
		body: (context) => {
			return Erlang["element/2"](
				Type.integer(1n),
				Elixir_Enum["map_reduce/3"](
					context.vars.enumerable_0,
					context.vars.offset_1,
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.variablePattern("x_2"),
									Type.variablePattern("i_3"),
								],
								guards: [],
								body: (context) => {
									return Type.tuple([
										Type.tuple([context.vars.x_2, context.vars.i_3]),
										Erlang["+/2"](context.vars.i_3, Type.integer(1n)),
									]);
								},
							},
						],
						context,
					),
				),
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("enumerable_0"),
			Type.variablePattern("fun_1"),
		],
		guards: [
			(context) =>
				Erlang["is_function/2"](context.vars.fun_1, Type.integer(2n)),
		],
		body: (context) => {
			return Erlang["element/2"](
				Type.integer(1n),
				Elixir_Enum["map_reduce/3"](
					context.vars.enumerable_0,
					Type.integer(0n),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.variablePattern("x_2"),
									Type.variablePattern("i_3"),
								],
								guards: [],
								body: (context) => {
									return Type.tuple([
										Interpreter.callAnonymousFunction(context.vars.fun_1, [
											context.vars.x_2,
											context.vars.i_3,
										]),
										Erlang["+/2"](context.vars.i_3, Type.integer(1n)),
									]);
								},
							},
						],
						context,
					),
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "with_index_list", 2, "private", [
	{
		params: (context) => [
			Type.consPattern(
				Type.variablePattern("head_0"),
				Type.variablePattern("tail_1"),
			),
			Type.variablePattern("offset_2"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.consOperator(
				Type.tuple([context.vars.head_0, context.vars.offset_2]),
				Elixir_Enum["with_index_list/2"](
					context.vars.tail_1,
					Erlang["+/2"](context.vars.offset_2, Type.integer(1n)),
				),
			);
		},
	},
	{
		params: (context) => [Type.list([]), Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
]);

Interpreter.defineElixirFunction("Enum", "with_index_list", 3, "private", [
	{
		params: (context) => [
			Type.consPattern(
				Type.variablePattern("head_0"),
				Type.variablePattern("tail_1"),
			),
			Type.variablePattern("offset_2"),
			Type.variablePattern("fun_3"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.consOperator(
				Interpreter.callAnonymousFunction(context.vars.fun_3, [
					context.vars.head_0,
					context.vars.offset_2,
				]),
				Elixir_Enum["with_index_list/3"](
					context.vars.tail_1,
					Erlang["+/2"](context.vars.offset_2, Type.integer(1n)),
					context.vars.fun_3,
				),
			);
		},
	},
	{
		params: (context) => [
			Type.list([]),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable", "impl_for", 1, "public", [
	{
		params: (context) => [
			Type.map([[Type.atom("__struct__"), Type.variablePattern("struct_0")]]),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enumerable["struct_impl_for/1"](context.vars.struct_0);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_tuple/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.Tuple")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_atom/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.Atom")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.List")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_map/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.Map")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_bitstring/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.Enumerable.BitString"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_integer/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.Enumerable.Integer"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_float/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.Float")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_function/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.Enumerable.Function"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_pid/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.PID")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_port/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.Port")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_reference/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.Enumerable.Reference"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.atom("nil");
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable", "impl_for!", 1, "public", [
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Elixir_Enumerable["impl_for/1"](context.vars.data_0),
				[
					{
						match: Type.variablePattern("x_1"),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["=:=/2"](context.vars.x_1, Type.atom("false")),
									(context) =>
										Erlang["=:=/2"](context.vars.x_1, Type.atom("nil")),
									context,
								),
						],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_Protocol_UndefinedError["exception/1"](
									Type.list([
										Type.tuple([
											Type.atom("protocol"),
											Type.atom("Elixir.Enumerable"),
										]),
										Type.tuple([Type.atom("value"), context.vars.data_0]),
										Type.tuple([Type.atom("description"), Type.bitstring("")]),
									]),
								),
							);
						},
					},
					{
						match: Type.variablePattern("x_2"),
						guards: [],
						body: (context) => {
							return context.vars.x_2;
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable", "member?", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("term_0"),
			Type.variablePattern("arg2_1"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.callNamedFunction(
				Elixir_Enumerable["impl_for!/1"](context.vars.term_0),
				Type.atom("member?"),
				Type.list([context.vars.term_0, context.vars.arg2_1]),
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable", "reduce", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("term_0"),
			Type.variablePattern("arg2_1"),
			Type.variablePattern("arg3_2"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.callNamedFunction(
				Elixir_Enumerable["impl_for!/1"](context.vars.term_0),
				Type.atom("reduce"),
				Type.list([
					context.vars.term_0,
					context.vars.arg2_1,
					context.vars.arg3_2,
				]),
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable", "slice", 1, "public", [
	{
		params: (context) => [Type.variablePattern("term_0")],
		guards: [],
		body: (context) => {
			return Interpreter.callNamedFunction(
				Elixir_Enumerable["impl_for!/1"](context.vars.term_0),
				Type.atom("slice"),
				Type.list([context.vars.term_0]),
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Enumerable",
	"struct_impl_for",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("struct_0")],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Elixir_Protocol["__concat__/2"](
							Type.atom("Elixir.Enumerable"),
							context.vars.struct_0,
						),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.DBConnection.PrepareStream",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.DBConnection.PrepareStream");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.DBConnection.PrepareStream",
	"member?",
	2,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.DBConnection.PrepareStream"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.DBConnection.PrepareStream",
	"reduce",
	3,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("stream_0"),
				Type.variablePattern("acc_1"),
				Type.variablePattern("fun_2"),
			],
			guards: [],
			body: (context) => {
				return Elixir_DBConnection["reduce/3"](
					context.vars.stream_0,
					context.vars.acc_1,
					context.vars.fun_2,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.DBConnection.PrepareStream",
	"slice",
	1,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.DBConnection.PrepareStream"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.DBConnection.Stream",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.DBConnection.Stream");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.DBConnection.Stream",
	"member?",
	2,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.DBConnection.Stream"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.DBConnection.Stream",
	"reduce",
	3,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("stream_0"),
				Type.variablePattern("acc_1"),
				Type.variablePattern("fun_2"),
			],
			guards: [],
			body: (context) => {
				return Elixir_DBConnection["reduce/3"](
					context.vars.stream_0,
					context.vars.acc_1,
					context.vars.fun_2,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.DBConnection.Stream",
	"slice",
	1,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.DBConnection.Stream"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Date.Range",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Date.Range");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Date.Range",
	"date_from_iso_days",
	2,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("days_0"),
				Type.atom("Elixir.Calendar.ISO"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Elixir_Calendar_ISO["date_from_iso_days/1"](context.vars.days_0),
					Type.tuple([
						Type.variablePattern("year_1"),
						Type.variablePattern("month_2"),
						Type.variablePattern("day_3"),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Date["__struct__/1"](
					Type.list([
						Type.tuple([Type.atom("year"), context.vars.year_1]),
						Type.tuple([Type.atom("month"), context.vars.month_2]),
						Type.tuple([Type.atom("day"), context.vars.day_3]),
						Type.tuple([
							Type.atom("calendar"),
							Type.atom("Elixir.Calendar.ISO"),
						]),
					]),
				);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("days_0"),
				Type.variablePattern("calendar_1"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.callNamedFunction(
						context.vars.calendar_1,
						Type.atom("naive_datetime_from_iso_days"),
						Type.list([
							Type.tuple([
								context.vars.days_0,
								Type.tuple([Type.integer(0n), Type.integer(86400000000n)]),
							]),
						]),
						context,
					),
					Type.tuple([
						Type.variablePattern("year_2"),
						Type.variablePattern("month_3"),
						Type.variablePattern("day_4"),
						Type.matchPlaceholder(),
						Type.matchPlaceholder(),
						Type.matchPlaceholder(),
						Type.matchPlaceholder(),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Date["__struct__/1"](
					Type.list([
						Type.tuple([Type.atom("year"), context.vars.year_2]),
						Type.tuple([Type.atom("month"), context.vars.month_3]),
						Type.tuple([Type.atom("day"), context.vars.day_4]),
						Type.tuple([Type.atom("calendar"), context.vars.calendar_1]),
					]),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Date.Range",
	"empty?",
	1,
	"private",
	[
		{
			params: (context) => [
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
					[
						Type.atom("first_in_iso_days"),
						Type.variablePattern("first_days_0"),
					],
					[Type.atom("last_in_iso_days"), Type.variablePattern("last_days_1")],
					[Type.atom("step"), Type.variablePattern("step_2")],
				]),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang[">/2"](context.vars.step_2, Type.integer(0n)),
						(context) =>
							Erlang[">/2"](
								context.vars.first_days_0,
								context.vars.last_days_1,
							),
						context,
					),
			],
			body: (context) => {
				return Type.atom("true");
			},
		},
		{
			params: (context) => [
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
					[
						Type.atom("first_in_iso_days"),
						Type.variablePattern("first_days_0"),
					],
					[Type.atom("last_in_iso_days"), Type.variablePattern("last_days_1")],
					[Type.atom("step"), Type.variablePattern("step_2")],
				]),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang["</2"](context.vars.step_2, Type.integer(0n)),
						(context) =>
							Erlang["</2"](
								context.vars.first_days_0,
								context.vars.last_days_1,
							),
						context,
					),
			],
			body: (context) => {
				return Type.atom("true");
			},
		},
		{
			params: (context) => [
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
					[Type.atom("step"), Type.matchPlaceholder()],
				]),
			],
			guards: [],
			body: (context) => {
				return Type.atom("false");
			},
		},
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("date_range_2"),
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
						[
							Type.atom("first_in_iso_days"),
							Type.variablePattern("first_days_0"),
						],
						[
							Type.atom("last_in_iso_days"),
							Type.variablePattern("last_days_1"),
						],
					]),
					context,
				),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.case(
						Erlang["=</2"](context.vars.first_days_0, context.vars.last_days_1),
						[
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return Type.integer(-1n);
								},
							},
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Type.integer(1n);
								},
							},
						],
						context,
					),
					Type.variablePattern("step_3"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Enumerable_Date_Range["empty?/1"](
					Erlang_Maps["put/3"](
						Type.atom("step"),
						context.vars.step_3,
						context.vars.date_range_2,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Date.Range",
	"member?",
	2,
	"public",
	[
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("range_4"),
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
						[
							Type.atom("first"),
							Type.map([
								[Type.atom("calendar"), Type.variablePattern("calendar_0")],
							]),
						],
						[
							Type.atom("first_in_iso_days"),
							Type.variablePattern("first_days_1"),
						],
						[
							Type.atom("last_in_iso_days"),
							Type.variablePattern("last_days_2"),
						],
						[Type.atom("step"), Type.variablePattern("step_3")],
					]),
					context,
				),
				Interpreter.matchOperator(
					Type.variablePattern("date_5"),
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Date")],
						[Type.atom("calendar"), Type.variablePattern("calendar_0")],
					]),
					context,
				),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Elixir_Date["to_iso_days/1"](context.vars.date_5),
					Type.tuple([Type.variablePattern("days_6"), Type.matchPlaceholder()]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Interpreter.cond(
					[
						{
							condition: (context) =>
								Elixir_Enumerable_Date_Range["empty?/1"](context.vars.range_4),
							body: (context) => {
								return Type.tuple([Type.atom("ok"), Type.atom("false")]);
							},
						},
						{
							condition: (context) =>
								Erlang["=</2"](
									context.vars.first_days_1,
									context.vars.last_days_2,
								),
							body: (context) => {
								return Type.tuple([
									Type.atom("ok"),
									Interpreter.case(
										Interpreter.case(
											Erlang["=</2"](
												context.vars.first_days_1,
												context.vars.days_6,
											),
											[
												{
													match: Type.atom("false"),
													guards: [],
													body: (context) => {
														return Type.atom("false");
													},
												},
												{
													match: Type.atom("true"),
													guards: [],
													body: (context) => {
														return Erlang["=</2"](
															context.vars.days_6,
															context.vars.last_days_2,
														);
													},
												},
											],
											context,
										),
										[
											{
												match: Type.atom("false"),
												guards: [],
												body: (context) => {
													return Type.atom("false");
												},
											},
											{
												match: Type.atom("true"),
												guards: [],
												body: (context) => {
													return Erlang["==/2"](
														Erlang["rem/2"](
															Erlang["-/2"](
																context.vars.days_6,
																context.vars.first_days_1,
															),
															context.vars.step_3,
														),
														Type.integer(0n),
													);
												},
											},
										],
										context,
									),
								]);
							},
						},
						{
							condition: (context) => Type.atom("true"),
							body: (context) => {
								return Type.tuple([
									Type.atom("ok"),
									Interpreter.case(
										Interpreter.case(
											Erlang["=</2"](
												context.vars.last_days_2,
												context.vars.days_6,
											),
											[
												{
													match: Type.atom("false"),
													guards: [],
													body: (context) => {
														return Type.atom("false");
													},
												},
												{
													match: Type.atom("true"),
													guards: [],
													body: (context) => {
														return Erlang["=</2"](
															context.vars.days_6,
															context.vars.first_days_1,
														);
													},
												},
											],
											context,
										),
										[
											{
												match: Type.atom("false"),
												guards: [],
												body: (context) => {
													return Type.atom("false");
												},
											},
											{
												match: Type.atom("true"),
												guards: [],
												body: (context) => {
													return Erlang["==/2"](
														Erlang["rem/2"](
															Erlang["-/2"](
																context.vars.days_6,
																context.vars.first_days_1,
															),
															context.vars.step_3,
														),
														Type.integer(0n),
													);
												},
											},
										],
										context,
									),
								]);
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
					[Type.atom("step"), Type.matchPlaceholder()],
				]),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([Type.atom("ok"), Type.atom("false")]);
			},
		},
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("date_range_2"),
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
						[
							Type.atom("first_in_iso_days"),
							Type.variablePattern("first_days_0"),
						],
						[
							Type.atom("last_in_iso_days"),
							Type.variablePattern("last_days_1"),
						],
					]),
					context,
				),
				Type.variablePattern("date_3"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.case(
						Erlang["=</2"](context.vars.first_days_0, context.vars.last_days_1),
						[
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return Type.integer(-1n);
								},
							},
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Type.integer(1n);
								},
							},
						],
						context,
					),
					Type.variablePattern("step_4"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Enumerable_Date_Range["member?/2"](
					Erlang_Maps["put/3"](
						Type.atom("step"),
						context.vars.step_4,
						context.vars.date_range_2,
					),
					context.vars.date_3,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Date.Range",
	"reduce",
	3,
	"public",
	[
		{
			params: (context) => [
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
					[
						Type.atom("first"),
						Type.map([
							[Type.atom("calendar"), Type.variablePattern("calendar_2")],
						]),
					],
					[
						Type.atom("first_in_iso_days"),
						Type.variablePattern("first_days_0"),
					],
					[Type.atom("last_in_iso_days"), Type.variablePattern("last_days_1")],
					[Type.atom("step"), Type.variablePattern("step_3")],
				]),
				Type.variablePattern("acc_4"),
				Type.variablePattern("fun_5"),
			],
			guards: [],
			body: (context) => {
				return Elixir_Enumerable_Date_Range["reduce/6"](
					context.vars.first_days_0,
					context.vars.last_days_1,
					context.vars.acc_4,
					context.vars.fun_5,
					context.vars.step_3,
					context.vars.calendar_2,
				);
			},
		},
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("date_range_2"),
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
						[
							Type.atom("first_in_iso_days"),
							Type.variablePattern("first_days_0"),
						],
						[
							Type.atom("last_in_iso_days"),
							Type.variablePattern("last_days_1"),
						],
					]),
					context,
				),
				Type.variablePattern("acc_3"),
				Type.variablePattern("fun_4"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.case(
						Erlang["=</2"](context.vars.first_days_0, context.vars.last_days_1),
						[
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return Type.integer(-1n);
								},
							},
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Type.integer(1n);
								},
							},
						],
						context,
					),
					Type.variablePattern("step_5"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Enumerable_Date_Range["reduce/3"](
					Erlang_Maps["put/3"](
						Type.atom("step"),
						context.vars.step_5,
						context.vars.date_range_2,
					),
					context.vars.acc_3,
					context.vars.fun_4,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Date.Range",
	"reduce",
	6,
	"private",
	[
		{
			params: (context) => [
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
				Type.tuple([Type.atom("halt"), Type.variablePattern("acc_2")]),
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([Type.atom("halted"), context.vars.acc_2]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("first_days_0"),
				Type.variablePattern("last_days_1"),
				Type.tuple([Type.atom("suspend"), Type.variablePattern("acc_2")]),
				Type.variablePattern("fun_3"),
				Type.variablePattern("step_4"),
				Type.variablePattern("calendar_5"),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("suspended"),
					context.vars.acc_2,
					Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("$47")],
								guards: [],
								body: (context) => {
									return Elixir_Enumerable_Date_Range["reduce/6"](
										context.vars.first_days_0,
										context.vars.last_days_1,
										context.vars["$47"],
										context.vars.fun_3,
										context.vars.step_4,
										context.vars.calendar_5,
									);
								},
							},
						],
						context,
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("first_days_0"),
				Type.variablePattern("last_days_1"),
				Type.tuple([Type.atom("cont"), Type.variablePattern("acc_2")]),
				Type.variablePattern("fun_3"),
				Type.variablePattern("step_4"),
				Type.variablePattern("calendar_5"),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang[">/2"](context.vars.step_4, Type.integer(0n)),
						(context) =>
							Erlang["=</2"](
								context.vars.first_days_0,
								context.vars.last_days_1,
							),
						context,
					),
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang["</2"](context.vars.step_4, Type.integer(0n)),
						(context) =>
							Erlang[">=/2"](
								context.vars.first_days_0,
								context.vars.last_days_1,
							),
						context,
					),
			],
			body: (context) => {
				return Elixir_Enumerable_Date_Range["reduce/6"](
					Erlang["+/2"](context.vars.first_days_0, context.vars.step_4),
					context.vars.last_days_1,
					Interpreter.callAnonymousFunction(context.vars.fun_3, [
						Elixir_Enumerable_Date_Range["date_from_iso_days/2"](
							context.vars.first_days_0,
							context.vars.calendar_5,
						),
						context.vars.acc_2,
					]),
					context.vars.fun_3,
					context.vars.step_4,
					context.vars.calendar_5,
				);
			},
		},
		{
			params: (context) => [
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
				Type.tuple([Type.atom("cont"), Type.variablePattern("acc_0")]),
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([Type.atom("done"), context.vars.acc_0]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Date.Range",
	"size",
	1,
	"private",
	[
		{
			params: (context) => [
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
					[
						Type.atom("first_in_iso_days"),
						Type.variablePattern("first_days_0"),
					],
					[Type.atom("last_in_iso_days"), Type.variablePattern("last_days_1")],
					[Type.atom("step"), Type.variablePattern("step_2")],
				]),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang[">/2"](context.vars.step_2, Type.integer(0n)),
						(context) =>
							Erlang[">/2"](
								context.vars.first_days_0,
								context.vars.last_days_1,
							),
						context,
					),
			],
			body: (context) => {
				return Type.integer(0n);
			},
		},
		{
			params: (context) => [
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
					[
						Type.atom("first_in_iso_days"),
						Type.variablePattern("first_days_0"),
					],
					[Type.atom("last_in_iso_days"), Type.variablePattern("last_days_1")],
					[Type.atom("step"), Type.variablePattern("step_2")],
				]),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang["</2"](context.vars.step_2, Type.integer(0n)),
						(context) =>
							Erlang["</2"](
								context.vars.first_days_0,
								context.vars.last_days_1,
							),
						context,
					),
			],
			body: (context) => {
				return Type.integer(0n);
			},
		},
		{
			params: (context) => [
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
					[
						Type.atom("first_in_iso_days"),
						Type.variablePattern("first_days_0"),
					],
					[Type.atom("last_in_iso_days"), Type.variablePattern("last_days_1")],
					[Type.atom("step"), Type.variablePattern("step_2")],
				]),
			],
			guards: [],
			body: (context) => {
				return Erlang["+/2"](
					Erlang["abs/1"](
						Erlang["div/2"](
							Erlang["-/2"](
								context.vars.last_days_1,
								context.vars.first_days_0,
							),
							context.vars.step_2,
						),
					),
					Type.integer(1n),
				);
			},
		},
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("date_range_2"),
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
						[
							Type.atom("first_in_iso_days"),
							Type.variablePattern("first_days_0"),
						],
						[
							Type.atom("last_in_iso_days"),
							Type.variablePattern("last_days_1"),
						],
					]),
					context,
				),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.case(
						Erlang["=</2"](context.vars.first_days_0, context.vars.last_days_1),
						[
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return Type.integer(-1n);
								},
							},
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Type.integer(1n);
								},
							},
						],
						context,
					),
					Type.variablePattern("step_3"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Enumerable_Date_Range["size/1"](
					Erlang_Maps["put/3"](
						Type.atom("step"),
						context.vars.step_3,
						context.vars.date_range_2,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Date.Range",
	"slice",
	1,
	"public",
	[
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("range_3"),
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
						[
							Type.atom("first"),
							Type.map([
								[Type.atom("calendar"), Type.variablePattern("calendar_1")],
							]),
						],
						[Type.atom("first_in_iso_days"), Type.variablePattern("first_0")],
						[Type.atom("step"), Type.variablePattern("step_2")],
					]),
					context,
				),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("ok"),
					Elixir_Enumerable_Date_Range["size/1"](context.vars.range_3),
					Type.anonymousFunction(
						3,
						[
							{
								params: (context) => [
									Type.variablePattern("$42"),
									Type.variablePattern("$44"),
									Type.variablePattern("$43"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Enumerable_Date_Range["slice/4"](
										Erlang["+/2"](
											context.vars.first_0,
											Erlang["*/2"](context.vars["$42"], context.vars.step_2),
										),
										Erlang["-/2"](
											Erlang["+/2"](context.vars.step_2, context.vars["$43"]),
											Type.integer(1n),
										),
										context.vars["$44"],
										context.vars.calendar_1,
									);
								},
							},
						],
						context,
					),
				]);
			},
		},
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("date_range_2"),
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Date.Range")],
						[
							Type.atom("first_in_iso_days"),
							Type.variablePattern("first_days_0"),
						],
						[
							Type.atom("last_in_iso_days"),
							Type.variablePattern("last_days_1"),
						],
					]),
					context,
				),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.case(
						Erlang["=</2"](context.vars.first_days_0, context.vars.last_days_1),
						[
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return Type.integer(-1n);
								},
							},
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Type.integer(1n);
								},
							},
						],
						context,
					),
					Type.variablePattern("step_3"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Enumerable_Date_Range["slice/1"](
					Erlang_Maps["put/3"](
						Type.atom("step"),
						context.vars.step_3,
						context.vars.date_range_2,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Date.Range",
	"slice",
	4,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("current_0"),
				Type.matchPlaceholder(),
				Type.integer(1n),
				Type.variablePattern("calendar_2"),
			],
			guards: [],
			body: (context) => {
				return Type.list([
					Elixir_Enumerable_Date_Range["date_from_iso_days/2"](
						context.vars.current_0,
						context.vars.calendar_2,
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("current_0"),
				Type.variablePattern("step_1"),
				Type.variablePattern("remaining_2"),
				Type.variablePattern("calendar_3"),
			],
			guards: [
				(context) => Erlang[">/2"](context.vars.remaining_2, Type.integer(1n)),
			],
			body: (context) => {
				return Interpreter.consOperator(
					Elixir_Enumerable_Date_Range["date_from_iso_days/2"](
						context.vars.current_0,
						context.vars.calendar_3,
					),
					Elixir_Enumerable_Date_Range["slice/4"](
						Erlang["+/2"](context.vars.current_0, context.vars.step_1),
						context.vars.step_1,
						Erlang["-/2"](context.vars.remaining_2, Type.integer(1n)),
						context.vars.calendar_3,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Ecto.Adapters.SQL.Stream",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Ecto.Adapters.SQL.Stream");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Ecto.Adapters.SQL.Stream",
	"member?",
	2,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.Ecto.Adapters.SQL.Stream"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Ecto.Adapters.SQL.Stream",
	"reduce",
	3,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("stream_0"),
				Type.variablePattern("acc_1"),
				Type.variablePattern("fun_2"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					context.vars.stream_0,
					Type.map([
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.Ecto.Adapters.SQL.Stream"),
						],
						[Type.atom("meta"), Type.variablePattern("meta_3")],
						[Type.atom("opts"), Type.variablePattern("opts_6")],
						[Type.atom("params"), Type.variablePattern("params_5")],
						[Type.atom("statement"), Type.variablePattern("statement_4")],
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Ecto_Adapters_SQL["reduce/6"](
					context.vars.meta_3,
					context.vars.statement_4,
					context.vars.params_5,
					context.vars.opts_6,
					context.vars.acc_1,
					context.vars.fun_2,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Ecto.Adapters.SQL.Stream",
	"slice",
	1,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.Ecto.Adapters.SQL.Stream"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Exqlite.Stream",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Exqlite.Stream");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Exqlite.Stream",
	"member?",
	2,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.Exqlite.Stream"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Exqlite.Stream",
	"reduce",
	3,
	"public",
	[
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("stream_1"),
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Exqlite.Stream")],
						[
							Type.atom("query"),
							Interpreter.matchOperator(
								Type.variablePattern("query_0"),
								Type.map([
									[Type.atom("__struct__"), Type.atom("Elixir.Exqlite.Query")],
								]),
								context,
							),
						],
					]),
					context,
				),
				Type.variablePattern("acc_2"),
				Type.variablePattern("fun_3"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					context.vars.stream_1,
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Exqlite.Stream")],
						[Type.atom("conn"), Type.variablePattern("conn_4")],
						[Type.atom("options"), Type.variablePattern("opts_6")],
						[Type.atom("params"), Type.variablePattern("params_5")],
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Elixir_DBConnection_Stream["__struct__/1"](
						Type.list([
							Type.tuple([Type.atom("conn"), context.vars.conn_4]),
							Type.tuple([Type.atom("query"), context.vars.query_0]),
							Type.tuple([Type.atom("params"), context.vars.params_5]),
							Type.tuple([Type.atom("opts"), context.vars.opts_6]),
						]),
					),
					Type.variablePattern("stream_7"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_DBConnection["reduce/3"](
					context.vars.stream_7,
					context.vars.acc_2,
					context.vars.fun_3,
				);
			},
		},
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("stream_1"),
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Exqlite.Stream")],
						[Type.atom("query"), Type.variablePattern("statement_0")],
					]),
					context,
				),
				Type.variablePattern("acc_2"),
				Type.variablePattern("fun_3"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					context.vars.stream_1,
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Exqlite.Stream")],
						[Type.atom("conn"), Type.variablePattern("conn_4")],
						[Type.atom("options"), Type.variablePattern("opts_6")],
						[Type.atom("params"), Type.variablePattern("params_5")],
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Elixir_Exqlite_Query["__struct__/1"](
						Type.list([
							Type.tuple([Type.atom("command"), Type.atom("nil")]),
							Type.tuple([Type.atom("ref"), Type.atom("nil")]),
							Type.tuple([Type.atom("name"), Type.bitstring("")]),
							Type.tuple([Type.atom("statement"), context.vars.statement_0]),
						]),
					),
					Type.variablePattern("query_7"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Elixir_DBConnection_PrepareStream["__struct__/1"](
						Type.list([
							Type.tuple([Type.atom("conn"), context.vars.conn_4]),
							Type.tuple([Type.atom("query"), context.vars.query_7]),
							Type.tuple([Type.atom("params"), context.vars.params_5]),
							Type.tuple([Type.atom("opts"), context.vars.opts_6]),
						]),
					),
					Type.variablePattern("stream_8"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_DBConnection["reduce/3"](
					context.vars.stream_8,
					context.vars.acc_2,
					context.vars.fun_3,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Exqlite.Stream",
	"slice",
	1,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.Exqlite.Stream"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.File.Stream",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.File.Stream");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.File.Stream",
	"bom_length",
	1,
	"private",
	[
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.integer(239n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(187n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(191n), { type: "integer" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.integer(3n);
			},
		},
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.integer(254n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(255n), { type: "integer" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.integer(2n);
			},
		},
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.integer(255n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(254n), { type: "integer" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.integer(2n);
			},
		},
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.integer(0n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(0n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(254n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(255n), { type: "integer" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.integer(4n);
			},
		},
		{
			params: (context) => [
				Type.bitstringPattern([
					Type.bitstringSegment(Type.integer(254n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(255n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(0n), { type: "integer" }),
					Type.bitstringSegment(Type.integer(0n), { type: "integer" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.integer(4n);
			},
		},
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.integer(0n);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.File.Stream",
	"get_read_offset",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("modes_0")],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Erlang_Lists["keyfind/3"](
						Type.atom("read_offset"),
						Type.integer(1n),
						context.vars.modes_0,
					),
					[
						{
							match: Type.tuple([
								Type.atom("read_offset"),
								Type.variablePattern("offset_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.offset_1;
							},
						},
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Type.integer(0n);
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.File.Stream",
	"member?",
	2,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.File.Stream"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.File.Stream",
	"read_modes",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("modes_0")],
			guards: [],
			body: (context) => {
				return Interpreter.comprehension(
					[
						{
							match: Type.variablePattern("mode_1"),
							guards: [],
							body: (context) => context.vars.modes_0,
						},
					],
					[
						(context) =>
							Erlang["not/1"](
								Erlang["orelse/2"](
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](
													context.vars.mode_1,
													Type.atom("write"),
												),
											(context) =>
												Erlang["=:=/2"](
													context.vars.mode_1,
													Type.atom("append"),
												),
											context,
										),
									(context) =>
										Erlang["=:=/2"](context.vars.mode_1, Type.atom("trim_bom")),
									context,
								),
							),
					],
					Type.list([]),
					false,
					(context) => {
						return context.vars.mode_1;
					},
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.File.Stream",
	"reduce",
	3,
	"public",
	[
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("stream_3"),
					Type.map([
						[
							Type.atom("line_or_bytes"),
							Type.variablePattern("line_or_bytes_1"),
						],
						[Type.atom("modes"), Type.variablePattern("modes_0")],
						[Type.atom("raw"), Type.variablePattern("raw_2")],
					]),
					context,
				),
				Type.variablePattern("acc_4"),
				Type.variablePattern("fun_5"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Type.anonymousFunction(
						0,
						[
							{
								params: (context) => [],
								guards: [],
								body: (context) => {
									return Interpreter.case(
										Elixir_File_Stream["__open__/2"](
											context.vars.stream_3,
											Elixir_Enumerable_File_Stream["read_modes/1"](
												context.vars.modes_0,
											),
										),
										[
											{
												match: Type.tuple([
													Type.atom("ok"),
													Type.variablePattern("device_6"),
												]),
												guards: [],
												body: (context) => {
													return Elixir_Enumerable_File_Stream[
														"skip_bom_and_offset/3"
													](
														context.vars.device_6,
														context.vars.raw_2,
														context.vars.modes_0,
													);
												},
											},
											{
												match: Type.tuple([
													Type.atom("error"),
													Type.variablePattern("reason_7"),
												]),
												guards: [],
												body: (context) => {
													return Erlang["error/1"](
														Elixir_File_Error["exception/1"](
															Type.list([
																Type.tuple([
																	Type.atom("reason"),
																	context.vars.reason_7,
																]),
																Type.tuple([
																	Type.atom("action"),
																	Type.bitstring("stream"),
																]),
																Type.tuple([
																	Type.atom("path"),
																	Interpreter.dotOperator(
																		context.vars.stream_3,
																		Type.atom("path"),
																	),
																]),
															]),
														),
													);
												},
											},
										],
										context,
									);
								},
							},
						],
						context,
					),
					Type.variablePattern("start_fun_8"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Interpreter.case(
						context.vars.raw_2,
						[
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Type.anonymousFunction(
										1,
										[
											{
												params: (context) => [Type.variablePattern("$38")],
												guards: [],
												body: (context) => {
													return Elixir_IO["each_binstream/2"](
														context.vars["$38"],
														context.vars.line_or_bytes_1,
													);
												},
											},
										],
										context,
									);
								},
							},
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return Type.anonymousFunction(
										1,
										[
											{
												params: (context) => [Type.variablePattern("$39")],
												guards: [],
												body: (context) => {
													return Elixir_IO["each_stream/2"](
														context.vars["$39"],
														context.vars.line_or_bytes_1,
													);
												},
											},
										],
										context,
									);
								},
							},
						],
						context,
					),
					Type.variablePattern("next_fun_11"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Interpreter.callAnonymousFunction(
					Elixir_Stream["resource/3"](
						context.vars.start_fun_8,
						context.vars.next_fun_11,
						Type.functionCapture(
							":file",
							"close",
							1,
							[
								{
									params: (context) => [Type.variablePattern("$1")],
									guards: [],
									body: (context) => {
										return Erlang_File["close/1"](context.vars["$1"]);
									},
								},
							],
							context,
						),
					),
					[context.vars.acc_4, context.vars.fun_5],
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.File.Stream",
	"skip_bom_and_offset",
	3,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("device_0"),
				Type.variablePattern("raw_1"),
				Type.variablePattern("modes_2"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.case(
						Elixir_Enum["member?/2"](
							context.vars.modes_2,
							Type.atom("trim_bom"),
						),
						[
							{
								match: Type.variablePattern("x_3"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_3, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_3, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return context.vars.device_0;
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Erlang["element/2"](
										Type.integer(1n),
										Elixir_Enumerable_File_Stream["trim_bom/2"](
											context.vars.device_0,
											context.vars.raw_1,
										),
									);
								},
							},
						],
						context,
					),
					Type.variablePattern("device_4"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Elixir_Enumerable_File_Stream["get_read_offset/1"](
						context.vars.modes_2,
					),
					Type.variablePattern("offset_5"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.case(
					Erlang[">/2"](context.vars.offset_5, Type.integer(0n)),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								globalThis.Hologram.return = Interpreter.matchOperator(
									Erlang_File["position/2"](
										context.vars.device_4,
										Type.tuple([Type.atom("cur"), context.vars.offset_5]),
									),
									Type.tuple([Type.atom("ok"), Type.matchPlaceholder()]),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								return globalThis.Hologram.return;
							},
						},
					],
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return context.vars.device_4;
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.File.Stream",
	"slice",
	1,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.File.Stream"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.File.Stream",
	"trim_bom",
	2,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("device_0"),
				Type.atom("true"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Elixir_Enumerable_File_Stream["bom_length/1"](
						Elixir_IO["binread/2"](context.vars.device_0, Type.integer(4n)),
					),
					Type.variablePattern("bom_length_1"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Erlang_File["position/2"](
						context.vars.device_0,
						context.vars.bom_length_1,
					),
					Type.tuple([Type.atom("ok"), Type.variablePattern("new_pos_2")]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Type.tuple([context.vars.device_0, context.vars.new_pos_2]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("device_0"),
				Type.atom("false"),
			],
			guards: [],
			body: (context) => {
				globalThis.Hologram.return = Interpreter.case(
					Elixir_Enumerable_File_Stream["bom_length/1"](
						Elixir_IO["read/2"](context.vars.device_0, Type.integer(1n)),
					),
					[
						{
							match: Type.integer(0n),
							guards: [],
							body: (context) => {
								Interpreter.matchOperator(
									Erlang_File["position/2"](
										context.vars.device_0,
										Type.integer(0n),
									),
									Type.tuple([Type.atom("ok"), Type.matchPlaceholder()]),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								return Type.tuple([context.vars.device_0, Type.integer(0n)]);
							},
						},
						{
							match: Type.matchPlaceholder(),
							guards: [],
							body: (context) => {
								return Type.tuple([context.vars.device_0, Type.integer(1n)]);
							},
						},
					],
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return globalThis.Hologram.return;
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Function",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Function");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Function",
	"member?",
	2,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.Function"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction("Enumerable.Function", "reduce", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("function_0"),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [
			(context) =>
				Erlang["is_function/2"](context.vars.function_0, Type.integer(2n)),
		],
		body: (context) => {
			return Interpreter.callAnonymousFunction(context.vars.function_0, [
				context.vars.acc_1,
				context.vars.fun_2,
			]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("function_0"),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Erlang["error/1"](
				Elixir_Protocol_UndefinedError["exception/1"](
					Type.list([
						Type.tuple([Type.atom("protocol"), Type.atom("Elixir.Enumerable")]),
						Type.tuple([Type.atom("value"), context.vars.function_0]),
						Type.tuple([
							Type.atom("description"),
							Type.bitstring(
								"only anonymous functions of arity 2 are enumerable",
							),
						]),
					]),
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Function", "slice", 1, "public", [
	{
		params: (context) => [Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("error"),
				Type.atom("Elixir.Enumerable.Function"),
			]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Enumerable.GenEvent.Stream",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.GenEvent.Stream");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.GenEvent.Stream",
	"flush_events",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("ref_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enumerable_GenEvent_Stream["receive/1"](
					Type.list([
						Type.tuple([
							Type.atom("do"),
							Type.list([
								{
									match: Type.tuple([
										Type.matchPlaceholder(),
										Type.tuple([Type.matchPlaceholder(), context.vars.ref_0]),
										Type.tuple([
											Type.variablePattern("notify_3"),
											Type.matchPlaceholder(),
										]),
									]),
									guards: [
										(context) =>
											Erlang["orelse/2"](
												(context) =>
													Erlang["orelse/2"](
														(context) =>
															Erlang["=:=/2"](
																context.vars.notify_3,
																Type.atom("notify"),
															),
														(context) =>
															Erlang["=:=/2"](
																context.vars.notify_3,
																Type.atom("ack_notify"),
															),
														context,
													),
												(context) =>
													Erlang["=:=/2"](
														context.vars.notify_3,
														Type.atom("sync_notify"),
													),
												context,
											),
									],
									body: (context) => {
										return Elixir_Enumerable_GenEvent_Stream["flush_events/1"](
											context.vars.ref_0,
										);
									},
								},
							]),
						]),
						Type.tuple([
							Type.atom("after"),
							Type.list([
								{
									match: Type.integer(0n),
									guards: [],
									body: (context) => {
										return Type.atom("ok");
									},
								},
							]),
						]),
					]),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.GenEvent.Stream",
	"member?",
	2,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.GenEvent.Stream"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.GenEvent.Stream",
	"next",
	2,
	"private",
	[
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("stream_1"),
					Type.map([[Type.atom("timeout"), Type.variablePattern("timeout_0")]]),
					context,
				),
				Interpreter.matchOperator(
					Type.variablePattern("acc_5"),
					Type.tuple([
						Type.variablePattern("pid_2"),
						Type.variablePattern("ref_3"),
						Type.variablePattern("mon_ref_4"),
					]),
					context,
				),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Erlang["self/0"](),
					Type.variablePattern("self_6"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				globalThis.Hologram.return = Elixir_Enumerable_GenEvent_Stream[
					"receive/1"
				](
					Type.list([
						Type.tuple([
							Type.atom("do"),
							Type.list([
								{
									match: Type.tuple([
										Type.matchPlaceholder(),
										Type.tuple([context.vars.pid_2, context.vars.ref_3]),
										Type.tuple([
											Type.atom("notify"),
											Type.variablePattern("event_8"),
										]),
									]),
									guards: [],
									body: (context) => {
										return Type.tuple([
											Type.list([
												Type.tuple([
													Type.atom("async"),
													context.vars.pid_2,
													context.vars.ref_3,
													context.vars.event_8,
												]),
											]),
											context.vars.acc_5,
										]);
									},
								},
								{
									match: Type.tuple([
										Type.matchPlaceholder(),
										Type.tuple([context.vars.pid_2, context.vars.ref_3]),
										Type.tuple([
											Type.atom("sync_notify"),
											Type.variablePattern("event_10"),
										]),
									]),
									guards: [],
									body: (context) => {
										return Type.tuple([
											Type.list([
												Type.tuple([
													Type.atom("sync"),
													context.vars.pid_2,
													context.vars.ref_3,
													context.vars.event_10,
												]),
											]),
											context.vars.acc_5,
										]);
									},
								},
								{
									match: Type.tuple([
										Type.matchPlaceholder(),
										Type.tuple([context.vars.pid_2, context.vars.ref_3]),
										Type.tuple([
											Type.atom("ack_notify"),
											Type.variablePattern("event_12"),
										]),
									]),
									guards: [],
									body: (context) => {
										return Type.tuple([
											Type.list([
												Type.tuple([
													Type.atom("ack"),
													context.vars.pid_2,
													context.vars.ref_3,
													context.vars.event_12,
												]),
											]),
											context.vars.acc_5,
										]);
									},
								},
								{
									match: Interpreter.matchOperator(
										Type.variablePattern("event_14"),
										Type.tuple([
											Type.atom("gen_event_EXIT"),
											Type.tuple([context.vars.pid_2, context.vars.ref_3]),
											Type.matchPlaceholder(),
										]),
										context,
									),
									guards: [],
									body: (context) => {
										Erlang["demonitor/2"](
											context.vars.mon_ref_4,
											Type.list([Type.atom("flush")]),
										);
										Erlang["send/2"](
											context.vars.self_6,
											context.vars.event_14,
										);
										return Type.tuple([
											Type.atom("halt"),
											Type.tuple([Type.atom("removed"), context.vars.acc_5]),
										]);
									},
								},
								{
									match: Interpreter.matchOperator(
										Type.variablePattern("event_15"),
										Type.tuple([
											Type.atom("DOWN"),
											context.vars.mon_ref_4,
											Type.matchPlaceholder(),
											Type.matchPlaceholder(),
											Type.matchPlaceholder(),
										]),
										context,
									),
									guards: [],
									body: (context) => {
										Erlang["send/2"](
											context.vars.self_6,
											context.vars.event_15,
										);
										return Type.tuple([
											Type.atom("halt"),
											Type.tuple([Type.atom("removed"), context.vars.acc_5]),
										]);
									},
								},
							]),
						]),
						Type.tuple([
							Type.atom("after"),
							Type.list([
								{
									match: Type.variablePattern("timeout_0"),
									guards: [],
									body: (context) => {
										return Erlang["exit/1"](
											Type.tuple([
												Type.atom("timeout"),
												Type.tuple([
													Type.atom("Elixir.Enumerable.GenEvent.Stream"),
													Type.atom("next"),
													Type.list([
														context.vars.stream_1,
														context.vars.acc_5,
													]),
												]),
											]),
										);
									},
								},
							]),
						]),
					]),
				);
				Interpreter.updateVarsToMatchedValues(context);
				return globalThis.Hologram.return;
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.GenEvent.Stream",
	"reduce",
	3,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("stream_0"),
				Type.variablePattern("acc_1"),
				Type.variablePattern("fun_2"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Type.anonymousFunction(
						0,
						[
							{
								params: (context) => [],
								guards: [],
								body: (context) => {
									return Elixir_Enumerable_GenEvent_Stream["start/1"](
										context.vars.stream_0,
									);
								},
							},
						],
						context,
					),
					Type.variablePattern("start_fun_3"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("$26")],
								guards: [],
								body: (context) => {
									return Elixir_Enumerable_GenEvent_Stream["next/2"](
										context.vars.stream_0,
										context.vars["$26"],
									);
								},
							},
						],
						context,
					),
					Type.variablePattern("next_fun_5"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("$27")],
								guards: [],
								body: (context) => {
									return Elixir_Enumerable_GenEvent_Stream["stop/2"](
										context.vars.stream_0,
										context.vars["$27"],
									);
								},
							},
						],
						context,
					),
					Type.variablePattern("stop_fun_7"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Interpreter.callAnonymousFunction(
					Elixir_Stream["resource/3"](
						context.vars.start_fun_3,
						context.vars.next_fun_5,
						context.vars.stop_fun_7,
					),
					[
						context.vars.acc_1,
						Elixir_Enumerable_GenEvent_Stream["wrap_reducer/1"](
							context.vars.fun_2,
						),
					],
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.GenEvent.Stream",
	"slice",
	1,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.GenEvent.Stream"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.GenEvent.Stream",
	"start",
	1,
	"private",
	[
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("stream_1"),
					Type.map([[Type.atom("manager"), Type.variablePattern("manager_0")]]),
					context,
				),
			],
			guards: [],
			body: (context) => {
				globalThis.Hologram.return = Interpreter.try(
					(context) => {
						Interpreter.matchOperator(
							Erlang_Gen["call/4"](
								context.vars.manager_0,
								Erlang["self/0"](),
								Type.tuple([
									Type.atom("add_process_handler"),
									Erlang["self/0"](),
									Erlang["self/0"](),
								]),
								Type.atom("infinity"),
							),
							Type.tuple([
								Type.atom("ok"),
								Type.tuple([
									Type.variablePattern("pid_2"),
									Type.variablePattern("ref_3"),
								]),
							]),
							context,
						);
						Interpreter.updateVarsToMatchedValues(context);
						Interpreter.matchOperator(
							Erlang["monitor/2"](Type.atom("process"), context.vars.pid_2),
							Type.variablePattern("mon_ref_4"),
							context,
						);
						Interpreter.updateVarsToMatchedValues(context);
						return Type.tuple([
							context.vars.pid_2,
							context.vars.ref_3,
							context.vars.mon_ref_4,
						]);
					},
					[],
					[],
					[],
					null,
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return globalThis.Hologram.return;
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.GenEvent.Stream",
	"stop",
	2,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("stream_0"),
				Type.tuple([
					Type.atom("removed"),
					Interpreter.matchOperator(
						Type.variablePattern("acc_4"),
						Type.tuple([
							Type.variablePattern("pid_1"),
							Type.variablePattern("ref_2"),
							Type.variablePattern("mon_ref_3"),
						]),
						context,
					),
				]),
			],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Elixir_Enumerable_GenEvent_Stream["wait_for_handler_removal/3"](
						context.vars.pid_1,
						context.vars.ref_2,
						context.vars.mon_ref_3,
					),
					[
						{
							match: Type.atom("ok"),
							guards: [],
							body: (context) => {
								return Elixir_Enumerable_GenEvent_Stream["flush_events/1"](
									context.vars.ref_2,
								);
							},
						},
						{
							match: Type.tuple([
								Type.atom("error"),
								Type.variablePattern("reason_5"),
							]),
							guards: [],
							body: (context) => {
								return Erlang["exit/1"](
									Type.tuple([
										context.vars.reason_5,
										Type.tuple([
											Type.atom("Elixir.Enumerable.GenEvent.Stream"),
											Type.atom("stop"),
											Type.list([context.vars.stream_0, context.vars.acc_4]),
										]),
									]),
								);
							},
						},
					],
					context,
				);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("stream_0"),
				Interpreter.matchOperator(
					Type.variablePattern("acc_3"),
					Type.tuple([
						Type.variablePattern("pid_1"),
						Type.variablePattern("ref_2"),
						Type.matchPlaceholder(),
					]),
					context,
				),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Erlang_Gen_Event["delete_handler/3"](
						context.vars.pid_1,
						Type.tuple([context.vars.pid_1, context.vars.ref_2]),
						Type.atom("shutdown"),
					),
					Type.matchPlaceholder(),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Enumerable_GenEvent_Stream["stop/2"](
					context.vars.stream_0,
					Type.tuple([Type.atom("removed"), context.vars.acc_3]),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.GenEvent.Stream",
	"wait_for_handler_removal",
	3,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("pid_0"),
				Type.variablePattern("ref_1"),
				Type.variablePattern("mon_ref_2"),
			],
			guards: [],
			body: (context) => {
				return Elixir_Enumerable_GenEvent_Stream["receive/1"](
					Type.list([
						Type.tuple([
							Type.atom("do"),
							Type.list([
								{
									match: Type.tuple([
										Type.atom("gen_event_EXIT"),
										Type.tuple([context.vars.pid_0, context.vars.ref_1]),
										Type.matchPlaceholder(),
									]),
									guards: [],
									body: (context) => {
										Erlang["demonitor/2"](
											context.vars.mon_ref_2,
											Type.list([Type.atom("flush")]),
										);
										return Type.atom("ok");
									},
								},
								{
									match: Type.tuple([
										Type.atom("DOWN"),
										context.vars.mon_ref_2,
										Type.matchPlaceholder(),
										Type.matchPlaceholder(),
										Type.variablePattern("reason_4"),
									]),
									guards: [],
									body: (context) => {
										return Type.tuple([
											Type.atom("error"),
											context.vars.reason_4,
										]);
									},
								},
							]),
						]),
					]),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.GenEvent.Stream",
	"wrap_reducer",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("fun_0")],
			guards: [],
			body: (context) => {
				return Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.atom("ack"),
									Type.variablePattern("manager_1"),
									Type.variablePattern("ref_2"),
									Type.variablePattern("event_3"),
								]),
								Type.variablePattern("acc_4"),
							],
							guards: [],
							body: (context) => {
								Erlang["send/2"](
									context.vars.manager_1,
									Type.tuple([context.vars.ref_2, Type.atom("ok")]),
								);
								return Interpreter.callAnonymousFunction(context.vars.fun_0, [
									context.vars.event_3,
									context.vars.acc_4,
								]);
							},
						},
						{
							params: (context) => [
								Type.tuple([
									Type.atom("async"),
									Type.matchPlaceholder(),
									Type.matchPlaceholder(),
									Type.variablePattern("event_7"),
								]),
								Type.variablePattern("acc_8"),
							],
							guards: [],
							body: (context) => {
								return Interpreter.callAnonymousFunction(context.vars.fun_0, [
									context.vars.event_7,
									context.vars.acc_8,
								]);
							},
						},
						{
							params: (context) => [
								Type.tuple([
									Type.atom("sync"),
									Type.variablePattern("manager_9"),
									Type.variablePattern("ref_10"),
									Type.variablePattern("event_11"),
								]),
								Type.variablePattern("acc_12"),
							],
							guards: [],
							body: (context) => {
								return Interpreter.try(
									(context) => {
										return Interpreter.callAnonymousFunction(
											context.vars.fun_0,
											[context.vars.event_11, context.vars.acc_12],
										);
									},
									[],
									[],
									[],
									null,
									context,
								);
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.HashDict",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.HashDict");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.HashDict",
	"member?",
	2,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("dict_0"),
				Type.tuple([
					Type.variablePattern("key_1"),
					Type.variablePattern("value_2"),
				]),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Erlang["binary_to_atom/1"](Type.bitstring("HashDict")),
					Type.variablePattern("module_3"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Type.tuple([
					Type.atom("ok"),
					Interpreter.case(
						Interpreter.callNamedFunction(
							context.vars.module_3,
							Type.atom("fetch"),
							Type.list([context.vars.dict_0, context.vars.key_1]),
							context,
						),
						[
							{
								match: Type.tuple([Type.atom("ok"), context.vars.value_2]),
								guards: [],
								body: (context) => {
									return Type.atom("true");
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Type.atom("false");
								},
							},
						],
						context,
					),
				]);
			},
		},
		{
			params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([Type.atom("ok"), Type.atom("false")]);
			},
		},
	],
);

Interpreter.defineElixirFunction("Enumerable.HashDict", "reduce", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("dict_0"),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["binary_to_atom/1"](Type.bitstring("HashDict")),
				Type.variablePattern("module_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.callNamedFunction(
				context.vars.module_3,
				Type.atom("reduce"),
				Type.list([
					context.vars.dict_0,
					context.vars.acc_1,
					context.vars.fun_2,
				]),
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.HashDict", "slice", 1, "public", [
	{
		params: (context) => [Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("error"),
				Type.atom("Elixir.Enumerable.HashDict"),
			]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Enumerable.HashSet",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.HashSet");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction("Enumerable.HashSet", "member?", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("set_0"),
			Type.variablePattern("term_1"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["binary_to_atom/1"](Type.bitstring("HashSet")),
				Type.variablePattern("module_2"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([
				Type.atom("ok"),
				Interpreter.callNamedFunction(
					context.vars.module_2,
					Type.atom("member?"),
					Type.list([context.vars.set_0, context.vars.term_1]),
					context,
				),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.HashSet", "reduce", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("set_0"),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["binary_to_atom/1"](Type.bitstring("HashSet")),
				Type.variablePattern("module_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.callNamedFunction(
				context.vars.module_3,
				Type.atom("reduce"),
				Type.list([context.vars.set_0, context.vars.acc_1, context.vars.fun_2]),
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.HashSet", "slice", 1, "public", [
	{
		params: (context) => [Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("error"),
				Type.atom("Elixir.Enumerable.HashSet"),
			]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Enumerable.IO.Stream",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.IO.Stream");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.IO.Stream",
	"member?",
	2,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.IO.Stream"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.IO.Stream",
	"reduce",
	3,
	"public",
	[
		{
			params: (context) => [
				Type.map([
					[Type.atom("device"), Type.variablePattern("device_0")],
					[Type.atom("line_or_bytes"), Type.variablePattern("line_or_bytes_2")],
					[Type.atom("raw"), Type.variablePattern("raw_1")],
				]),
				Type.variablePattern("acc_3"),
				Type.variablePattern("fun_4"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.case(
						context.vars.raw_1,
						[
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Type.anonymousFunction(
										1,
										[
											{
												params: (context) => [Type.variablePattern("$17")],
												guards: [],
												body: (context) => {
													return Elixir_IO["each_binstream/2"](
														context.vars["$17"],
														context.vars.line_or_bytes_2,
													);
												},
											},
										],
										context,
									);
								},
							},
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return Type.anonymousFunction(
										1,
										[
											{
												params: (context) => [Type.variablePattern("$18")],
												guards: [],
												body: (context) => {
													return Elixir_IO["each_stream/2"](
														context.vars["$18"],
														context.vars.line_or_bytes_2,
													);
												},
											},
										],
										context,
									);
								},
							},
						],
						context,
					),
					Type.variablePattern("next_fun_7"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Interpreter.callAnonymousFunction(
					Elixir_Stream["resource/3"](
						Type.anonymousFunction(
							0,
							[
								{
									params: (context) => [],
									guards: [],
									body: (context) => {
										return context.vars.device_0;
									},
								},
							],
							context,
						),
						context.vars.next_fun_7,
						Type.anonymousFunction(
							1,
							[
								{
									params: (context) => [Type.variablePattern("$19")],
									guards: [],
									body: (context) => {
										return context.vars["$19"];
									},
								},
							],
							context,
						),
					),
					[context.vars.acc_3, context.vars.fun_4],
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Enumerable.IO.Stream", "slice", 1, "public", [
	{
		params: (context) => [Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("error"),
				Type.atom("Elixir.Enumerable.IO.Stream"),
			]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Enumerable.Jason.OrderedObject",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Jason.OrderedObject");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Jason.OrderedObject",
	"member?",
	2,
	"public",
	[
		{
			params: (context) => [
				Type.map([[Type.atom("values"), Type.list([])]]),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([Type.atom("ok"), Type.atom("false")]);
			},
		},
		{
			params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.Jason.OrderedObject"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Jason.OrderedObject",
	"reduce",
	3,
	"public",
	[
		{
			params: (context) => [
				Type.map([[Type.atom("values"), Type.variablePattern("values_0")]]),
				Type.variablePattern("acc_1"),
				Type.variablePattern("fun_2"),
			],
			guards: [],
			body: (context) => {
				return Elixir_Enumerable_List["reduce/3"](
					context.vars.values_0,
					context.vars.acc_1,
					context.vars.fun_2,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Jason.OrderedObject",
	"slice",
	1,
	"public",
	[
		{
			params: (context) => [Type.map([[Type.atom("values"), Type.list([])]])],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("ok"),
					Type.integer(0n),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.matchPlaceholder(),
									Type.matchPlaceholder(),
								],
								guards: [],
								body: (context) => {
									return Type.list([]);
								},
							},
						],
						context,
					),
				]);
			},
		},
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.Jason.OrderedObject"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction("Enumerable.List", "__impl__", 1, "public", [
	{
		params: (context) => [Type.atom("for")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.List");
		},
	},
	{
		params: (context) => [Type.atom("protocol")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Enumerable");
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.List", "member?", 2, "public", [
	{
		params: (context) => [Type.list([]), Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.tuple([Type.atom("ok"), Type.atom("false")]);
		},
	},
	{
		params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("error"),
				Type.atom("Elixir.Enumerable.List"),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.List", "reduce", 3, "public", [
	{
		params: (context) => [
			Type.matchPlaceholder(),
			Type.tuple([Type.atom("halt"), Type.variablePattern("acc_1")]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([Type.atom("halted"), context.vars.acc_1]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("list_0"),
			Type.tuple([Type.atom("suspend"), Type.variablePattern("acc_1")]),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("suspended"),
				context.vars.acc_1,
				Type.anonymousFunction(
					1,
					[
						{
							params: (context) => [Type.variablePattern("$22")],
							guards: [],
							body: (context) => {
								return Elixir_Enumerable_List["reduce/3"](
									context.vars.list_0,
									context.vars["$22"],
									context.vars.fun_2,
								);
							},
						},
					],
					context,
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.list([]),
			Type.tuple([Type.atom("cont"), Type.variablePattern("acc_0")]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([Type.atom("done"), context.vars.acc_0]);
		},
	},
	{
		params: (context) => [
			Type.consPattern(
				Type.variablePattern("head_0"),
				Type.variablePattern("tail_1"),
			),
			Type.tuple([Type.atom("cont"), Type.variablePattern("acc_2")]),
			Type.variablePattern("fun_3"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enumerable_List["reduce/3"](
				context.vars.tail_1,
				Interpreter.callAnonymousFunction(context.vars.fun_3, [
					context.vars.head_0,
					context.vars.acc_2,
				]),
				context.vars.fun_3,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.List", "slice", 1, "public", [
	{
		params: (context) => [Type.list([])],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("ok"),
				Type.integer(0n),
				Type.anonymousFunction(
					3,
					[
						{
							params: (context) => [
								Type.matchPlaceholder(),
								Type.matchPlaceholder(),
								Type.matchPlaceholder(),
							],
							guards: [],
							body: (context) => {
								return Type.list([]);
							},
						},
					],
					context,
				),
			]);
		},
	},
	{
		params: (context) => [Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("error"),
				Type.atom("Elixir.Enumerable.List"),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Map", "__impl__", 1, "public", [
	{
		params: (context) => [Type.atom("for")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Map");
		},
	},
	{
		params: (context) => [Type.atom("protocol")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Enumerable");
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Map", "member?", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("map_0"),
			Type.tuple([
				Type.variablePattern("key_1"),
				Type.variablePattern("value_2"),
			]),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("ok"),
				Interpreter.case(
					context.vars.map_0,
					[
						{
							match: Type.map([[context.vars.key_1, context.vars.value_2]]),
							guards: [],
							body: (context) => {
								return Type.atom("true");
							},
						},
						{
							match: Type.matchPlaceholder(),
							guards: [],
							body: (context) => {
								return Type.atom("false");
							},
						},
					],
					context,
				),
			]);
		},
	},
	{
		params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.tuple([Type.atom("ok"), Type.atom("false")]);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Map", "reduce", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("map_0"),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enumerable_List["reduce/3"](
				Erlang_Maps["to_list/1"](context.vars.map_0),
				context.vars.acc_1,
				context.vars.fun_2,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Map", "slice", 1, "public", [
	{
		params: (context) => [Type.variablePattern("map_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["map_size/1"](context.vars.map_0),
				Type.variablePattern("size_1"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([
				Type.atom("ok"),
				context.vars.size_1,
				Type.functionCapture(
					":maps",
					"to_list",
					1,
					[
						{
							params: (context) => [Type.variablePattern("$1")],
							guards: [],
							body: (context) => {
								return Erlang_Maps["to_list/1"](context.vars["$1"]);
							},
						},
					],
					context,
				),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.MapSet", "__impl__", 1, "public", [
	{
		params: (context) => [Type.atom("for")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.MapSet");
		},
	},
	{
		params: (context) => [Type.atom("protocol")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Enumerable");
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.MapSet", "member?", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("map_set_0"),
			Type.variablePattern("val_1"),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("ok"),
				Elixir_MapSet["member?/2"](context.vars.map_set_0, context.vars.val_1),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.MapSet", "reduce", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("map_set_0"),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enumerable_List["reduce/3"](
				Elixir_MapSet["to_list/1"](context.vars.map_set_0),
				context.vars.acc_1,
				context.vars.fun_2,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.MapSet", "slice", 1, "public", [
	{
		params: (context) => [Type.variablePattern("map_set_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_MapSet["size/1"](context.vars.map_set_0),
				Type.variablePattern("size_1"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.tuple([
				Type.atom("ok"),
				context.vars.size_1,
				Type.functionCapture(
					"MapSet",
					"to_list",
					1,
					[
						{
							params: (context) => [Type.variablePattern("$1")],
							guards: [],
							body: (context) => {
								return Elixir_MapSet["to_list/1"](context.vars["$1"]);
							},
						},
					],
					context,
				),
			]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Enumerable.Phoenix.LiveView.LiveStream",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Phoenix.LiveView.LiveStream");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Phoenix.LiveView.LiveStream",
	"do_reduce",
	3,
	"private",
	[
		{
			params: (context) => [
				Type.matchPlaceholder(),
				Type.tuple([Type.atom("halt"), Type.variablePattern("acc_1")]),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([Type.atom("halted"), context.vars.acc_1]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("list_0"),
				Type.tuple([Type.atom("suspend"), Type.variablePattern("acc_1")]),
				Type.variablePattern("fun_2"),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("suspended"),
					context.vars.acc_1,
					Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("$28")],
								guards: [],
								body: (context) => {
									return Elixir_Enumerable_Phoenix_LiveView_LiveStream[
										"do_reduce/3"
									](
										context.vars.list_0,
										context.vars["$28"],
										context.vars.fun_2,
									);
								},
							},
						],
						context,
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.list([]),
				Type.tuple([Type.atom("cont"), Type.variablePattern("acc_0")]),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([Type.atom("done"), context.vars.acc_0]);
			},
		},
		{
			params: (context) => [
				Type.consPattern(
					Type.tuple([
						Type.variablePattern("dom_id_0"),
						Type.matchPlaceholder(),
						Type.variablePattern("item_2"),
						Type.matchPlaceholder(),
						Type.matchPlaceholder(),
					]),
					Type.variablePattern("tail_5"),
				),
				Type.tuple([Type.atom("cont"), Type.variablePattern("acc_6")]),
				Type.variablePattern("fun_7"),
			],
			guards: [],
			body: (context) => {
				return Elixir_Enumerable_Phoenix_LiveView_LiveStream["do_reduce/3"](
					context.vars.tail_5,
					Interpreter.callAnonymousFunction(context.vars.fun_7, [
						Type.tuple([context.vars.dom_id_0, context.vars.item_2]),
						context.vars.acc_6,
					]),
					context.vars.fun_7,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Phoenix.LiveView.LiveStream",
	"member?",
	2,
	"public",
	[
		{
			params: (context) => [
				Type.map([
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.Phoenix.LiveView.LiveStream"),
					],
				]),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				return Erlang["error/1"](
					Elixir_RuntimeError["exception/1"](Type.bitstring("not implemented")),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Phoenix.LiveView.LiveStream",
	"reduce",
	3,
	"public",
	[
		{
			params: (context) => [
				Interpreter.matchOperator(
					Type.variablePattern("stream_0"),
					Type.map([
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.Phoenix.LiveView.LiveStream"),
						],
					]),
					context,
				),
				Type.variablePattern("acc_1"),
				Type.variablePattern("fun_2"),
			],
			guards: [],
			body: (context) => {
				globalThis.Hologram.return = Interpreter.case(
					Interpreter.dotOperator(
						context.vars.stream_0,
						Type.atom("consumable?"),
					),
					[
						{
							match: Type.variablePattern("x_3"),
							guards: [
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](context.vars.x_3, Type.atom("false")),
										(context) =>
											Erlang["=:=/2"](context.vars.x_3, Type.atom("nil")),
										context,
									),
							],
							body: (context) => {
								return Erlang["error/1"](
									Elixir_ArgumentError["exception/1"](
										Type.bitstring([
											Type.bitstringSegment(
												Type.string(
													"streams can only be consumed directly by a for comprehension.\nIf you are attempting to consume the stream ahead of time, such as with\n`Enum.with_index(@streams.",
												),
												{ type: "binary" },
											),
											Type.bitstringSegment(
												Elixir_String_Chars["to_string/1"](
													Interpreter.dotOperator(
														context.vars.stream_0,
														Type.atom("name"),
													),
												),
												{ type: "binary" },
											),
											Type.bitstringSegment(
												Type.string(
													")`, you need to place the relevant information\nwithin the stream items instead.\n",
												),
												{ type: "binary" },
											),
										]),
									),
								);
							},
						},
						{
							match: Type.matchPlaceholder(),
							guards: [],
							body: (context) => {
								Interpreter.matchOperator(
									Interpreter.comprehension(
										[
											{
												match: Interpreter.matchOperator(
													Type.variablePattern("insert_5"),
													Type.tuple([
														Type.variablePattern("id_4"),
														Type.matchPlaceholder(),
														Type.matchPlaceholder(),
														Type.matchPlaceholder(),
														Type.matchPlaceholder(),
													]),
													context,
												),
												guards: [],
												body: (context) =>
													Interpreter.dotOperator(
														context.vars.stream_0,
														Type.atom("inserts"),
													),
											},
										],
										[],
										Type.list([]),
										false,
										null,
										context,
									),
									Type.tuple([
										Type.variablePattern("inserts_9"),
										Type.matchPlaceholder(),
									]),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								return Elixir_Enumerable_Phoenix_LiveView_LiveStream[
									"do_reduce/3"
								](
									context.vars.inserts_9,
									context.vars.acc_1,
									context.vars.fun_2,
								);
							},
						},
					],
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return globalThis.Hologram.return;
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Phoenix.LiveView.LiveStream",
	"slice",
	1,
	"public",
	[
		{
			params: (context) => [
				Type.map([
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.Phoenix.LiveView.LiveStream"),
					],
				]),
			],
			guards: [],
			body: (context) => {
				return Erlang["error/1"](
					Elixir_RuntimeError["exception/1"](Type.bitstring("not implemented")),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Enumerable.Range", "__impl__", 1, "public", [
	{
		params: (context) => [Type.atom("for")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Range");
		},
	},
	{
		params: (context) => [Type.atom("protocol")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Enumerable");
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Range", "member?", 2, "public", [
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Range")],
				[Type.atom("first"), Type.variablePattern("first_0")],
				[Type.atom("last"), Type.variablePattern("last_1")],
				[Type.atom("step"), Type.variablePattern("step_2")],
			]),
			Type.variablePattern("value_3"),
		],
		guards: [(context) => Erlang["is_integer/1"](context.vars.value_3)],
		body: (context) => {
			return Interpreter.case(
				Erlang[">/2"](context.vars.step_2, Type.integer(0n)),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Type.tuple([
								Type.atom("ok"),
								Interpreter.case(
									Interpreter.case(
										Erlang["=</2"](context.vars.last_1, context.vars.value_3),
										[
											{
												match: Type.atom("false"),
												guards: [],
												body: (context) => {
													return Type.atom("false");
												},
											},
											{
												match: Type.atom("true"),
												guards: [],
												body: (context) => {
													return Erlang["=</2"](
														context.vars.value_3,
														context.vars.first_0,
													);
												},
											},
										],
										context,
									),
									[
										{
											match: Type.atom("false"),
											guards: [],
											body: (context) => {
												return Type.atom("false");
											},
										},
										{
											match: Type.atom("true"),
											guards: [],
											body: (context) => {
												return Erlang["==/2"](
													Erlang["rem/2"](
														Erlang["-/2"](
															context.vars.value_3,
															context.vars.first_0,
														),
														context.vars.step_2,
													),
													Type.integer(0n),
												);
											},
										},
									],
									context,
								),
							]);
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Type.tuple([
								Type.atom("ok"),
								Interpreter.case(
									Interpreter.case(
										Erlang["=</2"](context.vars.first_0, context.vars.value_3),
										[
											{
												match: Type.atom("false"),
												guards: [],
												body: (context) => {
													return Type.atom("false");
												},
											},
											{
												match: Type.atom("true"),
												guards: [],
												body: (context) => {
													return Erlang["=</2"](
														context.vars.value_3,
														context.vars.last_1,
													);
												},
											},
										],
										context,
									),
									[
										{
											match: Type.atom("false"),
											guards: [],
											body: (context) => {
												return Type.atom("false");
											},
										},
										{
											match: Type.atom("true"),
											guards: [],
											body: (context) => {
												return Erlang["==/2"](
													Erlang["rem/2"](
														Erlang["-/2"](
															context.vars.value_3,
															context.vars.first_0,
														),
														context.vars.step_2,
													),
													Type.integer(0n),
												);
											},
										},
									],
									context,
								),
							]);
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("range_2"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Range")],
					[Type.atom("first"), Type.variablePattern("first_0")],
					[Type.atom("last"), Type.variablePattern("last_1")],
				]),
				context,
			),
			Type.variablePattern("value_3"),
		],
		guards: [(context) => Erlang["is_integer/1"](context.vars.value_3)],
		body: (context) => {
			Interpreter.matchOperator(
				Interpreter.case(
					Erlang["=</2"](context.vars.first_0, context.vars.last_1),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Type.integer(-1n);
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Type.integer(1n);
							},
						},
					],
					context,
				),
				Type.variablePattern("step_4"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_Enumerable_Range["member?/2"](
				Erlang_Maps["put/3"](
					Type.atom("step"),
					context.vars.step_4,
					context.vars.range_2,
				),
				context.vars.value_3,
			);
		},
	},
	{
		params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.tuple([Type.atom("ok"), Type.atom("false")]);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Range", "reduce", 3, "public", [
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Range")],
				[Type.atom("first"), Type.variablePattern("first_0")],
				[Type.atom("last"), Type.variablePattern("last_1")],
				[Type.atom("step"), Type.variablePattern("step_2")],
			]),
			Type.variablePattern("acc_3"),
			Type.variablePattern("fun_4"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enumerable_Range["reduce/5"](
				context.vars.first_0,
				context.vars.last_1,
				context.vars.acc_3,
				context.vars.fun_4,
				context.vars.step_2,
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("range_2"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Range")],
					[Type.atom("first"), Type.variablePattern("first_0")],
					[Type.atom("last"), Type.variablePattern("last_1")],
				]),
				context,
			),
			Type.variablePattern("acc_3"),
			Type.variablePattern("fun_4"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Interpreter.case(
					Erlang["=</2"](context.vars.first_0, context.vars.last_1),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Type.integer(-1n);
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Type.integer(1n);
							},
						},
					],
					context,
				),
				Type.variablePattern("step_5"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_Enumerable_Range["reduce/3"](
				Erlang_Maps["put/3"](
					Type.atom("step"),
					context.vars.step_5,
					context.vars.range_2,
				),
				context.vars.acc_3,
				context.vars.fun_4,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Range", "reduce", 5, "private", [
	{
		params: (context) => [
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
			Type.tuple([Type.atom("halt"), Type.variablePattern("acc_2")]),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([Type.atom("halted"), context.vars.acc_2]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("first_0"),
			Type.variablePattern("last_1"),
			Type.tuple([Type.atom("suspend"), Type.variablePattern("acc_2")]),
			Type.variablePattern("fun_3"),
			Type.variablePattern("step_4"),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("suspended"),
				context.vars.acc_2,
				Type.anonymousFunction(
					1,
					[
						{
							params: (context) => [Type.variablePattern("$29")],
							guards: [],
							body: (context) => {
								return Elixir_Enumerable_Range["reduce/5"](
									context.vars.first_0,
									context.vars.last_1,
									context.vars["$29"],
									context.vars.fun_3,
									context.vars.step_4,
								);
							},
						},
					],
					context,
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("first_0"),
			Type.variablePattern("last_1"),
			Type.tuple([Type.atom("cont"), Type.variablePattern("acc_2")]),
			Type.variablePattern("fun_3"),
			Type.variablePattern("step_4"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang[">/2"](context.vars.step_4, Type.integer(0n)),
					(context) =>
						Erlang["=</2"](context.vars.first_0, context.vars.last_1),
					context,
				),
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["</2"](context.vars.step_4, Type.integer(0n)),
					(context) =>
						Erlang[">=/2"](context.vars.first_0, context.vars.last_1),
					context,
				),
		],
		body: (context) => {
			return Elixir_Enumerable_Range["reduce/5"](
				Erlang["+/2"](context.vars.first_0, context.vars.step_4),
				context.vars.last_1,
				Interpreter.callAnonymousFunction(context.vars.fun_3, [
					context.vars.first_0,
					context.vars.acc_2,
				]),
				context.vars.fun_3,
				context.vars.step_4,
			);
		},
	},
	{
		params: (context) => [
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
			Type.tuple([Type.atom("cont"), Type.variablePattern("acc_0")]),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([Type.atom("done"), context.vars.acc_0]);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Range", "slice", 1, "public", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("range_2"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Range")],
					[Type.atom("first"), Type.variablePattern("first_0")],
					[Type.atom("last"), Type.matchPlaceholder()],
					[Type.atom("step"), Type.variablePattern("step_1")],
				]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("ok"),
				Elixir_Range["size/1"](context.vars.range_2),
				Type.anonymousFunction(
					3,
					[
						{
							params: (context) => [
								Type.variablePattern("$40"),
								Type.variablePattern("$42"),
								Type.variablePattern("$41"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Enumerable_Range["slice/3"](
									Erlang["+/2"](
										context.vars.first_0,
										Erlang["*/2"](context.vars["$40"], context.vars.step_1),
									),
									Erlang["-/2"](
										Erlang["+/2"](context.vars.step_1, context.vars["$41"]),
										Type.integer(1n),
									),
									context.vars["$42"],
								);
							},
						},
					],
					context,
				),
			]);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("range_2"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Range")],
					[Type.atom("first"), Type.variablePattern("first_0")],
					[Type.atom("last"), Type.variablePattern("last_1")],
				]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Interpreter.case(
					Erlang["=</2"](context.vars.first_0, context.vars.last_1),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Type.integer(-1n);
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Type.integer(1n);
							},
						},
					],
					context,
				),
				Type.variablePattern("step_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_Enumerable_Range["slice/1"](
				Erlang_Maps["put/3"](
					Type.atom("step"),
					context.vars.step_3,
					context.vars.range_2,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Range", "slice", 3, "private", [
	{
		params: (context) => [
			Type.variablePattern("current_0"),
			Type.matchPlaceholder(),
			Type.integer(1n),
		],
		guards: [],
		body: (context) => {
			return Type.list([context.vars.current_0]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("current_0"),
			Type.variablePattern("step_1"),
			Type.variablePattern("remaining_2"),
		],
		guards: [
			(context) => Erlang[">/2"](context.vars.remaining_2, Type.integer(1n)),
		],
		body: (context) => {
			return Interpreter.consOperator(
				context.vars.current_0,
				Elixir_Enumerable_Range["slice/3"](
					Erlang["+/2"](context.vars.current_0, context.vars.step_1),
					context.vars.step_1,
					Erlang["-/2"](context.vars.remaining_2, Type.integer(1n)),
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Enumerable.Req.Response.Async",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Req.Response.Async");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Enumerable");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Req.Response.Async",
	"cancel",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("async_0")],
			guards: [],
			body: (context) => {
				return Interpreter.callAnonymousFunction(
					Interpreter.dotOperator(
						context.vars.async_0,
						Type.atom("cancel_fun"),
					),
					[Interpreter.dotOperator(context.vars.async_0, Type.atom("ref"))],
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Req.Response.Async",
	"member?",
	2,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.Req.Response.Async"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Req.Response.Async",
	"reduce",
	3,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("async_0"),
				Type.tuple([Type.atom("halt"), Type.variablePattern("acc_1")]),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				Elixir_Enumerable_Req_Response_Async["cancel/1"](context.vars.async_0);
				return Type.tuple([Type.atom("halted"), context.vars.acc_1]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("async_0"),
				Type.tuple([Type.atom("suspend"), Type.variablePattern("acc_1")]),
				Type.variablePattern("fun_2"),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("suspended"),
					context.vars.acc_1,
					Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("$20")],
								guards: [],
								body: (context) => {
									return Elixir_Enumerable_Req_Response_Async["reduce/3"](
										context.vars.async_0,
										context.vars["$20"],
										context.vars.fun_2,
									);
								},
							},
						],
						context,
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("async_0"),
				Type.tuple([Type.atom("cont"), Type.variablePattern("acc_1")]),
				Type.variablePattern("fun_2"),
			],
			guards: [],
			body: (context) => {
				Interpreter.case(
					Erlang["/=/2"](
						Interpreter.dotOperator(context.vars.async_0, Type.atom("pid")),
						Erlang["self/0"](),
					),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Erlang["error/3"](
									Elixir_RuntimeError["exception/1"](
										Type.bitstring([
											Type.bitstringSegment(
												Type.string(
													"expected to read body chunk in the process ",
												),
												{ type: "binary" },
											),
											Type.bitstringSegment(
												Elixir_Kernel["inspect/1"](
													Interpreter.dotOperator(
														context.vars.async_0,
														Type.atom("pid"),
													),
												),
												{ type: "binary" },
											),
											Type.bitstringSegment(
												Type.string(" which made the request, got: "),
												{ type: "binary" },
											),
											Type.bitstringSegment(
												Elixir_Kernel["inspect/1"](Erlang["self/0"]()),
												{ type: "binary" },
											),
										]),
									),
									Type.atom("none"),
									Type.list([
										Type.tuple([
											Type.atom("error_info"),
											Type.map([
												[Type.atom("module"), Type.atom("Elixir.Exception")],
											]),
										]),
									]),
								);
							},
						},
					],
					context,
				);
				Interpreter.matchOperator(
					Interpreter.dotOperator(context.vars.async_0, Type.atom("ref")),
					Type.variablePattern("ref_3"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				globalThis.Hologram.return = Elixir_Enumerable_Req_Response_Async[
					"receive/1"
				](
					Type.list([
						Type.tuple([
							Type.atom("do"),
							Type.list([
								{
									match: Interpreter.matchOperator(
										Type.variablePattern("message_4"),
										Type.tuple([context.vars.ref_3, Type.matchPlaceholder()]),
										context,
									),
									guards: [],
									body: (context) => {
										globalThis.Hologram.return = Interpreter.case(
											Interpreter.callAnonymousFunction(
												Interpreter.dotOperator(
													context.vars.async_0,
													Type.atom("stream_fun"),
												),
												[
													Interpreter.dotOperator(
														context.vars.async_0,
														Type.atom("ref"),
													),
													context.vars.message_4,
												],
											),
											[
												{
													match: Type.tuple([
														Type.atom("ok"),
														Type.list([
															Type.tuple([
																Type.atom("data"),
																Type.variablePattern("data_5"),
															]),
														]),
													]),
													guards: [],
													body: (context) => {
														Interpreter.matchOperator(
															Interpreter.try(
																(context) => {
																	return Interpreter.callAnonymousFunction(
																		context.vars.fun_2,
																		[context.vars.data_5, context.vars.acc_1],
																	);
																},
																[
																	{
																		variable: Type.variablePattern("e_6"),
																		modules: [],
																		body: (context) => {
																			Elixir_Enumerable_Req_Response_Async[
																				"cancel/1"
																			](context.vars.async_0);
																			return Erlang["raise/3"](
																				Type.atom("error"),
																				Elixir_Kernel_Utils["raise/1"](
																					context.vars.e_6,
																				),
																				Type.matchPlaceholder(),
																			);
																		},
																	},
																],
																[],
																[],
																null,
																context,
															),
															Type.variablePattern("result_7"),
															context,
														);
														Interpreter.updateVarsToMatchedValues(context);
														return Elixir_Enumerable_Req_Response_Async[
															"reduce/3"
														](
															context.vars.async_0,
															context.vars.result_7,
															context.vars.fun_2,
														);
													},
												},
												{
													match: Type.tuple([
														Type.atom("ok"),
														Type.list([Type.atom("done")]),
													]),
													guards: [],
													body: (context) => {
														return Type.tuple([
															Type.atom("done"),
															context.vars.acc_1,
														]);
													},
												},
												{
													match: Type.tuple([
														Type.atom("ok"),
														Type.list([
															Type.tuple([
																Type.atom("trailers"),
																Type.matchPlaceholder(),
															]),
														]),
													]),
													guards: [],
													body: (context) => {
														return Elixir_Enumerable_Req_Response_Async[
															"reduce/3"
														](
															context.vars.async_0,
															Type.tuple([
																Type.atom("cont"),
																context.vars.acc_1,
															]),
															context.vars.fun_2,
														);
													},
												},
												{
													match: Type.tuple([
														Type.atom("error"),
														Type.variablePattern("e_9"),
													]),
													guards: [],
													body: (context) => {
														return Erlang["error/3"](
															Elixir_Kernel_Utils["raise/1"](context.vars.e_9),
															Type.atom("none"),
															Type.list([
																Type.tuple([
																	Type.atom("error_info"),
																	Type.map([
																		[
																			Type.atom("module"),
																			Type.atom("Elixir.Exception"),
																		],
																	]),
																]),
															]),
														);
													},
												},
												{
													match: Type.variablePattern("other_10"),
													guards: [],
													body: (context) => {
														return Erlang["error/3"](
															Elixir_RuntimeError["exception/1"](
																Type.bitstring([
																	Type.bitstringSegment(
																		Type.string("unexpected message: "),
																		{ type: "binary" },
																	),
																	Type.bitstringSegment(
																		Elixir_Kernel["inspect/1"](
																			context.vars.other_10,
																		),
																		{ type: "binary" },
																	),
																]),
															),
															Type.atom("none"),
															Type.list([
																Type.tuple([
																	Type.atom("error_info"),
																	Type.map([
																		[
																			Type.atom("module"),
																			Type.atom("Elixir.Exception"),
																		],
																	]),
																]),
															]),
														);
													},
												},
											],
											context,
										);
										Interpreter.updateVarsToMatchedValues(context);
										return globalThis.Hologram.return;
									},
								},
							]),
						]),
					]),
				);
				Interpreter.updateVarsToMatchedValues(context);
				return globalThis.Hologram.return;
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Enumerable.Req.Response.Async",
	"slice",
	1,
	"public",
	[
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("error"),
					Type.atom("Elixir.Enumerable.Req.Response.Async"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction("Enumerable.Stream", "__impl__", 1, "public", [
	{
		params: (context) => [Type.atom("for")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Stream");
		},
	},
	{
		params: (context) => [Type.atom("protocol")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Enumerable");
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Stream", "do_done", 2, "private", [
	{
		params: (context) => [
			Type.tuple([
				Type.variablePattern("reason_0"),
				Type.consPattern(
					Type.variablePattern("acc_1"),
					Type.matchPlaceholder(),
				),
			]),
			Type.atom("nil"),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([context.vars.reason_0, context.vars.acc_1]);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.variablePattern("reason_0"),
				Type.consPattern(
					Type.variablePattern("acc_1"),
					Type.variablePattern("t_2"),
				),
			]),
			Type.tuple([
				Type.variablePattern("done_3"),
				Type.variablePattern("fun_4"),
			]),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang_Lists["reverse/1"](context.vars.t_2),
				Type.consPattern(Type.variablePattern("h_5"), Type.matchPlaceholder()),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				Interpreter.callAnonymousFunction(context.vars.done_3, [
					Type.list([context.vars.acc_1, context.vars.h_5]),
					context.vars.fun_4,
				]),
				[
					{
						match: Type.tuple([
							Type.atom("cont"),
							Type.consPattern(
								Type.variablePattern("acc_6"),
								Type.matchPlaceholder(),
							),
						]),
						guards: [],
						body: (context) => {
							return Type.tuple([context.vars.reason_0, context.vars.acc_6]);
						},
					},
					{
						match: Type.tuple([
							Type.atom("halt"),
							Type.consPattern(
								Type.variablePattern("acc_7"),
								Type.matchPlaceholder(),
							),
						]),
						guards: [],
						body: (context) => {
							return Type.tuple([Type.atom("halted"), context.vars.acc_7]);
						},
					},
					{
						match: Type.tuple([
							Type.atom("suspend"),
							Type.consPattern(
								Type.variablePattern("acc_8"),
								Type.matchPlaceholder(),
							),
						]),
						guards: [],
						body: (context) => {
							return Type.tuple([
								Type.atom("suspended"),
								context.vars.acc_8,
								Type.anonymousFunction(
									1,
									[
										{
											params: (context) => [Type.variablePattern("$26")],
											guards: [],
											body: (context) => {
												return Type.tuple([
													Type.atom("done"),
													Erlang["element/2"](
														Type.integer(2n),
														context.vars["$26"],
													),
												]);
											},
										},
									],
									context,
								),
							]);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Stream", "do_each", 4, "private", [
	{
		params: (context) => [
			Type.variablePattern("reduce_0"),
			Type.variablePattern("done_1"),
			Type.variablePattern("accs_2"),
			Type.tuple([
				Type.variablePattern("command_3"),
				Type.variablePattern("acc_4"),
			]),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Interpreter.callAnonymousFunction(context.vars.reduce_0, [
					Type.tuple([
						context.vars.command_3,
						Interpreter.consOperator(context.vars.acc_4, context.vars.accs_2),
					]),
				]),
				[
					{
						match: Type.tuple([
							Type.atom("suspended"),
							Type.consPattern(
								Type.variablePattern("acc_5"),
								Type.variablePattern("accs_6"),
							),
							Type.variablePattern("continuation_7"),
						]),
						guards: [],
						body: (context) => {
							return Type.tuple([
								Type.atom("suspended"),
								context.vars.acc_5,
								Type.anonymousFunction(
									1,
									[
										{
											params: (context) => [Type.variablePattern("$25")],
											guards: [],
											body: (context) => {
												return Elixir_Enumerable_Stream["do_each/4"](
													context.vars.continuation_7,
													context.vars.done_1,
													context.vars.accs_6,
													context.vars["$25"],
												);
											},
										},
									],
									context,
								),
							]);
						},
					},
					{
						match: Type.tuple([
							Type.atom("halted"),
							Type.variablePattern("accs_9"),
						]),
						guards: [],
						body: (context) => {
							return Elixir_Enumerable_Stream["do_done/2"](
								Type.tuple([Type.atom("halted"), context.vars.accs_9]),
								context.vars.done_1,
							);
						},
					},
					{
						match: Type.tuple([
							Type.atom("done"),
							Type.variablePattern("accs_10"),
						]),
						guards: [],
						body: (context) => {
							return Elixir_Enumerable_Stream["do_done/2"](
								Type.tuple([Type.atom("done"), context.vars.accs_10]),
								context.vars.done_1,
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Enumerable.Stream",
	"do_reduce",
	3,
	"private",
	[
		{
			params: (context) => [
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Stream")],
					[Type.atom("accs"), Type.variablePattern("accs_2")],
					[Type.atom("done"), Type.variablePattern("done_3")],
					[Type.atom("enum"), Type.variablePattern("enum_0")],
					[Type.atom("funs"), Type.variablePattern("funs_1")],
				]),
				Type.variablePattern("acc_4"),
				Type.variablePattern("fun_5"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Erlang_Lists["foldl/3"](
						Type.anonymousFunction(
							2,
							[
								{
									params: (context) => [
										Type.variablePattern("entry_fun_6"),
										Type.variablePattern("acc_7"),
									],
									guards: [],
									body: (context) => {
										return Interpreter.callAnonymousFunction(
											context.vars.entry_fun_6,
											[context.vars.acc_7],
										);
									},
								},
							],
							context,
						),
						context.vars.fun_5,
						context.vars.funs_1,
					),
					Type.variablePattern("composed_8"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Type.anonymousFunction(
						1,
						[
							{
								params: (context) => [Type.variablePattern("$22")],
								guards: [],
								body: (context) => {
									return Elixir_Enumerable["reduce/3"](
										context.vars.enum_0,
										context.vars["$22"],
										context.vars.composed_8,
									);
								},
							},
						],
						context,
					),
					Type.variablePattern("reduce_10"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Elixir_Enumerable_Stream["do_each/4"](
					context.vars.reduce_10,
					Interpreter.case(
						context.vars.done_3,
						[
							{
								match: Type.variablePattern("x_11"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_11, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_11, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return context.vars.x_11;
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Type.tuple([context.vars.done_3, context.vars.fun_5]);
								},
							},
						],
						context,
					),
					Erlang_Lists["reverse/1"](context.vars.accs_2),
					context.vars.acc_4,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Enumerable.Stream", "member?", 2, "public", [
	{
		params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("error"),
				Type.atom("Elixir.Enumerable.Stream"),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Stream", "reduce", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("lazy_0"),
			Type.variablePattern("acc_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			globalThis.Hologram.return = Elixir_Enumerable_Stream["do_reduce/3"](
				context.vars.lazy_0,
				context.vars.acc_1,
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.variablePattern("x_3"),
								Type.list([Type.variablePattern("acc_4")]),
							],
							guards: [],
							body: (context) => {
								Interpreter.matchOperator(
									Interpreter.callAnonymousFunction(context.vars.fun_2, [
										context.vars.x_3,
										context.vars.acc_4,
									]),
									Type.tuple([
										Type.variablePattern("reason_5"),
										Type.variablePattern("acc_6"),
									]),
									context,
								);
								Interpreter.updateVarsToMatchedValues(context);
								return Type.tuple([
									context.vars.reason_5,
									Type.list([context.vars.acc_6]),
								]);
							},
						},
					],
					context,
				),
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("Enumerable.Stream", "slice", 1, "public", [
	{
		params: (context) => [Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("error"),
				Type.atom("Elixir.Enumerable.Stream"),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("ErlangError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.ErlangError")],
				[Type.atom("original"), Type.atom("nil")],
				[Type.atom("reason"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("ErlangError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.ErlangError")],
					[Type.atom("original"), Type.atom("nil")],
					[Type.atom("reason"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("ErlangError", "error_info", 3, "private", [
	{
		params: (context) => [
			Type.variablePattern("erl_exception_0"),
			Type.variablePattern("stacktrace_1"),
			Type.variablePattern("default_reason_2"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.with();
		},
	},
]);

Interpreter.defineElixirFunction(
	"ErlangError",
	"from_stacktrace",
	1,
	"private",
	[
		{
			params: (context) => [
				Type.consPattern(
					Type.tuple([
						Type.variablePattern("module_0"),
						Type.variablePattern("function_1"),
						Type.variablePattern("args_2"),
						Type.matchPlaceholder(),
					]),
					Type.matchPlaceholder(),
				),
			],
			guards: [(context) => Erlang["is_list/1"](context.vars.args_2)],
			body: (context) => {
				return Type.tuple([
					context.vars.module_0,
					context.vars.function_1,
					Erlang["length/1"](context.vars.args_2),
				]);
			},
		},
		{
			params: (context) => [
				Type.consPattern(
					Type.tuple([
						Type.variablePattern("module_0"),
						Type.variablePattern("function_1"),
						Type.variablePattern("arity_2"),
						Type.matchPlaceholder(),
					]),
					Type.matchPlaceholder(),
				),
			],
			guards: [],
			body: (context) => {
				return Type.tuple([
					context.vars.module_0,
					context.vars.function_1,
					context.vars.arity_2,
				]);
			},
		},
		{
			params: (context) => [Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.tuple([
					Type.atom("nil"),
					Type.atom("nil"),
					Type.atom("nil"),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction("ErlangError", "normalize", 2, "public", [
	{
		params: (context) => [
			Type.atom("badarg"),
			Type.variablePattern("stacktrace_0"),
		],
		guards: [],
		body: (context) => {
			globalThis.Hologram.return = Interpreter.case(
				context.vars.stacktrace_0,
				[
					{
						match: Type.consPattern(
							Type.tuple([
								Type.atom("erlang"),
								Type.atom("apply"),
								Type.list([
									Type.variablePattern("module_1"),
									Type.variablePattern("function_2"),
									Type.variablePattern("args_3"),
								]),
								Type.matchPlaceholder(),
							]),
							Type.matchPlaceholder(),
						),
						guards: [
							(context) =>
								Erlang["not/1"](Erlang["is_atom/1"](context.vars.module_1)),
						],
						body: (context) => {
							Interpreter.matchOperator(
								Interpreter.cond(
									[
										{
											condition: (context) =>
												Interpreter.case(
													Interpreter.case(
														Erlang["is_map/1"](context.vars.module_1),
														[
															{
																match: Type.atom("false"),
																guards: [],
																body: (context) => {
																	return Type.atom("false");
																},
															},
															{
																match: Type.atom("true"),
																guards: [],
																body: (context) => {
																	return Erlang["is_atom/1"](
																		context.vars.function_2,
																	);
																},
															},
														],
														context,
													),
													[
														{
															match: Type.atom("false"),
															guards: [],
															body: (context) => {
																return Type.atom("false");
															},
														},
														{
															match: Type.atom("true"),
															guards: [],
															body: (context) => {
																return Erlang["is_map_key/2"](
																	context.vars.function_2,
																	context.vars.module_1,
																);
															},
														},
													],
													context,
												),
											body: (context) => {
												return Type.bitstring([
													Type.bitstringSegment(
														Type.string(
															"you attempted to apply a function named ",
														),
														{ type: "binary" },
													),
													Type.bitstringSegment(
														Elixir_Kernel["inspect/1"](context.vars.function_2),
														{ type: "binary" },
													),
													Type.bitstringSegment(
														Type.string(" on a map/struct. "),
														{ type: "binary" },
													),
													Type.bitstringSegment(
														Type.string(
															"If you are using Kernel.apply/3, make sure the module is an atom. ",
														),
														{ type: "binary" },
													),
													Type.bitstringSegment(
														Interpreter.case(
															Erlang["is_function/1"](
																Elixir_Map["get/2"](
																	context.vars.module_1,
																	context.vars.function_2,
																),
															),
															[
																{
																	match: Type.atom("false"),
																	guards: [],
																	body: (context) => {
																		return Type.bitstring([
																			Type.bitstringSegment(
																				Type.string(
																					"If you are using the dot syntax, ensure there are no parentheses ",
																				),
																				{ type: "binary" },
																			),
																			Type.bitstringSegment(
																				Type.string(
																					"after the field name, such as map.",
																				),
																				{ type: "binary" },
																			),
																			Type.bitstringSegment(
																				Elixir_String_Chars["to_string/1"](
																					context.vars.function_2,
																				),
																				{ type: "binary" },
																			),
																		]);
																	},
																},
																{
																	match: Type.atom("true"),
																	guards: [],
																	body: (context) => {
																		return Type.bitstring([
																			Type.bitstringSegment(
																				Type.string(
																					"If you are trying to invoke an anonymous function in a map/struct, ",
																				),
																				{ type: "binary" },
																			),
																			Type.bitstringSegment(
																				Type.string(
																					"add a dot between the function name and the parenthesis: map.",
																				),
																				{ type: "binary" },
																			),
																			Type.bitstringSegment(
																				Elixir_String_Chars["to_string/1"](
																					context.vars.function_2,
																				),
																				{ type: "binary" },
																			),
																			Type.bitstringSegment(
																				Type.string(".()"),
																				{ type: "binary" },
																			),
																		]);
																	},
																},
															],
															context,
														),
														{ type: "binary" },
													),
												]);
											},
										},
										{
											condition: (context) =>
												Interpreter.case(
													Erlang["is_atom/1"](context.vars.function_2),
													[
														{
															match: Type.atom("false"),
															guards: [],
															body: (context) => {
																return Type.atom("false");
															},
														},
														{
															match: Type.atom("true"),
															guards: [],
															body: (context) => {
																return Erlang["==/2"](
																	context.vars.args_3,
																	Type.list([]),
																);
															},
														},
													],
													context,
												),
											body: (context) => {
												return Type.bitstring([
													Type.bitstringSegment(
														Type.string(
															"you attempted to apply a function named ",
														),
														{ type: "binary" },
													),
													Type.bitstringSegment(
														Elixir_Kernel["inspect/1"](context.vars.function_2),
														{ type: "binary" },
													),
													Type.bitstringSegment(Type.string(" on "), {
														type: "binary",
													}),
													Type.bitstringSegment(
														Elixir_Kernel["inspect/1"](context.vars.module_1),
														{ type: "binary" },
													),
													Type.bitstringSegment(Type.string(". "), {
														type: "binary",
													}),
													Type.bitstringSegment(
														Type.string(
															"If you are using Kernel.apply/3, make sure the module is an atom. ",
														),
														{ type: "binary" },
													),
													Type.bitstringSegment(
														Type.string(
															"If you are using the dot syntax, such as module.function(), ",
														),
														{ type: "binary" },
													),
													Type.bitstringSegment(
														Type.string(
															"make sure the left-hand side of the dot is an atom representing a module",
														),
														{ type: "binary" },
													),
												]);
											},
										},
										{
											condition: (context) => Type.atom("true"),
											body: (context) => {
												return Type.bitstring([
													Type.bitstringSegment(
														Type.string(
															"you attempted to apply a function on ",
														),
														{ type: "binary" },
													),
													Type.bitstringSegment(
														Elixir_Kernel["inspect/1"](context.vars.module_1),
														{ type: "binary" },
													),
													Type.bitstringSegment(Type.string(". "), {
														type: "binary",
													}),
													Type.bitstringSegment(
														Type.string(
															"Modules (the first argument of apply) must always be an atom",
														),
														{ type: "binary" },
													),
												]);
											},
										},
									],
									context,
								),
								Type.variablePattern("message_4"),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							return Elixir_ArgumentError["__struct__/1"](
								Type.list([
									Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
									Type.tuple([Type.atom("message"), context.vars.message_4]),
								]),
							);
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Interpreter.case(
								Elixir_ErlangError["error_info/3"](
									Type.atom("badarg"),
									context.vars.stacktrace_0,
									Type.bitstring("errors were found at the given arguments"),
								),
								[
									{
										match: Type.tuple([
											Type.atom("ok"),
											Type.variablePattern("reason_5"),
											Type.variablePattern("details_6"),
										]),
										guards: [],
										body: (context) => {
											return Elixir_ArgumentError["__struct__/1"](
												Type.list([
													Type.tuple([
														Type.atom("__exception__"),
														Type.atom("true"),
													]),
													Type.tuple([
														Type.atom("message"),
														Type.bitstring([
															Type.bitstringSegment(context.vars.reason_5, {
																type: "binary",
															}),
															Type.bitstringSegment(context.vars.details_6, {
																type: "binary",
															}),
														]),
													]),
												]),
											);
										},
									},
									{
										match: Type.atom("error"),
										guards: [],
										body: (context) => {
											return Elixir_ArgumentError["__struct__/1"](
												Type.list([
													Type.tuple([
														Type.atom("__exception__"),
														Type.atom("true"),
													]),
													Type.tuple([
														Type.atom("message"),
														Type.bitstring("argument error"),
													]),
												]),
											);
										},
									},
								],
								context,
							);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
	{
		params: (context) => [Type.atom("badarith"), Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Elixir_ArithmeticError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([
						Type.atom("message"),
						Type.bitstring("bad argument in arithmetic expression"),
					]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.atom("system_limit"),
			Type.variablePattern("stacktrace_0"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Type.bitstring(
					"a system limit has been reached due to errors at the given arguments",
				),
				Type.variablePattern("default_reason_1"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				Elixir_ErlangError["error_info/3"](
					Type.atom("system_limit"),
					context.vars.stacktrace_0,
					context.vars.default_reason_1,
				),
				[
					{
						match: Type.tuple([
							Type.atom("ok"),
							Type.variablePattern("reason_2"),
							Type.variablePattern("details_3"),
						]),
						guards: [],
						body: (context) => {
							return Elixir_SystemLimitError["__struct__/1"](
								Type.list([
									Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
									Type.tuple([
										Type.atom("message"),
										Type.bitstring([
											Type.bitstringSegment(context.vars.reason_2, {
												type: "binary",
											}),
											Type.bitstringSegment(context.vars.details_3, {
												type: "binary",
											}),
										]),
									]),
								]),
							);
						},
					},
					{
						match: Type.atom("error"),
						guards: [],
						body: (context) => {
							return Elixir_SystemLimitError["__struct__/1"](
								Type.list([
									Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
									Type.tuple([
										Type.atom("message"),
										Type.bitstring("a system limit has been reached"),
									]),
								]),
							);
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.atom("cond_clause"), Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Elixir_CondClauseError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("badarity"),
				Type.tuple([
					Type.variablePattern("fun_0"),
					Type.variablePattern("args_1"),
				]),
			]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_BadArityError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("function"), context.vars.fun_0]),
					Type.tuple([Type.atom("args"), context.vars.args_1]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([Type.atom("badfun"), Type.variablePattern("term_0")]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_BadFunctionError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("term"), context.vars.term_0]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("badstruct"),
				Type.variablePattern("struct_0"),
				Type.variablePattern("term_1"),
			]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_BadStructError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("struct"), context.vars.struct_0]),
					Type.tuple([Type.atom("term"), context.vars.term_1]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([Type.atom("badmatch"), Type.variablePattern("term_0")]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_MatchError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("term"), context.vars.term_0]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([Type.atom("badmap"), Type.variablePattern("term_0")]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_BadMapError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("term"), context.vars.term_0]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("badbool"),
				Type.variablePattern("op_0"),
				Type.variablePattern("term_1"),
			]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_BadBooleanError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("operator"), context.vars.op_0]),
					Type.tuple([Type.atom("term"), context.vars.term_1]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([Type.atom("badkey"), Type.variablePattern("key_0")]),
			Type.variablePattern("stacktrace_1"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Interpreter.case(
					context.vars.stacktrace_1,
					[
						{
							match: Type.consPattern(
								Type.tuple([
									Type.atom("Elixir.Map"),
									Type.atom("get_and_update!"),
									Type.list([
										Type.variablePattern("map_2"),
										Type.matchPlaceholder(),
										Type.matchPlaceholder(),
									]),
									Type.matchPlaceholder(),
								]),
								Type.matchPlaceholder(),
							),
							guards: [],
							body: (context) => {
								return context.vars.map_2;
							},
						},
						{
							match: Type.consPattern(
								Type.tuple([
									Type.atom("Elixir.Map"),
									Type.atom("update!"),
									Type.list([
										Type.variablePattern("map_3"),
										Type.matchPlaceholder(),
										Type.matchPlaceholder(),
									]),
									Type.matchPlaceholder(),
								]),
								Type.matchPlaceholder(),
							),
							guards: [],
							body: (context) => {
								return context.vars.map_3;
							},
						},
						{
							match: Type.consPattern(
								Type.tuple([
									Type.atom("maps"),
									Type.atom("update"),
									Type.list([
										Type.matchPlaceholder(),
										Type.matchPlaceholder(),
										Type.variablePattern("map_4"),
									]),
									Type.matchPlaceholder(),
								]),
								Type.matchPlaceholder(),
							),
							guards: [],
							body: (context) => {
								return context.vars.map_4;
							},
						},
						{
							match: Type.consPattern(
								Type.tuple([
									Type.atom("maps"),
									Type.atom("get"),
									Type.list([
										Type.matchPlaceholder(),
										Type.variablePattern("map_5"),
									]),
									Type.matchPlaceholder(),
								]),
								Type.matchPlaceholder(),
							),
							guards: [],
							body: (context) => {
								return context.vars.map_5;
							},
						},
						{
							match: Type.consPattern(
								Type.tuple([
									Type.atom("erlang"),
									Type.atom("map_get"),
									Type.list([
										Type.matchPlaceholder(),
										Type.variablePattern("map_6"),
									]),
									Type.matchPlaceholder(),
								]),
								Type.matchPlaceholder(),
							),
							guards: [],
							body: (context) => {
								return context.vars.map_6;
							},
						},
						{
							match: Type.matchPlaceholder(),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				),
				Type.variablePattern("term_7"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_KeyError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("message"), Type.atom("nil")]),
					Type.tuple([Type.atom("key"), context.vars.key_0]),
					Type.tuple([Type.atom("term"), context.vars.term_7]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("badkey"),
				Type.variablePattern("key_0"),
				Type.variablePattern("map_1"),
			]),
			Type.matchPlaceholder(),
		],
		guards: [(context) => Erlang["is_map/1"](context.vars.map_1)],
		body: (context) => {
			return Elixir_KeyError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("message"), Type.atom("nil")]),
					Type.tuple([Type.atom("key"), context.vars.key_0]),
					Type.tuple([Type.atom("term"), context.vars.map_1]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("badkey"),
				Type.variablePattern("key_0"),
				Type.variablePattern("term_1"),
			]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Type.bitstring([
					Type.bitstringSegment(Type.string("key "), { type: "binary" }),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.key_0),
						{ type: "binary" },
					),
					Type.bitstringSegment(Type.string(" not found in: "), {
						type: "binary",
					}),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/2"](
							context.vars.term_1,
							Type.list([
								Type.tuple([Type.atom("pretty"), Type.atom("true")]),
								Type.tuple([Type.atom("limit"), Type.atom("infinity")]),
							]),
						),
						{ type: "binary" },
					),
					Type.bitstringSegment(Type.string("\n\n"), { type: "binary" }),
					Type.bitstringSegment(
						Type.string("If you are using the dot syntax, such as map.field, "),
						{ type: "binary" },
					),
					Type.bitstringSegment(
						Type.string("make sure the left-hand side of the dot is a map"),
						{ type: "binary" },
					),
				]),
				Type.variablePattern("message_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_KeyError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("key"), context.vars.key_0]),
					Type.tuple([Type.atom("term"), context.vars.term_1]),
					Type.tuple([Type.atom("message"), context.vars.message_3]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([Type.atom("case_clause"), Type.variablePattern("term_0")]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_CaseClauseError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("term"), context.vars.term_0]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([Type.atom("else_clause"), Type.variablePattern("term_0")]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_WithClauseError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("term"), context.vars.term_0]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([Type.atom("try_clause"), Type.variablePattern("term_0")]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_TryClauseError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("term"), context.vars.term_0]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.atom("undef"),
			Type.variablePattern("stacktrace_0"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_ErlangError["from_stacktrace/1"](context.vars.stacktrace_0),
				Type.tuple([
					Type.variablePattern("mod_1"),
					Type.variablePattern("fun_2"),
					Type.variablePattern("arity_3"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_UndefinedFunctionError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("message"), Type.atom("nil")]),
					Type.tuple([Type.atom("reason"), Type.atom("nil")]),
					Type.tuple([Type.atom("module"), context.vars.mod_1]),
					Type.tuple([Type.atom("function"), context.vars.fun_2]),
					Type.tuple([Type.atom("arity"), context.vars.arity_3]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.atom("function_clause"),
			Type.variablePattern("stacktrace_0"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_ErlangError["from_stacktrace/1"](context.vars.stacktrace_0),
				Type.tuple([
					Type.variablePattern("mod_1"),
					Type.variablePattern("fun_2"),
					Type.variablePattern("arity_3"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_FunctionClauseError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([Type.atom("args"), Type.atom("nil")]),
					Type.tuple([Type.atom("clauses"), Type.atom("nil")]),
					Type.tuple([Type.atom("kind"), Type.atom("nil")]),
					Type.tuple([Type.atom("module"), context.vars.mod_1]),
					Type.tuple([Type.atom("function"), context.vars.fun_2]),
					Type.tuple([Type.atom("arity"), context.vars.arity_3]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([Type.atom("badarg"), Type.variablePattern("payload_0")]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_ArgumentError["__struct__/1"](
				Type.list([
					Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
					Type.tuple([
						Type.atom("message"),
						Type.bitstring([
							Type.bitstringSegment(Type.string("argument error: "), {
								type: "binary",
							}),
							Type.bitstringSegment(
								Elixir_Kernel["inspect/1"](context.vars.payload_0),
								{ type: "binary" },
							),
						]),
					]),
				]),
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("other_0"),
			Type.variablePattern("stacktrace_1"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Elixir_ErlangError["error_info/3"](
					context.vars.other_0,
					context.vars.stacktrace_1,
					Type.bitstring(""),
				),
				[
					{
						match: Type.tuple([
							Type.atom("ok"),
							Type.matchPlaceholder(),
							Type.variablePattern("details_3"),
						]),
						guards: [],
						body: (context) => {
							return Elixir_ErlangError["__struct__/1"](
								Type.list([
									Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
									Type.tuple([Type.atom("original"), context.vars.other_0]),
									Type.tuple([Type.atom("reason"), context.vars.details_3]),
								]),
							);
						},
					},
					{
						match: Type.atom("error"),
						guards: [],
						body: (context) => {
							return Elixir_ErlangError["__struct__/1"](
								Type.list([
									Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
									Type.tuple([Type.atom("reason"), Type.atom("nil")]),
									Type.tuple([Type.atom("original"), context.vars.other_0]),
								]),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Exception", "format", 3, "public", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("kind_0"),
				Type.tuple([Type.atom("EXIT"), Type.matchPlaceholder()]),
				context,
			),
			Type.variablePattern("any_1"),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_Exception["format_banner/2"](
				context.vars.kind_0,
				context.vars.any_1,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("kind_0"),
			Type.variablePattern("payload_1"),
			Type.variablePattern("stacktrace_2"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Exception["format_banner/3"](
					context.vars.kind_0,
					context.vars.payload_1,
					context.vars.stacktrace_2,
				),
				Type.variablePattern("message_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				context.vars.stacktrace_2,
				[
					{
						match: Type.list([]),
						guards: [],
						body: (context) => {
							return context.vars.message_3;
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Type.bitstring([
								Type.bitstringSegment(context.vars.message_3, {
									type: "binary",
								}),
								Type.bitstringSegment(Type.string("\n"), { type: "binary" }),
								Type.bitstringSegment(
									Elixir_Exception["format_stacktrace/1"](
										context.vars.stacktrace_2,
									),
									{ type: "binary" },
								),
							]);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Exception",
	"format_application",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("module_0")],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Erlang_Application["get_application/1"](context.vars.module_0),
					[
						{
							match: Type.tuple([
								Type.atom("ok"),
								Type.variablePattern("app_1"),
							]),
							guards: [],
							body: (context) => {
								return Interpreter.case(
									Erlang_Application["get_key/2"](
										context.vars.app_1,
										Type.atom("vsn"),
									),
									[
										{
											match: Type.tuple([
												Type.atom("ok"),
												Type.variablePattern("vsn_2"),
											]),
											guards: [
												(context) => Erlang["is_list/1"](context.vars.vsn_2),
											],
											body: (context) => {
												return Type.bitstring([
													Type.bitstringSegment(Type.string("("), {
														type: "binary",
													}),
													Type.bitstringSegment(
														Erlang["atom_to_binary/1"](context.vars.app_1),
														{ type: "binary" },
													),
													Type.bitstringSegment(Type.string(" "), {
														type: "binary",
													}),
													Type.bitstringSegment(
														Elixir_List["to_string/1"](context.vars.vsn_2),
														{ type: "binary" },
													),
													Type.bitstringSegment(Type.string(") "), {
														type: "binary",
													}),
												]);
											},
										},
										{
											match: Type.matchPlaceholder(),
											guards: [],
											body: (context) => {
												return Type.bitstring([
													Type.bitstringSegment(Type.string("("), {
														type: "binary",
													}),
													Type.bitstringSegment(
														Erlang["atom_to_binary/1"](context.vars.app_1),
														{ type: "binary" },
													),
													Type.bitstringSegment(Type.string(") "), {
														type: "binary",
													}),
												]);
											},
										},
									],
									context,
								);
							},
						},
						{
							match: Type.atom("undefined"),
							guards: [],
							body: (context) => {
								return Type.bitstring("");
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Exception", "format_arity", 1, "private", [
	{
		params: (context) => [Type.variablePattern("arity_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.arity_0)],
		body: (context) => {
			Interpreter.matchOperator(
				Interpreter.comprehension(
					[
						{
							match: Type.variablePattern("x_1"),
							guards: [],
							body: (context) => context.vars.arity_0,
						},
					],
					[],
					Type.list([]),
					false,
					(context) => {
						return Elixir_Kernel["inspect/1"](context.vars.x_1);
					},
					context,
				),
				Type.variablePattern("inspected_2"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.bitstring([
				Type.bitstringSegment(Type.string("("), { type: "binary" }),
				Type.bitstringSegment(
					Elixir_Enum["join/2"](context.vars.inspected_2, Type.bitstring(", ")),
					{ type: "binary" },
				),
				Type.bitstringSegment(Type.string(")"), { type: "binary" }),
			]);
		},
	},
	{
		params: (context) => [Type.variablePattern("arity_0")],
		guards: [(context) => Erlang["is_integer/1"](context.vars.arity_0)],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("/"), { type: "binary" }),
				Type.bitstringSegment(
					Erlang["integer_to_binary/1"](context.vars.arity_0),
					{ type: "binary" },
				),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Exception", "format_banner", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("x0_0"),
			Type.variablePattern("x1_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Exception["format_banner/3"](
				context.vars.x0_0,
				context.vars.x1_1,
				Type.list([]),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Exception", "format_banner", 3, "public", [
	{
		params: (context) => [
			Type.atom("error"),
			Type.variablePattern("exception_0"),
			Type.variablePattern("stacktrace_1"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Exception["normalize/3"](
					Type.atom("error"),
					context.vars.exception_0,
					context.vars.stacktrace_1,
				),
				Type.variablePattern("exception_2"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.bitstring([
				Type.bitstringSegment(Type.string("** ("), { type: "binary" }),
				Type.bitstringSegment(
					Elixir_Kernel["inspect/1"](
						Interpreter.dotOperator(
							context.vars.exception_2,
							Type.atom("__struct__"),
						),
					),
					{ type: "binary" },
				),
				Type.bitstringSegment(Type.string(") "), { type: "binary" }),
				Type.bitstringSegment(
					Elixir_Exception["message/1"](context.vars.exception_2),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.atom("throw"),
			Type.variablePattern("reason_0"),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("** (throw) "), { type: "binary" }),
				Type.bitstringSegment(
					Elixir_Kernel["inspect/1"](context.vars.reason_0),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.atom("exit"),
			Type.variablePattern("reason_0"),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("** (exit) "), { type: "binary" }),
				Type.bitstringSegment(
					Elixir_Exception["format_exit/2"](
						context.vars.reason_0,
						Type.bitstring([
							Type.bitstringSegment(Type.string("\n    "), { type: "binary" }),
						]),
					),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([Type.atom("EXIT"), Type.variablePattern("pid_0")]),
			Type.variablePattern("reason_1"),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("** (EXIT from "), {
					type: "binary",
				}),
				Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.pid_0), {
					type: "binary",
				}),
				Type.bitstringSegment(Type.string(") "), { type: "binary" }),
				Type.bitstringSegment(
					Elixir_Exception["format_exit/2"](
						context.vars.reason_1,
						Type.bitstring([
							Type.bitstringSegment(Type.string("\n    "), { type: "binary" }),
						]),
					),
					{ type: "binary" },
				),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Exception", "format_exit", 2, "private", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("reason_2"),
				Type.tuple([
					Type.variablePattern("exception_0"),
					Type.variablePattern("maybe_stacktrace_1"),
				]),
				context,
			),
			Type.variablePattern("joiner_3"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_list/1"](context.vars.maybe_stacktrace_1),
					(context) =>
						Erlang["=/=/2"](context.vars.maybe_stacktrace_1, Type.list([])),
					context,
				),
		],
		body: (context) => {
			globalThis.Hologram.return = Interpreter.try(
				(context) => {
					return Elixir_Enum["map/2"](
						context.vars.maybe_stacktrace_1,
						Type.functionCapture(
							"Exception",
							"format_stacktrace_entry",
							1,
							[
								{
									params: (context) => [Type.variablePattern("$1")],
									guards: [],
									body: (context) => {
										return Elixir_Exception["format_stacktrace_entry/1"](
											context.vars["$1"],
										);
									},
								},
							],
							context,
						),
					);
				},
				[],
				[],
				[],
				null,
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("shutdown"),
				Type.tuple([
					Type.atom("failed_to_start_child"),
					Type.variablePattern("child_0"),
					Type.tuple([Type.atom("EXIT"), Type.variablePattern("reason_1")]),
				]),
			]),
			Type.variablePattern("joiner_2"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Exception["format_start_child/3"](
				context.vars.child_0,
				context.vars.reason_1,
				context.vars.joiner_2,
			);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("shutdown"),
				Type.tuple([
					Type.atom("failed_to_start_child"),
					Type.variablePattern("child_0"),
					Type.variablePattern("reason_1"),
				]),
			]),
			Type.variablePattern("joiner_2"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Exception["format_start_child/3"](
				context.vars.child_0,
				context.vars.reason_1,
				context.vars.joiner_2,
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("reason_4"),
				Type.tuple([
					Type.variablePattern("reason2_0"),
					Type.tuple([
						Type.variablePattern("mod_1"),
						Type.variablePattern("fun_2"),
						Type.variablePattern("args_3"),
					]),
				]),
				context,
			),
			Type.variablePattern("joiner_5"),
		],
		guards: [
			(context) =>
				Erlang["</2"](
					Erlang["length/1"](context.vars.args_3),
					Type.integer(256n),
				),
		],
		body: (context) => {
			return Interpreter.try(
				(context) => {
					return Elixir_Exception["format_mfa/3"](
						context.vars.mod_1,
						context.vars.fun_2,
						context.vars.args_3,
					);
				},
				[],
				[],
				[],
				null,
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("reason_0"),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_Exception["format_exit_reason/1"](context.vars.reason_0);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Exception",
	"format_exit_reason",
	1,
	"private",
	[
		{
			params: (context) => [Type.atom("normal")],
			guards: [],
			body: (context) => {
				return Type.bitstring("normal");
			},
		},
		{
			params: (context) => [Type.atom("shutdown")],
			guards: [],
			body: (context) => {
				return Type.bitstring("shutdown");
			},
		},
		{
			params: (context) => [
				Type.tuple([Type.atom("shutdown"), Type.variablePattern("reason_0")]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(Type.string("shutdown: "), { type: "binary" }),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.reason_0),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [Type.atom("calling_self")],
			guards: [],
			body: (context) => {
				return Type.bitstring("process attempted to call itself");
			},
		},
		{
			params: (context) => [Type.atom("timeout")],
			guards: [],
			body: (context) => {
				return Type.bitstring("time out");
			},
		},
		{
			params: (context) => [Type.atom("killed")],
			guards: [],
			body: (context) => {
				return Type.bitstring("killed");
			},
		},
		{
			params: (context) => [Type.atom("noconnection")],
			guards: [],
			body: (context) => {
				return Type.bitstring("no connection");
			},
		},
		{
			params: (context) => [Type.atom("noproc")],
			guards: [],
			body: (context) => {
				return Type.bitstring(
					"no process: the process is not alive or there's no process currently associated with the given name, possibly because its application isn't started",
				);
			},
		},
		{
			params: (context) => [
				Type.tuple([
					Type.atom("nodedown"),
					Type.variablePattern("node_name_0"),
				]),
			],
			guards: [(context) => Erlang["is_atom/1"](context.vars.node_name_0)],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(Type.string("no connection to "), {
						type: "binary",
					}),
					Type.bitstringSegment(
						Elixir_String_Chars["to_string/1"](context.vars.node_name_0),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([
					Type.atom("already_started"),
					Type.variablePattern("pid_0"),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(Type.string("already started: "), {
						type: "binary",
					}),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.pid_0),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([
					Type.atom("bad_return_value"),
					Type.variablePattern("value_0"),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(Type.string("bad return value: "), {
						type: "binary",
					}),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.value_0),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([Type.atom("bad_call"), Type.variablePattern("request_0")]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(Type.string("bad call: "), { type: "binary" }),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.request_0),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([Type.atom("bad_cast"), Type.variablePattern("request_0")]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(Type.string("bad cast: "), { type: "binary" }),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.request_0),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([
					Type.atom("bad_return"),
					Type.tuple([
						Type.variablePattern("mod_0"),
						Type.atom("init"),
						Type.variablePattern("value_1"),
					]),
				]),
			],
			guards: [(context) => Erlang["is_atom/1"](context.vars.mod_0)],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(
						Elixir_Exception["format_mfa/3"](
							context.vars.mod_0,
							Type.atom("init"),
							Type.integer(1n),
						),
						{ type: "binary" },
					),
					Type.bitstringSegment(Type.string(" returned a bad value: "), {
						type: "binary",
					}),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.value_1),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([
					Type.atom("bad_start_spec"),
					Type.variablePattern("start_spec_0"),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(
						Type.string("bad child specification, invalid children: "),
						{ type: "binary" },
					),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.start_spec_0),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([
					Type.atom("start_spec"),
					Type.variablePattern("start_spec_0"),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(Type.string("bad child specification, "), {
						type: "binary",
					}),
					Type.bitstringSegment(
						Elixir_Exception["format_sup_spec/1"](context.vars.start_spec_0),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([
					Type.atom("supervisor_data"),
					Type.variablePattern("data_0"),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(Type.string("bad supervisor configuration, "), {
						type: "binary",
					}),
					Type.bitstringSegment(
						Elixir_Exception["format_sup_data/1"](context.vars.data_0),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [Type.variablePattern("reason_0")],
			guards: [],
			body: (context) => {
				return Elixir_Kernel["inspect/1"](context.vars.reason_0);
			},
		},
	],
);

Interpreter.defineElixirFunction("Exception", "format_fa", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("fun_0"),
			Type.variablePattern("arity_1"),
		],
		guards: [(context) => Erlang["is_function/1"](context.vars.fun_0)],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.fun_0), {
					type: "binary",
				}),
				Type.bitstringSegment(
					Elixir_String_Chars["to_string/1"](
						Elixir_Exception["format_arity/1"](context.vars.arity_1),
					),
					{ type: "binary" },
				),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Exception", "format_file_line", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("file_0"),
			Type.variablePattern("line_1"),
			Type.variablePattern("suffix_2"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.cond(
				[
					{
						condition: (context) =>
							Erlang["==/2"](context.vars.file_0, Type.atom("nil")),
						body: (context) => {
							return Type.bitstring("");
						},
					},
					{
						condition: (context) =>
							Interpreter.case(
								Erlang["==/2"](context.vars.line_1, Type.atom("nil")),
								[
									{
										match: Type.atom("false"),
										guards: [],
										body: (context) => {
											return Erlang["==/2"](
												context.vars.line_1,
												Type.integer(0n),
											);
										},
									},
									{
										match: Type.atom("true"),
										guards: [],
										body: (context) => {
											return Type.atom("true");
										},
									},
								],
								context,
							),
						body: (context) => {
							return Type.bitstring([
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](context.vars.file_0),
									{ type: "binary" },
								),
								Type.bitstringSegment(Type.string(":"), { type: "binary" }),
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](context.vars.suffix_2),
									{ type: "binary" },
								),
							]);
						},
					},
					{
						condition: (context) => Type.atom("true"),
						body: (context) => {
							return Type.bitstring([
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](context.vars.file_0),
									{ type: "binary" },
								),
								Type.bitstringSegment(Type.string(":"), { type: "binary" }),
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](context.vars.line_1),
									{ type: "binary" },
								),
								Type.bitstringSegment(Type.string(":"), { type: "binary" }),
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](context.vars.suffix_2),
									{ type: "binary" },
								),
							]);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Exception",
	"format_file_line_column",
	4,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("file_0"),
				Type.variablePattern("line_1"),
				Type.variablePattern("column_2"),
				Type.variablePattern("suffix_3"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.cond(
					[
						{
							condition: (context) =>
								Erlang["==/2"](context.vars.file_0, Type.atom("nil")),
							body: (context) => {
								return Type.bitstring("");
							},
						},
						{
							condition: (context) =>
								Interpreter.case(
									Erlang["==/2"](context.vars.line_1, Type.atom("nil")),
									[
										{
											match: Type.atom("false"),
											guards: [],
											body: (context) => {
												return Erlang["==/2"](
													context.vars.line_1,
													Type.integer(0n),
												);
											},
										},
										{
											match: Type.atom("true"),
											guards: [],
											body: (context) => {
												return Type.atom("true");
											},
										},
									],
									context,
								),
							body: (context) => {
								return Type.bitstring([
									Type.bitstringSegment(
										Elixir_String_Chars["to_string/1"](context.vars.file_0),
										{ type: "binary" },
									),
									Type.bitstringSegment(Type.string(":"), { type: "binary" }),
									Type.bitstringSegment(
										Elixir_String_Chars["to_string/1"](context.vars.suffix_3),
										{ type: "binary" },
									),
								]);
							},
						},
						{
							condition: (context) =>
								Interpreter.case(
									Erlang["==/2"](context.vars.column_2, Type.atom("nil")),
									[
										{
											match: Type.atom("false"),
											guards: [],
											body: (context) => {
												return Erlang["==/2"](
													context.vars.column_2,
													Type.integer(0n),
												);
											},
										},
										{
											match: Type.atom("true"),
											guards: [],
											body: (context) => {
												return Type.atom("true");
											},
										},
									],
									context,
								),
							body: (context) => {
								return Type.bitstring([
									Type.bitstringSegment(
										Elixir_String_Chars["to_string/1"](context.vars.file_0),
										{ type: "binary" },
									),
									Type.bitstringSegment(Type.string(":"), { type: "binary" }),
									Type.bitstringSegment(
										Elixir_String_Chars["to_string/1"](context.vars.line_1),
										{ type: "binary" },
									),
									Type.bitstringSegment(Type.string(":"), { type: "binary" }),
									Type.bitstringSegment(
										Elixir_String_Chars["to_string/1"](context.vars.suffix_3),
										{ type: "binary" },
									),
								]);
							},
						},
						{
							condition: (context) => Type.atom("true"),
							body: (context) => {
								return Type.bitstring([
									Type.bitstringSegment(
										Elixir_String_Chars["to_string/1"](context.vars.file_0),
										{ type: "binary" },
									),
									Type.bitstringSegment(Type.string(":"), { type: "binary" }),
									Type.bitstringSegment(
										Elixir_String_Chars["to_string/1"](context.vars.line_1),
										{ type: "binary" },
									),
									Type.bitstringSegment(Type.string(":"), { type: "binary" }),
									Type.bitstringSegment(
										Elixir_String_Chars["to_string/1"](context.vars.column_2),
										{ type: "binary" },
									),
									Type.bitstringSegment(Type.string(":"), { type: "binary" }),
									Type.bitstringSegment(
										Elixir_String_Chars["to_string/1"](context.vars.suffix_3),
										{ type: "binary" },
									),
								]);
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Exception", "format_location", 1, "private", [
	{
		params: (context) => [Type.variablePattern("opts_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.opts_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Access["get/2"](context.vars.opts_0, Type.atom("column")),
				[
					{
						match: Type.atom("nil"),
						guards: [],
						body: (context) => {
							return Elixir_Exception["format_file_line/3"](
								Elixir_Keyword["get/2"](context.vars.opts_0, Type.atom("file")),
								Elixir_Keyword["get/2"](context.vars.opts_0, Type.atom("line")),
								Type.bitstring(" "),
							);
						},
					},
					{
						match: Type.variablePattern("col_1"),
						guards: [],
						body: (context) => {
							return Elixir_Exception["format_file_line_column/4"](
								Elixir_Keyword["get/2"](context.vars.opts_0, Type.atom("file")),
								Elixir_Keyword["get/2"](context.vars.opts_0, Type.atom("line")),
								context.vars.col_1,
								Type.bitstring(" "),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Exception", "format_mfa", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("module_0"),
			Type.variablePattern("fun_1"),
			Type.variablePattern("arity_2"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_atom/1"](context.vars.module_0),
					(context) => Erlang["is_atom/1"](context.vars.fun_1),
					context,
				),
		],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code_Identifier["extract_anonymous_fun_parent/1"](
					context.vars.fun_1,
				),
				[
					{
						match: Type.tuple([
							Type.variablePattern("outer_name_3"),
							Type.variablePattern("outer_arity_4"),
						]),
						guards: [],
						body: (context) => {
							return Type.bitstring([
								Type.bitstringSegment(Type.string("anonymous fn"), {
									type: "binary",
								}),
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](
										Elixir_Exception["format_arity/1"](context.vars.arity_2),
									),
									{ type: "binary" },
								),
								Type.bitstringSegment(Type.string(" in "), { type: "binary" }),
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](
										Elixir_Macro["inspect_atom/2"](
											Type.atom("literal"),
											context.vars.module_0,
										),
									),
									{ type: "binary" },
								),
								Type.bitstringSegment(Type.string("."), { type: "binary" }),
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](
										Elixir_Macro["inspect_atom/2"](
											Type.atom("remote_call"),
											context.vars.outer_name_3,
										),
									),
									{ type: "binary" },
								),
								Type.bitstringSegment(Type.string("/"), { type: "binary" }),
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](
										context.vars.outer_arity_4,
									),
									{ type: "binary" },
								),
							]);
						},
					},
					{
						match: Type.atom("error"),
						guards: [],
						body: (context) => {
							return Type.bitstring([
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](
										Elixir_Macro["inspect_atom/2"](
											Type.atom("literal"),
											context.vars.module_0,
										),
									),
									{ type: "binary" },
								),
								Type.bitstringSegment(Type.string("."), { type: "binary" }),
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](
										Elixir_Macro["inspect_atom/2"](
											Type.atom("remote_call"),
											context.vars.fun_1,
										),
									),
									{ type: "binary" },
								),
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](
										Elixir_Exception["format_arity/1"](context.vars.arity_2),
									),
									{ type: "binary" },
								),
							]);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Exception",
	"format_stacktrace",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("trace_0")],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.case(
						context.vars.trace_0,
						[
							{
								match: Type.variablePattern("x_1"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_1, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_1, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return Interpreter.case(
										Elixir_Process["info/2"](
											Erlang["self/0"](),
											Type.atom("current_stacktrace"),
										),
										[
											{
												match: Type.tuple([
													Type.atom("current_stacktrace"),
													Type.variablePattern("t_2"),
												]),
												guards: [],
												body: (context) => {
													return Elixir_Enum["drop/2"](
														context.vars.t_2,
														Type.integer(3n),
													);
												},
											},
										],
										context,
									);
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return context.vars.trace_0;
								},
							},
						],
						context,
					),
					Type.variablePattern("trace_3"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Interpreter.case(
					context.vars.trace_3,
					[
						{
							match: Type.list([]),
							guards: [],
							body: (context) => {
								return Type.bitstring("\n");
							},
						},
						{
							match: Type.matchPlaceholder(),
							guards: [],
							body: (context) => {
								return Type.bitstring([
									Type.bitstringSegment(Type.string("    "), {
										type: "binary",
									}),
									Type.bitstringSegment(
										Elixir_Enum["map_join/3"](
											context.vars.trace_3,
											Type.bitstring("\n    "),
											Type.functionCapture(
												"Exception",
												"format_stacktrace_entry",
												1,
												[
													{
														params: (context) => [Type.variablePattern("$1")],
														guards: [],
														body: (context) => {
															return Elixir_Exception[
																"format_stacktrace_entry/1"
															](context.vars["$1"]);
														},
													},
												],
												context,
											),
										),
										{ type: "binary" },
									),
									Type.bitstringSegment(Type.string("\n"), { type: "binary" }),
								]);
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Exception",
	"format_stacktrace_entry",
	1,
	"public",
	[
		{
			params: (context) => [
				Type.tuple([
					Type.variablePattern("module_0"),
					Type.atom("__MODULE__"),
					Type.integer(0n),
					Type.variablePattern("location_1"),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(
						Elixir_Exception["format_location/1"](context.vars.location_1),
						{ type: "binary" },
					),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.module_0),
						{ type: "binary" },
					),
					Type.bitstringSegment(Type.string(" (module)"), { type: "binary" }),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([
					Type.matchPlaceholder(),
					Type.atom("__MODULE__"),
					Type.integer(1n),
					Type.variablePattern("location_1"),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(
						Elixir_Exception["format_location/1"](context.vars.location_1),
						{ type: "binary" },
					),
					Type.bitstringSegment(Type.string("(module)"), { type: "binary" }),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([
					Type.matchPlaceholder(),
					Type.atom("__FILE__"),
					Type.integer(1n),
					Type.variablePattern("location_1"),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(
						Elixir_Exception["format_location/1"](context.vars.location_1),
						{ type: "binary" },
					),
					Type.bitstringSegment(Type.string("(file)"), { type: "binary" }),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([
					Type.variablePattern("module_0"),
					Type.variablePattern("fun_1"),
					Type.variablePattern("arity_2"),
					Type.variablePattern("location_3"),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(
						Elixir_Exception["format_application/1"](context.vars.module_0),
						{ type: "binary" },
					),
					Type.bitstringSegment(
						Elixir_Exception["format_location/1"](context.vars.location_3),
						{ type: "binary" },
					),
					Type.bitstringSegment(
						Elixir_Exception["format_mfa/3"](
							context.vars.module_0,
							context.vars.fun_1,
							context.vars.arity_2,
						),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.tuple([
					Type.variablePattern("fun_0"),
					Type.variablePattern("arity_1"),
					Type.variablePattern("location_2"),
				]),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(
						Elixir_Exception["format_location/1"](context.vars.location_2),
						{ type: "binary" },
					),
					Type.bitstringSegment(
						Elixir_Exception["format_fa/2"](
							context.vars.fun_0,
							context.vars.arity_1,
						),
						{ type: "binary" },
					),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Exception",
	"format_start_child",
	3,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("child_0"),
				Type.variablePattern("reason_1"),
				Type.variablePattern("joiner_2"),
			],
			guards: [],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(
						Type.string("shutdown: failed to start child: "),
						{ type: "binary" },
					),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.child_0),
						{ type: "binary" },
					),
					Type.bitstringSegment(context.vars.joiner_2, { type: "binary" }),
					Type.bitstringSegment(Type.string("** (EXIT) "), { type: "binary" }),
					Type.bitstringSegment(
						Elixir_Exception["format_exit/2"](
							context.vars.reason_1,
							Type.bitstring([
								Type.bitstringSegment(context.vars.joiner_2, {
									type: "binary",
								}),
								Type.bitstringSegment(Type.string("    "), { type: "binary" }),
							]),
						),
						{ type: "binary" },
					),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction("Exception", "format_sup_data", 1, "private", [
	{
		params: (context) => [
			Type.tuple([Type.atom("invalid_type"), Type.variablePattern("type_0")]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("invalid type: "), {
					type: "binary",
				}),
				Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.type_0), {
					type: "binary",
				}),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("invalid_strategy"),
				Type.variablePattern("strategy_0"),
			]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("invalid strategy: "), {
					type: "binary",
				}),
				Type.bitstringSegment(
					Elixir_Kernel["inspect/1"](context.vars.strategy_0),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("invalid_intensity"),
				Type.variablePattern("intensity_0"),
			]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(
					Type.string("invalid max_restarts (intensity): "),
					{ type: "binary" },
				),
				Type.bitstringSegment(
					Elixir_Kernel["inspect/1"](context.vars.intensity_0),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("invalid_period"),
				Type.variablePattern("period_0"),
			]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("invalid max_seconds (period): "), {
					type: "binary",
				}),
				Type.bitstringSegment(
					Elixir_Kernel["inspect/1"](context.vars.period_0),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("invalid_max_children"),
				Type.variablePattern("max_children_0"),
			]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("invalid max_children: "), {
					type: "binary",
				}),
				Type.bitstringSegment(
					Elixir_Kernel["inspect/1"](context.vars.max_children_0),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("invalid_extra_arguments"),
				Type.variablePattern("extra_0"),
			]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("invalid extra_arguments: "), {
					type: "binary",
				}),
				Type.bitstringSegment(
					Elixir_Kernel["inspect/1"](context.vars.extra_0),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [Type.variablePattern("other_0")],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("got: "), { type: "binary" }),
				Type.bitstringSegment(
					Elixir_Kernel["inspect/1"](context.vars.other_0),
					{ type: "binary" },
				),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Exception", "format_sup_spec", 1, "private", [
	{
		params: (context) => [
			Type.tuple([
				Type.atom("duplicate_child_name"),
				Type.variablePattern("id_0"),
			]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(
					Type.string("more than one child specification has the id: "),
					{ type: "binary" },
				),
				Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.id_0), {
					type: "binary",
				}),
				Type.bitstringSegment(
					Type.string(
						".\nIf using maps as child specifications, make sure the :id keys are unique.\nIf using a module or {module, arg} as child, use Supervisor.child_spec/2 to change the :id, for example:\n\n    children = [\n      Supervisor.child_spec({MyWorker, arg}, id: :my_worker_1),\n      Supervisor.child_spec({MyWorker, arg}, id: :my_worker_2)\n    ]\n",
					),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("invalid_child_spec"),
				Type.variablePattern("child_spec_0"),
			]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("invalid child specification: "), {
					type: "binary",
				}),
				Type.bitstringSegment(
					Elixir_Kernel["inspect/1"](context.vars.child_spec_0),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("invalid_child_type"),
				Type.variablePattern("type_0"),
			]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("invalid child type: "), {
					type: "binary",
				}),
				Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.type_0), {
					type: "binary",
				}),
				Type.bitstringSegment(
					Type.string(". Must be :worker or :supervisor."),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([Type.atom("invalid_mfa"), Type.variablePattern("mfa_0")]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("invalid mfa: "), { type: "binary" }),
				Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.mfa_0), {
					type: "binary",
				}),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("invalid_restart_type"),
				Type.variablePattern("restart_0"),
			]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("invalid restart type: "), {
					type: "binary",
				}),
				Type.bitstringSegment(
					Elixir_Kernel["inspect/1"](context.vars.restart_0),
					{ type: "binary" },
				),
				Type.bitstringSegment(
					Type.string(". Must be :permanent, :transient or :temporary."),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("invalid_shutdown"),
				Type.variablePattern("shutdown_0"),
			]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("invalid shutdown: "), {
					type: "binary",
				}),
				Type.bitstringSegment(
					Elixir_Kernel["inspect/1"](context.vars.shutdown_0),
					{ type: "binary" },
				),
				Type.bitstringSegment(
					Type.string(". Must be an integer >= 0, :infinity or :brutal_kill."),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([Type.atom("invalid_module"), Type.variablePattern("mod_0")]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("invalid module: "), {
					type: "binary",
				}),
				Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.mod_0), {
					type: "binary",
				}),
				Type.bitstringSegment(Type.string(". Must be an atom."), {
					type: "binary",
				}),
			]);
		},
	},
	{
		params: (context) => [
			Type.tuple([
				Type.atom("invalid_modules"),
				Type.variablePattern("modules_0"),
			]),
		],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("invalid modules: "), {
					type: "binary",
				}),
				Type.bitstringSegment(
					Elixir_Kernel["inspect/1"](context.vars.modules_0),
					{ type: "binary" },
				),
				Type.bitstringSegment(
					Type.string(". Must be a list of atoms or :dynamic."),
					{ type: "binary" },
				),
			]);
		},
	},
	{
		params: (context) => [Type.variablePattern("other_0")],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("got: "), { type: "binary" }),
				Type.bitstringSegment(
					Elixir_Kernel["inspect/1"](context.vars.other_0),
					{ type: "binary" },
				),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Exception", "message", 1, "public", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("exception_1"),
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.variablePattern("module_0")],
				]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return Interpreter.try(
				(context) => {
					return Interpreter.callNamedFunction(
						context.vars.module_0,
						Type.atom("message"),
						Type.list([context.vars.exception_1]),
						context,
					);
				},
				[
					{
						variable: Type.variablePattern("caught_exception_2"),
						modules: [],
						body: (context) => {
							return Type.bitstring([
								Type.bitstringSegment(Type.string("got "), { type: "binary" }),
								Type.bitstringSegment(
									Elixir_Kernel["inspect/1"](
										Interpreter.dotOperator(
											context.vars.caught_exception_2,
											Type.atom("__struct__"),
										),
									),
									{ type: "binary" },
								),
								Type.bitstringSegment(Type.string(" with message "), {
									type: "binary",
								}),
								Type.bitstringSegment(
									Elixir_Kernel["inspect/1"](
										Elixir_Exception["message/1"](
											context.vars.caught_exception_2,
										),
									),
									{ type: "binary" },
								),
								Type.bitstringSegment(
									Type.string(" while retrieving Exception.message/1 "),
									{ type: "binary" },
								),
								Type.bitstringSegment(Type.string("for "), { type: "binary" }),
								Type.bitstringSegment(
									Elixir_Kernel["inspect/1"](context.vars.exception_1),
									{ type: "binary" },
								),
								Type.bitstringSegment(Type.string(". Stacktrace:\n"), {
									type: "binary",
								}),
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](
										Elixir_Exception["format_stacktrace/1"](
											Type.matchPlaceholder(),
										),
									),
									{ type: "binary" },
								),
							]);
						},
					},
				],
				[],
				[],
				null,
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Exception", "normalize", 3, "public", [
	{
		params: (context) => [
			Type.atom("error"),
			Interpreter.matchOperator(
				Type.variablePattern("payload_0"),
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.matchPlaceholder()],
				]),
				context,
			),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return context.vars.payload_0;
		},
	},
	{
		params: (context) => [
			Type.atom("error"),
			Type.variablePattern("payload_0"),
			Type.variablePattern("stacktrace_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_ErlangError["normalize/2"](
				context.vars.payload_0,
				context.vars.stacktrace_1,
			);
		},
	},
	{
		params: (context) => [
			Type.matchPlaceholder(),
			Type.variablePattern("payload_1"),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return context.vars.payload_1;
		},
	},
]);

Interpreter.defineElixirFunction("Exqlite.Query", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Exqlite.Query")],
				[Type.atom("command"), Type.atom("nil")],
				[Type.atom("name"), Type.atom("nil")],
				[Type.atom("ref"), Type.atom("nil")],
				[Type.atom("statement"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Exqlite.Query", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Exqlite.Query")],
					[Type.atom("command"), Type.atom("nil")],
					[Type.atom("name"), Type.atom("nil")],
					[Type.atom("ref"), Type.atom("nil")],
					[Type.atom("statement"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Exqlite.Stream", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Exqlite.Stream")],
				[Type.atom("conn"), Type.atom("nil")],
				[Type.atom("options"), Type.atom("nil")],
				[Type.atom("params"), Type.atom("nil")],
				[Type.atom("query"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Exqlite.Stream", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Exqlite.Stream")],
					[Type.atom("conn"), Type.atom("nil")],
					[Type.atom("options"), Type.atom("nil")],
					[Type.atom("params"), Type.atom("nil")],
					[Type.atom("query"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("File.Error", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.File.Error")],
				[Type.atom("action"), Type.bitstring("")],
				[Type.atom("path"), Type.atom("nil")],
				[Type.atom("reason"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("File.Error", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.File.Error")],
					[Type.atom("action"), Type.bitstring("")],
					[Type.atom("path"), Type.atom("nil")],
					[Type.atom("reason"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("File.Error", "exception", 1, "public", [
	{
		params: (context) => [Type.variablePattern("args_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.args_0)],
		body: (context) => {
			return Elixir_Kernel["struct!/2"](
				Type.atom("Elixir.File.Error"),
				context.vars.args_0,
			);
		},
	},
]);

Interpreter.defineElixirFunction("File.Stream", "__open__", 2, "public", [
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.File.Stream")],
				[Type.atom("node"), Type.variablePattern("node_1")],
				[Type.atom("path"), Type.variablePattern("path_0")],
			]),
			Type.variablePattern("modes_2"),
		],
		guards: [
			(context) => Erlang["==/2"](context.vars.node_1, Erlang["node/0"]()),
		],
		body: (context) => {
			return Erlang_File["open/2"](context.vars.path_0, context.vars.modes_2);
		},
	},
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.File.Stream")],
				[Type.atom("node"), Type.variablePattern("node_1")],
				[Type.atom("path"), Type.variablePattern("path_0")],
			]),
			Type.variablePattern("modes_2"),
		],
		guards: [],
		body: (context) => {
			return Erlang_Erpc["call/4"](
				context.vars.node_1,
				Type.atom("file_io_server"),
				Type.atom("start"),
				Type.list([
					Erlang["self/0"](),
					context.vars.path_0,
					Elixir_List["delete/2"](context.vars.modes_2, Type.atom("raw")),
				]),
			);
		},
	},
]);

Interpreter.defineElixirFunction("File.Stream", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.File.Stream")],
				[Type.atom("line_or_bytes"), Type.atom("line")],
				[Type.atom("modes"), Type.list([])],
				[Type.atom("node"), Type.atom("nil")],
				[Type.atom("path"), Type.atom("nil")],
				[Type.atom("raw"), Type.atom("true")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("File.Stream", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.File.Stream")],
					[Type.atom("line_or_bytes"), Type.atom("line")],
					[Type.atom("modes"), Type.list([])],
					[Type.atom("node"), Type.atom("nil")],
					[Type.atom("path"), Type.atom("nil")],
					[Type.atom("raw"), Type.atom("true")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"FunctionClauseError",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.FunctionClauseError")],
					[Type.atom("args"), Type.atom("nil")],
					[Type.atom("arity"), Type.atom("nil")],
					[Type.atom("clauses"), Type.atom("nil")],
					[Type.atom("function"), Type.atom("nil")],
					[Type.atom("kind"), Type.atom("nil")],
					[Type.atom("module"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"FunctionClauseError",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[Type.atom("__exception__"), Type.atom("true")],
						[Type.atom("__struct__"), Type.atom("Elixir.FunctionClauseError")],
						[Type.atom("args"), Type.atom("nil")],
						[Type.atom("arity"), Type.atom("nil")],
						[Type.atom("clauses"), Type.atom("nil")],
						[Type.atom("function"), Type.atom("nil")],
						[Type.atom("kind"), Type.atom("nil")],
						[Type.atom("module"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("GenEvent.Stream", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.GenEvent.Stream")],
				[Type.atom("manager"), Type.atom("nil")],
				[Type.atom("timeout"), Type.atom("infinity")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("GenEvent.Stream", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.GenEvent.Stream")],
					[Type.atom("manager"), Type.atom("nil")],
					[Type.atom("timeout"), Type.atom("infinity")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("HashDict", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.HashDict")],
				[
					Type.atom("root"),
					Type.tuple([
						Type.list([]),
						Type.list([]),
						Type.list([]),
						Type.list([]),
						Type.list([]),
						Type.list([]),
						Type.list([]),
						Type.list([]),
					]),
				],
				[Type.atom("size"), Type.integer(0n)],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("HashDict", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.HashDict")],
					[
						Type.atom("root"),
						Type.tuple([
							Type.list([]),
							Type.list([]),
							Type.list([]),
							Type.list([]),
							Type.list([]),
							Type.list([]),
							Type.list([]),
							Type.list([]),
						]),
					],
					[Type.atom("size"), Type.integer(0n)],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("HashSet", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.HashSet")],
				[
					Type.atom("root"),
					Type.tuple([
						Type.list([]),
						Type.list([]),
						Type.list([]),
						Type.list([]),
						Type.list([]),
						Type.list([]),
						Type.list([]),
						Type.list([]),
					]),
				],
				[Type.atom("size"), Type.integer(0n)],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("HashSet", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.HashSet")],
					[
						Type.atom("root"),
						Type.tuple([
							Type.list([]),
							Type.list([]),
							Type.list([]),
							Type.list([]),
							Type.list([]),
							Type.list([]),
							Type.list([]),
							Type.list([]),
						]),
					],
					[Type.atom("size"), Type.integer(0n)],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Hologram.Component",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Hologram.Component")],
					[Type.atom("emitted_context"), Type.map([])],
					[Type.atom("next_action"), Type.atom("nil")],
					[Type.atom("next_command"), Type.atom("nil")],
					[Type.atom("next_page"), Type.atom("nil")],
					[Type.atom("state"), Type.map([])],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Hologram.Component",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Hologram.Component")],
						[Type.atom("emitted_context"), Type.map([])],
						[Type.atom("next_action"), Type.atom("nil")],
						[Type.atom("next_command"), Type.atom("nil")],
						[Type.atom("next_page"), Type.atom("nil")],
						[Type.atom("state"), Type.map([])],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Hologram.Component.Action",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.Hologram.Component.Action"),
					],
					[Type.atom("delay"), Type.integer(0n)],
					[Type.atom("name"), Type.atom("nil")],
					[Type.atom("params"), Type.map([])],
					[Type.atom("target"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Hologram.Component.Action",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.Hologram.Component.Action"),
						],
						[Type.atom("delay"), Type.integer(0n)],
						[Type.atom("name"), Type.atom("nil")],
						[Type.atom("params"), Type.map([])],
						[Type.atom("target"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Hologram.Component.Command",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.Hologram.Component.Command"),
					],
					[Type.atom("name"), Type.atom("nil")],
					[Type.atom("params"), Type.map([])],
					[Type.atom("target"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Hologram.Component.Command",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.Hologram.Component.Command"),
						],
						[Type.atom("name"), Type.atom("nil")],
						[Type.atom("params"), Type.map([])],
						[Type.atom("target"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Hologram.Reflection",
	"module_name",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("module_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["join/2"](
					Elixir_Module["split/1"](context.vars.module_0),
					Type.bitstring("."),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Hologram.Router.Helpers",
	"encode_param_value",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("value_0")],
			guards: [],
			body: (context) => {
				return Elixir_URI["encode/2"](
					Elixir_String_Chars["to_string/1"](context.vars.value_0),
					Type.functionCapture(
						"URI",
						"char_unreserved?",
						1,
						[
							{
								params: (context) => [Type.variablePattern("$1")],
								guards: [],
								body: (context) => {
									return Elixir_URI["char_unreserved?/1"](context.vars["$1"]);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Hologram.Router.Helpers",
	"ensure_no_extra_params!",
	2,
	"private",
	[
		{
			params: (context) => [Type.list([]), Type.matchPlaceholder()],
			guards: [],
			body: (context) => {
				return Type.atom("ok");
			},
		},
		{
			params: (context) => [
				Type.variablePattern("remaining_params_0"),
				Type.variablePattern("page_module_1"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Erlang["hd/1"](context.vars.remaining_params_0),
					Type.tuple([Type.variablePattern("key_2"), Type.matchPlaceholder()]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Erlang["error/1"](
					Elixir_ArgumentError["exception/1"](
						Type.bitstring([
							Type.bitstringSegment(Type.string('page "'), { type: "binary" }),
							Type.bitstringSegment(
								Elixir_String_Chars["to_string/1"](
									Elixir_Hologram_Reflection["module_name/1"](
										context.vars.page_module_1,
									),
								),
								{ type: "binary" },
							),
							Type.bitstringSegment(Type.string('" doesn\'t expect "'), {
								type: "binary",
							}),
							Type.bitstringSegment(
								Elixir_String_Chars["to_string/1"](context.vars.key_2),
								{ type: "binary" },
							),
							Type.bitstringSegment(Type.string('" param'), { type: "binary" }),
						]),
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Hologram.Router.Helpers",
	"ensure_param_exists!",
	3,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("params_0"),
				Type.variablePattern("key_1"),
				Type.variablePattern("page_module_2"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Erlang["not/1"](
						Elixir_Keyword["has_key?/2"](
							context.vars.params_0,
							context.vars.key_1,
						),
					),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Erlang["error/1"](
									Elixir_ArgumentError["exception/1"](
										Type.bitstring([
											Type.bitstringSegment(Type.string('page "'), {
												type: "binary",
											}),
											Type.bitstringSegment(
												Elixir_String_Chars["to_string/1"](
													Elixir_Hologram_Reflection["module_name/1"](
														context.vars.page_module_2,
													),
												),
												{ type: "binary" },
											),
											Type.bitstringSegment(Type.string('" expects "'), {
												type: "binary",
											}),
											Type.bitstringSegment(
												Elixir_String_Chars["to_string/1"](context.vars.key_1),
												{ type: "binary" },
											),
											Type.bitstringSegment(Type.string('" param'), {
												type: "binary",
											}),
										]),
									),
								);
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Hologram.Router.Helpers",
	"page_path",
	1,
	"public",
	[
		{
			params: (context) => [
				Type.tuple([
					Type.variablePattern("module_0"),
					Type.variablePattern("params_1"),
				]),
			],
			guards: [],
			body: (context) => {
				return Elixir_Hologram_Router_Helpers["page_path/2"](
					context.vars.module_0,
					context.vars.params_1,
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("module_0")],
			guards: [],
			body: (context) => {
				return Elixir_Hologram_Router_Helpers["page_path/2"](
					context.vars.module_0,
					Type.list([]),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Hologram.Router.Helpers",
	"page_path",
	2,
	"public",
	[
		{
			params: (context) => [
				Type.variablePattern("page_module_0"),
				Type.variablePattern("params_1"),
			],
			guards: [(context) => Erlang["is_map/1"](context.vars.params_1)],
			body: (context) => {
				return Elixir_Hologram_Router_Helpers["page_path/2"](
					context.vars.page_module_0,
					Erlang_Maps["to_list/1"](context.vars.params_1),
				);
			},
		},
		{
			params: (context) => [
				Type.variablePattern("page_module_0"),
				Type.variablePattern("params_1"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Interpreter.callNamedFunction(
						context.vars.page_module_0,
						Type.atom("__params__"),
						Type.list([]),
						context,
					),
					Type.variablePattern("required_params_2"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Type.tuple([
						context.vars.params_1,
						Interpreter.callNamedFunction(
							context.vars.page_module_0,
							Type.atom("__route__"),
							Type.list([]),
							context,
						),
					]),
					Type.variablePattern("initial_acc_3"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Elixir_Enum["reduce/3"](
						context.vars.required_params_2,
						context.vars.initial_acc_3,
						Type.anonymousFunction(
							2,
							[
								{
									params: (context) => [
										Type.variablePattern("$23"),
										Type.variablePattern("$24"),
									],
									guards: [],
									body: (context) => {
										return Elixir_Hologram_Router_Helpers["process_param/4"](
											context.vars["$23"],
											context.vars["$24"],
											context.vars.page_module_0,
											context.vars.params_1,
										);
									},
								},
							],
							context,
						),
					),
					Type.tuple([
						Type.variablePattern("remaining_params_6"),
						Type.variablePattern("path_7"),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Elixir_Hologram_Router_Helpers["ensure_no_extra_params!/2"](
					context.vars.remaining_params_6,
					context.vars.page_module_0,
				);
				return context.vars.path_7;
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Hologram.Router.Helpers",
	"process_param",
	4,
	"private",
	[
		{
			params: (context) => [
				Type.tuple([
					Type.variablePattern("key_0"),
					Type.matchPlaceholder(),
					Type.matchPlaceholder(),
				]),
				Type.tuple([
					Type.variablePattern("params_acc_3"),
					Type.variablePattern("path_acc_4"),
				]),
				Type.variablePattern("page_module_5"),
				Type.variablePattern("original_params_6"),
			],
			guards: [],
			body: (context) => {
				Elixir_Hologram_Router_Helpers["ensure_param_exists!/3"](
					context.vars.params_acc_3,
					context.vars.key_0,
					context.vars.page_module_5,
				);
				Interpreter.matchOperator(
					Elixir_Hologram_Router_Helpers["encode_param_value/1"](
						Elixir_Access["get/2"](
							context.vars.original_params_6,
							context.vars.key_0,
						),
					),
					Type.variablePattern("encoded_value_7"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Elixir_String["replace/3"](
						context.vars.path_acc_4,
						Type.bitstring([
							Type.bitstringSegment(Type.string(":"), { type: "binary" }),
							Type.bitstringSegment(
								Elixir_String_Chars["to_string/1"](context.vars.key_0),
								{ type: "binary" },
							),
						]),
						context.vars.encoded_value_7,
					),
					Type.variablePattern("new_path_8"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Elixir_Keyword["drop/2"](
						context.vars.params_acc_3,
						Type.list([context.vars.key_0]),
					),
					Type.variablePattern("new_params_9"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Type.tuple([context.vars.new_params_9, context.vars.new_path_8]);
			},
		},
	],
);

Interpreter.defineElixirFunction("IO", "binread", 2, "public", [
	{
		params: (context) => [Type.variablePattern("device_0"), Type.atom("all")],
		guards: [],
		body: (context) => {
			Elixir_IO["warn/1"](
				Type.bitstring(
					"IO.binread(device, :all) is deprecated, use IO.binread(device, :eof) instead",
				),
			);
			return Interpreter.with();
		},
	},
	{
		params: (context) => [Type.variablePattern("device_0"), Type.atom("eof")],
		guards: [],
		body: (context) => {
			return Elixir_IO["binread_eof/2"](
				Elixir_IO["map_dev/1"](context.vars.device_0),
				Type.bitstring(""),
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("device_0"), Type.atom("line")],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Erlang_File["read_line/1"](
					Elixir_IO["map_dev/1"](context.vars.device_0),
				),
				[
					{
						match: Type.tuple([
							Type.atom("ok"),
							Type.variablePattern("data_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.data_1;
						},
					},
					{
						match: Type.variablePattern("other_2"),
						guards: [],
						body: (context) => {
							return context.vars.other_2;
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("device_0"),
			Type.variablePattern("count_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_integer/1"](context.vars.count_1),
					(context) => Erlang[">=/2"](context.vars.count_1, Type.integer(0n)),
					context,
				),
		],
		body: (context) => {
			return Interpreter.case(
				Erlang_File["read/2"](
					Elixir_IO["map_dev/1"](context.vars.device_0),
					context.vars.count_1,
				),
				[
					{
						match: Type.tuple([
							Type.atom("ok"),
							Type.variablePattern("data_2"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.data_2;
						},
					},
					{
						match: Type.variablePattern("other_3"),
						guards: [],
						body: (context) => {
							return context.vars.other_3;
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("IO", "binread_eof", 2, "private", [
	{
		params: (context) => [
			Type.variablePattern("mapped_dev_0"),
			Type.variablePattern("acc_1"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Erlang_File["read/2"](context.vars.mapped_dev_0, Type.integer(4096n)),
				[
					{
						match: Type.tuple([
							Type.atom("ok"),
							Type.variablePattern("data_2"),
						]),
						guards: [],
						body: (context) => {
							return Elixir_IO["binread_eof/2"](
								context.vars.mapped_dev_0,
								Type.bitstring([
									Type.bitstringSegment(context.vars.acc_1, { type: "binary" }),
									Type.bitstringSegment(context.vars.data_2, {
										type: "binary",
									}),
								]),
							);
						},
					},
					{
						match: Type.atom("eof"),
						guards: [],
						body: (context) => {
							return Interpreter.case(
								Erlang["==/2"](context.vars.acc_1, Type.bitstring("")),
								[
									{
										match: Type.atom("false"),
										guards: [],
										body: (context) => {
											return context.vars.acc_1;
										},
									},
									{
										match: Type.atom("true"),
										guards: [],
										body: (context) => {
											return Type.atom("eof");
										},
									},
								],
								context,
							);
						},
					},
					{
						match: Type.variablePattern("other_3"),
						guards: [],
						body: (context) => {
							return context.vars.other_3;
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("IO", "binwrite", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("device_0"),
			Type.variablePattern("iodata_1"),
		],
		guards: [
			(context) =>
				Erlang["orelse/2"](
					(context) => Erlang["is_list/1"](context.vars.iodata_1),
					(context) => Erlang["is_binary/1"](context.vars.iodata_1),
					context,
				),
		],
		body: (context) => {
			return Interpreter.with();
		},
	},
]);

Interpreter.defineElixirFunction("IO", "each_binstream", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("device_0"),
			Type.variablePattern("line_or_chars_1"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Elixir_IO["binread/2"](
					context.vars.device_0,
					context.vars.line_or_chars_1,
				),
				[
					{
						match: Type.atom("eof"),
						guards: [],
						body: (context) => {
							return Type.tuple([Type.atom("halt"), context.vars.device_0]);
						},
					},
					{
						match: Type.tuple([
							Type.atom("error"),
							Type.variablePattern("reason_2"),
						]),
						guards: [],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_IO_StreamError["exception/1"](
									Type.list([
										Type.tuple([Type.atom("reason"), context.vars.reason_2]),
									]),
								),
							);
						},
					},
					{
						match: Type.variablePattern("data_3"),
						guards: [],
						body: (context) => {
							return Type.tuple([
								Type.list([context.vars.data_3]),
								context.vars.device_0,
							]);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("IO", "each_stream", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("device_0"),
			Type.variablePattern("line_or_codepoints_1"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Elixir_IO["read/2"](
					context.vars.device_0,
					context.vars.line_or_codepoints_1,
				),
				[
					{
						match: Type.atom("eof"),
						guards: [],
						body: (context) => {
							return Type.tuple([Type.atom("halt"), context.vars.device_0]);
						},
					},
					{
						match: Type.tuple([
							Type.atom("error"),
							Type.variablePattern("reason_2"),
						]),
						guards: [],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_IO_StreamError["exception/1"](
									Type.list([
										Type.tuple([Type.atom("reason"), context.vars.reason_2]),
									]),
								),
							);
						},
					},
					{
						match: Type.variablePattern("data_3"),
						guards: [],
						body: (context) => {
							return Type.tuple([
								Type.list([context.vars.data_3]),
								context.vars.device_0,
							]);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("IO", "getn", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("device_0"),
			Type.variablePattern("prompt_1"),
			Type.atom("eof"),
		],
		guards: [],
		body: (context) => {
			return Elixir_IO["getn_eof/3"](
				Elixir_IO["map_dev/1"](context.vars.device_0),
				Elixir_IO["to_chardata/1"](context.vars.prompt_1),
				Type.list([]),
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("device_0"),
			Type.variablePattern("prompt_1"),
			Type.variablePattern("count_2"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_integer/1"](context.vars.count_2),
					(context) => Erlang[">/2"](context.vars.count_2, Type.integer(0n)),
					context,
				),
		],
		body: (context) => {
			return Erlang_Io["get_chars/3"](
				Elixir_IO["map_dev/1"](context.vars.device_0),
				Elixir_IO["to_chardata/1"](context.vars.prompt_1),
				context.vars.count_2,
			);
		},
	},
]);

Interpreter.defineElixirFunction("IO", "getn_eof", 3, "private", [
	{
		params: (context) => [
			Type.variablePattern("device_0"),
			Type.variablePattern("prompt_1"),
			Type.variablePattern("acc_2"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Erlang_Io["get_line/2"](context.vars.device_0, context.vars.prompt_1),
				[
					{
						match: Type.variablePattern("line_3"),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) => Erlang["is_binary/1"](context.vars.line_3),
									(context) => Erlang["is_list/1"](context.vars.line_3),
									context,
								),
						],
						body: (context) => {
							return Elixir_IO["getn_eof/3"](
								context.vars.device_0,
								Type.list([]),
								Interpreter.consOperator(
									context.vars.line_3,
									context.vars.acc_2,
								),
							);
						},
					},
					{
						match: Type.atom("eof"),
						guards: [],
						body: (context) => {
							return Elixir_IO["wrap_eof/1"](
								Erlang_Lists["reverse/1"](context.vars.acc_2),
							);
						},
					},
					{
						match: Type.variablePattern("other_4"),
						guards: [],
						body: (context) => {
							return context.vars.other_4;
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("IO", "map_dev", 1, "private", [
	{
		params: (context) => [Type.atom("stdio")],
		guards: [],
		body: (context) => {
			return Type.atom("standard_io");
		},
	},
	{
		params: (context) => [Type.atom("stderr")],
		guards: [],
		body: (context) => {
			return Type.atom("standard_error");
		},
	},
	{
		params: (context) => [Type.variablePattern("other_0")],
		guards: [
			(context) =>
				Erlang["orelse/2"](
					(context) =>
						Erlang["orelse/2"](
							(context) => Erlang["is_atom/1"](context.vars.other_0),
							(context) => Erlang["is_pid/1"](context.vars.other_0),
							context,
						),
					(context) => Erlang["is_tuple/1"](context.vars.other_0),
					context,
				),
		],
		body: (context) => {
			return context.vars.other_0;
		},
	},
]);

Interpreter.defineElixirFunction("IO", "read", 2, "public", [
	{
		params: (context) => [Type.variablePattern("device_0"), Type.atom("all")],
		guards: [],
		body: (context) => {
			Elixir_IO["warn/1"](
				Type.bitstring(
					"IO.read(device, :all) is deprecated, use IO.read(device, :eof) instead",
				),
			);
			return Interpreter.with();
		},
	},
	{
		params: (context) => [Type.variablePattern("device_0"), Type.atom("eof")],
		guards: [],
		body: (context) => {
			return Elixir_IO["getn/3"](
				context.vars.device_0,
				Type.list([]),
				Type.atom("eof"),
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("device_0"), Type.atom("line")],
		guards: [],
		body: (context) => {
			return Erlang_Io["get_line/2"](
				Elixir_IO["map_dev/1"](context.vars.device_0),
				Type.list([]),
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("device_0"),
			Type.variablePattern("count_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_integer/1"](context.vars.count_1),
					(context) => Erlang[">=/2"](context.vars.count_1, Type.integer(0n)),
					context,
				),
		],
		body: (context) => {
			return Erlang_Io["get_chars/3"](
				Elixir_IO["map_dev/1"](context.vars.device_0),
				Type.list([]),
				context.vars.count_1,
			);
		},
	},
]);

Interpreter.defineElixirFunction("IO", "to_chardata", 1, "private", [
	{
		params: (context) => [Type.variablePattern("list_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.list_0)],
		body: (context) => {
			return context.vars.list_0;
		},
	},
	{
		params: (context) => [Type.variablePattern("other_0")],
		guards: [],
		body: (context) => {
			return Elixir_String_Chars["to_string/1"](context.vars.other_0);
		},
	},
]);

Interpreter.defineElixirFunction("IO", "wrap_eof", 1, "private", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("acc_1"),
				Type.consPattern(Type.variablePattern("h_0"), Type.matchPlaceholder()),
				context,
			),
		],
		guards: [(context) => Erlang["is_binary/1"](context.vars.h_0)],
		body: (context) => {
			return Erlang["iolist_to_binary/1"](context.vars.acc_1);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("acc_1"),
				Type.consPattern(Type.variablePattern("h_0"), Type.matchPlaceholder()),
				context,
			),
		],
		guards: [(context) => Erlang["is_list/1"](context.vars.h_0)],
		body: (context) => {
			return Erlang_Lists["flatten/1"](context.vars.acc_1);
		},
	},
	{
		params: (context) => [Type.list([])],
		guards: [],
		body: (context) => {
			return Type.atom("eof");
		},
	},
]);

Interpreter.defineElixirFunction("IO", "write", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("device_0"),
			Type.variablePattern("chardata_1"),
		],
		guards: [],
		body: (context) => {
			return Erlang_Io["put_chars/2"](
				Elixir_IO["map_dev/1"](context.vars.device_0),
				Elixir_IO["to_chardata/1"](context.vars.chardata_1),
			);
		},
	},
]);

Interpreter.defineElixirFunction("IO.ANSI", "light_black", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("["), { type: "binary" }),
				Type.bitstringSegment(
					Elixir_String_Chars["to_string/1"](Type.integer(90n)),
					{ type: "binary" },
				),
				Type.bitstringSegment(Type.string("m"), { type: "binary" }),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("IO.ANSI", "reset", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("["), { type: "binary" }),
				Type.bitstringSegment(
					Elixir_String_Chars["to_string/1"](Type.integer(0n)),
					{ type: "binary" },
				),
				Type.bitstringSegment(Type.string("m"), { type: "binary" }),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("IO.Stream", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.IO.Stream")],
				[Type.atom("device"), Type.atom("nil")],
				[Type.atom("line_or_bytes"), Type.atom("line")],
				[Type.atom("raw"), Type.atom("true")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("IO.Stream", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.IO.Stream")],
					[Type.atom("device"), Type.atom("nil")],
					[Type.atom("line_or_bytes"), Type.atom("line")],
					[Type.atom("raw"), Type.atom("true")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("IO.StreamError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.IO.StreamError")],
				[Type.atom("reason"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("IO.StreamError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.IO.StreamError")],
					[Type.atom("reason"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("IO.StreamError", "exception", 1, "public", [
	{
		params: (context) => [Type.variablePattern("args_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.args_0)],
		body: (context) => {
			return Elixir_Kernel["struct!/2"](
				Type.atom("Elixir.IO.StreamError"),
				context.vars.args_0,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Jason.OrderedObject",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Jason.OrderedObject")],
					[Type.atom("values"), Type.list([])],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Jason.OrderedObject",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Jason.OrderedObject")],
						[Type.atom("values"), Type.list([])],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Kernel", "struct", 3, "private", [
	{
		params: (context) => [
			Type.variablePattern("struct_0"),
			Type.list([]),
			Type.matchPlaceholder(),
		],
		guards: [(context) => Erlang["is_atom/1"](context.vars.struct_0)],
		body: (context) => {
			return Elixir_Kernel["validate_struct!/3"](
				Interpreter.callNamedFunction(
					context.vars.struct_0,
					Type.atom("__struct__"),
					Type.list([]),
					context,
				),
				context.vars.struct_0,
				Type.integer(0n),
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("struct_0"),
			Type.variablePattern("fields_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [(context) => Erlang["is_atom/1"](context.vars.struct_0)],
		body: (context) => {
			return Elixir_Kernel["struct/3"](
				Elixir_Kernel["validate_struct!/3"](
					Interpreter.callNamedFunction(
						context.vars.struct_0,
						Type.atom("__struct__"),
						Type.list([]),
						context,
					),
					context.vars.struct_0,
					Type.integer(0n),
				),
				context.vars.fields_1,
				context.vars.fun_2,
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("struct_0"),
				Type.map([[Type.atom("__struct__"), Type.matchPlaceholder()]]),
				context,
			),
			Type.list([]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return context.vars.struct_0;
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("struct_0"),
				Type.map([[Type.atom("__struct__"), Type.matchPlaceholder()]]),
				context,
			),
			Type.variablePattern("fields_1"),
			Type.variablePattern("fun_2"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.fields_1,
				context.vars.struct_0,
				context.vars.fun_2,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Kernel", "struct!", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("struct_0"),
			Type.variablePattern("fields_1"),
		],
		guards: [(context) => Erlang["is_atom/1"](context.vars.struct_0)],
		body: (context) => {
			return Elixir_Kernel["validate_struct!/3"](
				Interpreter.callNamedFunction(
					context.vars.struct_0,
					Type.atom("__struct__"),
					Type.list([context.vars.fields_1]),
					context,
				),
				context.vars.struct_0,
				Type.integer(1n),
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("struct_0"),
			Type.variablePattern("fields_1"),
		],
		guards: [(context) => Erlang["is_map/1"](context.vars.struct_0)],
		body: (context) => {
			return Elixir_Kernel["struct/3"](
				context.vars.struct_0,
				context.vars.fields_1,
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([Type.atom("__struct__"), Type.matchPlaceholder()]),
								Type.variablePattern("acc_2"),
							],
							guards: [],
							body: (context) => {
								return context.vars.acc_2;
							},
						},
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_3"),
									Type.variablePattern("val_4"),
								]),
								Type.variablePattern("acc_5"),
							],
							guards: [],
							body: (context) => {
								return Erlang_Maps["update/3"](
									context.vars.key_3,
									context.vars.val_4,
									context.vars.acc_5,
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Kernel", "validate_struct!", 3, "private", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("struct_1"),
				Type.map([[Type.atom("__struct__"), Type.variablePattern("module_0")]]),
				context,
			),
			Type.variablePattern("module_0"),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return context.vars.struct_1;
		},
	},
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.variablePattern("struct_name_0")],
			]),
			Type.variablePattern("module_1"),
			Type.variablePattern("arity_2"),
		],
		guards: [(context) => Erlang["is_atom/1"](context.vars.struct_name_0)],
		body: (context) => {
			Interpreter.matchOperator(
				Type.bitstring([
					Type.bitstringSegment(
						Type.string("expected struct name returned by "),
						{ type: "binary" },
					),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.module_1),
						{ type: "binary" },
					),
					Type.bitstringSegment(Type.string(".__struct__/"), {
						type: "binary",
					}),
					Type.bitstringSegment(
						Elixir_String_Chars["to_string/1"](context.vars.arity_2),
						{ type: "binary" },
					),
					Type.bitstringSegment(Type.string(" to be "), { type: "binary" }),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.module_1),
						{ type: "binary" },
					),
					Type.bitstringSegment(Type.string(", got: "), { type: "binary" }),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.struct_name_0),
						{ type: "binary" },
					),
				]),
				Type.variablePattern("error_message_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Erlang["error/1"](
				Elixir_ArgumentError["exception/1"](context.vars.error_message_3),
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("expr_0"),
			Type.variablePattern("module_1"),
			Type.variablePattern("arity_2"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Type.bitstring([
					Type.bitstringSegment(Type.string("expected "), { type: "binary" }),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.module_1),
						{ type: "binary" },
					),
					Type.bitstringSegment(Type.string(".__struct__/"), {
						type: "binary",
					}),
					Type.bitstringSegment(
						Elixir_String_Chars["to_string/1"](context.vars.arity_2),
						{ type: "binary" },
					),
					Type.bitstringSegment(
						Type.string(" to return a map with a :__struct__ "),
						{ type: "binary" },
					),
					Type.bitstringSegment(
						Type.string("key that holds the name of the struct (atom), got: "),
						{ type: "binary" },
					),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.expr_0),
						{ type: "binary" },
					),
				]),
				Type.variablePattern("error_message_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Erlang["error/1"](
				Elixir_ArgumentError["exception/1"](context.vars.error_message_3),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Kernel.ErrorHandler",
	"ensure_compiled",
	4,
	"public",
	[
		{
			params: (context) => [
				Type.atom("nil"),
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
				Type.matchPlaceholder(),
			],
			guards: [],
			body: (context) => {
				return Type.atom("not_found");
			},
		},
		{
			params: (context) => [
				Type.variablePattern("module_0"),
				Type.variablePattern("kind_1"),
				Type.variablePattern("deadlock_2"),
				Type.variablePattern("position_3"),
			],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					Erlang["get/1"](Type.atom("elixir_compiler_info")),
					Type.tuple([
						Type.variablePattern("compiler_pid_4"),
						Type.variablePattern("file_pid_5"),
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Erlang["make_ref/0"](),
					Type.variablePattern("ref_6"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Interpreter.matchOperator(
					Erlang_Elixir_Module["compiler_modules/0"](),
					Type.variablePattern("modules_7"),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				Erlang["send/2"](
					context.vars.compiler_pid_4,
					Type.tuple([
						Type.atom("waiting"),
						context.vars.kind_1,
						Erlang["self/0"](),
						context.vars.ref_6,
						context.vars.file_pid_5,
						context.vars.position_3,
						context.vars.module_0,
						context.vars.modules_7,
						context.vars.deadlock_2,
					]),
				);
				Erlang["garbage_collect/1"](Erlang["self/0"]());
				globalThis.Hologram.return = Elixir_Kernel_ErrorHandler["receive/1"](
					Type.list([
						Type.tuple([
							Type.atom("do"),
							Type.list([
								{
									match: Type.tuple([
										context.vars.ref_6,
										Type.tuple([
											Type.atom("loading"),
											Type.variablePattern("pid_8"),
										]),
									]),
									guards: [],
									body: (context) => {
										Interpreter.matchOperator(
											Erlang["monitor/2"](
												Type.atom("process"),
												context.vars.pid_8,
											),
											Type.variablePattern("ref_9"),
											context,
										);
										Interpreter.updateVarsToMatchedValues(context);
										return Elixir_Kernel_ErrorHandler["receive/1"](
											Type.list([
												Type.tuple([
													Type.atom("do"),
													Type.list([
														{
															match: Type.tuple([
																Type.atom("DOWN"),
																context.vars.ref_9,
																Type.matchPlaceholder(),
																Type.matchPlaceholder(),
																Type.matchPlaceholder(),
															]),
															guards: [],
															body: (context) => {
																return Type.atom("found");
															},
														},
													]),
												]),
											]),
										);
									},
								},
								{
									match: Type.tuple([
										context.vars.ref_6,
										Type.variablePattern("value_10"),
									]),
									guards: [],
									body: (context) => {
										return context.vars.value_10;
									},
								},
							]),
						]),
					]),
				);
				Interpreter.updateVarsToMatchedValues(context);
				return globalThis.Hologram.return;
			},
		},
	],
);

Interpreter.defineElixirFunction("Kernel.Utils", "raise", 1, "public", [
	{
		params: (context) => [Type.variablePattern("msg_0")],
		guards: [(context) => Erlang["is_binary/1"](context.vars.msg_0)],
		body: (context) => {
			return Elixir_RuntimeError["exception/1"](context.vars.msg_0);
		},
	},
	{
		params: (context) => [Type.variablePattern("module_0")],
		guards: [(context) => Erlang["is_atom/1"](context.vars.module_0)],
		body: (context) => {
			return Interpreter.callNamedFunction(
				context.vars.module_0,
				Type.atom("exception"),
				Type.list([Type.list([])]),
				context,
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("exception_0"),
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.matchPlaceholder()],
				]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return context.vars.exception_0;
		},
	},
	{
		params: (context) => [Type.variablePattern("other_0")],
		guards: [],
		body: (context) => {
			return Elixir_ArgumentError["exception/1"](
				Type.bitstring([
					Type.bitstringSegment(
						Type.string(
							"raise/1 and reraise/2 expect a module name, string or exception ",
						),
						{ type: "binary" },
					),
					Type.bitstringSegment(Type.string("as the first argument, got: "), {
						type: "binary",
					}),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.other_0),
						{ type: "binary" },
					),
				]),
			);
		},
	},
]);

Interpreter.defineElixirFunction("KeyError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.KeyError")],
				[Type.atom("key"), Type.atom("nil")],
				[Type.atom("message"), Type.atom("nil")],
				[Type.atom("term"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("KeyError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.KeyError")],
					[Type.atom("key"), Type.atom("nil")],
					[Type.atom("message"), Type.atom("nil")],
					[Type.atom("term"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("KeyError", "exception", 1, "public", [
	{
		params: (context) => [Type.variablePattern("msg_0")],
		guards: [(context) => Erlang["is_binary/1"](context.vars.msg_0)],
		body: (context) => {
			return Elixir_KeyError["exception/1"](
				Type.list([Type.tuple([Type.atom("message"), context.vars.msg_0])]),
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("args_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.args_0)],
		body: (context) => {
			return Elixir_Kernel["struct!/2"](
				Type.atom("Elixir.KeyError"),
				context.vars.args_0,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Keyword", "drop", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("keywords_0"),
			Type.variablePattern("keys_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_list/1"](context.vars.keywords_0),
					(context) => Erlang["is_list/1"](context.vars.keys_1),
					context,
				),
		],
		body: (context) => {
			return Erlang_Lists["filter/2"](
				Type.anonymousFunction(
					1,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("k_2"),
									Type.matchPlaceholder(),
								]),
							],
							guards: [],
							body: (context) => {
								return Erlang["not/1"](
									Elixir_Enum["member?/2"](
										context.vars.keys_1,
										context.vars.k_2,
									),
								);
							},
						},
					],
					context,
				),
				context.vars.keywords_0,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Keyword", "fetch!", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("keywords_0"),
			Type.variablePattern("key_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_list/1"](context.vars.keywords_0),
					(context) => Erlang["is_atom/1"](context.vars.key_1),
					context,
				),
		],
		body: (context) => {
			return Interpreter.case(
				Erlang_Lists["keyfind/3"](
					context.vars.key_1,
					Type.integer(1n),
					context.vars.keywords_0,
				),
				[
					{
						match: Type.tuple([
							context.vars.key_1,
							Type.variablePattern("value_2"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.value_2;
						},
					},
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_KeyError["exception/1"](
									Type.list([
										Type.tuple([Type.atom("key"), context.vars.key_1]),
										Type.tuple([Type.atom("term"), context.vars.keywords_0]),
									]),
								),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Keyword", "get", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("x0_0"),
			Type.variablePattern("x1_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Keyword["get/3"](
				context.vars.x0_0,
				context.vars.x1_1,
				Type.atom("nil"),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Keyword", "get", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("keywords_0"),
			Type.variablePattern("key_1"),
			Type.variablePattern("default_2"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_list/1"](context.vars.keywords_0),
					(context) => Erlang["is_atom/1"](context.vars.key_1),
					context,
				),
		],
		body: (context) => {
			return Interpreter.case(
				Erlang_Lists["keyfind/3"](
					context.vars.key_1,
					Type.integer(1n),
					context.vars.keywords_0,
				),
				[
					{
						match: Type.tuple([
							context.vars.key_1,
							Type.variablePattern("value_3"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.value_3;
						},
					},
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return context.vars.default_2;
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Keyword", "has_key?", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("keywords_0"),
			Type.variablePattern("key_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_list/1"](context.vars.keywords_0),
					(context) => Erlang["is_atom/1"](context.vars.key_1),
					context,
				),
		],
		body: (context) => {
			return Erlang_Lists["keymember/3"](
				context.vars.key_1,
				Type.integer(1n),
				context.vars.keywords_0,
			);
		},
	},
]);

Interpreter.defineElixirFunction("List", "delete", 2, "public", [
	{
		params: (context) => [
			Type.consPattern(
				Type.variablePattern("element_0"),
				Type.variablePattern("list_1"),
			),
			Type.variablePattern("element_0"),
		],
		guards: [],
		body: (context) => {
			return context.vars.list_1;
		},
	},
	{
		params: (context) => [
			Type.consPattern(
				Type.variablePattern("other_0"),
				Type.variablePattern("list_1"),
			),
			Type.variablePattern("element_2"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.consOperator(
				context.vars.other_0,
				Elixir_List["delete/2"](context.vars.list_1, context.vars.element_2),
			);
		},
	},
	{
		params: (context) => [Type.list([]), Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
]);

Interpreter.defineElixirFunction("List", "do_insert_at", 3, "private", [
	{
		params: (context) => [
			Type.list([]),
			Type.matchPlaceholder(),
			Type.variablePattern("value_1"),
		],
		guards: [],
		body: (context) => {
			return Type.list([context.vars.value_1]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("list_0"),
			Type.integer(0n),
			Type.variablePattern("value_1"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.consOperator(
				context.vars.value_1,
				context.vars.list_0,
			);
		},
	},
	{
		params: (context) => [
			Type.consPattern(
				Type.variablePattern("head_0"),
				Type.variablePattern("tail_1"),
			),
			Type.variablePattern("index_2"),
			Type.variablePattern("value_3"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.consOperator(
				context.vars.head_0,
				Elixir_List["do_insert_at/3"](
					context.vars.tail_1,
					Erlang["-/2"](context.vars.index_2, Type.integer(1n)),
					context.vars.value_3,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("List", "insert_at", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("list_0"),
			Type.variablePattern("index_1"),
			Type.variablePattern("value_2"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_list/1"](context.vars.list_0),
					(context) => Erlang["is_integer/1"](context.vars.index_1),
					context,
				),
		],
		body: (context) => {
			return Interpreter.case(
				context.vars.index_1,
				[
					{
						match: Type.integer(-1n),
						guards: [],
						body: (context) => {
							return Erlang["++/2"](
								context.vars.list_0,
								Type.list([context.vars.value_2]),
							);
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [
							(context) =>
								Erlang["</2"](context.vars.index_1, Type.integer(0n)),
						],
						body: (context) => {
							return Interpreter.case(
								Erlang["+/2"](
									Erlang["+/2"](
										Erlang["length/1"](context.vars.list_0),
										context.vars.index_1,
									),
									Type.integer(1n),
								),
								[
									{
										match: Type.variablePattern("index_3"),
										guards: [
											(context) =>
												Erlang["</2"](context.vars.index_3, Type.integer(0n)),
										],
										body: (context) => {
											return Interpreter.consOperator(
												context.vars.value_2,
												context.vars.list_0,
											);
										},
									},
									{
										match: Type.variablePattern("index_4"),
										guards: [],
										body: (context) => {
											return Elixir_List["do_insert_at/3"](
												context.vars.list_0,
												context.vars.index_4,
												context.vars.value_2,
											);
										},
									},
								],
								context,
							);
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Elixir_List["do_insert_at/3"](
								context.vars.list_0,
								context.vars.index_1,
								context.vars.value_2,
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("List", "to_string", 1, "public", [
	{
		params: (context) => [Type.variablePattern("list_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.list_0)],
		body: (context) => {
			return Interpreter.try(
				(context) => {
					return Erlang_Unicode["characters_to_binary/1"](context.vars.list_0);
				},
				[
					{
						variable: Type.matchPlaceholder(),
						modules: [Type.atom("Elixir.ArgumentError")],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_ArgumentError["exception/1"](
									Type.bitstring([
										Type.bitstringSegment(
											Type.string(
												"cannot convert the given list to a string.\n\nTo be converted to a string, a list must either be empty or only\ncontain the following elements:\n\n  * strings\n  * integers representing Unicode code points\n  * a list containing one of these three elements\n\nPlease check the given list or call inspect/1 to get the list representation, got:\n\n",
											),
											{ type: "binary" },
										),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](context.vars.list_0),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string("\n"), {
											type: "binary",
										}),
									]),
								),
							);
						},
					},
				],
				[],
				[],
				null,
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Logger", "__do_log__", 4, "public", [
	{
		params: (context) => [
			Type.variablePattern("level_0"),
			Type.variablePattern("fun_1"),
			Type.variablePattern("location_2"),
			Type.variablePattern("metadata_3"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) =>
						Erlang["andalso/2"](
							(context) =>
								Erlang["is_function/2"](context.vars.fun_1, Type.integer(0n)),
							(context) => Erlang["is_map/1"](context.vars.location_2),
							context,
						),
					(context) => Erlang["is_map/1"](context.vars.metadata_3),
					context,
				),
		],
		body: (context) => {
			return Interpreter.case(
				Interpreter.callAnonymousFunction(context.vars.fun_1, []),
				[
					{
						match: Type.tuple([
							Type.variablePattern("msg_4"),
							Type.variablePattern("meta_5"),
						]),
						guards: [],
						body: (context) => {
							return Elixir_Logger["__do_log__/4"](
								context.vars.level_0,
								context.vars.msg_4,
								context.vars.location_2,
								Elixir_Enum["into/2"](
									context.vars.meta_5,
									context.vars.metadata_3,
								),
							);
						},
					},
					{
						match: Type.variablePattern("msg_6"),
						guards: [],
						body: (context) => {
							return Elixir_Logger["__do_log__/4"](
								context.vars.level_0,
								context.vars.msg_6,
								context.vars.location_2,
								context.vars.metadata_3,
							);
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("level_0"),
			Type.variablePattern("msg_1"),
			Type.variablePattern("location_2"),
			Type.variablePattern("metadata_3"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) =>
						Erlang["andalso/2"](
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["orelse/2"](
													(context) =>
														Erlang["orelse/2"](
															(context) =>
																Erlang["orelse/2"](
																	(context) =>
																		Erlang["orelse/2"](
																			(context) =>
																				Erlang["orelse/2"](
																					(context) =>
																						Erlang["=:=/2"](
																							context.vars.level_0,
																							Type.atom("error"),
																						),
																					(context) =>
																						Erlang["=:=/2"](
																							context.vars.level_0,
																							Type.atom("info"),
																						),
																					context,
																				),
																			(context) =>
																				Erlang["=:=/2"](
																					context.vars.level_0,
																					Type.atom("debug"),
																				),
																			context,
																		),
																	(context) =>
																		Erlang["=:=/2"](
																			context.vars.level_0,
																			Type.atom("emergency"),
																		),
																	context,
																),
															(context) =>
																Erlang["=:=/2"](
																	context.vars.level_0,
																	Type.atom("alert"),
																),
															context,
														),
													(context) =>
														Erlang["=:=/2"](
															context.vars.level_0,
															Type.atom("critical"),
														),
													context,
												),
											(context) =>
												Erlang["=:=/2"](
													context.vars.level_0,
													Type.atom("warning"),
												),
											context,
										),
									(context) =>
										Erlang["=:=/2"](context.vars.level_0, Type.atom("notice")),
									context,
								),
							(context) => Erlang["is_map/1"](context.vars.location_2),
							context,
						),
					(context) => Erlang["is_map/1"](context.vars.metadata_3),
					context,
				),
		],
		body: (context) => {
			return Interpreter.case(
				Interpreter.case(
					Interpreter.case(
						Erlang["is_binary/1"](context.vars.msg_1),
						[
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return Erlang["is_list/1"](context.vars.msg_1);
								},
							},
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Type.atom("true");
								},
							},
						],
						context,
					),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Erlang["is_map/1"](context.vars.msg_1);
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Type.atom("true");
							},
						},
					],
					context,
				),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							Elixir_IO["warn/1"](
								Type.bitstring([
									Type.bitstringSegment(Type.string("passing "), {
										type: "binary",
									}),
									Type.bitstringSegment(
										Elixir_Kernel["inspect/1"](context.vars.msg_1),
										{ type: "binary" },
									),
									Type.bitstringSegment(
										Type.string(
											" to Logger is deprecated, expected a map, a keyword list, ",
										),
										{ type: "binary" },
									),
									Type.bitstringSegment(
										Type.string(
											"a string, a list of strings, or a zero-arity anonymous function",
										),
										{ type: "binary" },
									),
								]),
							);
							return Erlang_Logger["macro_log/4"](
								context.vars.location_2,
								context.vars.level_0,
								Elixir_String_Chars["to_string/1"](context.vars.msg_1),
								Elixir_Logger["add_elixir_domain/1"](context.vars.metadata_3),
							);
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Erlang_Logger["macro_log/4"](
								context.vars.location_2,
								context.vars.level_0,
								context.vars.msg_1,
								Elixir_Logger["add_elixir_domain/1"](context.vars.metadata_3),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Logger", "__should_log__", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("level_0"),
			Type.variablePattern("module_1"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Logger["elixir_level_to_erlang_level/1"](context.vars.level_0),
				Type.variablePattern("level_2"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				Erlang_Logger_Config["allow/2"](
					context.vars.level_2,
					context.vars.module_1,
				),
				[
					{
						match: Type.variablePattern("x_3"),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["=:=/2"](context.vars.x_3, Type.atom("false")),
									(context) =>
										Erlang["=:=/2"](context.vars.x_3, Type.atom("nil")),
									context,
								),
						],
						body: (context) => {
							return Type.atom("nil");
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return context.vars.level_2;
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Logger", "add_elixir_domain", 1, "private", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("metadata_1"),
				Type.map([[Type.atom("domain"), Type.variablePattern("domain_0")]]),
				context,
			),
		],
		guards: [(context) => Erlang["is_list/1"](context.vars.domain_0)],
		body: (context) => {
			return Elixir_Map["merge/2"](
				context.vars.metadata_1,
				Type.map([
					[
						Type.atom("domain"),
						Interpreter.consOperator(
							Type.atom("elixir"),
							context.vars.domain_0,
						),
					],
				]),
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("metadata_0")],
		guards: [],
		body: (context) => {
			return Erlang_Maps["put/3"](
				Type.atom("domain"),
				Type.list([Type.atom("elixir")]),
				context.vars.metadata_0,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Logger",
	"elixir_level_to_erlang_level",
	1,
	"private",
	[
		{
			params: (context) => [Type.atom("warn")],
			guards: [],
			body: (context) => {
				Elixir_IO["warn/1"](
					Type.bitstring(
						"the log level :warn is deprecated, use :warning instead",
					),
				);
				return Type.atom("warning");
			},
		},
		{
			params: (context) => [Type.variablePattern("other_0")],
			guards: [],
			body: (context) => {
				return context.vars.other_0;
			},
		},
	],
);

Interpreter.defineElixirFunction("Macro", "classify_atom", 1, "public", [
	{
		params: (context) => [Type.variablePattern("atom_0")],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Elixir_Macro["inner_classify/1"](context.vars.atom_0),
				[
					{
						match: Type.atom("alias"),
						guards: [],
						body: (context) => {
							return Type.atom("alias");
						},
					},
					{
						match: Type.atom("identifier"),
						guards: [],
						body: (context) => {
							return Type.atom("identifier");
						},
					},
					{
						match: Type.variablePattern("type_1"),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["=:=/2"](
											context.vars.type_1,
											Type.atom("unquoted_operator"),
										),
									(context) =>
										Erlang["=:=/2"](
											context.vars.type_1,
											Type.atom("not_callable"),
										),
									context,
								),
						],
						body: (context) => {
							return Type.atom("unquoted");
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Type.atom("quoted");
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Macro", "inner_classify", 1, "private", [
	{
		params: (context) => [Type.variablePattern("atom_0")],
		guards: [(context) => Erlang["is_atom/1"](context.vars.atom_0)],
		body: (context) => {
			globalThis.Hologram.return = Interpreter.cond(
				[
					{
						condition: (context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["orelse/2"](
												(context) =>
													Erlang["orelse/2"](
														(context) =>
															Erlang["orelse/2"](
																(context) =>
																	Erlang["orelse/2"](
																		(context) =>
																			Erlang["orelse/2"](
																				(context) =>
																					Erlang["orelse/2"](
																						(context) =>
																							Erlang["=:=/2"](
																								context.vars.atom_0,
																								Type.atom("%"),
																							),
																						(context) =>
																							Erlang["=:=/2"](
																								context.vars.atom_0,
																								Type.atom("%{}"),
																							),
																						context,
																					),
																				(context) =>
																					Erlang["=:=/2"](
																						context.vars.atom_0,
																						Type.atom("{}"),
																					),
																				context,
																			),
																		(context) =>
																			Erlang["=:=/2"](
																				context.vars.atom_0,
																				Type.atom("<<>>"),
																			),
																		context,
																	),
																(context) =>
																	Erlang["=:=/2"](
																		context.vars.atom_0,
																		Type.atom("..."),
																	),
																context,
															),
														(context) =>
															Erlang["=:=/2"](
																context.vars.atom_0,
																Type.atom(".."),
															),
														context,
													),
												(context) =>
													Erlang["=:=/2"](context.vars.atom_0, Type.atom(".")),
												context,
											),
										(context) =>
											Erlang["=:=/2"](context.vars.atom_0, Type.atom("..//")),
										context,
									),
								(context) =>
									Erlang["=:=/2"](context.vars.atom_0, Type.atom("->")),
								context,
							),
						body: (context) => {
							return Type.atom("not_callable");
						},
					},
					{
						condition: (context) =>
							Erlang["orelse/2"](
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["orelse/2"](
												(context) =>
													Erlang["=:=/2"](context.vars.atom_0, Type.atom("::")),
												(context) =>
													Erlang["=:=/2"](
														context.vars.atom_0,
														Type.atom("^^^"),
													),
												context,
											),
										(context) =>
											Erlang["=:=/2"](context.vars.atom_0, Type.atom("~~~")),
										context,
									),
								(context) =>
									Erlang["=:=/2"](context.vars.atom_0, Type.atom("<|>")),
								context,
							),
						body: (context) => {
							return Type.atom("quoted_operator");
						},
					},
					{
						condition: (context) =>
							Interpreter.case(
								Elixir_Macro["operator?/2"](
									context.vars.atom_0,
									Type.integer(1n),
								),
								[
									{
										match: Type.atom("false"),
										guards: [],
										body: (context) => {
											return Elixir_Macro["operator?/2"](
												context.vars.atom_0,
												Type.integer(2n),
											);
										},
									},
									{
										match: Type.atom("true"),
										guards: [],
										body: (context) => {
											return Type.atom("true");
										},
									},
									{
										match: Type.variablePattern("other_1"),
										guards: [],
										body: (context) => {
											return Erlang["error/1"](
												Type.tuple([
													Type.atom("badbool"),
													Type.atom("or"),
													context.vars.other_1,
												]),
											);
										},
									},
								],
								context,
							),
						body: (context) => {
							return Type.atom("unquoted_operator");
						},
					},
					{
						condition: (context) => Type.atom("true"),
						body: (context) => {
							Interpreter.matchOperator(
								Erlang["atom_to_list/1"](context.vars.atom_0),
								Type.variablePattern("charlist_2"),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							return Interpreter.case(
								Elixir_Macro["valid_alias?/1"](context.vars.charlist_2),
								[
									{
										match: Type.variablePattern("x_3"),
										guards: [
											(context) =>
												Erlang["orelse/2"](
													(context) =>
														Erlang["=:=/2"](
															context.vars.x_3,
															Type.atom("false"),
														),
													(context) =>
														Erlang["=:=/2"](context.vars.x_3, Type.atom("nil")),
													context,
												),
										],
										body: (context) => {
											return Interpreter.case(
												Interpreter.callNamedFunction(
													Erlang_Elixir_Config["identifier_tokenizer/0"](),
													Type.atom("tokenize"),
													Type.list([context.vars.charlist_2]),
													context,
												),
												[
													{
														match: Type.tuple([
															Type.variablePattern("kind_4"),
															Type.matchPlaceholder(),
															Type.list([]),
															Type.matchPlaceholder(),
															Type.matchPlaceholder(),
															Type.variablePattern("special_6"),
														]),
														guards: [],
														body: (context) => {
															return Interpreter.cond(
																[
																	{
																		condition: (context) =>
																			Interpreter.case(
																				Erlang["/=/2"](
																					context.vars.kind_4,
																					Type.atom("identifier"),
																				),
																				[
																					{
																						match: Type.atom("false"),
																						guards: [],
																						body: (context) => {
																							return Erlang_Lists["member/2"](
																								Type.atom("at"),
																								context.vars.special_6,
																							);
																						},
																					},
																					{
																						match: Type.atom("true"),
																						guards: [],
																						body: (context) => {
																							return Type.atom("true");
																						},
																					},
																				],
																				context,
																			),
																		body: (context) => {
																			return Type.atom("not_callable");
																		},
																	},
																	{
																		condition: (context) =>
																			Erlang_Lists["member/2"](
																				Type.atom("nfkc"),
																				context.vars.special_6,
																			),
																		body: (context) => {
																			return Type.atom("other");
																		},
																	},
																	{
																		condition: (context) => Type.atom("true"),
																		body: (context) => {
																			return Type.atom("identifier");
																		},
																	},
																],
																context,
															);
														},
													},
													{
														match: Type.matchPlaceholder(),
														guards: [],
														body: (context) => {
															return Type.atom("other");
														},
													},
												],
												context,
											);
										},
									},
									{
										match: Type.matchPlaceholder(),
										guards: [],
										body: (context) => {
											return Type.atom("alias");
										},
									},
								],
								context,
							);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("Macro", "inspect_atom", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("x0_0"),
			Type.variablePattern("x1_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Macro["inspect_atom/3"](
				context.vars.x0_0,
				context.vars.x1_1,
				Type.list([]),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Macro", "inspect_atom", 3, "public", [
	{
		params: (context) => [
			Type.atom("literal"),
			Type.variablePattern("atom_0"),
			Type.matchPlaceholder(),
		],
		guards: [
			(context) =>
				Erlang["orelse/2"](
					(context) => Erlang["==/2"](context.vars.atom_0, Type.atom("nil")),
					(context) => Erlang["is_boolean/1"](context.vars.atom_0),
					context,
				),
		],
		body: (context) => {
			return Erlang["atom_to_binary/1"](context.vars.atom_0);
		},
	},
	{
		params: (context) => [
			Type.atom("literal"),
			Type.variablePattern("atom_0"),
			Type.variablePattern("opts_1"),
		],
		guards: [(context) => Erlang["is_atom/1"](context.vars.atom_0)],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["atom_to_binary/1"](context.vars.atom_0),
				Type.variablePattern("binary_2"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			globalThis.Hologram.return = Interpreter.case(
				Elixir_Macro["classify_atom/1"](context.vars.atom_0),
				[
					{
						match: Type.atom("alias"),
						guards: [],
						body: (context) => {
							return Interpreter.case(
								context.vars.binary_2,
								[
									{
										match: Type.variablePattern("binary_3"),
										guards: [
											(context) =>
												Erlang["orelse/2"](
													(context) =>
														Erlang["=:=/2"](
															context.vars.binary_3,
															Type.bitstring("Elixir"),
														),
													(context) =>
														Erlang["=:=/2"](
															context.vars.binary_3,
															Type.bitstring("Elixir.Elixir"),
														),
													context,
												),
										],
										body: (context) => {
											return context.vars.binary_3;
										},
									},
									{
										match: Type.bitstringPattern([
											Type.bitstringSegment(Type.string("Elixir.Elixir."), {
												type: "binary",
											}),
											Type.bitstringSegment(Type.matchPlaceholder(), {
												type: "binary",
											}),
										]),
										guards: [],
										body: (context) => {
											return context.vars.binary_2;
										},
									},
									{
										match: Type.bitstringPattern([
											Type.bitstringSegment(Type.string("Elixir."), {
												type: "binary",
											}),
											Type.bitstringSegment(Type.variablePattern("rest_5"), {
												type: "binary",
											}),
										]),
										guards: [],
										body: (context) => {
											return context.vars.rest_5;
										},
									},
								],
								context,
							);
						},
					},
					{
						match: Type.atom("quoted"),
						guards: [],
						body: (context) => {
							Interpreter.matchOperator(
								Elixir_Macro["inspect_atom_escape/3"](
									context.vars.opts_1,
									context.vars.binary_2,
									Type.integer(34n),
								),
								Type.variablePattern("escaped_6"),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							return Erlang["iolist_to_binary/1"](
								Type.list([
									Type.integer(58n),
									Type.integer(34n),
									context.vars.escaped_6,
									Type.integer(34n),
								]),
							);
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Type.bitstring([
								Type.bitstringSegment(Type.string(":"), { type: "binary" }),
								Type.bitstringSegment(context.vars.binary_2, {
									type: "binary",
								}),
							]);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
	{
		params: (context) => [
			Type.atom("key"),
			Type.variablePattern("atom_0"),
			Type.variablePattern("opts_1"),
		],
		guards: [(context) => Erlang["is_atom/1"](context.vars.atom_0)],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["atom_to_binary/1"](context.vars.atom_0),
				Type.variablePattern("binary_2"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			globalThis.Hologram.return = Interpreter.case(
				Elixir_Macro["classify_atom/1"](context.vars.atom_0),
				[
					{
						match: Type.atom("alias"),
						guards: [],
						body: (context) => {
							return Erlang["iolist_to_binary/1"](
								Type.list([
									Type.integer(34n),
									context.vars.binary_2,
									Type.integer(34n),
									Type.integer(58n),
								]),
							);
						},
					},
					{
						match: Type.atom("quoted"),
						guards: [],
						body: (context) => {
							Interpreter.matchOperator(
								Elixir_Macro["inspect_atom_escape/3"](
									context.vars.opts_1,
									context.vars.binary_2,
									Type.integer(34n),
								),
								Type.variablePattern("escaped_3"),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							return Erlang["iolist_to_binary/1"](
								Type.list([
									Type.integer(34n),
									context.vars.escaped_3,
									Type.integer(34n),
									Type.integer(58n),
								]),
							);
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Erlang["iolist_to_binary/1"](
								Type.list([context.vars.binary_2, Type.integer(58n)]),
							);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
	{
		params: (context) => [
			Type.atom("remote_call"),
			Type.variablePattern("atom_0"),
			Type.variablePattern("opts_1"),
		],
		guards: [(context) => Erlang["is_atom/1"](context.vars.atom_0)],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["atom_to_binary/1"](context.vars.atom_0),
				Type.variablePattern("binary_2"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			globalThis.Hologram.return = Interpreter.case(
				Elixir_Macro["inner_classify/1"](context.vars.atom_0),
				[
					{
						match: Type.variablePattern("type_3"),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](
													context.vars.type_3,
													Type.atom("identifier"),
												),
											(context) =>
												Erlang["=:=/2"](
													context.vars.type_3,
													Type.atom("unquoted_operator"),
												),
											context,
										),
									(context) =>
										Erlang["=:=/2"](
											context.vars.type_3,
											Type.atom("quoted_operator"),
										),
									context,
								),
						],
						body: (context) => {
							return context.vars.binary_2;
						},
					},
					{
						match: Type.variablePattern("type_4"),
						guards: [],
						body: (context) => {
							Interpreter.matchOperator(
								Interpreter.case(
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](
												context.vars.type_4,
												Type.atom("not_callable"),
											),
										(context) =>
											Erlang["=:=/2"](context.vars.type_4, Type.atom("alias")),
										context,
									),
									[
										{
											match: Type.atom("false"),
											guards: [],
											body: (context) => {
												return Elixir_Macro["inspect_atom_escape/3"](
													context.vars.opts_1,
													context.vars.binary_2,
													Type.integer(34n),
												);
											},
										},
										{
											match: Type.atom("true"),
											guards: [],
											body: (context) => {
												return context.vars.binary_2;
											},
										},
									],
									context,
								),
								Type.variablePattern("escaped_5"),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							return Erlang["iolist_to_binary/1"](
								Type.list([
									Type.integer(34n),
									context.vars.escaped_5,
									Type.integer(34n),
								]),
							);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("Macro", "inspect_atom_escape", 3, "private", [
	{
		params: (context) => [
			Type.variablePattern("opts_0"),
			Type.variablePattern("string_1"),
			Type.variablePattern("char_2"),
		],
		guards: [],
		body: (context) => {
			globalThis.Hologram.return = Interpreter.case(
				Interpreter.matchOperator(
					Elixir_Access["get/2"](context.vars.opts_0, Type.atom("escape")),
					Type.variablePattern("escape_3"),
					context,
				),
				[
					{
						match: Type.variablePattern("x_4"),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["=:=/2"](context.vars.x_4, Type.atom("false")),
									(context) =>
										Erlang["=:=/2"](context.vars.x_4, Type.atom("nil")),
									context,
								),
						],
						body: (context) => {
							Interpreter.matchOperator(
								Elixir_Code_Identifier["escape/2"](
									context.vars.string_1,
									context.vars.char_2,
								),
								Type.tuple([
									Type.variablePattern("escaped_5"),
									Type.matchPlaceholder(),
								]),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							return context.vars.escaped_5;
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Interpreter.callAnonymousFunction(context.vars.escape_3, [
								context.vars.string_1,
								context.vars.char_2,
							]);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("Macro", "operator?", 2, "public", [
	{
		params: (context) => [Type.atom("..//"), Type.integer(3n)],
		guards: [],
		body: (context) => {
			return Type.atom("true");
		},
	},
	{
		params: (context) => [Type.variablePattern("name_0"), Type.integer(2n)],
		guards: [(context) => Erlang["is_atom/1"](context.vars.name_0)],
		body: (context) => {
			return Interpreter.case(
				Erlang["/=/2"](
					Elixir_Code_Identifier["binary_op/1"](context.vars.name_0),
					Type.atom("error"),
				),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Type.atom("false");
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Erlang["/=/2"](context.vars.name_0, Type.atom("//"));
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("name_0"), Type.integer(1n)],
		guards: [(context) => Erlang["is_atom/1"](context.vars.name_0)],
		body: (context) => {
			return Erlang["/=/2"](
				Elixir_Code_Identifier["unary_op/1"](context.vars.name_0),
				Type.atom("error"),
			);
		},
	},
	{
		params: (context) => [Type.atom(".."), Type.integer(0n)],
		guards: [],
		body: (context) => {
			return Type.atom("true");
		},
	},
	{
		params: (context) => [Type.atom("..."), Type.integer(0n)],
		guards: [],
		body: (context) => {
			return Type.atom("true");
		},
	},
	{
		params: (context) => [
			Type.variablePattern("name_0"),
			Type.variablePattern("arity_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_atom/1"](context.vars.name_0),
					(context) => Erlang["is_integer/1"](context.vars.arity_1),
					context,
				),
		],
		body: (context) => {
			return Type.atom("false");
		},
	},
]);

Interpreter.defineElixirFunction(
	"Macro",
	"trim_leading_while_valid_identifier",
	1,
	"private",
	[
		{
			params: (context) => [
				Type.consPattern(
					Type.variablePattern("char_0"),
					Type.variablePattern("rest_1"),
				),
			],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang[">=/2"](context.vars.char_0, Type.integer(97n)),
						(context) =>
							Erlang["=</2"](context.vars.char_0, Type.integer(122n)),
						context,
					),
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang[">=/2"](context.vars.char_0, Type.integer(65n)),
						(context) => Erlang["=</2"](context.vars.char_0, Type.integer(90n)),
						context,
					),
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang[">=/2"](context.vars.char_0, Type.integer(48n)),
						(context) => Erlang["=</2"](context.vars.char_0, Type.integer(57n)),
						context,
					),
				(context) => Erlang["==/2"](context.vars.char_0, Type.integer(95n)),
			],
			body: (context) => {
				return Elixir_Macro["trim_leading_while_valid_identifier/1"](
					context.vars.rest_1,
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("other_0")],
			guards: [],
			body: (context) => {
				return context.vars.other_0;
			},
		},
	],
);

Interpreter.defineElixirFunction("Macro", "valid_alias?", 1, "private", [
	{
		params: (context) => [
			Type.consPattern(
				Type.integer(69n),
				Type.consPattern(
					Type.integer(108n),
					Type.consPattern(
						Type.integer(105n),
						Type.consPattern(
							Type.integer(120n),
							Type.consPattern(
								Type.integer(105n),
								Type.consPattern(
									Type.integer(114n),
									Type.variablePattern("rest_0"),
								),
							),
						),
					),
				),
			),
		],
		guards: [],
		body: (context) => {
			return Elixir_Macro["valid_alias_piece?/1"](context.vars.rest_0);
		},
	},
	{
		params: (context) => [Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.atom("false");
		},
	},
]);

Interpreter.defineElixirFunction("Macro", "valid_alias_piece?", 1, "private", [
	{
		params: (context) => [
			Type.consPattern(
				Type.integer(46n),
				Type.consPattern(
					Type.variablePattern("char_0"),
					Type.variablePattern("rest_1"),
				),
			),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang[">=/2"](context.vars.char_0, Type.integer(65n)),
					(context) => Erlang["=</2"](context.vars.char_0, Type.integer(90n)),
					context,
				),
		],
		body: (context) => {
			return Elixir_Macro["valid_alias_piece?/1"](
				Elixir_Macro["trim_leading_while_valid_identifier/1"](
					context.vars.rest_1,
				),
			);
		},
	},
	{
		params: (context) => [Type.list([])],
		guards: [],
		body: (context) => {
			return Type.atom("true");
		},
	},
	{
		params: (context) => [Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.atom("false");
		},
	},
]);

Interpreter.defineElixirFunction("Map", "get", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("x0_0"),
			Type.variablePattern("x1_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Map["get/3"](
				context.vars.x0_0,
				context.vars.x1_1,
				Type.atom("nil"),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Map", "get", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("map_0"),
			Type.variablePattern("key_1"),
			Type.variablePattern("default_2"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				context.vars.map_0,
				[
					{
						match: Type.map([
							[context.vars.key_1, Type.variablePattern("value_3")],
						]),
						guards: [],
						body: (context) => {
							return context.vars.value_3;
						},
					},
					{
						match: Type.map([]),
						guards: [],
						body: (context) => {
							return context.vars.default_2;
						},
					},
					{
						match: Type.variablePattern("other_4"),
						guards: [],
						body: (context) => {
							return Erlang["error/2"](
								Type.tuple([Type.atom("badmap"), context.vars.other_4]),
								Type.list([
									context.vars.map_0,
									context.vars.key_1,
									context.vars.default_2,
								]),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Map", "merge", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("map1_0"),
			Type.variablePattern("map2_1"),
		],
		guards: [],
		body: (context) => {
			return Erlang_Maps["merge/2"](context.vars.map1_0, context.vars.map2_1);
		},
	},
]);

Interpreter.defineElixirFunction("Map", "new", 1, "public", [
	{
		params: (context) => [Type.variablePattern("list_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.list_0)],
		body: (context) => {
			return Erlang_Maps["from_list/1"](context.vars.list_0);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("struct_0"),
				Type.map([[Type.atom("__struct__"), Type.matchPlaceholder()]]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return Elixir_Map["new_from_enum/1"](context.vars.struct_0);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("map_0"),
				Type.map([]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return context.vars.map_0;
		},
	},
	{
		params: (context) => [Type.variablePattern("enum_0")],
		guards: [],
		body: (context) => {
			return Elixir_Map["new_from_enum/1"](context.vars.enum_0);
		},
	},
]);

Interpreter.defineElixirFunction("Map", "new_from_enum", 1, "private", [
	{
		params: (context) => [Type.variablePattern("enumerable_0")],
		guards: [],
		body: (context) => {
			return Erlang_Maps["from_list/1"](
				Elixir_Enum["to_list/1"](context.vars.enumerable_0),
			);
		},
	},
]);

Interpreter.defineElixirFunction("MapSet", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.MapSet")],
				[Type.atom("map"), Type.map([])],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("MapSet", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.MapSet")],
					[Type.atom("map"), Type.map([])],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("MapSet", "member?", 2, "public", [
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.MapSet")],
				[Type.atom("map"), Type.variablePattern("set_0")],
			]),
			Type.variablePattern("value_1"),
		],
		guards: [],
		body: (context) => {
			return Erlang_Sets["is_element/2"](
				context.vars.value_1,
				context.vars.set_0,
			);
		},
	},
]);

Interpreter.defineElixirFunction("MapSet", "new", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Elixir_MapSet["__struct__/1"](
				Type.list([Type.tuple([Type.atom("map"), Type.map([])])]),
			);
		},
	},
]);

Interpreter.defineElixirFunction("MapSet", "put", 2, "public", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("map_set_1"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.MapSet")],
					[Type.atom("map"), Type.variablePattern("set_0")],
				]),
				context,
			),
			Type.variablePattern("value_2"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Map["merge/2"](
				context.vars.map_set_1,
				Type.map([
					[
						Type.atom("map"),
						Erlang_Sets["add_element/2"](
							context.vars.value_2,
							context.vars.set_0,
						),
					],
				]),
			);
		},
	},
]);

Interpreter.defineElixirFunction("MapSet", "size", 1, "public", [
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.MapSet")],
				[Type.atom("map"), Type.variablePattern("set_0")],
			]),
		],
		guards: [],
		body: (context) => {
			return Erlang_Sets["size/1"](context.vars.set_0);
		},
	},
]);

Interpreter.defineElixirFunction("MapSet", "to_list", 1, "public", [
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.MapSet")],
				[Type.atom("map"), Type.variablePattern("set_0")],
			]),
		],
		guards: [],
		body: (context) => {
			return Erlang_Sets["to_list/1"](context.vars.set_0);
		},
	},
]);

Interpreter.defineElixirFunction("MatchError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.MatchError")],
				[Type.atom("term"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("MatchError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.MatchError")],
					[Type.atom("term"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Mix.Shell", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Mix.Shell")],
				[Type.atom("callback"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Mix.Shell", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Mix.Shell")],
					[Type.atom("callback"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Module", "split", 1, "public", [
	{
		params: (context) => [Type.variablePattern("module_0")],
		guards: [(context) => Erlang["is_atom/1"](context.vars.module_0)],
		body: (context) => {
			globalThis.Hologram.return = Elixir_Module["split/2"](
				Erlang["atom_to_binary/1"](context.vars.module_0),
				Interpreter.matchOperator(
					context.vars.module_0,
					Type.matchPlaceholder(),
					context,
				),
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
	{
		params: (context) => [Type.variablePattern("module_0")],
		guards: [(context) => Erlang["is_binary/1"](context.vars.module_0)],
		body: (context) => {
			globalThis.Hologram.return = Elixir_Module["split/2"](
				context.vars.module_0,
				Interpreter.matchOperator(
					context.vars.module_0,
					Type.matchPlaceholder(),
					context,
				),
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("Module", "split", 2, "private", [
	{
		params: (context) => [
			Type.bitstringPattern([
				Type.bitstringSegment(Type.string("Elixir."), { type: "binary" }),
				Type.bitstringSegment(Type.variablePattern("name_0"), {
					type: "binary",
				}),
			]),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Elixir_String["split/2"](context.vars.name_0, Type.bitstring("."));
		},
	},
	{
		params: (context) => [
			Type.matchPlaceholder(),
			Type.variablePattern("original_1"),
		],
		guards: [],
		body: (context) => {
			return Erlang["error/1"](
				Elixir_ArgumentError["exception/1"](
					Type.bitstring([
						Type.bitstringSegment(
							Type.string("expected an Elixir module, got: "),
							{ type: "binary" },
						),
						Type.bitstringSegment(
							Elixir_Kernel["inspect/1"](context.vars.original_1),
							{ type: "binary" },
						),
					]),
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("NaiveDateTime", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.NaiveDateTime")],
				[Type.atom("calendar"), Type.atom("Elixir.Calendar.ISO")],
				[Type.atom("day"), Type.atom("nil")],
				[Type.atom("hour"), Type.atom("nil")],
				[
					Type.atom("microsecond"),
					Type.tuple([Type.integer(0n), Type.integer(0n)]),
				],
				[Type.atom("minute"), Type.atom("nil")],
				[Type.atom("month"), Type.atom("nil")],
				[Type.atom("second"), Type.atom("nil")],
				[Type.atom("year"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("NaiveDateTime", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.tuple([
						Type.map([
							[Type.atom("__struct__"), Type.atom("Elixir.NaiveDateTime")],
							[Type.atom("calendar"), Type.atom("Elixir.Calendar.ISO")],
							[Type.atom("day"), Type.atom("nil")],
							[Type.atom("hour"), Type.atom("nil")],
							[
								Type.atom("microsecond"),
								Type.tuple([Type.integer(0n), Type.integer(0n)]),
							],
							[Type.atom("minute"), Type.atom("nil")],
							[Type.atom("month"), Type.atom("nil")],
							[Type.atom("second"), Type.atom("nil")],
							[Type.atom("year"), Type.atom("nil")],
						]),
						Type.list([
							Type.atom("year"),
							Type.atom("month"),
							Type.atom("day"),
							Type.atom("hour"),
							Type.atom("minute"),
							Type.atom("second"),
						]),
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.tuple([
										Type.variablePattern("map_3"),
										Type.variablePattern("keys_4"),
									]),
								],
								guards: [],
								body: (context) => {
									return Type.tuple([
										Elixir_Map["merge/2"](
											context.vars.map_3,
											Type.map([[context.vars.key_1, context.vars.val_2]]),
										),
										Elixir_List["delete/2"](
											context.vars.keys_4,
											context.vars.key_1,
										),
									]);
								},
							},
						],
						context,
					),
				),
				Type.tuple([
					Type.variablePattern("map_5"),
					Type.variablePattern("keys_6"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				context.vars.keys_6,
				[
					{
						match: Type.list([]),
						guards: [],
						body: (context) => {
							return context.vars.map_5;
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_ArgumentError["exception/1"](
									Type.bitstring([
										Type.bitstringSegment(
											Type.string(
												"the following keys must also be given when building ",
											),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string("struct "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](
												Type.atom("Elixir.NaiveDateTime"),
											),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string(": "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](context.vars.keys_6),
											{ type: "binary" },
										),
									]),
								),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Phoenix.LiveComponent.CID",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.Phoenix.LiveComponent.CID"),
					],
					[Type.atom("cid"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Phoenix.LiveComponent.CID",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.Phoenix.LiveComponent.CID"),
						],
						[Type.atom("cid"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Phoenix.LiveView.LiveStream",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.Phoenix.LiveView.LiveStream"),
					],
					[Type.atom("consumable?"), Type.atom("false")],
					[Type.atom("deletes"), Type.list([])],
					[Type.atom("dom_id"), Type.atom("nil")],
					[Type.atom("inserts"), Type.list([])],
					[Type.atom("name"), Type.atom("nil")],
					[Type.atom("ref"), Type.atom("nil")],
					[Type.atom("reset?"), Type.atom("false")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Phoenix.LiveView.LiveStream",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.Phoenix.LiveView.LiveStream"),
						],
						[Type.atom("consumable?"), Type.atom("false")],
						[Type.atom("deletes"), Type.list([])],
						[Type.atom("dom_id"), Type.atom("nil")],
						[Type.atom("inserts"), Type.list([])],
						[Type.atom("name"), Type.atom("nil")],
						[Type.atom("ref"), Type.atom("nil")],
						[Type.atom("reset?"), Type.atom("false")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Process", "get", 1, "public", [
	{
		params: (context) => [Type.variablePattern("x0_0")],
		guards: [],
		body: (context) => {
			return Elixir_Process["get/2"](context.vars.x0_0, Type.atom("nil"));
		},
	},
]);

Interpreter.defineElixirFunction("Process", "get", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("key_0"),
			Type.variablePattern("default_1"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Erlang["get/1"](context.vars.key_0),
				[
					{
						match: Type.atom("undefined"),
						guards: [],
						body: (context) => {
							return context.vars.default_1;
						},
					},
					{
						match: Type.variablePattern("value_2"),
						guards: [],
						body: (context) => {
							return context.vars.value_2;
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Process", "info", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("pid_0"),
			Type.atom("registered_name"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Erlang["process_info/2"](
					context.vars.pid_0,
					Type.atom("registered_name"),
				),
				[
					{
						match: Type.atom("undefined"),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
					{
						match: Type.list([]),
						guards: [],
						body: (context) => {
							return Type.tuple([Type.atom("registered_name"), Type.list([])]);
						},
					},
					{
						match: Type.variablePattern("other_1"),
						guards: [],
						body: (context) => {
							return context.vars.other_1;
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("pid_0"),
			Type.variablePattern("spec_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Process["nilify/1"](
				Erlang["process_info/2"](context.vars.pid_0, context.vars.spec_1),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Process", "nilify", 1, "private", [
	{
		params: (context) => [Type.atom("undefined")],
		guards: [],
		body: (context) => {
			return Type.atom("nil");
		},
	},
	{
		params: (context) => [Type.variablePattern("other_0")],
		guards: [],
		body: (context) => {
			return context.vars.other_0;
		},
	},
]);

Interpreter.defineElixirFunction("Protocol", "__concat__", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("left_0"),
			Type.variablePattern("right_1"),
		],
		guards: [],
		body: (context) => {
			return Erlang["binary_to_atom/1"](
				Type.bitstring([
					Type.bitstringSegment(
						Elixir_Protocol["ensure_prefix/1"](
							Erlang["atom_to_binary/1"](context.vars.left_0),
						),
						{ type: "binary" },
					),
					Type.bitstringSegment(Type.string("."), { type: "binary" }),
					Type.bitstringSegment(
						Elixir_Protocol["remove_prefix/1"](
							Erlang["atom_to_binary/1"](context.vars.right_1),
						),
						{ type: "binary" },
					),
				]),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Protocol", "ensure_prefix", 1, "private", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("left_0"),
				Type.bitstringPattern([
					Type.bitstringSegment(Type.string("Elixir."), { type: "binary" }),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			return context.vars.left_0;
		},
	},
	{
		params: (context) => [Type.variablePattern("left_0")],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("Elixir."), { type: "binary" }),
				Type.bitstringSegment(context.vars.left_0, { type: "binary" }),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Protocol", "remove_prefix", 1, "private", [
	{
		params: (context) => [
			Type.bitstringPattern([
				Type.bitstringSegment(Type.string("Elixir."), { type: "binary" }),
				Type.bitstringSegment(Type.variablePattern("right_0"), {
					type: "binary",
				}),
			]),
		],
		guards: [],
		body: (context) => {
			return context.vars.right_0;
		},
	},
	{
		params: (context) => [Type.variablePattern("right_0")],
		guards: [],
		body: (context) => {
			return context.vars.right_0;
		},
	},
]);

Interpreter.defineElixirFunction(
	"Protocol.UndefinedError",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.Protocol.UndefinedError"),
					],
					[Type.atom("description"), Type.bitstring("")],
					[Type.atom("protocol"), Type.atom("nil")],
					[Type.atom("value"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Protocol.UndefinedError",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[Type.atom("__exception__"), Type.atom("true")],
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.Protocol.UndefinedError"),
						],
						[Type.atom("description"), Type.bitstring("")],
						[Type.atom("protocol"), Type.atom("nil")],
						[Type.atom("value"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Protocol.UndefinedError",
	"exception",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("args_0")],
			guards: [(context) => Erlang["is_list/1"](context.vars.args_0)],
			body: (context) => {
				return Elixir_Kernel["struct!/2"](
					Type.atom("Elixir.Protocol.UndefinedError"),
					context.vars.args_0,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Range", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Range")],
				[Type.atom("first"), Type.atom("nil")],
				[Type.atom("last"), Type.atom("nil")],
				[Type.atom("step"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Range", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.tuple([
						Type.map([
							[Type.atom("__struct__"), Type.atom("Elixir.Range")],
							[Type.atom("first"), Type.atom("nil")],
							[Type.atom("last"), Type.atom("nil")],
							[Type.atom("step"), Type.atom("nil")],
						]),
						Type.list([
							Type.atom("first"),
							Type.atom("last"),
							Type.atom("step"),
						]),
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.tuple([
										Type.variablePattern("map_3"),
										Type.variablePattern("keys_4"),
									]),
								],
								guards: [],
								body: (context) => {
									return Type.tuple([
										Elixir_Map["merge/2"](
											context.vars.map_3,
											Type.map([[context.vars.key_1, context.vars.val_2]]),
										),
										Elixir_List["delete/2"](
											context.vars.keys_4,
											context.vars.key_1,
										),
									]);
								},
							},
						],
						context,
					),
				),
				Type.tuple([
					Type.variablePattern("map_5"),
					Type.variablePattern("keys_6"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				context.vars.keys_6,
				[
					{
						match: Type.list([]),
						guards: [],
						body: (context) => {
							return context.vars.map_5;
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_ArgumentError["exception/1"](
									Type.bitstring([
										Type.bitstringSegment(
											Type.string(
												"the following keys must also be given when building ",
											),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string("struct "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](Type.atom("Elixir.Range")),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string(": "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](context.vars.keys_6),
											{ type: "binary" },
										),
									]),
								),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Range", "size", 1, "public", [
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Range")],
				[Type.atom("first"), Type.variablePattern("first_0")],
				[Type.atom("last"), Type.variablePattern("last_1")],
				[Type.atom("step"), Type.variablePattern("step_2")],
			]),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang[">/2"](context.vars.step_2, Type.integer(0n)),
					(context) => Erlang[">/2"](context.vars.first_0, context.vars.last_1),
					context,
				),
		],
		body: (context) => {
			return Type.integer(0n);
		},
	},
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Range")],
				[Type.atom("first"), Type.variablePattern("first_0")],
				[Type.atom("last"), Type.variablePattern("last_1")],
				[Type.atom("step"), Type.variablePattern("step_2")],
			]),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["</2"](context.vars.step_2, Type.integer(0n)),
					(context) => Erlang["</2"](context.vars.first_0, context.vars.last_1),
					context,
				),
		],
		body: (context) => {
			return Type.integer(0n);
		},
	},
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Range")],
				[Type.atom("first"), Type.variablePattern("first_0")],
				[Type.atom("last"), Type.variablePattern("last_1")],
				[Type.atom("step"), Type.variablePattern("step_2")],
			]),
		],
		guards: [],
		body: (context) => {
			return Erlang["+/2"](
				Erlang["abs/1"](
					Erlang["div/2"](
						Erlang["-/2"](context.vars.last_1, context.vars.first_0),
						context.vars.step_2,
					),
				),
				Type.integer(1n),
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("range_2"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Range")],
					[Type.atom("first"), Type.variablePattern("first_0")],
					[Type.atom("last"), Type.variablePattern("last_1")],
				]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Interpreter.case(
					Erlang["=</2"](context.vars.first_0, context.vars.last_1),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Type.integer(-1n);
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Type.integer(1n);
							},
						},
					],
					context,
				),
				Type.variablePattern("step_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Elixir_Range["size/1"](
				Erlang_Maps["put/3"](
					Type.atom("step"),
					context.vars.step_3,
					context.vars.range_2,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Range", "to_list", 1, "public", [
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Range")],
				[Type.atom("first"), Type.variablePattern("first_0")],
				[Type.atom("last"), Type.variablePattern("last_1")],
				[Type.atom("step"), Type.variablePattern("step_2")],
			]),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang[">/2"](context.vars.step_2, Type.integer(0n)),
					(context) =>
						Erlang["=</2"](context.vars.first_0, context.vars.last_1),
					context,
				),
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["</2"](context.vars.step_2, Type.integer(0n)),
					(context) =>
						Erlang[">=/2"](context.vars.first_0, context.vars.last_1),
					context,
				),
		],
		body: (context) => {
			return Erlang_Lists["seq/3"](
				context.vars.first_0,
				context.vars.last_1,
				context.vars.step_2,
			);
		},
	},
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Range")],
				[Type.atom("first"), Type.matchPlaceholder()],
				[Type.atom("last"), Type.matchPlaceholder()],
				[Type.atom("step"), Type.matchPlaceholder()],
			]),
		],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
	{
		params: (context) => [
			Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Range")],
				[Type.atom("first"), Type.variablePattern("first_0")],
				[Type.atom("last"), Type.variablePattern("last_1")],
			]),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Interpreter.case(
					Erlang["=</2"](context.vars.first_0, context.vars.last_1),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Type.integer(-1n);
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Type.integer(1n);
							},
						},
					],
					context,
				),
				Type.variablePattern("step_2"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Erlang_Lists["seq/3"](
				context.vars.first_0,
				context.vars.last_1,
				context.vars.step_2,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Regex", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Regex")],
				[Type.atom("opts"), Type.list([])],
				[Type.atom("re_pattern"), Type.atom("nil")],
				[Type.atom("source"), Type.bitstring("")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Regex", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Regex")],
					[Type.atom("opts"), Type.list([])],
					[Type.atom("re_pattern"), Type.atom("nil")],
					[Type.atom("source"), Type.bitstring("")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("Regex", "do_split", 6, "private", [
	{
		params: (context) => [
			Type.matchPlaceholder(),
			Type.variablePattern("string_0"),
			Type.variablePattern("offset_1"),
			Type.matchPlaceholder(),
			Type.atom("true"),
			Type.matchPlaceholder(),
		],
		guards: [
			(context) =>
				Erlang["=</2"](
					Erlang["byte_size/1"](context.vars.string_0),
					context.vars.offset_1,
				),
		],
		body: (context) => {
			return Type.list([]);
		},
	},
	{
		params: (context) => [
			Type.matchPlaceholder(),
			Type.variablePattern("string_0"),
			Type.variablePattern("offset_1"),
			Type.integer(1n),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.list([
				Erlang["binary_part/3"](
					context.vars.string_0,
					context.vars.offset_1,
					Erlang["-/2"](
						Erlang["byte_size/1"](context.vars.string_0),
						context.vars.offset_1,
					),
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.list([]),
			Type.variablePattern("string_0"),
			Type.variablePattern("offset_1"),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.list([
				Erlang["binary_part/3"](
					context.vars.string_0,
					context.vars.offset_1,
					Erlang["-/2"](
						Erlang["byte_size/1"](context.vars.string_0),
						context.vars.offset_1,
					),
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.consPattern(
				Type.consPattern(
					Type.tuple([Type.variablePattern("pos_0"), Type.matchPlaceholder()]),
					Type.variablePattern("h_1"),
				),
				Type.variablePattern("t_2"),
			),
			Type.variablePattern("string_3"),
			Type.variablePattern("offset_4"),
			Type.variablePattern("counter_5"),
			Type.variablePattern("trim_6"),
			Type.variablePattern("with_captures_7"),
		],
		guards: [
			(context) =>
				Erlang["</2"](
					Erlang["-/2"](context.vars.pos_0, context.vars.offset_4),
					Type.integer(0n),
				),
		],
		body: (context) => {
			return Elixir_Regex["do_split/6"](
				Interpreter.consOperator(context.vars.h_1, context.vars.t_2),
				context.vars.string_3,
				context.vars.offset_4,
				context.vars.counter_5,
				context.vars.trim_6,
				context.vars.with_captures_7,
			);
		},
	},
	{
		params: (context) => [
			Type.consPattern(Type.list([]), Type.variablePattern("t_0")),
			Type.variablePattern("string_1"),
			Type.variablePattern("offset_2"),
			Type.variablePattern("counter_3"),
			Type.variablePattern("trim_4"),
			Type.variablePattern("with_captures_5"),
		],
		guards: [],
		body: (context) => {
			return Elixir_Regex["do_split/6"](
				context.vars.t_0,
				context.vars.string_1,
				context.vars.offset_2,
				context.vars.counter_3,
				context.vars.trim_4,
				context.vars.with_captures_5,
			);
		},
	},
	{
		params: (context) => [
			Type.consPattern(
				Type.consPattern(
					Type.tuple([
						Type.variablePattern("pos_0"),
						Type.variablePattern("length_1"),
					]),
					Type.variablePattern("h_2"),
				),
				Type.variablePattern("t_3"),
			),
			Type.variablePattern("string_4"),
			Type.variablePattern("offset_5"),
			Type.variablePattern("counter_6"),
			Type.variablePattern("trim_7"),
			Type.atom("true"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["+/2"](context.vars.pos_0, context.vars.length_1),
				Type.variablePattern("new_offset_8"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Erlang["-/2"](context.vars.pos_0, context.vars.offset_5),
				Type.variablePattern("keep_9"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				context.vars.string_4,
				Type.bitstringPattern([
					Type.bitstringSegment(Type.matchPlaceholder(), {
						size: context.vars.offset_5,
						type: "binary",
					}),
					Type.bitstringSegment(Type.variablePattern("part_10"), {
						size: context.vars.keep_9,
						type: "binary",
					}),
					Type.bitstringSegment(Type.variablePattern("match_11"), {
						size: context.vars.length_1,
						type: "binary",
					}),
					Type.bitstringSegment(Type.matchPlaceholder(), { type: "binary" }),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.cond(
				[
					{
						condition: (context) =>
							Interpreter.case(
								Erlang["==/2"](context.vars.keep_9, Type.integer(0n)),
								[
									{
										match: Type.atom("false"),
										guards: [],
										body: (context) => {
											return Type.atom("false");
										},
									},
									{
										match: Type.atom("true"),
										guards: [],
										body: (context) => {
											return Interpreter.case(
												Erlang["/=/2"](context.vars.offset_5, Type.integer(0n)),
												[
													{
														match: Type.atom("false"),
														guards: [],
														body: (context) => {
															return Type.atom("false");
														},
													},
													{
														match: Type.atom("true"),
														guards: [],
														body: (context) => {
															return Erlang["==/2"](
																context.vars.length_1,
																Type.integer(0n),
															);
														},
													},
												],
												context,
											);
										},
									},
								],
								context,
							),
						body: (context) => {
							return Elixir_Regex["do_split/6"](
								Interpreter.consOperator(context.vars.h_2, context.vars.t_3),
								context.vars.string_4,
								context.vars.new_offset_8,
								Erlang["-/2"](context.vars.counter_6, Type.integer(1n)),
								context.vars.trim_7,
								Type.atom("true"),
							);
						},
					},
					{
						condition: (context) =>
							Interpreter.case(
								Erlang["==/2"](context.vars.keep_9, Type.integer(0n)),
								[
									{
										match: Type.atom("false"),
										guards: [],
										body: (context) => {
											return Type.atom("false");
										},
									},
									{
										match: Type.atom("true"),
										guards: [],
										body: (context) => {
											return context.vars.trim_7;
										},
									},
								],
								context,
							),
						body: (context) => {
							return Interpreter.consOperator(
								context.vars.match_11,
								Elixir_Regex["do_split/6"](
									Interpreter.consOperator(context.vars.h_2, context.vars.t_3),
									context.vars.string_4,
									context.vars.new_offset_8,
									Erlang["-/2"](context.vars.counter_6, Type.integer(1n)),
									context.vars.trim_7,
									Type.atom("true"),
								),
							);
						},
					},
					{
						condition: (context) => Type.atom("true"),
						body: (context) => {
							return Interpreter.consOperator(
								context.vars.part_10,
								Interpreter.consOperator(
									context.vars.match_11,
									Elixir_Regex["do_split/6"](
										Interpreter.consOperator(
											context.vars.h_2,
											context.vars.t_3,
										),
										context.vars.string_4,
										context.vars.new_offset_8,
										Erlang["-/2"](context.vars.counter_6, Type.integer(1n)),
										context.vars.trim_7,
										Type.atom("true"),
									),
								),
							);
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.consPattern(
				Type.consPattern(
					Type.tuple([
						Type.variablePattern("pos_0"),
						Type.variablePattern("length_1"),
					]),
					Type.variablePattern("h_2"),
				),
				Type.variablePattern("t_3"),
			),
			Type.variablePattern("string_4"),
			Type.variablePattern("offset_5"),
			Type.variablePattern("counter_6"),
			Type.variablePattern("trim_7"),
			Type.atom("false"),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Erlang["+/2"](context.vars.pos_0, context.vars.length_1),
				Type.variablePattern("new_offset_8"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Erlang["-/2"](context.vars.pos_0, context.vars.offset_5),
				Type.variablePattern("keep_9"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			globalThis.Hologram.return = Interpreter.case(
				Interpreter.case(
					Erlang["==/2"](context.vars.keep_9, Type.integer(0n)),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Type.atom("false");
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Interpreter.case(
									context.vars.trim_7,
									[
										{
											match: Type.atom("false"),
											guards: [],
											body: (context) => {
												return Interpreter.case(
													Erlang["/=/2"](
														context.vars.offset_5,
														Type.integer(0n),
													),
													[
														{
															match: Type.atom("false"),
															guards: [],
															body: (context) => {
																return Type.atom("false");
															},
														},
														{
															match: Type.atom("true"),
															guards: [],
															body: (context) => {
																return Erlang["==/2"](
																	context.vars.length_1,
																	Type.integer(0n),
																);
															},
														},
													],
													context,
												);
											},
										},
										{
											match: Type.atom("true"),
											guards: [],
											body: (context) => {
												return Type.atom("true");
											},
										},
										{
											match: Type.variablePattern("other_10"),
											guards: [],
											body: (context) => {
												return Erlang["error/1"](
													Type.tuple([
														Type.atom("badbool"),
														Type.atom("or"),
														context.vars.other_10,
													]),
												);
											},
										},
									],
									context,
								);
							},
						},
					],
					context,
				),
				[
					{
						match: Type.variablePattern("x_11"),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["=:=/2"](context.vars.x_11, Type.atom("false")),
									(context) =>
										Erlang["=:=/2"](context.vars.x_11, Type.atom("nil")),
									context,
								),
						],
						body: (context) => {
							Interpreter.matchOperator(
								context.vars.string_4,
								Type.bitstringPattern([
									Type.bitstringSegment(Type.matchPlaceholder(), {
										size: context.vars.offset_5,
										type: "binary",
									}),
									Type.bitstringSegment(Type.variablePattern("part_12"), {
										size: context.vars.keep_9,
										type: "binary",
									}),
									Type.bitstringSegment(Type.matchPlaceholder(), {
										type: "binary",
									}),
								]),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							return Interpreter.consOperator(
								context.vars.part_12,
								Elixir_Regex["do_split/6"](
									Interpreter.consOperator(context.vars.h_2, context.vars.t_3),
									context.vars.string_4,
									context.vars.new_offset_8,
									Erlang["-/2"](context.vars.counter_6, Type.integer(1n)),
									context.vars.trim_7,
									Type.atom("false"),
								),
							);
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Elixir_Regex["do_split/6"](
								Interpreter.consOperator(context.vars.h_2, context.vars.t_3),
								context.vars.string_4,
								context.vars.new_offset_8,
								context.vars.counter_6,
								context.vars.trim_7,
								Type.atom("false"),
							);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("Regex", "parts_to_index", 1, "private", [
	{
		params: (context) => [Type.atom("infinity")],
		guards: [],
		body: (context) => {
			return Type.integer(0n);
		},
	},
	{
		params: (context) => [Type.variablePattern("n_0")],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_integer/1"](context.vars.n_0),
					(context) => Erlang[">/2"](context.vars.n_0, Type.integer(0n)),
					context,
				),
		],
		body: (context) => {
			return context.vars.n_0;
		},
	},
]);

Interpreter.defineElixirFunction("Regex", "safe_run", 3, "private", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("regex_1"),
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Regex")],
					[Type.atom("re_pattern"), Type.variablePattern("re_pattern_0")],
				]),
				context,
			),
			Type.variablePattern("string_2"),
			Type.variablePattern("options_3"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.with();
		},
	},
]);

Interpreter.defineElixirFunction("Regex", "split", 3, "public", [
	{
		params: (context) => [
			Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Regex")]]),
			Type.bitstring(""),
			Type.variablePattern("opts_0"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Elixir_Keyword["get/3"](
					context.vars.opts_0,
					Type.atom("trim"),
					Type.atom("false"),
				),
				[
					{
						match: Type.variablePattern("x_1"),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["=:=/2"](context.vars.x_1, Type.atom("false")),
									(context) =>
										Erlang["=:=/2"](context.vars.x_1, Type.atom("nil")),
									context,
								),
						],
						body: (context) => {
							return Type.list([Type.bitstring("")]);
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Type.list([]);
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("regex_0"),
				Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Regex")]]),
				context,
			),
			Type.variablePattern("string_1"),
			Type.variablePattern("opts_2"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_binary/1"](context.vars.string_1),
					(context) => Erlang["is_list/1"](context.vars.opts_2),
					context,
				),
		],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Keyword["get/3"](
					context.vars.opts_2,
					Type.atom("on"),
					Type.atom("first"),
				),
				Type.variablePattern("on_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			globalThis.Hologram.return = Interpreter.case(
				Elixir_Regex["safe_run/3"](
					context.vars.regex_0,
					context.vars.string_1,
					Type.list([
						Type.atom("global"),
						Type.tuple([Type.atom("capture"), context.vars.on_3]),
					]),
				),
				[
					{
						match: Type.tuple([
							Type.atom("match"),
							Type.variablePattern("matches_4"),
						]),
						guards: [],
						body: (context) => {
							Interpreter.matchOperator(
								Elixir_Regex["parts_to_index/1"](
									Elixir_Keyword["get/3"](
										context.vars.opts_2,
										Type.atom("parts"),
										Type.atom("infinity"),
									),
								),
								Type.variablePattern("index_5"),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							Interpreter.matchOperator(
								Elixir_Keyword["get/3"](
									context.vars.opts_2,
									Type.atom("trim"),
									Type.atom("false"),
								),
								Type.variablePattern("trim_6"),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							Interpreter.matchOperator(
								Elixir_Keyword["get/3"](
									context.vars.opts_2,
									Type.atom("include_captures"),
									Type.atom("false"),
								),
								Type.variablePattern("include_captures_7"),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							return Elixir_Regex["do_split/6"](
								context.vars.matches_4,
								context.vars.string_1,
								Type.integer(0n),
								context.vars.index_5,
								context.vars.trim_6,
								context.vars.include_captures_7,
							);
						},
					},
					{
						match: Type.atom("match"),
						guards: [],
						body: (context) => {
							return Type.list([context.vars.string_1]);
						},
					},
					{
						match: Type.atom("nomatch"),
						guards: [],
						body: (context) => {
							return Type.list([context.vars.string_1]);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction(
	"Req.Response.Async",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Req.Response.Async")],
					[Type.atom("cancel_fun"), Type.atom("nil")],
					[Type.atom("pid"), Type.atom("nil")],
					[Type.atom("ref"), Type.atom("nil")],
					[Type.atom("stream_fun"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Req.Response.Async",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Req.Response.Async")],
						[Type.atom("cancel_fun"), Type.atom("nil")],
						[Type.atom("pid"), Type.atom("nil")],
						[Type.atom("ref"), Type.atom("nil")],
						[Type.atom("stream_fun"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Req.Utils.CollectWithHash",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[
						Type.atom("__struct__"),
						Type.atom("Elixir.Req.Utils.CollectWithHash"),
					],
					[Type.atom("collectable"), Type.atom("nil")],
					[Type.atom("type"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Req.Utils.CollectWithHash",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.Req.Utils.CollectWithHash"),
						],
						[Type.atom("collectable"), Type.atom("nil")],
						[Type.atom("type"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("RuntimeError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.RuntimeError")],
				[Type.atom("message"), Type.bitstring("runtime error")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("RuntimeError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.RuntimeError")],
					[Type.atom("message"), Type.bitstring("runtime error")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("RuntimeError", "exception", 1, "public", [
	{
		params: (context) => [Type.variablePattern("msg_0")],
		guards: [(context) => Erlang["is_binary/1"](context.vars.msg_0)],
		body: (context) => {
			return Elixir_RuntimeError["exception/1"](
				Type.list([Type.tuple([Type.atom("message"), context.vars.msg_0])]),
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("args_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.args_0)],
		body: (context) => {
			return Elixir_Kernel["struct!/2"](
				Type.atom("Elixir.RuntimeError"),
				context.vars.args_0,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Stream", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Stream")],
				[Type.atom("accs"), Type.list([])],
				[Type.atom("done"), Type.atom("nil")],
				[Type.atom("enum"), Type.atom("nil")],
				[Type.atom("funs"), Type.list([])],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Stream", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Stream")],
					[Type.atom("accs"), Type.list([])],
					[Type.atom("done"), Type.atom("nil")],
					[Type.atom("enum"), Type.atom("nil")],
					[Type.atom("funs"), Type.list([])],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Stream",
	"do_element_resource",
	6,
	"private",
	[
		{
			params: (context) => [
				Type.variablePattern("next_acc_0"),
				Type.variablePattern("next_fun_1"),
				Type.variablePattern("acc_2"),
				Type.variablePattern("fun_3"),
				Type.variablePattern("after_fun_4"),
				Type.variablePattern("v_5"),
			],
			guards: [],
			body: (context) => {
				return Interpreter.try(
					(context) => {
						return Interpreter.callAnonymousFunction(context.vars.fun_3, [
							context.vars.v_5,
							context.vars.acc_2,
						]);
					},
					[],
					[],
					[],
					null,
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Stream", "do_enum_resource", 6, "private", [
	{
		params: (context) => [
			Type.variablePattern("next_acc_0"),
			Type.variablePattern("next_fun_1"),
			Type.tuple([Type.variablePattern("op_2"), Type.variablePattern("acc_3")]),
			Type.variablePattern("fun_4"),
			Type.variablePattern("after_fun_5"),
			Type.variablePattern("reduce_6"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.try(
				(context) => {
					return Interpreter.callAnonymousFunction(context.vars.reduce_6, [
						Type.tuple([
							context.vars.op_2,
							Interpreter.consOperator(Type.atom("cont"), context.vars.acc_3),
						]),
					]);
				},
				[],
				[],
				[],
				null,
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Stream", "do_list_resource", 6, "private", [
	{
		params: (context) => [
			Type.variablePattern("next_acc_0"),
			Type.variablePattern("next_fun_1"),
			Type.variablePattern("acc_2"),
			Type.variablePattern("fun_3"),
			Type.variablePattern("after_fun_4"),
			Type.variablePattern("reduce_5"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.try(
				(context) => {
					return Interpreter.callAnonymousFunction(context.vars.reduce_5, [
						context.vars.acc_2,
					]);
				},
				[],
				[],
				[],
				null,
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Stream", "do_resource", 5, "private", [
	{
		params: (context) => [
			Type.variablePattern("next_acc_0"),
			Type.variablePattern("next_fun_1"),
			Type.tuple([Type.atom("suspend"), Type.variablePattern("acc_2")]),
			Type.variablePattern("fun_3"),
			Type.variablePattern("after_fun_4"),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([
				Type.atom("suspended"),
				context.vars.acc_2,
				Type.anonymousFunction(
					1,
					[
						{
							params: (context) => [Type.variablePattern("$409")],
							guards: [],
							body: (context) => {
								return Elixir_Stream["do_resource/5"](
									context.vars.next_acc_0,
									context.vars.next_fun_1,
									context.vars["$409"],
									context.vars.fun_3,
									context.vars.after_fun_4,
								);
							},
						},
					],
					context,
				),
			]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("next_acc_0"),
			Type.matchPlaceholder(),
			Type.tuple([Type.atom("halt"), Type.variablePattern("acc_2")]),
			Type.matchPlaceholder(),
			Type.variablePattern("after_fun_4"),
		],
		guards: [],
		body: (context) => {
			Interpreter.callAnonymousFunction(context.vars.after_fun_4, [
				context.vars.next_acc_0,
			]);
			return Type.tuple([Type.atom("halted"), context.vars.acc_2]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("next_acc_0"),
			Type.variablePattern("next_fun_1"),
			Type.tuple([Type.atom("cont"), Type.variablePattern("acc_2")]),
			Type.variablePattern("fun_3"),
			Type.variablePattern("after_fun_4"),
		],
		guards: [],
		body: (context) => {
			globalThis.Hologram.return = Interpreter.try(
				(context) => {
					return Interpreter.callAnonymousFunction(context.vars.next_fun_1, [
						context.vars.next_acc_0,
					]);
				},
				[],
				[],
				[],
				null,
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("Stream", "do_resource_each", 3, "private", [
	{
		params: (context) => [
			Type.variablePattern("x_0"),
			Type.consPattern(Type.atom("cont"), Type.variablePattern("acc_1")),
			Type.variablePattern("f_2"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Interpreter.callAnonymousFunction(context.vars.f_2, [
					context.vars.x_0,
					context.vars.acc_1,
				]),
				[
					{
						match: Type.tuple([
							Type.variablePattern("op_3"),
							Type.variablePattern("res_4"),
						]),
						guards: [],
						body: (context) => {
							return Type.tuple([
								context.vars.op_3,
								Interpreter.consOperator(context.vars.op_3, context.vars.res_4),
							]);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Stream", "resource", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("start_fun_0"),
			Type.variablePattern("next_fun_1"),
			Type.variablePattern("after_fun_2"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) =>
						Erlang["andalso/2"](
							(context) =>
								Erlang["is_function/2"](
									context.vars.start_fun_0,
									Type.integer(0n),
								),
							(context) =>
								Erlang["is_function/2"](
									context.vars.next_fun_1,
									Type.integer(1n),
								),
							context,
						),
					(context) =>
						Erlang["is_function/2"](context.vars.after_fun_2, Type.integer(1n)),
					context,
				),
		],
		body: (context) => {
			return Type.anonymousFunction(
				2,
				[
					{
						params: (context) => [
							Type.variablePattern("$407"),
							Type.variablePattern("$408"),
						],
						guards: [],
						body: (context) => {
							return Elixir_Stream["do_resource/5"](
								Interpreter.callAnonymousFunction(context.vars.start_fun_0, []),
								context.vars.next_fun_1,
								context.vars["$407"],
								context.vars["$408"],
								context.vars.after_fun_2,
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("String", "do_splitter", 3, "private", [
	{
		params: (context) => [
			Type.atom("nomatch"),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
		],
		guards: [],
		body: (context) => {
			return Type.atom("nil");
		},
	},
	{
		params: (context) => [
			Type.bitstring(""),
			Type.matchPlaceholder(),
			Type.atom("false"),
		],
		guards: [],
		body: (context) => {
			return Type.tuple([Type.bitstring(""), Type.atom("nomatch")]);
		},
	},
	{
		params: (context) => [
			Type.bitstring(""),
			Type.matchPlaceholder(),
			Type.atom("true"),
		],
		guards: [],
		body: (context) => {
			return Type.atom("nil");
		},
	},
	{
		params: (context) => [
			Type.variablePattern("bin_0"),
			Type.variablePattern("pattern_1"),
			Type.variablePattern("trim_2"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Erlang_Binary["split/2"](context.vars.bin_0, context.vars.pattern_1),
				[
					{
						match: Type.list([
							Type.bitstring(""),
							Type.variablePattern("second_3"),
						]),
						guards: [(context) => context.vars.trim_2],
						body: (context) => {
							return Elixir_String["do_splitter/3"](
								context.vars.second_3,
								context.vars.pattern_1,
								context.vars.trim_2,
							);
						},
					},
					{
						match: Type.list([
							Type.variablePattern("first_4"),
							Type.variablePattern("second_5"),
						]),
						guards: [],
						body: (context) => {
							return Type.tuple([context.vars.first_4, context.vars.second_5]);
						},
					},
					{
						match: Type.list([Type.variablePattern("first_6")]),
						guards: [],
						body: (context) => {
							return Type.tuple([context.vars.first_6, Type.atom("nomatch")]);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("String", "grapheme_to_binary", 1, "private", [
	{
		params: (context) => [Type.variablePattern("cp_0")],
		guards: [(context) => Erlang["is_integer/1"](context.vars.cp_0)],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(context.vars.cp_0, { type: "utf8" }),
			]);
		},
	},
	{
		params: (context) => [Type.variablePattern("gc_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.gc_0)],
		body: (context) => {
			return Interpreter.comprehension(
				[
					{
						match: Type.variablePattern("cp_1"),
						guards: [],
						body: (context) => context.vars.gc_0,
					},
				],
				[],
				Type.bitstring(""),
				false,
				(context) => {
					return Type.bitstring([
						Type.bitstringSegment(context.vars.cp_1, { type: "utf8" }),
					]);
				},
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"String",
	"maybe_compile_pattern",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("pattern_0")],
			guards: [(context) => Erlang["is_tuple/1"](context.vars.pattern_0)],
			body: (context) => {
				return context.vars.pattern_0;
			},
		},
		{
			params: (context) => [Type.variablePattern("pattern_0")],
			guards: [],
			body: (context) => {
				return Erlang_Binary["compile_pattern/1"](context.vars.pattern_0);
			},
		},
	],
);

Interpreter.defineElixirFunction("String", "parts_to_index", 1, "private", [
	{
		params: (context) => [Type.atom("infinity")],
		guards: [],
		body: (context) => {
			return Type.integer(0n);
		},
	},
	{
		params: (context) => [Type.variablePattern("n_0")],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_integer/1"](context.vars.n_0),
					(context) => Erlang[">/2"](context.vars.n_0, Type.integer(0n)),
					context,
				),
		],
		body: (context) => {
			return context.vars.n_0;
		},
	},
]);

Interpreter.defineElixirFunction("String", "split", 2, "public", [
	{
		params: (context) => [
			Type.variablePattern("x0_0"),
			Type.variablePattern("x1_1"),
		],
		guards: [],
		body: (context) => {
			return Elixir_String["split/3"](
				context.vars.x0_0,
				context.vars.x1_1,
				Type.list([]),
			);
		},
	},
]);

Interpreter.defineElixirFunction("String", "split", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("string_0"),
			Interpreter.matchOperator(
				Type.variablePattern("pattern_1"),
				Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Regex")]]),
				context,
			),
			Type.variablePattern("options_2"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_binary/1"](context.vars.string_0),
					(context) => Erlang["is_list/1"](context.vars.options_2),
					context,
				),
		],
		body: (context) => {
			return Elixir_Regex["split/3"](
				context.vars.pattern_1,
				context.vars.string_0,
				context.vars.options_2,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("string_0"),
			Type.bitstring(""),
			Type.variablePattern("options_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_binary/1"](context.vars.string_0),
					(context) => Erlang["is_list/1"](context.vars.options_1),
					context,
				),
		],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Keyword["get/3"](
					context.vars.options_1,
					Type.atom("parts"),
					Type.atom("infinity"),
				),
				Type.variablePattern("parts_2"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_String["parts_to_index/1"](context.vars.parts_2),
				Type.variablePattern("index_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_Keyword["get/3"](
					context.vars.options_1,
					Type.atom("trim"),
					Type.atom("false"),
				),
				Type.variablePattern("trim_4"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				Interpreter.case(
					Erlang["==/2"](context.vars.trim_4, Type.atom("false")),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Type.atom("false");
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Erlang["/=/2"](context.vars.index_3, Type.integer(1n));
							},
						},
					],
					context,
				),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Elixir_String["split_empty/3"](
								context.vars.string_0,
								context.vars.trim_4,
								context.vars.index_3,
							);
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Interpreter.consOperator(
								Type.bitstring(""),
								Elixir_String["split_empty/3"](
									context.vars.string_0,
									context.vars.trim_4,
									Erlang["-/2"](context.vars.index_3, Type.integer(1n)),
								),
							);
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("string_0"),
			Type.list([]),
			Type.variablePattern("options_1"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_binary/1"](context.vars.string_0),
					(context) => Erlang["is_list/1"](context.vars.options_1),
					context,
				),
		],
		body: (context) => {
			return Interpreter.case(
				Interpreter.case(
					Erlang["==/2"](context.vars.string_0, Type.bitstring("")),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Type.atom("false");
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Elixir_Keyword["get/3"](
									context.vars.options_1,
									Type.atom("trim"),
									Type.atom("false"),
								);
							},
						},
					],
					context,
				),
				[
					{
						match: Type.variablePattern("x_2"),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["=:=/2"](context.vars.x_2, Type.atom("false")),
									(context) =>
										Erlang["=:=/2"](context.vars.x_2, Type.atom("nil")),
									context,
								),
						],
						body: (context) => {
							return Type.list([context.vars.string_0]);
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Type.list([]);
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("string_0"),
			Type.variablePattern("pattern_1"),
			Type.variablePattern("options_2"),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) => Erlang["is_binary/1"](context.vars.string_0),
					(context) => Erlang["is_list/1"](context.vars.options_2),
					context,
				),
		],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Keyword["get/3"](
					context.vars.options_2,
					Type.atom("parts"),
					Type.atom("infinity"),
				),
				Type.variablePattern("parts_3"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_Keyword["get/3"](
					context.vars.options_2,
					Type.atom("trim"),
					Type.atom("false"),
				),
				Type.variablePattern("trim_4"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			globalThis.Hologram.return = Interpreter.case(
				Type.tuple([context.vars.parts_3, context.vars.trim_4]),
				[
					{
						match: Type.tuple([Type.atom("infinity"), Type.atom("false")]),
						guards: [],
						body: (context) => {
							return Erlang_Binary["split/3"](
								context.vars.string_0,
								context.vars.pattern_1,
								Type.list([Type.atom("global")]),
							);
						},
					},
					{
						match: Type.tuple([Type.atom("infinity"), Type.atom("true")]),
						guards: [],
						body: (context) => {
							return Erlang_Binary["split/3"](
								context.vars.string_0,
								context.vars.pattern_1,
								Type.list([Type.atom("global"), Type.atom("trim_all")]),
							);
						},
					},
					{
						match: Type.tuple([Type.integer(2n), Type.atom("false")]),
						guards: [],
						body: (context) => {
							return Erlang_Binary["split/2"](
								context.vars.string_0,
								context.vars.pattern_1,
							);
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							Interpreter.matchOperator(
								Elixir_String["maybe_compile_pattern/1"](
									context.vars.pattern_1,
								),
								Type.variablePattern("pattern_5"),
								context,
							);
							Interpreter.updateVarsToMatchedValues(context);
							return Elixir_String["split_each/4"](
								context.vars.string_0,
								context.vars.pattern_5,
								context.vars.trim_4,
								Elixir_String["parts_to_index/1"](context.vars.parts_3),
							);
						},
					},
				],
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return globalThis.Hologram.return;
		},
	},
]);

Interpreter.defineElixirFunction("String", "split_each", 4, "private", [
	{
		params: (context) => [
			Type.bitstring(""),
			Type.matchPlaceholder(),
			Type.atom("true"),
			Type.integer(1n),
		],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("string_0"),
			Type.matchPlaceholder(),
			Type.matchPlaceholder(),
			Type.integer(1n),
		],
		guards: [(context) => Erlang["is_binary/1"](context.vars.string_0)],
		body: (context) => {
			return Type.list([context.vars.string_0]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("string_0"),
			Type.variablePattern("pattern_1"),
			Type.variablePattern("trim_2"),
			Type.variablePattern("count_3"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Elixir_String["do_splitter/3"](
					context.vars.string_0,
					context.vars.pattern_1,
					context.vars.trim_2,
				),
				[
					{
						match: Type.tuple([
							Type.variablePattern("h_4"),
							Type.variablePattern("t_5"),
						]),
						guards: [],
						body: (context) => {
							return Interpreter.consOperator(
								context.vars.h_4,
								Elixir_String["split_each/4"](
									context.vars.t_5,
									context.vars.pattern_1,
									context.vars.trim_2,
									Erlang["-/2"](context.vars.count_3, Type.integer(1n)),
								),
							);
						},
					},
					{
						match: Type.atom("nil"),
						guards: [],
						body: (context) => {
							return Type.list([]);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("String", "split_empty", 3, "private", [
	{
		params: (context) => [
			Type.bitstring(""),
			Type.atom("true"),
			Type.integer(1n),
		],
		guards: [],
		body: (context) => {
			return Type.list([]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("string_0"),
			Type.matchPlaceholder(),
			Type.integer(1n),
		],
		guards: [],
		body: (context) => {
			return Type.list([Erlang["iolist_to_binary/1"](context.vars.string_0)]);
		},
	},
	{
		params: (context) => [
			Type.variablePattern("string_0"),
			Type.variablePattern("trim_1"),
			Type.variablePattern("count_2"),
		],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Erlang_Unicode_Util["gc/1"](context.vars.string_0),
				[
					{
						match: Type.list([Type.variablePattern("gc_3")]),
						guards: [],
						body: (context) => {
							return Interpreter.consOperator(
								Elixir_String["grapheme_to_binary/1"](context.vars.gc_3),
								Elixir_String["split_empty/3"](
									Type.bitstring(""),
									context.vars.trim_1,
									Type.integer(1n),
								),
							);
						},
					},
					{
						match: Type.consPattern(
							Type.variablePattern("gc_4"),
							Type.variablePattern("rest_5"),
						),
						guards: [],
						body: (context) => {
							return Interpreter.consOperator(
								Elixir_String["grapheme_to_binary/1"](context.vars.gc_4),
								Elixir_String["split_empty/3"](
									context.vars.rest_5,
									context.vars.trim_1,
									Erlang["-/2"](context.vars.count_2, Type.integer(1n)),
								),
							);
						},
					},
					{
						match: Type.list([]),
						guards: [],
						body: (context) => {
							return Elixir_String["split_empty/3"](
								Type.bitstring(""),
								context.vars.trim_1,
								Type.integer(1n),
							);
						},
					},
					{
						match: Type.tuple([
							Type.atom("error"),
							Type.bitstringPattern([
								Type.bitstringSegment(Type.variablePattern("byte_6"), {
									type: "integer",
								}),
								Type.bitstringSegment(Type.variablePattern("rest_7"), {
									type: "bitstring",
								}),
							]),
						]),
						guards: [],
						body: (context) => {
							return Interpreter.consOperator(
								Type.bitstring([
									Type.bitstringSegment(context.vars.byte_6, {
										type: "integer",
									}),
								]),
								Elixir_String["split_empty/3"](
									context.vars.rest_7,
									context.vars.trim_1,
									Erlang["-/2"](context.vars.count_2, Type.integer(1n)),
								),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("String.Chars", "impl_for", 1, "public", [
	{
		params: (context) => [
			Type.map([[Type.atom("__struct__"), Type.variablePattern("struct_0")]]),
		],
		guards: [],
		body: (context) => {
			return Elixir_String_Chars["struct_impl_for/1"](context.vars.struct_0);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_tuple/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.String.Chars.Tuple"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_atom/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.Atom")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_list/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.List")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_map/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.Map")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_bitstring/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.String.Chars.BitString"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_integer/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.String.Chars.Integer"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_float/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.String.Chars.Float"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_function/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.String.Chars.Function"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_pid/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.PID")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_port/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.Port")),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [(context) => Erlang["is_reference/1"](context.vars.data_0)],
		body: (context) => {
			return Interpreter.case(
				Elixir_Code["ensure_compiled/1"](
					Type.atom("Elixir.String.Chars.Reference"),
				),
				[
					{
						match: Type.tuple([
							Type.atom("module"),
							Type.variablePattern("module_1"),
						]),
						guards: [],
						body: (context) => {
							return context.vars.module_1;
						},
					},
					{
						match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
						guards: [],
						body: (context) => {
							return Type.atom("nil");
						},
					},
				],
				context,
			);
		},
	},
	{
		params: (context) => [Type.matchPlaceholder()],
		guards: [],
		body: (context) => {
			return Type.atom("nil");
		},
	},
]);

Interpreter.defineElixirFunction("String.Chars", "impl_for!", 1, "public", [
	{
		params: (context) => [Type.variablePattern("data_0")],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Elixir_String_Chars["impl_for/1"](context.vars.data_0),
				[
					{
						match: Type.variablePattern("x_1"),
						guards: [
							(context) =>
								Erlang["orelse/2"](
									(context) =>
										Erlang["=:=/2"](context.vars.x_1, Type.atom("false")),
									(context) =>
										Erlang["=:=/2"](context.vars.x_1, Type.atom("nil")),
									context,
								),
						],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_Protocol_UndefinedError["exception/1"](
									Type.list([
										Type.tuple([
											Type.atom("protocol"),
											Type.atom("Elixir.String.Chars"),
										]),
										Type.tuple([Type.atom("value"), context.vars.data_0]),
										Type.tuple([Type.atom("description"), Type.bitstring("")]),
									]),
								),
							);
						},
					},
					{
						match: Type.variablePattern("x_2"),
						guards: [],
						body: (context) => {
							return context.vars.x_2;
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"String.Chars",
	"struct_impl_for",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("struct_0")],
			guards: [],
			body: (context) => {
				return Interpreter.case(
					Elixir_Code["ensure_compiled/1"](
						Elixir_Protocol["__concat__/2"](
							Type.atom("Elixir.String.Chars"),
							context.vars.struct_0,
						),
					),
					[
						{
							match: Type.tuple([
								Type.atom("module"),
								Type.variablePattern("module_1"),
							]),
							guards: [],
							body: (context) => {
								return context.vars.module_1;
							},
						},
						{
							match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]),
							guards: [],
							body: (context) => {
								return Type.atom("nil");
							},
						},
					],
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("String.Chars", "to_string", 1, "public", [
	{
		params: (context) => [Type.variablePattern("term_0")],
		guards: [],
		body: (context) => {
			return Interpreter.callNamedFunction(
				Elixir_String_Chars["impl_for!/1"](context.vars.term_0),
				Type.atom("to_string"),
				Type.list([context.vars.term_0]),
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("String.Chars.Atom", "__impl__", 1, "public", [
	{
		params: (context) => [Type.atom("for")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Atom");
		},
	},
	{
		params: (context) => [Type.atom("protocol")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.String.Chars");
		},
	},
]);

Interpreter.defineElixirFunction(
	"String.Chars.Atom",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("nil")],
			guards: [],
			body: (context) => {
				return Type.bitstring("");
			},
		},
		{
			params: (context) => [Type.variablePattern("atom_0")],
			guards: [],
			body: (context) => {
				return Erlang["atom_to_binary/1"](context.vars.atom_0);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.BitString",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.BitString");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.String.Chars");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.BitString",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("term_0")],
			guards: [(context) => Erlang["is_binary/1"](context.vars.term_0)],
			body: (context) => {
				return context.vars.term_0;
			},
		},
		{
			params: (context) => [Type.variablePattern("term_0")],
			guards: [],
			body: (context) => {
				return Erlang["error/1"](
					Elixir_Protocol_UndefinedError["exception/1"](
						Type.list([
							Type.tuple([
								Type.atom("protocol"),
								Type.atom("Elixir.String.Chars"),
							]),
							Type.tuple([Type.atom("value"), context.vars.term_0]),
							Type.tuple([
								Type.atom("description"),
								Type.bitstring("cannot convert a bitstring to a string"),
							]),
						]),
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("String.Chars.Date", "__impl__", 1, "public", [
	{
		params: (context) => [Type.atom("for")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Date");
		},
	},
	{
		params: (context) => [Type.atom("protocol")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.String.Chars");
		},
	},
]);

Interpreter.defineElixirFunction(
	"String.Chars.Date",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [
				Type.map([
					[Type.atom("calendar"), Type.variablePattern("calendar_0")],
					[Type.atom("day"), Type.variablePattern("day_3")],
					[Type.atom("month"), Type.variablePattern("month_2")],
					[Type.atom("year"), Type.variablePattern("year_1")],
				]),
			],
			guards: [],
			body: (context) => {
				return Interpreter.callNamedFunction(
					context.vars.calendar_0,
					Type.atom("date_to_string"),
					Type.list([
						context.vars.year_1,
						context.vars.month_2,
						context.vars.day_3,
					]),
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.DateTime",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.DateTime");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.String.Chars");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.DateTime",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("datetime_0")],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					context.vars.datetime_0,
					Type.map([
						[Type.atom("calendar"), Type.variablePattern("calendar_1")],
						[Type.atom("day"), Type.variablePattern("day_4")],
						[Type.atom("hour"), Type.variablePattern("hour_5")],
						[Type.atom("microsecond"), Type.variablePattern("microsecond_8")],
						[Type.atom("minute"), Type.variablePattern("minute_6")],
						[Type.atom("month"), Type.variablePattern("month_3")],
						[Type.atom("second"), Type.variablePattern("second_7")],
						[Type.atom("std_offset"), Type.variablePattern("std_offset_12")],
						[Type.atom("time_zone"), Type.variablePattern("time_zone_9")],
						[Type.atom("utc_offset"), Type.variablePattern("utc_offset_11")],
						[Type.atom("year"), Type.variablePattern("year_2")],
						[Type.atom("zone_abbr"), Type.variablePattern("zone_abbr_10")],
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Interpreter.callNamedFunction(
					context.vars.calendar_1,
					Type.atom("datetime_to_string"),
					Type.list([
						context.vars.year_2,
						context.vars.month_3,
						context.vars.day_4,
						context.vars.hour_5,
						context.vars.minute_6,
						context.vars.second_7,
						context.vars.microsecond_8,
						context.vars.time_zone_9,
						context.vars.zone_abbr_10,
						context.vars.utc_offset_11,
						context.vars.std_offset_12,
					]),
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.Decimal",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Decimal");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.String.Chars");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.Decimal",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("dec_0")],
			guards: [],
			body: (context) => {
				return Elixir_Decimal["to_string/1"](context.vars.dec_0);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.Exqlite.Query",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Exqlite.Query");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.String.Chars");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.Exqlite.Query",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [
				Type.map([
					[Type.atom("statement"), Type.variablePattern("statement_0")],
				]),
			],
			guards: [],
			body: (context) => {
				return Erlang["iolist_to_binary/1"](context.vars.statement_0);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.Float",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Float");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.String.Chars");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.Float",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("term_0")],
			guards: [],
			body: (context) => {
				return Erlang["float_to_binary/2"](
					context.vars.term_0,
					Type.list([Type.atom("short")]),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.Integer",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Integer");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.String.Chars");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.Integer",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("term_0")],
			guards: [],
			body: (context) => {
				return Erlang["integer_to_binary/1"](context.vars.term_0);
			},
		},
	],
);

Interpreter.defineElixirFunction("String.Chars.List", "__impl__", 1, "public", [
	{
		params: (context) => [Type.atom("for")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.List");
		},
	},
	{
		params: (context) => [Type.atom("protocol")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.String.Chars");
		},
	},
]);

Interpreter.defineElixirFunction(
	"String.Chars.List",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("charlist_0")],
			guards: [],
			body: (context) => {
				return Elixir_List["to_string/1"](context.vars.charlist_0);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.NaiveDateTime",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.NaiveDateTime");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.String.Chars");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.NaiveDateTime",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("naive_datetime_0")],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					context.vars.naive_datetime_0,
					Type.map([
						[Type.atom("calendar"), Type.variablePattern("calendar_1")],
						[Type.atom("day"), Type.variablePattern("day_4")],
						[Type.atom("hour"), Type.variablePattern("hour_5")],
						[Type.atom("microsecond"), Type.variablePattern("microsecond_8")],
						[Type.atom("minute"), Type.variablePattern("minute_6")],
						[Type.atom("month"), Type.variablePattern("month_3")],
						[Type.atom("second"), Type.variablePattern("second_7")],
						[Type.atom("year"), Type.variablePattern("year_2")],
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Interpreter.callNamedFunction(
					context.vars.calendar_1,
					Type.atom("naive_datetime_to_string"),
					Type.list([
						context.vars.year_2,
						context.vars.month_3,
						context.vars.day_4,
						context.vars.hour_5,
						context.vars.minute_6,
						context.vars.second_7,
						context.vars.microsecond_8,
					]),
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.Phoenix.LiveComponent.CID",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Phoenix.LiveComponent.CID");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.String.Chars");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.Phoenix.LiveComponent.CID",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [
				Type.map([[Type.atom("cid"), Type.variablePattern("cid_0")]]),
			],
			guards: [],
			body: (context) => {
				return Erlang["integer_to_binary/1"](context.vars.cid_0);
			},
		},
	],
);

Interpreter.defineElixirFunction("String.Chars.Time", "__impl__", 1, "public", [
	{
		params: (context) => [Type.atom("for")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.Time");
		},
	},
	{
		params: (context) => [Type.atom("protocol")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.String.Chars");
		},
	},
]);

Interpreter.defineElixirFunction(
	"String.Chars.Time",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("time_0")],
			guards: [],
			body: (context) => {
				Interpreter.matchOperator(
					context.vars.time_0,
					Type.map([
						[Type.atom("calendar"), Type.variablePattern("calendar_5")],
						[Type.atom("hour"), Type.variablePattern("hour_1")],
						[Type.atom("microsecond"), Type.variablePattern("microsecond_4")],
						[Type.atom("minute"), Type.variablePattern("minute_2")],
						[Type.atom("second"), Type.variablePattern("second_3")],
					]),
					context,
				);
				Interpreter.updateVarsToMatchedValues(context);
				return Interpreter.callNamedFunction(
					context.vars.calendar_5,
					Type.atom("time_to_string"),
					Type.list([
						context.vars.hour_1,
						context.vars.minute_2,
						context.vars.second_3,
						context.vars.microsecond_4,
					]),
					context,
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("String.Chars.URI", "__impl__", 1, "public", [
	{
		params: (context) => [Type.atom("for")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.URI");
		},
	},
	{
		params: (context) => [Type.atom("protocol")],
		guards: [],
		body: (context) => {
			return Type.atom("Elixir.String.Chars");
		},
	},
]);

Interpreter.defineElixirFunction(
	"String.Chars.URI",
	"extract_authority",
	1,
	"private",
	[
		{
			params: (context) => [
				Type.map([
					[Type.atom("authority"), Type.variablePattern("authority_0")],
					[Type.atom("host"), Type.atom("nil")],
				]),
			],
			guards: [],
			body: (context) => {
				return context.vars.authority_0;
			},
		},
		{
			params: (context) => [
				Type.map([
					[Type.atom("host"), Type.variablePattern("host_0")],
					[Type.atom("port"), Type.variablePattern("port_2")],
					[Type.atom("userinfo"), Type.variablePattern("userinfo_1")],
				]),
			],
			guards: [],
			body: (context) => {
				return Type.list([
					Interpreter.case(
						context.vars.userinfo_1,
						[
							{
								match: Type.variablePattern("x_3"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_3, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_3, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return Type.list([]);
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Interpreter.consOperator(
										context.vars.userinfo_1,
										Type.bitstring("@"),
									);
								},
							},
						],
						context,
					),
					Interpreter.case(
						Elixir_String["contains?/2"](
							context.vars.host_0,
							Type.bitstring(":"),
						),
						[
							{
								match: Type.variablePattern("x_4"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_4, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_4, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return context.vars.host_0;
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Interpreter.consOperator(
										Type.bitstring("["),
										Interpreter.consOperator(
											context.vars.host_0,
											Type.bitstring("]"),
										),
									);
								},
							},
						],
						context,
					),
					Interpreter.case(
						context.vars.port_2,
						[
							{
								match: Type.variablePattern("x_5"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_5, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_5, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return Type.list([]);
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Interpreter.consOperator(
										Type.bitstring(":"),
										Erlang["integer_to_binary/1"](context.vars.port_2),
									);
								},
							},
						],
						context,
					),
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction("String.Chars.URI", "to_string", 1, "public", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("uri_2"),
				Type.map([
					[Type.atom("host"), Type.variablePattern("host_0")],
					[Type.atom("path"), Type.variablePattern("path_1")],
				]),
				context,
			),
		],
		guards: [
			(context) =>
				Erlang["andalso/2"](
					(context) =>
						Erlang["andalso/2"](
							(context) =>
								Erlang["andalso/2"](
									(context) =>
										Erlang["/=/2"](context.vars.host_0, Type.atom("nil")),
									(context) => Erlang["is_binary/1"](context.vars.path_1),
									context,
								),
							(context) =>
								Erlang["/=/2"](context.vars.path_1, Type.bitstring("")),
							context,
						),
					(context) =>
						Erlang["/=/2"](
							Erlang["binary_part/3"](
								context.vars.path_1,
								Type.integer(0n),
								Type.integer(1n),
							),
							Type.bitstring("/"),
						),
					context,
				),
		],
		body: (context) => {
			return Erlang["error/1"](
				Elixir_ArgumentError["exception/1"](
					Type.bitstring([
						Type.bitstringSegment(
							Type.string(
								":path in URI must be empty or an absolute path if URL has a :host, got: ",
							),
							{ type: "binary" },
						),
						Type.bitstringSegment(
							Elixir_Kernel["inspect/1"](context.vars.uri_2),
							{ type: "binary" },
						),
					]),
				),
			);
		},
	},
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("uri_5"),
				Type.map([
					[Type.atom("fragment"), Type.variablePattern("fragment_4")],
					[Type.atom("path"), Type.variablePattern("path_2")],
					[Type.atom("port"), Type.variablePattern("port_1")],
					[Type.atom("query"), Type.variablePattern("query_3")],
					[Type.atom("scheme"), Type.variablePattern("scheme_0")],
				]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Interpreter.case(
					Interpreter.case(
						context.vars.scheme_0,
						[
							{
								match: Type.variablePattern("x_6"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_6, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_6, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return context.vars.x_6;
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Elixir_URI["default_port/1"](context.vars.scheme_0);
								},
							},
						],
						context,
					),
					[
						{
							match: context.vars.port_1,
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.uri_5,
									Type.map([[Type.atom("port"), Type.atom("nil")]]),
								);
							},
						},
						{
							match: Type.matchPlaceholder(),
							guards: [],
							body: (context) => {
								return context.vars.uri_5;
							},
						},
					],
					context,
				),
				Type.variablePattern("uri_7"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Elixir_String_Chars_URI["extract_authority/1"](context.vars.uri_7),
				Type.variablePattern("authority_8"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Erlang["iolist_to_binary/1"](
				Type.list([
					Interpreter.case(
						context.vars.scheme_0,
						[
							{
								match: Type.variablePattern("x_9"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_9, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_9, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return Type.list([]);
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Type.list([context.vars.scheme_0, Type.integer(58n)]);
								},
							},
						],
						context,
					),
					Interpreter.case(
						context.vars.authority_8,
						[
							{
								match: Type.variablePattern("x_10"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_10, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_10, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return Type.list([]);
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Interpreter.consOperator(
										Type.bitstring("//"),
										context.vars.authority_8,
									);
								},
							},
						],
						context,
					),
					Interpreter.case(
						context.vars.path_2,
						[
							{
								match: Type.variablePattern("x_11"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_11, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_11, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return Type.list([]);
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return context.vars.path_2;
								},
							},
						],
						context,
					),
					Interpreter.case(
						context.vars.query_3,
						[
							{
								match: Type.variablePattern("x_12"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_12, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_12, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return Type.list([]);
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Interpreter.consOperator(
										Type.bitstring("?"),
										context.vars.query_3,
									);
								},
							},
						],
						context,
					),
					Interpreter.case(
						context.vars.fragment_4,
						[
							{
								match: Type.variablePattern("x_13"),
								guards: [
									(context) =>
										Erlang["orelse/2"](
											(context) =>
												Erlang["=:=/2"](context.vars.x_13, Type.atom("false")),
											(context) =>
												Erlang["=:=/2"](context.vars.x_13, Type.atom("nil")),
											context,
										),
								],
								body: (context) => {
									return Type.list([]);
								},
							},
							{
								match: Type.matchPlaceholder(),
								guards: [],
								body: (context) => {
									return Interpreter.consOperator(
										Type.bitstring("#"),
										context.vars.fragment_4,
									);
								},
							},
						],
						context,
					),
				]),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"String.Chars.Version",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Version");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.String.Chars");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.Version",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("version_0")],
			guards: [],
			body: (context) => {
				return Elixir_Version["to_string/1"](context.vars.version_0);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.Version.Requirement",
	"__impl__",
	1,
	"public",
	[
		{
			params: (context) => [Type.atom("for")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.Version.Requirement");
			},
		},
		{
			params: (context) => [Type.atom("protocol")],
			guards: [],
			body: (context) => {
				return Type.atom("Elixir.String.Chars");
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"String.Chars.Version.Requirement",
	"to_string",
	1,
	"public",
	[
		{
			params: (context) => [
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Version.Requirement")],
					[Type.atom("source"), Type.variablePattern("source_0")],
				]),
			],
			guards: [],
			body: (context) => {
				return context.vars.source_0;
			},
		},
	],
);

Interpreter.defineElixirFunction("System", "convert_time_unit", 3, "public", [
	{
		params: (context) => [
			Type.variablePattern("time_0"),
			Type.variablePattern("from_unit_1"),
			Type.variablePattern("to_unit_2"),
		],
		guards: [],
		body: (context) => {
			return Erlang["convert_time_unit/3"](
				context.vars.time_0,
				Elixir_System["normalize_time_unit/1"](context.vars.from_unit_1),
				Elixir_System["normalize_time_unit/1"](context.vars.to_unit_2),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"System",
	"normalize_time_unit",
	1,
	"private",
	[
		{
			params: (context) => [Type.atom("native")],
			guards: [],
			body: (context) => {
				return Type.atom("native");
			},
		},
		{
			params: (context) => [Type.atom("second")],
			guards: [],
			body: (context) => {
				return Type.atom("second");
			},
		},
		{
			params: (context) => [Type.atom("millisecond")],
			guards: [],
			body: (context) => {
				return Type.atom("millisecond");
			},
		},
		{
			params: (context) => [Type.atom("microsecond")],
			guards: [],
			body: (context) => {
				return Type.atom("microsecond");
			},
		},
		{
			params: (context) => [Type.atom("nanosecond")],
			guards: [],
			body: (context) => {
				return Type.atom("nanosecond");
			},
		},
		{
			params: (context) => [Type.atom("seconds")],
			guards: [],
			body: (context) => {
				return Elixir_System["warn/2"](
					Type.atom("seconds"),
					Type.atom("second"),
				);
			},
		},
		{
			params: (context) => [Type.atom("milliseconds")],
			guards: [],
			body: (context) => {
				return Elixir_System["warn/2"](
					Type.atom("milliseconds"),
					Type.atom("millisecond"),
				);
			},
		},
		{
			params: (context) => [Type.atom("microseconds")],
			guards: [],
			body: (context) => {
				return Elixir_System["warn/2"](
					Type.atom("microseconds"),
					Type.atom("microsecond"),
				);
			},
		},
		{
			params: (context) => [Type.atom("nanoseconds")],
			guards: [],
			body: (context) => {
				return Elixir_System["warn/2"](
					Type.atom("nanoseconds"),
					Type.atom("nanosecond"),
				);
			},
		},
		{
			params: (context) => [Type.atom("milli_seconds")],
			guards: [],
			body: (context) => {
				return Elixir_System["warn/2"](
					Type.atom("milli_seconds"),
					Type.atom("millisecond"),
				);
			},
		},
		{
			params: (context) => [Type.atom("micro_seconds")],
			guards: [],
			body: (context) => {
				return Elixir_System["warn/2"](
					Type.atom("micro_seconds"),
					Type.atom("microsecond"),
				);
			},
		},
		{
			params: (context) => [Type.atom("nano_seconds")],
			guards: [],
			body: (context) => {
				return Elixir_System["warn/2"](
					Type.atom("nano_seconds"),
					Type.atom("nanosecond"),
				);
			},
		},
		{
			params: (context) => [Type.variablePattern("unit_0")],
			guards: [
				(context) =>
					Erlang["andalso/2"](
						(context) => Erlang["is_integer/1"](context.vars.unit_0),
						(context) => Erlang[">/2"](context.vars.unit_0, Type.integer(0n)),
						context,
					),
			],
			body: (context) => {
				return context.vars.unit_0;
			},
		},
		{
			params: (context) => [Type.variablePattern("other_0")],
			guards: [],
			body: (context) => {
				return Erlang["error/1"](
					Elixir_ArgumentError["exception/1"](
						Type.bitstring([
							Type.bitstringSegment(
								Type.string(
									"unsupported time unit. Expected :second, :millisecond, ",
								),
								{ type: "binary" },
							),
							Type.bitstringSegment(
								Type.string(
									":microsecond, :nanosecond, or a positive integer, ",
								),
								{ type: "binary" },
							),
							Type.bitstringSegment(Type.string("got "), { type: "binary" }),
							Type.bitstringSegment(
								Elixir_Kernel["inspect/1"](context.vars.other_0),
								{ type: "binary" },
							),
						]),
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("System", "warn", 2, "private", [
	{
		params: (context) => [
			Type.variablePattern("unit_0"),
			Type.variablePattern("replacement_unit_1"),
		],
		guards: [],
		body: (context) => {
			Elixir_IO["warn_once/3"](
				Type.tuple([Type.atom("Elixir.System"), context.vars.unit_0]),
				Type.anonymousFunction(
					0,
					[
						{
							params: (context) => [],
							guards: [],
							body: (context) => {
								return Type.bitstring([
									Type.bitstringSegment(Type.string("deprecated time unit: "), {
										type: "binary",
									}),
									Type.bitstringSegment(
										Elixir_Kernel["inspect/1"](context.vars.unit_0),
										{ type: "binary" },
									),
									Type.bitstringSegment(
										Type.string(". A time unit should be "),
										{ type: "binary" },
									),
									Type.bitstringSegment(
										Type.string(
											":second, :millisecond, :microsecond, :nanosecond, or a positive integer",
										),
										{ type: "binary" },
									),
								]);
							},
						},
					],
					context,
				),
				Interpreter.matchOperator(
					Type.integer(4n),
					Type.matchPlaceholder(),
					context,
				),
			);
			Interpreter.updateVarsToMatchedValues(context);
			return context.vars.replacement_unit_1;
		},
	},
]);

Interpreter.defineElixirFunction(
	"SystemLimitError",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.SystemLimitError")],
					[
						Type.atom("message"),
						Type.bitstring("a system limit has been reached"),
					],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"SystemLimitError",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[Type.atom("__exception__"), Type.atom("true")],
						[Type.atom("__struct__"), Type.atom("Elixir.SystemLimitError")],
						[
							Type.atom("message"),
							Type.bitstring("a system limit has been reached"),
						],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Time", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Time")],
				[Type.atom("calendar"), Type.atom("Elixir.Calendar.ISO")],
				[Type.atom("hour"), Type.atom("nil")],
				[
					Type.atom("microsecond"),
					Type.tuple([Type.integer(0n), Type.integer(0n)]),
				],
				[Type.atom("minute"), Type.atom("nil")],
				[Type.atom("second"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Time", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.tuple([
						Type.map([
							[Type.atom("__struct__"), Type.atom("Elixir.Time")],
							[Type.atom("calendar"), Type.atom("Elixir.Calendar.ISO")],
							[Type.atom("hour"), Type.atom("nil")],
							[
								Type.atom("microsecond"),
								Type.tuple([Type.integer(0n), Type.integer(0n)]),
							],
							[Type.atom("minute"), Type.atom("nil")],
							[Type.atom("second"), Type.atom("nil")],
						]),
						Type.list([
							Type.atom("hour"),
							Type.atom("minute"),
							Type.atom("second"),
						]),
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.tuple([
										Type.variablePattern("map_3"),
										Type.variablePattern("keys_4"),
									]),
								],
								guards: [],
								body: (context) => {
									return Type.tuple([
										Elixir_Map["merge/2"](
											context.vars.map_3,
											Type.map([[context.vars.key_1, context.vars.val_2]]),
										),
										Elixir_List["delete/2"](
											context.vars.keys_4,
											context.vars.key_1,
										),
									]);
								},
							},
						],
						context,
					),
				),
				Type.tuple([
					Type.variablePattern("map_5"),
					Type.variablePattern("keys_6"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				context.vars.keys_6,
				[
					{
						match: Type.list([]),
						guards: [],
						body: (context) => {
							return context.vars.map_5;
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_ArgumentError["exception/1"](
									Type.bitstring([
										Type.bitstringSegment(
											Type.string(
												"the following keys must also be given when building ",
											),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string("struct "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](Type.atom("Elixir.Time")),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string(": "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](context.vars.keys_6),
											{ type: "binary" },
										),
									]),
								),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("TryClauseError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.TryClauseError")],
				[Type.atom("term"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("TryClauseError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.TryClauseError")],
					[Type.atom("term"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("URI", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.URI")],
				[Type.atom("authority"), Type.atom("nil")],
				[Type.atom("fragment"), Type.atom("nil")],
				[Type.atom("host"), Type.atom("nil")],
				[Type.atom("path"), Type.atom("nil")],
				[Type.atom("port"), Type.atom("nil")],
				[Type.atom("query"), Type.atom("nil")],
				[Type.atom("scheme"), Type.atom("nil")],
				[Type.atom("userinfo"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("URI", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.URI")],
					[Type.atom("authority"), Type.atom("nil")],
					[Type.atom("fragment"), Type.atom("nil")],
					[Type.atom("host"), Type.atom("nil")],
					[Type.atom("path"), Type.atom("nil")],
					[Type.atom("port"), Type.atom("nil")],
					[Type.atom("query"), Type.atom("nil")],
					[Type.atom("scheme"), Type.atom("nil")],
					[Type.atom("userinfo"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

Interpreter.defineElixirFunction("URI", "char_unreserved?", 1, "public", [
	{
		params: (context) => [Type.variablePattern("character_0")],
		guards: [],
		body: (context) => {
			return Interpreter.case(
				Interpreter.case(
					Interpreter.case(
						Erlang["andalso/2"](
							(context) => Erlang["is_integer/1"](context.vars.character_0),
							(context) =>
								Erlang["andalso/2"](
									(context) =>
										Erlang[">=/2"](context.vars.character_0, Type.integer(48n)),
									(context) =>
										Erlang["=</2"](context.vars.character_0, Type.integer(57n)),
									context,
								),
							context,
						),
						[
							{
								match: Type.atom("false"),
								guards: [],
								body: (context) => {
									return Erlang["andalso/2"](
										(context) =>
											Erlang["is_integer/1"](context.vars.character_0),
										(context) =>
											Erlang["andalso/2"](
												(context) =>
													Erlang[">=/2"](
														context.vars.character_0,
														Type.integer(97n),
													),
												(context) =>
													Erlang["=</2"](
														context.vars.character_0,
														Type.integer(122n),
													),
												context,
											),
										context,
									);
								},
							},
							{
								match: Type.atom("true"),
								guards: [],
								body: (context) => {
									return Type.atom("true");
								},
							},
						],
						context,
					),
					[
						{
							match: Type.atom("false"),
							guards: [],
							body: (context) => {
								return Erlang["andalso/2"](
									(context) => Erlang["is_integer/1"](context.vars.character_0),
									(context) =>
										Erlang["andalso/2"](
											(context) =>
												Erlang[">=/2"](
													context.vars.character_0,
													Type.integer(65n),
												),
											(context) =>
												Erlang["=</2"](
													context.vars.character_0,
													Type.integer(90n),
												),
											context,
										),
									context,
								);
							},
						},
						{
							match: Type.atom("true"),
							guards: [],
							body: (context) => {
								return Type.atom("true");
							},
						},
					],
					context,
				),
				[
					{
						match: Type.atom("false"),
						guards: [],
						body: (context) => {
							return Erlang_Lists["member/2"](
								context.vars.character_0,
								Type.list([
									Type.integer(126n),
									Type.integer(95n),
									Type.integer(45n),
									Type.integer(46n),
								]),
							);
						},
					},
					{
						match: Type.atom("true"),
						guards: [],
						body: (context) => {
							return Type.atom("true");
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("URI", "default_port", 1, "public", [
	{
		params: (context) => [Type.variablePattern("scheme_0")],
		guards: [(context) => Erlang["is_binary/1"](context.vars.scheme_0)],
		body: (context) => {
			return Erlang_Elixir_Config["get/2"](
				Type.tuple([Type.atom("uri"), context.vars.scheme_0]),
				Type.atom("nil"),
			);
		},
	},
]);

Interpreter.defineElixirFunction(
	"UndefinedFunctionError",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.UndefinedFunctionError")],
					[Type.atom("arity"), Type.atom("nil")],
					[Type.atom("function"), Type.atom("nil")],
					[Type.atom("message"), Type.atom("nil")],
					[Type.atom("module"), Type.atom("nil")],
					[Type.atom("reason"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"UndefinedFunctionError",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[Type.atom("__exception__"), Type.atom("true")],
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.UndefinedFunctionError"),
						],
						[Type.atom("arity"), Type.atom("nil")],
						[Type.atom("function"), Type.atom("nil")],
						[Type.atom("message"), Type.atom("nil")],
						[Type.atom("module"), Type.atom("nil")],
						[Type.atom("reason"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"UnicodeConversionError",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.UnicodeConversionError")],
					[Type.atom("encoded"), Type.atom("nil")],
					[Type.atom("message"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"UnicodeConversionError",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[Type.atom("__exception__"), Type.atom("true")],
						[
							Type.atom("__struct__"),
							Type.atom("Elixir.UnicodeConversionError"),
						],
						[Type.atom("encoded"), Type.atom("nil")],
						[Type.atom("message"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"UnicodeConversionError",
	"detail",
	1,
	"private",
	[
		{
			params: (context) => [Type.variablePattern("rest_0")],
			guards: [(context) => Erlang["is_binary/1"](context.vars.rest_0)],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(Type.string("encoding starting at "), {
						type: "binary",
					}),
					Type.bitstringSegment(
						Elixir_Kernel["inspect/1"](context.vars.rest_0),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.consPattern(Type.variablePattern("h_0"), Type.matchPlaceholder()),
			],
			guards: [(context) => Erlang["is_integer/1"](context.vars.h_0)],
			body: (context) => {
				return Type.bitstring([
					Type.bitstringSegment(Type.string("code point "), { type: "binary" }),
					Type.bitstringSegment(
						Elixir_String_Chars["to_string/1"](context.vars.h_0),
						{ type: "binary" },
					),
				]);
			},
		},
		{
			params: (context) => [
				Type.consPattern(Type.variablePattern("h_0"), Type.matchPlaceholder()),
			],
			guards: [],
			body: (context) => {
				return Elixir_UnicodeConversionError["detail/1"](context.vars.h_0);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"UnicodeConversionError",
	"exception",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("opts_0")],
			guards: [],
			body: (context) => {
				return Elixir_UnicodeConversionError["__struct__/1"](
					Type.list([
						Type.tuple([Type.atom("__exception__"), Type.atom("true")]),
						Type.tuple([
							Type.atom("encoded"),
							Elixir_Keyword["fetch!/2"](
								context.vars.opts_0,
								Type.atom("encoded"),
							),
						]),
						Type.tuple([
							Type.atom("message"),
							Type.bitstring([
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](
										Elixir_Keyword["fetch!/2"](
											context.vars.opts_0,
											Type.atom("kind"),
										),
									),
									{ type: "binary" },
								),
								Type.bitstringSegment(Type.string(" "), { type: "binary" }),
								Type.bitstringSegment(
									Elixir_String_Chars["to_string/1"](
										Elixir_UnicodeConversionError["detail/1"](
											Elixir_Keyword["fetch!/2"](
												context.vars.opts_0,
												Type.atom("rest"),
											),
										),
									),
									{ type: "binary" },
								),
							]),
						]),
					]),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("Version", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__struct__"), Type.atom("Elixir.Version")],
				[Type.atom("build"), Type.atom("nil")],
				[Type.atom("major"), Type.atom("nil")],
				[Type.atom("minor"), Type.atom("nil")],
				[Type.atom("patch"), Type.atom("nil")],
				[Type.atom("pre"), Type.list([])],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Version", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.tuple([
						Type.map([
							[Type.atom("__struct__"), Type.atom("Elixir.Version")],
							[Type.atom("build"), Type.atom("nil")],
							[Type.atom("major"), Type.atom("nil")],
							[Type.atom("minor"), Type.atom("nil")],
							[Type.atom("patch"), Type.atom("nil")],
							[Type.atom("pre"), Type.list([])],
						]),
						Type.list([
							Type.atom("major"),
							Type.atom("minor"),
							Type.atom("patch"),
						]),
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.tuple([
										Type.variablePattern("map_3"),
										Type.variablePattern("keys_4"),
									]),
								],
								guards: [],
								body: (context) => {
									return Type.tuple([
										Elixir_Map["merge/2"](
											context.vars.map_3,
											Type.map([[context.vars.key_1, context.vars.val_2]]),
										),
										Elixir_List["delete/2"](
											context.vars.keys_4,
											context.vars.key_1,
										),
									]);
								},
							},
						],
						context,
					),
				),
				Type.tuple([
					Type.variablePattern("map_5"),
					Type.variablePattern("keys_6"),
				]),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Interpreter.case(
				context.vars.keys_6,
				[
					{
						match: Type.list([]),
						guards: [],
						body: (context) => {
							return context.vars.map_5;
						},
					},
					{
						match: Type.matchPlaceholder(),
						guards: [],
						body: (context) => {
							return Erlang["error/1"](
								Elixir_ArgumentError["exception/1"](
									Type.bitstring([
										Type.bitstringSegment(
											Type.string(
												"the following keys must also be given when building ",
											),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string("struct "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](Type.atom("Elixir.Version")),
											{ type: "binary" },
										),
										Type.bitstringSegment(Type.string(": "), {
											type: "binary",
										}),
										Type.bitstringSegment(
											Elixir_Kernel["inspect/1"](context.vars.keys_6),
											{ type: "binary" },
										),
									]),
								),
							);
						},
					},
				],
				context,
			);
		},
	},
]);

Interpreter.defineElixirFunction("Version", "pre_to_string", 1, "private", [
	{
		params: (context) => [Type.list([])],
		guards: [],
		body: (context) => {
			return Type.bitstring("");
		},
	},
	{
		params: (context) => [Type.variablePattern("pre_0")],
		guards: [],
		body: (context) => {
			return Type.bitstring([
				Type.bitstringSegment(Type.string("-"), { type: "binary" }),
				Type.bitstringSegment(
					Elixir_Enum["map_join/3"](
						context.vars.pre_0,
						Type.bitstring("."),
						Type.anonymousFunction(
							1,
							[
								{
									params: (context) => [Type.variablePattern("int_1")],
									guards: [
										(context) => Erlang["is_integer/1"](context.vars.int_1),
									],
									body: (context) => {
										return Erlang["integer_to_binary/1"](context.vars.int_1);
									},
								},
								{
									params: (context) => [Type.variablePattern("string_2")],
									guards: [
										(context) => Erlang["is_binary/1"](context.vars.string_2),
									],
									body: (context) => {
										return context.vars.string_2;
									},
								},
							],
							context,
						),
					),
					{ type: "binary" },
				),
			]);
		},
	},
]);

Interpreter.defineElixirFunction("Version", "to_string", 1, "public", [
	{
		params: (context) => [
			Interpreter.matchOperator(
				Type.variablePattern("version_0"),
				Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Version")]]),
				context,
			),
		],
		guards: [],
		body: (context) => {
			Interpreter.matchOperator(
				Elixir_Version["pre_to_string/1"](
					Interpreter.dotOperator(context.vars.version_0, Type.atom("pre")),
				),
				Type.variablePattern("pre_1"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			Interpreter.matchOperator(
				Interpreter.case(
					Interpreter.matchOperator(
						Interpreter.dotOperator(context.vars.version_0, Type.atom("build")),
						Type.variablePattern("build_2"),
						context,
					),
					[
						{
							match: Type.variablePattern("x_3"),
							guards: [
								(context) =>
									Erlang["orelse/2"](
										(context) =>
											Erlang["=:=/2"](context.vars.x_3, Type.atom("false")),
										(context) =>
											Erlang["=:=/2"](context.vars.x_3, Type.atom("nil")),
										context,
									),
							],
							body: (context) => {
								return Type.atom("nil");
							},
						},
						{
							match: Type.matchPlaceholder(),
							guards: [],
							body: (context) => {
								return Type.bitstring([
									Type.bitstringSegment(Type.string("+"), { type: "binary" }),
									Type.bitstringSegment(
										Elixir_String_Chars["to_string/1"](context.vars.build_2),
										{ type: "binary" },
									),
								]);
							},
						},
					],
					context,
				),
				Type.variablePattern("build_4"),
				context,
			);
			Interpreter.updateVarsToMatchedValues(context);
			return Type.bitstring([
				Type.bitstringSegment(
					Elixir_String_Chars["to_string/1"](
						Interpreter.dotOperator(context.vars.version_0, Type.atom("major")),
					),
					{ type: "binary" },
				),
				Type.bitstringSegment(Type.string("."), { type: "binary" }),
				Type.bitstringSegment(
					Elixir_String_Chars["to_string/1"](
						Interpreter.dotOperator(context.vars.version_0, Type.atom("minor")),
					),
					{ type: "binary" },
				),
				Type.bitstringSegment(Type.string("."), { type: "binary" }),
				Type.bitstringSegment(
					Elixir_String_Chars["to_string/1"](
						Interpreter.dotOperator(context.vars.version_0, Type.atom("patch")),
					),
					{ type: "binary" },
				),
				Type.bitstringSegment(
					Elixir_String_Chars["to_string/1"](context.vars.pre_1),
					{ type: "binary" },
				),
				Type.bitstringSegment(
					Elixir_String_Chars["to_string/1"](context.vars.build_4),
					{ type: "binary" },
				),
			]);
		},
	},
]);

Interpreter.defineElixirFunction(
	"Version.Requirement",
	"__struct__",
	0,
	"public",
	[
		{
			params: (context) => [],
			guards: [],
			body: (context) => {
				return Type.map([
					[Type.atom("__struct__"), Type.atom("Elixir.Version.Requirement")],
					[Type.atom("lexed"), Type.atom("nil")],
					[Type.atom("source"), Type.atom("nil")],
				]);
			},
		},
	],
);

Interpreter.defineElixirFunction(
	"Version.Requirement",
	"__struct__",
	1,
	"public",
	[
		{
			params: (context) => [Type.variablePattern("kv_0")],
			guards: [],
			body: (context) => {
				return Elixir_Enum["reduce/3"](
					context.vars.kv_0,
					Type.map([
						[Type.atom("__struct__"), Type.atom("Elixir.Version.Requirement")],
						[Type.atom("lexed"), Type.atom("nil")],
						[Type.atom("source"), Type.atom("nil")],
					]),
					Type.anonymousFunction(
						2,
						[
							{
								params: (context) => [
									Type.tuple([
										Type.variablePattern("key_1"),
										Type.variablePattern("val_2"),
									]),
									Type.variablePattern("map_3"),
								],
								guards: [],
								body: (context) => {
									return Elixir_Map["merge/2"](
										context.vars.map_3,
										Type.map([[context.vars.key_1, context.vars.val_2]]),
									);
								},
							},
						],
						context,
					),
				);
			},
		},
	],
);

Interpreter.defineElixirFunction("WithClauseError", "__struct__", 0, "public", [
	{
		params: (context) => [],
		guards: [],
		body: (context) => {
			return Type.map([
				[Type.atom("__exception__"), Type.atom("true")],
				[Type.atom("__struct__"), Type.atom("Elixir.WithClauseError")],
				[Type.atom("term"), Type.atom("nil")],
			]);
		},
	},
]);

Interpreter.defineElixirFunction("WithClauseError", "__struct__", 1, "public", [
	{
		params: (context) => [Type.variablePattern("kv_0")],
		guards: [],
		body: (context) => {
			return Elixir_Enum["reduce/3"](
				context.vars.kv_0,
				Type.map([
					[Type.atom("__exception__"), Type.atom("true")],
					[Type.atom("__struct__"), Type.atom("Elixir.WithClauseError")],
					[Type.atom("term"), Type.atom("nil")],
				]),
				Type.anonymousFunction(
					2,
					[
						{
							params: (context) => [
								Type.tuple([
									Type.variablePattern("key_1"),
									Type.variablePattern("val_2"),
								]),
								Type.variablePattern("map_3"),
							],
							guards: [],
							body: (context) => {
								return Elixir_Map["merge/2"](
									context.vars.map_3,
									Type.map([[context.vars.key_1, context.vars.val_2]]),
								);
							},
						},
					],
					context,
				),
			);
		},
	},
]);

document.addEventListener("hologram:pageScriptLoaded", () => Hologram.run());

if (globalThis.Hologram.pageScriptLoaded) {
	document.dispatchEvent(new CustomEvent("hologram:pageScriptLoaded"));
}

console.debug(
	"Hologram: runtime script executed in",
	PerformanceTimer.diff(startTime),
);
